/**
 * Parse / assemble offer letter HTML for the non-technical template editor.
 * Preserves fixed layout (styles, salary tables, images) while exposing
 * intro, job responsibilities, body, terms, closing, signature, page break, and salary as editable regions.
 */

export const OFFER_PLACEHOLDERS = [
  { token: '{{CANDIDATE.NAME_ONLY}}', label: 'Candidate Name' },
  { token: '{{CANDIDATE.NAME}}', label: 'Candidate Name' },
  { token: '{{OFFER_DETAILS.OFFERED_DATE_FORMATTED}}', label: 'Offer Date' },
  { token: '{joiningDate}', label: 'Joining Date' },
  { token: '{reportingManager}', label: 'Reporting Manager' },
  { token: '{jobTitle}', label: 'Job Title' },
  { token: '{locations}', label: 'Location' },
  { token: '{{METADATA.IMAGES.HEADER}}', label: 'Header Image' },
  { token: '{{METADATA.IMAGES.LOGO}}', label: 'Logo' },
  { token: '{{METADATA.IMAGES.WATERMARK}}', label: 'Watermark' },
  { token: '{{METADATA.IMAGES.SIGNATURE}}', label: 'Signature Image' },
]

export const PAGE_BREAK_MARKER = '<!-- OFFER_PAGE_BREAK -->'
export const INTRO_PAGE_BREAK_MARKER = '<!-- OFFER_INTRO_PAGE_BREAK -->'
export const JOB_RESP_PAGE_BREAK_MARKER = '<!-- OFFER_JOB_RESP_PAGE_BREAK -->'
export const POLICIES_PAGE_BREAK_MARKER = '<!-- OFFER_POLICIES_PAGE_BREAK -->'
export const TERMS_PAGE_BREAK_MARKER = '<!-- OFFER_TERMS_PAGE_BREAK -->'
export const CLOSING_PAGE_BREAK_MARKER = '<!-- OFFER_CLOSING_PAGE_BREAK -->'
export const SIGNATURE_PAGE_BREAK_MARKER = '<!-- OFFER_SIGNATURE_PAGE_BREAK -->'

/**
 * Where the company stamp/signature block may appear.
 * Persisted as metadata.offerSignaturePlacement — string (legacy) or string[].
 * Missing / empty / legacy lastPage-only → first page + after Terms & Conditions.
 */
export const OFFER_SIGNATURE_PLACEMENT = {
  LAST_PAGE: 'lastPage',
  WITH_CLOSING: 'withClosing',
  AFTER_POLICIES: 'afterPolicies',
  AFTER_TERMS: 'afterTerms',
  AFTER_SALARY: 'afterSalary',
  EVERY_PAGE: 'everyPage',
}

/** @deprecated use DEFAULT_OFFER_SIGNATURE_LOCATIONS — kept as string for older callers */
export const DEFAULT_OFFER_SIGNATURE_PLACEMENT = OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING

export const DEFAULT_OFFER_SIGNATURE_LOCATIONS = [
  OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING,
  OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS,
]

/** Checkbox options for Offer Configure (multi-select). */
export const OFFER_SIGNATURE_PLACEMENT_OPTIONS = [
  {
    value: OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING,
    text: 'First page — with opening / closing text',
  },
  {
    value: OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS,
    text: 'After Terms & Conditions section',
  },
  {
    value: OFFER_SIGNATURE_PLACEMENT.AFTER_POLICIES,
    text: 'After Employment Policies section',
  },
  {
    value: OFFER_SIGNATURE_PLACEMENT.AFTER_SALARY,
    text: 'After Salary annexure',
  },
  {
    value: OFFER_SIGNATURE_PLACEMENT.LAST_PAGE,
    text: 'Also on a dedicated last page (optional extra page)',
  },
  {
    value: OFFER_SIGNATURE_PLACEMENT.EVERY_PAGE,
    text: 'Every page — small stamp above footer',
  },
]

const OFFER_SIGNATURE_LOCATION_SET = new Set(Object.values(OFFER_SIGNATURE_PLACEMENT))

/** HTML comment embeds one or more locations: lastPage or lastPage,withClosing */
const OFFER_SIGNATURE_PLACEMENT_MARKER_RE = /<!--\s*OFFER_SIGNATURE_PLACEMENT:([\w,]+)\s*-->/i

/**
 * HTML comment embeds employee acceptance config so PDF chrome can honor Offer Configure
 * toggles without metadata: withStamp,showName,showSignature,layout
 * e.g. <!-- OFFER_EMPLOYEE_SIGN:1,1,1,stacked -->
 */
const OFFER_EMPLOYEE_SIGN_MARKER_RE = /<!--\s*OFFER_EMPLOYEE_SIGN:([01]),([01]),([01]),(row|stacked)\s*-->/i

/** Embeds Configure Starts so generate/regenerate can apply them without metadata.offerLayout. */
const OFFER_PAGE_STARTS_MARKER_RE = /<!--\s*OFFER_PAGE_STARTS:([\w,=:]+)\s*-->/i
const OFFER_PAGE_STARTS_SECTION_KEYS = [
  'intro',
  'jobResponsibilities',
  'body',
  'terms',
  'closing',
  'salary',
]

/** Manual in-content page break (Quill insert). Survives sanitize → real PDF break. */
export const OFFER_MANUAL_PAGE_BREAK_MARKER = '<!-- OFFER_MANUAL_PAGE_BREAK -->'
export const OFFER_MANUAL_PAGE_BREAK_EDITOR_HTML = `${OFFER_MANUAL_PAGE_BREAK_MARKER}
<div class="offer-manual-page-break" contenteditable="false"><hr /><span>Page break</span></div>`
export const OFFER_MANUAL_PAGE_BREAK_PRINT_HTML = `${OFFER_MANUAL_PAGE_BREAK_MARKER}
<div class="offer-page-break offer-manual-page-break" style="page-break-before: always;"></div>`

/**
 * Employee Name / Signature line placement.
 * Persisted as metadata.offerEmployeeSignPlacement.
 * Default OFF — last-page Employee Name / Signature lines are not wanted.
 */
export const DEFAULT_OFFER_EMPLOYEE_SIGN_PLACEMENT = {
  withStamp: false,
  showName: false,
  showSignature: false,
  layout: 'row',
  locations: 'sameAsStamp',
}

export const OFFER_EMPLOYEE_SIGN_LAYOUT_OPTIONS = [
  { value: 'row', text: 'Same line (Name | Signature)' },
  { value: 'stacked', text: 'Stacked (Name above Signature)' },
]

export const MARKER_TAGS = {
  intro: ['<!-- OFFER_INTRO_START -->', '<!-- OFFER_INTRO_END -->'],
  jobResponsibilities: ['<!-- OFFER_JOB_RESP_START -->', '<!-- OFFER_JOB_RESP_END -->'],
  body: ['<!-- OFFER_BODY_START -->', '<!-- OFFER_BODY_END -->'],
  terms: ['<!-- OFFER_TERMS_START -->', '<!-- OFFER_TERMS_END -->'],
  closing: ['<!-- OFFER_CLOSING_START -->', '<!-- OFFER_CLOSING_END -->'],
  signature: ['<!-- OFFER_SIGNATURE_START -->', '<!-- OFFER_SIGNATURE_END -->'],
  salary: ['<!-- OFFER_SALARY_START -->', '<!-- OFFER_SALARY_END -->'],
}

/**
 * Legacy top-clear spacer (130px). Prefer splitting print-flow tables at breaks so
 * each page gets a repeating thead spacer — top-clear double-counts with thead and
 * caused blank pages (PDF 25 pp.3–4). Kept only as fallback when print-flow is absent.
 */
const PAGE_BREAK_TOP_CLEAR_HTML = '<div class="offer-page-top-clear" aria-hidden="true">&nbsp;</div>'
/** Forced page break between print-flow sections (no top-clear — next table thead clears letterhead). */
const PAGE_BREAK_HTML = '<div class="offer-page-break" style="page-break-before: always; break-before: page;"></div>'
/**
 * Named Starts: New page unit that sits BEFORE a stamped after-break table.
 * Must NOT carry page-break-before — the table is the only Chromium break.
 * Empty + force-break + table all breaking produced a blank A4.
 */
const PAGE_BREAK_UNIT_HTML = '<div class="offer-page-break offer-page-break-unit" style="page-break-before: auto; break-before: auto; height: 0; margin: 0; padding: 0; border: 0;"></div>'
const PDF_FORCE_BREAK_RE = /<div[^>]*class="[^"]*\boffer-pdf-force-break\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*/gi
const PAGE_BREAK_HTML_WITH_TOP_CLEAR = `${PAGE_BREAK_HTML}\n${PAGE_BREAK_TOP_CLEAR_HTML}`

/** Empty letterhead spacer only — never [\s\S]*? (that deleted Employment Policies in live preview). */
const OFFER_PAGE_TOP_CLEAR_EMPTY_RE = /<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>/i
/** Exact class token `content` — not `offer-content-padded`. */
const OFFER_CONTENT_WRAPPER_OPEN_RE = /<div\b([^>]*class\s*=\s*["'](?:[^"']*\s)?content(?:\s[^"']*)?["'][^>]*)>/i

const PAGE_BREAK_PATTERNS = [
  /<!--\s*OFFER_PAGE_BREAK\s*-->/i,
  // Page-break div + optional empty top-clear only (tight top-clear — no [\s\S]*?).
  /<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>(?:\s*<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>)?/i,
  /<div[^>]*class="[^"]*page-?break(?:er)?[^"]*"[^>]*>\s*<\/div>/i,
  /<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>/i,
  /<div[^>]*style="[^"]*break-before:\s*page[^"]*"[^>]*>\s*<\/div>/i,
  /<p[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/p>/i,
  /<p[^>]*style="[^"]*break-before:\s*page[^"]*"[^>]*>\s*<\/p>/i,
  // Standalone empty top-clear (spacer). Do not use [\s\S]*? — unclosed/mis-nested
  // top-clear previously swallowed OFFER_BODY / Employment Policies.
  OFFER_PAGE_TOP_CLEAR_EMPTY_RE,
]

const SALARY_HINT_PATTERN = /Salary\s+Structure|CONFIG\.SALARY_STRUCTURE|salary-table|class="[^"]*salary/i

const ANNEXURE_SALARY_PATTERNS = [
  /Annexure\s+A[:\s\-—]*\s*Salary\s+Structure/i,
  /Salary\s+Structure\s+for\s+(?:Minimum|Less\s+than)/i,
  /<h[1-6][^>]*>[\s\S]*?Annexure\s+A[\s\S]*?Salary\s+Structure[\s\S]*?<\/h[1-6]>/i,
  /<h[1-6][^>]*>[\s\S]*?Salary\s+Structure[\s\S]*?<\/h[1-6]>/i,
]

const TERMS_SECTION_PATTERNS = [
  /Terms\s+and\s+Conditions\s+(?:Of\s+)?Employment/i,
  /<h[1-6][^>]*>[\s\S]*?Terms\s+and\s+Conditions[\s\S]*?<\/h[1-6]>/i,
]

const CLOSING_TEXT_PATTERNS = [
  /Yours\s+(?:faithfully|sincerely)/i,
  /Best\s+regards/i,
  /Warm\s+regards/i,
  /Kind\s+regards/i,
]


/** Letter Content sections the user can Show / Hide in Offer Configure. */
export const OFFER_SECTION_KEYS = [
  'intro',
  'jobResponsibilities',
  'body',
  'terms',
  'salary',
  'closing',
]

export function defaultOfferSections() {
  return {
    intro: true,
    jobResponsibilities: true,
    body: true,
    terms: true,
    salary: true,
    closing: true,
  }
}

/**
 * Normalize persisted metadata.offerSections (booleans or { show } objects).
 * Missing keys default to shown so existing templates keep current behavior.
 * @param {object|null|undefined} raw
 * @returns {{ intro: boolean, jobResponsibilities: boolean, body: boolean, terms: boolean, closing: boolean }}
 */
export function normalizeOfferSections(raw) {
  const defaults = defaultOfferSections()
  if (!raw || typeof raw !== 'object') return { ...defaults }
  const result = { ...defaults }
  OFFER_SECTION_KEYS.forEach(key => {
    const value = raw[key]
    if (typeof value === 'boolean') {
      result[key] = value
    } else if (value && typeof value === 'object' && typeof value.show === 'boolean') {
      result[key] = value.show
    }
  })
  return result
}

export function isSectionVisible(fields, key) {
  const sections = (fields && fields.offerSections) || defaultOfferSections()
  return sections[key] !== false
}

/**
 * Reorderable body content sections (header/title/footer chrome stay fixed).
 * Persisted as metadata.offerSectionOrder: string[].
 */
/**
 * Default reading order for marked body sections.
 * Salary annexure, then Terms, then closing/signature (stamp last).
 */
export const OFFER_CONTENT_ORDER_KEYS = [
  'intro',
  'jobResponsibilities',
  'body',
  'salary',
  'terms',
  'closing',
  'signature',
]

/** Trailing block must stay in this relative order for PDF pagination. */
const OFFER_TRAILING_SECTION_ORDER = ['salary', 'terms', 'closing', 'signature']

export const OFFER_SECTION_ORDER_LABELS = {
  intro: 'Opening',
  jobResponsibilities: 'Job responsibilities',
  body: 'Employment policies',
  terms: 'Terms & conditions',
  closing: 'Closing',
  signature: 'Signature block',
  salary: 'Salary annexure',
}

export function defaultOfferSectionOrder() {
  return [...OFFER_CONTENT_ORDER_KEYS]
}

/**
 * Normalize persisted metadata.offerSectionOrder.
 * Unknown keys dropped; missing keys appended in default relative order.
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeOfferSectionOrder(raw) {
  const defaults = defaultOfferSectionOrder()
  if (!Array.isArray(raw) || !raw.length) return defaults
  const allowed = new Set(OFFER_CONTENT_ORDER_KEYS)
  const seen = new Set()
  const result = []
  raw.forEach(key => {
    if (typeof key !== 'string' || !allowed.has(key) || seen.has(key)) return
    seen.add(key)
    result.push(key)
  })
  defaults.forEach(key => {
    if (!seen.has(key)) result.push(key)
  })
  // Opening must stay before Employment Policies (body). Dragging Policies above
  // Opening made the letter look like the first page disappeared.
  const introIdx = result.indexOf('intro')
  const bodyIdx = result.indexOf('body')
  if (introIdx >= 0 && bodyIdx >= 0 && bodyIdx < introIdx) {
    result.splice(introIdx, 1)
    const newBodyIdx = result.indexOf('body')
    result.splice(newBodyIdx, 0, 'intro')
  }
  // Force Annexure → Terms → Closing → Signature regardless of legacy drag order.
  const head = result.filter(key => !OFFER_TRAILING_SECTION_ORDER.includes(key))
  const tail = OFFER_TRAILING_SECTION_ORDER.filter(key => result.includes(key))
  return [...head, ...tail]
}

/**
 * Move a section one step up or down in the order list.
 * @param {string[]|null|undefined} order
 * @param {string} key
 * @param {'up'|'down'} direction
 * @returns {string[]}
 */
export function moveOfferSectionOrder(order, key, direction) {
  const next = normalizeOfferSectionOrder(order)
  const idx = next.indexOf(key)
  if (idx < 0) return next
  const swapWith = direction === 'up' ? idx - 1 : idx + 1
  if (swapWith < 0 || swapWith >= next.length) return next
  const tmp = next[idx]
  next[idx] = next[swapWith]
  next[swapWith] = tmp
  return next
}

const SECTION_ORDER_PAGE_BREAK = {
  intro: INTRO_PAGE_BREAK_MARKER,
  jobResponsibilities: JOB_RESP_PAGE_BREAK_MARKER,
  body: POLICIES_PAGE_BREAK_MARKER,
  terms: TERMS_PAGE_BREAK_MARKER,
  closing: CLOSING_PAGE_BREAK_MARKER,
  signature: SIGNATURE_PAGE_BREAK_MARKER,
  salary: PAGE_BREAK_MARKER,
}

/** Remove stray page-break / top-clear markup from orphan fragments between sections. */
function stripOrphanPageBreakMarkup(fragment) {
  if (!fragment) return ''
  let result = String(fragment)
  SECTION_PAGE_BREAK_MARKERS.forEach(marker => {
    result = result.split(marker).join('')
  })
  result = result.split(PAGE_BREAK_MARKER).join('')
  PAGE_BREAK_PATTERNS.forEach(pattern => {
    result = result.replace(pattern, '')
  })
  return result
}

function isUserConfiguredPageBreakMarker(marker) {
  return /<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->/i.test(String(marker || ''))
}

/**
 * Collapse leftover wrapper breaks after flatten (nested after-break tables
 * become stacked empty `offer-page-break` divs — those caused 157 letterheads).
 * Do NOT merge distinct user-configured section markers (Starts: New page).
 */
export function collapseConsecutiveOfferPageBreaks(html) {
  if (!html) return html
  const unit = '(?:'
    + '(?:<!--\\s*OFFER_[A-Z_]*PAGE_BREAK\\s*-->\\s*)?'
    + '<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\\s*<\\/div>\\s*'
    + '(?:<div[^>]*class="[^"]*offer-pdf-force-break[^"]*"[^>]*>[\\s\\S]*?<\\/div>\\s*)?'
    + '(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\\s*(?:&nbsp;|&#160;|\\u00a0|\\s)*<\\/div>\\s*)?'
    + ')'
  const consecutive = new RegExp(`${unit}(?:\\s*${unit})+`, 'gi')
  return String(html).replace(consecutive, (match) => {
    const markers = match.match(/<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->/gi) || []
    const seen = new Set()
    const unique = []
    markers.forEach((marker) => {
      const key = String(marker).replace(/\s+/g, ' ').toUpperCase()
      if (seen.has(key)) return
      seen.add(key)
      unique.push(marker)
    })
    if (unique.length) {
      return unique.map(marker => `${marker}\n${PAGE_BREAK_HTML}\n`).join('')
    }
    return `${PAGE_BREAK_HTML}\n`
  })
}

/**
 * Locate a marked section plus its leading page-break block (when present).
 * @returns {{ start: number, end: number, unit: string }|null}
 */
function findSectionUnit(html, key) {
  const markers = MARKER_TAGS[key]
  if (!html || !markers) return null
  const [startMarker, endMarker] = markers
  const contentStart = html.indexOf(startMarker)
  if (contentStart < 0) return null
  const contentEndMarker = html.indexOf(endMarker, contentStart)
  if (contentEndMarker < 0) return null
  let start = contentStart
  const end = contentEndMarker + endMarker.length

  const pbMarker = SECTION_ORDER_PAGE_BREAK[key]
  const windowStart = Math.max(0, contentStart - 500)
  const before = html.slice(windowStart, contentStart)
  if (pbMarker) {
    const pbIdx = before.lastIndexOf(pbMarker)
    if (pbIdx >= 0) {
      const between = before.slice(pbIdx + pbMarker.length)
      if (/^\s*(?:<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*)?(?:<div[^>]*class="[^"]*offer-pdf-force-break[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i.test(between)) {
        start = windowStart + pbIdx
      }
    }
  }
  // Keep an immediately-adjacent forced break with the section (print or preview).
  if (start === contentStart) {
    const adjacent = before.match(
      /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?(?:<div[^>]*class="[^"]*\boffer-(?:preview-)?page-break\b[^"]*"[^>]*>\s*<\/div>\s*)(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i,
    )
    if (adjacent) start = contentStart - adjacent[0].length
  }

  return { start, end, unit: html.slice(start, end) }
}

/**
 * Reorder marked content sections in assembled HTML.
 * Header / title / greeting / footer stay put; only body content markers move.
 * Non-marked orphans in the section range (e.g. joining-date line, signature img)
 * are preserved before salary when possible.
 * @param {string} html
 * @param {string[]|null|undefined} order
 * @returns {string}
 */
export function applyOfferSectionOrder(html, order) {
  if (!html) return html
  const normalized = normalizeOfferSectionOrder(order)
  const units = []
  OFFER_CONTENT_ORDER_KEYS.forEach(key => {
    const found = findSectionUnit(html, key)
    if (found) units.push({ key, ...found })
  })
  if (units.length < 2) return html

  const byStart = [...units].sort((a, b) => a.start - b.start)
  // Overlapping units — refuse to reorder rather than corrupt HTML
  for (let i = 1; i < byStart.length; i += 1) {
    if (byStart[i].start < byStart[i - 1].end) {
      return html
    }
  }

  const desired = normalized.filter(key => units.some(u => u.key === key))
  const current = byStart.map(u => u.key)
  if (desired.join('|') === current.join('|')) return html

  const rangeStart = byStart[0].start
  const rangeEnd = byStart[byStart.length - 1].end

  // Refuse to reorder across `.content` boundary — that ejects Salary/Terms
  // after the letter body and page-wise preview drops them.
  const contentMatch = String(html).match(
    /<div\b[^>]*class\s*=\s*["'][^"']*\bcontent\b[^"']*["'][^>]*>/i,
  )
  if (contentMatch && contentMatch.index != null) {
    const contentOpenEnd = contentMatch.index + contentMatch[0].length
    const contentClose = findMatchingDivClose(html, contentOpenEnd)
    if (contentClose >= 0 && (rangeStart < contentOpenEnd || rangeEnd > contentClose)) {
      return html
    }
  }

  let orphans = ''
  let cursor = rangeStart
  byStart.forEach(unit => {
    if (unit.start > cursor) orphans += html.slice(cursor, unit.start)
    cursor = Math.max(cursor, unit.end)
  })
  if (cursor < rangeEnd) orphans += html.slice(cursor, rangeEnd)
  // Orphan page-break divs between moved sections create blank PDF pages.
  orphans = stripOrphanPageBreakMarkup(orphans).trim()
  // Stray closings in gaps close `.content` early and dump later sections
  // after the footer (OFF→ON salary toggle + live preview).
  if (/<\/div>/i.test(orphans)) {
    orphans = orphans.replace(/<\/?div\b[^>]*>/gi, ' ').replace(/\s+/g, ' ').trim()
  }
  if (!orphans.replace(/\s+/g, '')) orphans = ''

  const unitMap = Object.fromEntries(units.map(u => [u.key, u.unit]))
  const salaryIdx = desired.indexOf('salary')
  let ordered
  if (orphans && salaryIdx > 0) {
    // Keep joining-date / misc lines with the offer body, never after the salary break.
    const before = desired.slice(0, salaryIdx).map(k => unitMap[k]).join('\n')
    const after = desired.slice(salaryIdx).map(k => unitMap[k]).join('\n')
    ordered = `${before}\n${orphans}\n${after}`
  } else if (orphans) {
    ordered = `${desired.map(k => unitMap[k]).join('\n')}\n${orphans}`
  } else {
    ordered = desired.map(k => unitMap[k]).join('\n')
  }

  return `${html.slice(0, rangeStart)}${ordered}${html.slice(rangeEnd)}`
}

/** Snapshot of editable letter fields for round-trip when sections are omitted from HTML. */
export function extractOfferSectionDrafts(fields = {}) {
  return {
    intro: fields.intro || '',
    jobResponsibilities: fields.jobResponsibilities || '',
    body: fields.body || '',
    termsAndConditions: fields.termsAndConditions || '',
    closing: fields.closing || '',
    salaryStructure: fields.salaryStructure || '',
    signatoryName: sanitizeCompanySignatoryFieldValue(fields.signatoryName || ''),
    signatoryDesignation: sanitizeCompanySignatoryFieldValue(fields.signatoryDesignation || ''),
  }
}

/**
 * Restore Quill/field content for hidden sections from metadata.offerSectionDrafts.
 * Visible sections keep parsed HTML unless fillStaleSectionDrafts restores empty/stale drafts.
 * @param {object} fields
 * @param {object|null|undefined} drafts
 * @returns {object}
 */
export function applyOfferSectionDrafts(fields, drafts) {
  if (!fields || !drafts || typeof drafts !== 'object') return fields
  const sections = normalizeOfferSections(fields.offerSections)
  const next = { ...fields, offerSections: sections }

  if (!sections.intro && drafts.intro != null) next.intro = drafts.intro
  if (!sections.jobResponsibilities && drafts.jobResponsibilities != null) {
    next.jobResponsibilities = drafts.jobResponsibilities
  }
  if (!sections.body && drafts.body != null) next.body = drafts.body
  if (!sections.terms && drafts.termsAndConditions != null) {
    next.termsAndConditions = drafts.termsAndConditions
  }
  if (!sections.salary && drafts.salaryStructure != null) {
    next.salaryStructure = drafts.salaryStructure
  }
  if (!sections.closing) {
    if (drafts.closing != null) next.closing = drafts.closing
    if (drafts.signatoryName != null) next.signatoryName = drafts.signatoryName
    if (drafts.signatoryDesignation != null) {
      next.signatoryDesignation = drafts.signatoryDesignation
    }
  }
  // Never keep Employee Name / Signature lines inside company designation fields.
  next.signatoryName = sanitizeCompanySignatoryFieldValue(next.signatoryName)
  next.signatoryDesignation = sanitizeCompanySignatoryFieldValue(next.signatoryDesignation)
  return next
}

/** Plain-text length ignoring tags/whitespace — for draft vs parsed section comparison. */
function offerSectionPlainLen(value) {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, '')
    .trim()
    .length
}

/**
 * Heuristic parse can leak print-flow chrome / nested markers into fields.body when
 * OFFER_BODY markers are empty. That HTML shreds `.content` on re-assemble and drops
 * Employment Policies from page-wise live preview.
 *
 * Do NOT treat Starts: New page comments or a real <table> inside policies as broken
 * — flatten leftover </table> used to wipe the whole Policies section on Save.
 */
function isBrokenOfferSectionHtml(value) {
  const raw = String(value || '')
  if (!raw.trim()) return false
  // Starts: New page comments are valid Policies/section HTML — never "broken".
  const withoutPageBreaks = raw
    .replace(/<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->/gi, '')
    .replace(/<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>/gi, '')
  if (/<!--\s*OFFER_(?:BODY|INTRO|JOB_RESP|TERMS|CLOSING|SIGNATURE|SALARY)_(?:START|END)\s*-->/i.test(withoutPageBreaks)) {
    return true
  }
  if (/OFFER_PRINT_FLOW/i.test(withoutPageBreaks)) return true
  if (/offer-print-flow/i.test(withoutPageBreaks)) return true
  // Orphan closings that unbalance `.content` when upserted.
  if (/^\s*<\//.test(withoutPageBreaks)) return true
  if (/<\/div>\s*<!--\s*OFFER_(?:BODY|INTRO|JOB_RESP|TERMS|CLOSING|SIGNATURE|SALARY)_/i.test(withoutPageBreaks)) {
    return true
  }
  return false
}

/** Strip print-flow / nested-marker chrome so Policies text can still be persisted. */
function stripBrokenOfferSectionChrome(value) {
  let raw = String(value || '')
  raw = raw.replace(/<!--\s*OFFER_PRINT_FLOW_(?:START|END)\s*-->/gi, '')
  raw = raw.replace(/<table\b[^>]*class="[^"]*\boffer-print-flow\b[^"]*"[^>]*>/gi, '')
  raw = raw.replace(
    /<!--\s*OFFER_(?:BODY|INTRO|JOB_RESP|TERMS|CLOSING|SIGNATURE|SALARY)_(?:START|END)\s*-->/gi,
    '',
  )
  raw = raw.replace(/^\s*(?:<\/(?:td|tr|tbody|thead|tfoot|table|div)>\s*)+/i, '')
  return raw
}

/** Plain-text length inside a marked section (not whole HTML). */
function offerMarkedSectionPlain(html, markers) {
  const unit = extractBetweenMarkers(html, markers)
  return unit ? offerSectionPlainLen(unit.content) : 0
}

function offerMarkedBodyHasPolicies(html) {
  const unit = extractBetweenMarkers(html, MARKER_TAGS.body)
  return unit ? /Employment Policies and Provisions/i.test(String(unit.content || '')) : false
}

/**
 * Prefer section drafts when parsed HTML fields are empty, missing canonical
 * titles (e.g. Employment Policies), or polluted with print-flow chrome.
 * Used by Offer Configure hydrate, live preview assemble, and PDF generation.
 * @param {object} fields
 * @param {object|null|undefined} drafts
 * @param {string} [html]
 * @returns {object}
 */
export function fillStaleSectionDrafts(fields, drafts, html = '') {
  if (!fields || !drafts || typeof drafts !== 'object') return fields
  const next = { ...fields }

  if (drafts.intro != null && offerSectionPlainLen(drafts.intro) > 0 && !offerSectionPlainLen(next.intro)) {
    next.intro = drafts.intro
  }
  if (drafts.jobResponsibilities != null && !offerSectionPlainLen(next.jobResponsibilities)) {
    next.jobResponsibilities = drafts.jobResponsibilities
  }
  if (drafts.body != null) {
    const draftPlain = offerSectionPlainLen(drafts.body)
    const bodyPlain = offerSectionPlainLen(next.body)
    const markedPlain = offerMarkedSectionPlain(html, MARKER_TAGS.body)
    const draftHasPolicies = /Employment Policies and Provisions/i.test(String(drafts.body || ''))
    const parsedHasPolicies = /Employment Policies and Provisions/i.test(String(next.body || ''))
    const markedHasPolicies = offerMarkedBodyHasPolicies(html)
    const bodyHasPolicies = parsedHasPolicies || markedHasPolicies
    const bodyBroken = isBrokenOfferSectionHtml(next.body)
    // Restore draft when field body is empty/broken, or policies title exists only
    // in the draft (saved HTML markers emptied). Do not overwrite a non-broken
    // editor body just because markers in htmlTemplate are still empty.
    const shouldRestoreBody = draftPlain > 0 && (
      !bodyPlain
      || bodyBroken
      || (draftHasPolicies && !bodyHasPolicies && !markedPlain)
      || (draftHasPolicies && !bodyHasPolicies && !markedHasPolicies && bodyPlain < draftPlain)
    )
    if (shouldRestoreBody) {
      next.body = drafts.body
    } else if (bodyBroken && !draftPlain) {
      next.body = ''
    }
  }
  if (drafts.termsAndConditions != null) {
    const termsPlain = offerSectionPlainLen(next.termsAndConditions)
    const draftPlain = String(drafts.termsAndConditions || '')
    const draftHasTerms = /Terms and Conditions/i.test(draftPlain)
    const htmlHasTerms = /Terms and Conditions/i.test(String(next.termsAndConditions || ''))
    const termsBroken = isBrokenOfferSectionHtml(next.termsAndConditions)
    if (!termsPlain || termsBroken || (draftHasTerms && !htmlHasTerms)) {
      if (offerSectionPlainLen(drafts.termsAndConditions) > 0) {
        next.termsAndConditions = drafts.termsAndConditions
      } else if (termsBroken) {
        next.termsAndConditions = ''
      }
    }
  }
  if (drafts.closing != null && offerSectionPlainLen(drafts.closing) > 0 && !offerSectionPlainLen(next.closing)) {
    next.closing = drafts.closing
  }
  if (drafts.salaryStructure != null && offerSectionPlainLen(drafts.salaryStructure) > 0) {
    const fieldHasTable = /<table[^>]*class="[^"]*salary-table/i.test(String(next.salaryStructure || ''))
    const draftHasTable = /<table[^>]*class="[^"]*salary-table/i.test(String(drafts.salaryStructure || ''))
    const fieldTables = (String(next.salaryStructure || '').match(/<table[^>]*class="[^"]*salary-table/gi) || []).length
    const draftTables = (String(drafts.salaryStructure || '').match(/<table[^>]*class="[^"]*salary-table/gi) || []).length
    if (!offerSectionPlainLen(next.salaryStructure) || (!fieldHasTable && draftHasTable) || draftTables > fieldTables) {
      next.salaryStructure = drafts.salaryStructure
    }
  }
  if (drafts.signatoryName != null && !String(next.signatoryName || '').trim()) {
    next.signatoryName = sanitizeCompanySignatoryFieldValue(drafts.signatoryName)
  }
  if (drafts.signatoryDesignation != null && !String(next.signatoryDesignation || '').trim()) {
    next.signatoryDesignation = sanitizeCompanySignatoryFieldValue(drafts.signatoryDesignation)
  }
  next.signatoryName = sanitizeCompanySignatoryFieldValue(next.signatoryName)
  next.signatoryDesignation = sanitizeCompanySignatoryFieldValue(next.signatoryDesignation)
  return next
}

/** @deprecated alias — PDF path historically used this name. */
const fillStaleSectionDraftsForPdf = fillStaleSectionDrafts

/**
 * Re-insert Employment Policies from metadata draft when assemble/chrome dropped or emptied body (PDF 50).
 * @param {string} html
 * @param {object} fields
 * @param {object} metadata
 * @returns {string}
 */
function repairOfferBodyForPdfFromDraft(html, fields, metadata = {}) {
  if (!html || !isSectionVisible(fields, 'body')) return html
  const draft = metadata?.offerSectionDrafts?.body
  const bodySource = offerSectionPlainLen(fields.body) > 0 ? fields.body : draft
  if (!bodySource || !String(bodySource).trim()) return html

  const marked = extractBetweenMarkers(html, MARKER_TAGS.body)
  const markedPlain = marked ? offerSectionPlainLen(marked.content) : 0
  const markedHasPolicies = marked && /Employment Policies and Provisions/i.test(marked.content)
  if (marked && markedPlain > 0 && markedHasPolicies) return html

  let result = flattenOfferContentPrintFlow(html)
  result = upsertBodySection(result, bodySource, fields)
  return finalizeOfferPdfLayout(result, { ...fields, body: bodySource }, metadata)
}

/**
 * Re-insert the salary annexure when generate/PDF dropped tables but the section
 * is still ON. Uses field HTML, then metadata draft, then a default table.
 */
export function repairOfferSalaryForPdfFromDraft(html, fields, metadata = {}) {
  if (!html || !isSectionVisible(fields, 'salary')) return html
  const resolved = resolveRichestSalaryStructureHtml(fields, metadata, html)
  const htmlTables = (String(html).match(/<table[^>]*class="[^"]*salary-table/gi) || []).length
  const sourceTables = resolved ? resolved.tableCount : 0
  const hasTable = htmlTables > 0
  const markedPlain = offerMarkedSectionPlain(html, MARKER_TAGS.salary)
  const markedTables = (markedSalaryInnerHtml(html).match(/<table[^>]*class="[^"]*salary-table/gi) || []).length
  // Keep existing annexure only when every parsed/draft table is already inside markers.
  if (
    hasTable
    && markedPlain > 0
    && markedTables >= Math.max(1, sourceTables)
    && htmlTables >= sourceTables
    && !/\{\{\s*CONFIG\.SALARY_STRUCTURE/i.test(html)
  ) {
    return stripOrphanSalaryOutsideMarkers(html)
  }
  const salaryHtml = (resolved && resolved.html) || defaultSalaryStructureHtml()
  const parseResult = parseOfferTemplate(html)
  return upsertSalarySection(
    html,
    { ...fields, salaryStructure: salaryHtml },
    parseResult,
  )
}

function markedSalaryInnerHtml(html) {
  const start = String(html || '').indexOf(MARKER_TAGS.salary[0])
  const end = String(html || '').indexOf(MARKER_TAGS.salary[1], start + 1)
  if (start < 0 || end < 0) return ''
  return html.slice(start, end)
}

/** Prefer the salary source (draft / field / full HTML) that still has every structure. */
function resolveRichestSalaryStructureHtml(fields = {}, metadata = {}, html = '') {
  const candidates = [
    metadata && metadata.offerSectionDrafts ? metadata.offerSectionDrafts.salaryStructure : '',
    fields.salaryStructure,
    html,
  ]
  let best = null
  candidates.forEach((src) => {
    if (!src || !String(src).trim()) return
    const parsed = parseSalaryTableHtml(src)
    if (!parsed.parsed) return
    const tableCount = (parsed.data.structures || []).length
    const rows = (parsed.data.structures || []).reduce(
      (sum, block) => sum + ((block && block.rows) || []).length,
      0,
    )
    if (!best || tableCount > best.tableCount || (tableCount === best.tableCount && rows > best.rows)) {
      best = {
        tableCount,
        rows,
        html: buildSalaryTableHtml(parsed.data),
      }
    }
  })
  return best
}

/**
 * Flatten print-flow, enforce section order/joining, re-chrome for PDF output.
 * @param {string} html
 * @param {object} fields
 * @param {object} metadata
 * @returns {string}
 */
function finalizeOfferPdfLayout(html, fields, metadata = {}) {
  const layout = getOfferLayoutFromFields(fields)
  const chromeOpts = chromeOptionsFromFields(fields)
  const pdfFields = resolvePdfSectionFields(fields, metadata)
  const showSalary = isSectionVisible(pdfFields, 'salary')
  const salaryPageBreak = showSalary && resolveSectionPageBreakEnabled(
    layout,
    'salary',
    pdfFields.includePageBreak !== false,
  )
  const showBody = isSectionVisible(pdfFields, 'body')
  const showTerms = isSectionVisible(pdfFields, 'terms')
  const showClosing = isSectionVisible(pdfFields, 'closing')

  let result = /offer-print-flow/i.test(html) ? flattenOfferContentPrintFlow(html) : html
  result = ensureContentMarkers(result)
  const joiningBeforeResync = (() => {
    const m = String(result).match(/<p[^>]*>\s*Your date of joining will be[\s\S]*?<\/p>/i)
    return m ? m[0] : ''
  })()

  let parseResult = parseOfferTemplate(result)
  result = resyncMarkedSectionsForPdf(result, pdfFields, parseResult)

  result = applyOfferSectionOrder(result, pdfFields.offerSectionOrder)
  result = repairOfferSectionReadingOrder(result)
  result = stripTrailingPageBreaksInsideMarkedSection(result, MARKER_TAGS.body)
  if (joiningBeforeResync && !/<p[^>]*>\s*Your date of joining will be/i.test(result)) {
    const bodyEnd = result.indexOf(MARKER_TAGS.body[1])
    if (bodyEnd >= 0) {
      result = `${result.slice(0, bodyEnd)}\n${joiningBeforeResync}\n${result.slice(bodyEnd)}`
    }
  }
  result = ensureJoiningDateBeforeAnnexure(result)

  const sectionParseResult = (() => {
    const reparsed = parseHeuristic(result)
    if (reparsed.regions) return { ...parseResult, regions: reparsed.regions }
    return parseResult
  })()
  if (showBody) {
    result = upsertPoliciesPageBreak(result, sectionParseResult, showBody, layout)
  }
  result = upsertTermsPageBreak(
    result,
    showTerms,
    sectionParseResult,
    layout,
  )
  result = upsertClosingPageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertPageBreak(result, salaryPageBreak)
  result = assertAssembledBodyContent(result, pdfFields, layout, showBody)
  const reparseAfterOrder = (() => {
    const reparsed = parseHeuristic(result)
    if (reparsed.regions) return { ...parseResult, regions: reparsed.regions }
    return parseResult
  })()
  if (showBody) {
    result = upsertPoliciesPageBreak(result, reparseAfterOrder, showBody, layout)
  }
  result = upsertTermsPageBreak(
    result,
    showTerms,
    sectionParseResult,
    layout,
  )
  result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertPageBreak(result, salaryPageBreak)
  result = repairSectionPageBreakDivs(result)

  return finalizeAssembledOfferHtml(result, pdfFields, chromeOpts, layout)
}

function assertAssembledBodyContent(result, fields, layout, showBody) {
  if (!showBody || !offerSectionPlainLen(fields.body)) return result
  const markedPlain = offerMarkedSectionPlain(result, MARKER_TAGS.body)
  const fieldHasPolicies = /Employment Policies and Provisions/i.test(String(fields.body || ''))
  const markedHasPolicies = offerMarkedBodyHasPolicies(result)
  if (markedPlain > 0 && (!fieldHasPolicies || markedHasPolicies)) return result
  let next = upsertBodySection(result, fields.body, fields)
  const reparse = parseOfferTemplate(next)
  next = upsertPoliciesPageBreak(next, reparse, showBody, layout)
  return next
}

/** Split closing paragraphs that leaked into intro back out for the closing section (PDF 52/53). */
function splitIntroClosingBleed(introHtml, closingHint) {
  const intro = String(introHtml || '')
  const closingPhrases = [
    'Kindly reach out to your recruitment partner',
    'Please return the signed copy of this letter',
    'confirm your acceptance within',
    'confirm your acceptance of this offer',
    'reach out to your recruitment partner',
  ]
  const hintPlain = String(closingHint || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (hintPlain.length >= 16 && !/^Congratulations/i.test(hintPlain)) {
    closingPhrases.push(hintPlain.slice(0, 48))
  }

  const isClosingParagraph = (paragraphHtml) => {
    const plain = String(paragraphHtml || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (!plain) return false
    return closingPhrases.some(phrase => phrase && plain.includes(phrase))
  }

  const introParts = []
  const closingParts = []
  const paragraphRe = /<p[^>]*>[\s\S]*?<\/p>/gi
  let paragraphMatch = paragraphRe.exec(intro)
  while (paragraphMatch) {
    const paragraph = paragraphMatch[0]
    if (isClosingParagraph(paragraph)) closingParts.push(paragraph)
    else introParts.push(paragraph)
    paragraphMatch = paragraphRe.exec(intro)
  }

  if (!introParts.length && !closingParts.length) {
    return { intro: intro.trim(), closing: hintPlain.length >= 16 && !/^Congratulations/i.test(hintPlain) ? String(closingHint).trim() : '' }
  }

  const closingFromSplit = closingParts.join('\n')
  const hintLooksLikeClosing = hintPlain.length >= 16
    && !/^Congratulations/i.test(hintPlain)
    && closingPhrases.some(phrase => hintPlain.includes(phrase))
  const result = {
    intro: introParts.join('\n').trim() || intro.trim(),
    closing: closingFromSplit || (hintLooksLikeClosing ? String(closingHint).trim() : ''),
  }
  return result
}

/** Prefer Offer Configure drafts for PDF resync when saved HTML intro/closing merged. */
function resolvePdfSectionFields(fields, metadata = {}) {
  const drafts = metadata?.offerSectionDrafts || {}
  const next = { ...fields }
  const parsedIntroLen = offerSectionPlainLen(fields.intro)
  const draftIntroLen = offerSectionPlainLen(drafts.intro)
  if (drafts.intro != null && draftIntroLen > 0 && draftIntroLen >= parsedIntroLen) {
    next.intro = drafts.intro
  }
  const draftClosingLen = offerSectionPlainLen(drafts.closing)
  if (drafts.closing != null && draftClosingLen > 0) {
    next.closing = drafts.closing
  }
  if (drafts.termsAndConditions != null && offerSectionPlainLen(drafts.termsAndConditions) > 0) {
    next.termsAndConditions = drafts.termsAndConditions
  }
  if (next.intro && /Kindly reach out|confirm your acceptance within|Please return the signed copy/i.test(next.intro)) {
    const closingRef = offerSectionPlainLen(next.closing) > 0 ? next.closing : fields.closing
    const split = splitIntroClosingBleed(next.intro, closingRef)
    if (offerSectionPlainLen(split.intro) > 0) {
      next.intro = split.intro
    }
    if (offerSectionPlainLen(split.closing) > 0 && offerSectionPlainLen(next.closing) === 0) {
      next.closing = split.closing
    }
  }
  return next
}

/** Rewrite marked sections from resolved fields before reorder (PDF 52). */
function resyncMarkedSectionsForPdf(html, fields, parseResult) {
  let result = html
  if (offerSectionPlainLen(fields.intro)) {
    result = upsertIntroSection(result, fields.intro)
  }
  if (isSectionVisible(fields, 'jobResponsibilities') && offerSectionPlainLen(fields.jobResponsibilities)) {
    result = upsertJobResponsibilitiesSection(result, fields, parseResult)
  }
  if (offerSectionPlainLen(fields.body) || isSectionVisible(fields, 'body')) {
    result = upsertBodySection(result, fields.body, fields)
  }
  if (offerSectionPlainLen(fields.termsAndConditions)) {
    result = upsertTermsSection(result, fields, parseResult)
  }
  if (isSectionVisible(fields, 'salary')) {
    result = upsertSalarySection(result, fields, parseResult)
  }
  if (isSectionVisible(fields, 'closing') && offerSectionPlainLen(fields.closing)) {
    result = upsertClosingSection(result, fields)
  }
  if (isSectionVisible(fields, 'closing')) {
    result = upsertSignatureSection(result, fields)
  }
  return result
}

/** Configurable layout keys (typography + page breaks) stored in metadata.offerLayout. */
export const OFFER_LAYOUT_SECTION_KEYS = [
  'header',
  'title',
  'intro',
  'jobResponsibilities',
  'body',
  'terms',
  'closing',
  'signature',
  'salary',
  'footer',
]

export const OFFER_FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18]

export const OFFER_FONT_FAMILY_OPTIONS = [
  { value: 'Calibri', text: 'Calibri' },
  { value: 'Arial', text: 'Arial' },
  { value: 'Georgia', text: 'Georgia' },
  { value: 'Times New Roman', text: 'Times New Roman' },
]

/** Letter body sections — wrapper font-weight stays normal; bold is inline in Quill. */
const OFFER_LETTER_BODY_SECTION_KEYS = new Set([
  'intro',
  'jobResponsibilities',
  'body',
  'terms',
  'closing',
  'signature',
])

/** How a letter section begins relative to the previous page. */
export const OFFER_PAGE_START_MODE = {
  CONTINUE: 'continue',
  NEW_PAGE: 'newPage',
  NEW_PAGE_WITH_TITLE: 'newPageWithTitle',
}

export const OFFER_PAGE_START_MODE_OPTIONS = [
  { value: OFFER_PAGE_START_MODE.CONTINUE, text: 'Continue on same page' },
  { value: OFFER_PAGE_START_MODE.NEW_PAGE, text: 'Start on a new page (no title)' },
  { value: OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE, text: 'Start on a new page with section title' },
]

export const OFFER_TITLE_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
}

export const OFFER_TITLE_ALIGN_OPTIONS = [
  { value: OFFER_TITLE_ALIGN.LEFT, text: 'Left' },
  { value: OFFER_TITLE_ALIGN.CENTER, text: 'Center' },
  { value: OFFER_TITLE_ALIGN.RIGHT, text: 'Right' },
]

const OFFER_TITLE_ALIGN_SET = new Set(Object.values(OFFER_TITLE_ALIGN))

export const DEFAULT_SECTION_TITLE_TEXT = {
  jobResponsibilities: 'Job Responsibilities',
  body: 'Employment Policies and Provisions',
  terms: 'Terms and Conditions of Employment',
  salary: 'Annexure A — Salary Structure',
}

const OFFER_PAGE_START_MODE_SET = new Set(Object.values(OFFER_PAGE_START_MODE))

const OFFER_LAYOUT_CSS_MARKER = '/* OFFER_LAYOUT:v1 */'

const WRAPPED_LAYOUT_SECTION_KEYS = [
  'intro',
  'jobResponsibilities',
  'body',
  'terms',
  'closing',
  'signature',
  'salary',
]

export function defaultOfferLayout() {
  return {
    defaults: {
      fontSizePx: 10,
      fontWeight: 'normal',
      lineHeight: 1.4,
    },
    sections: {
      header: {
        fontSizePx: 10,
        fontWeight: 'bold',
        logoWidthPx: 80,
        showCompanyName: true,
      },
      title: { fontSizePx: 15, fontWeight: 'bold', titleText: 'OFFER LETTER', titleAlign: 'center' },
      intro: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: false,
        pageStartMode: OFFER_PAGE_START_MODE.CONTINUE,
      },
      // Offer body flows continuously after intro unless HR chooses a new page.
      jobResponsibilities: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: false,
        pageStartMode: OFFER_PAGE_START_MODE.CONTINUE,
        sectionTitleText: DEFAULT_SECTION_TITLE_TEXT.jobResponsibilities,
        sectionTitleAlign: 'center',
        sectionTitleFontSizePx: 11,
        sectionTitleFontWeight: 'bold',
      },
      body: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: false,
        pageStartMode: OFFER_PAGE_START_MODE.CONTINUE,
        sectionTitleText: DEFAULT_SECTION_TITLE_TEXT.body,
        sectionTitleAlign: 'center',
        sectionTitleFontSizePx: 11,
        sectionTitleFontWeight: 'bold',
      },
      terms: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: true,
        pageStartMode: OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE,
        sectionTitleText: DEFAULT_SECTION_TITLE_TEXT.terms,
        sectionTitleAlign: 'center',
        sectionTitleFontSizePx: 11,
        sectionTitleFontWeight: 'bold',
      },
      // Closing + signature share the final stamp page.
      closing: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: true,
        pageStartMode: OFFER_PAGE_START_MODE.NEW_PAGE,
      },
      signature: { fontSizePx: 10, fontWeight: 'normal', pageBreakBefore: false },
      salary: {
        fontSizePx: 10,
        fontWeight: 'normal',
        pageBreakBefore: true,
        pageStartMode: OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE,
        sectionTitleText: DEFAULT_SECTION_TITLE_TEXT.salary,
        sectionTitleAlign: 'center',
        sectionTitleFontSizePx: 11,
        sectionTitleFontWeight: 'bold',
      },
      footer: { fontSizePx: 8, fontWeight: 'normal' },
    },
  }
}

function clampFontSizePx(value, fallback = 10) {
  const num = Number(value)
  if (!Number.isFinite(num)) return fallback
  return Math.min(24, Math.max(6, Math.round(num)))
}

function normalizeFontWeight(value, fallback = 'normal') {
  const weight = String(value || fallback).trim().toLowerCase()
  if (weight === 'bold' || weight === '600' || weight === '700') return 'bold'
  return 'normal'
}

function normalizeTitleAlign(value, fallback = 'center') {
  const align = String(value || fallback).trim().toLowerCase()
  return OFFER_TITLE_ALIGN_SET.has(align) ? align : fallback
}

/** High-specificity selectors so body heading left-align rules do not override title alignment. */
function offerLetterTitleAlignSelectors() {
  return '.content h2.offer-letter-title, .content .offer-letter-title, h2.offer-letter-title, p.offer-letter-title, div.offer-letter-title, .offer-letter-title'
}

/** High-specificity selectors for auto section titles (Employment Policies, Annexure A, etc.). */
function offerSectionTitleAlignSelectors(sectionKey) {
  const scoped = `.offer-section-${sectionKey}`
  return [
    `${scoped} .offer-major-heading`,
    `${scoped} .offer-major-heading strong`,
    `.content ${scoped} .offer-major-heading`,
    `.content ${scoped} p.offer-major-heading`,
    `.offer-section ${scoped} .offer-major-heading`,
    `.offer-print-flow-body ${scoped} .offer-major-heading`,
    `${scoped} h1.offer-major-heading`,
    `${scoped} h2.offer-major-heading`,
    `${scoped} h3.offer-major-heading`,
    `${scoped} p.offer-major-heading`,
  ].join(', ')
}

function mergeOfferLetterTitleAlignAttrs(attrs, align = 'center') {
  const normalizedAlign = normalizeTitleAlign(align, 'center')
  let next = attrs || ''
  const alignDecl = `text-align:${normalizedAlign}`
  if (/\bstyle\s*=\s*(["'])([^"']*)\1/i.test(next)) {
    next = next.replace(/\bstyle\s*=\s*(["'])([^"']*)\1/i, (_, q, style) => {
      const cleaned = String(style)
        .replace(/text-align\s*:\s*[^;]+;?/gi, '')
        .replace(/;\s*;/g, ';')
        .trim()
        .replace(/^;|;$/g, '')
      const merged = `${cleaned ? `${cleaned}; ` : ''}${alignDecl}`
      return `style=${q}${merged}${q}`
    })
  } else {
    next = `${next} style="${alignDecl}"`
  }
  if (/\bclass\s*=\s*(["'])([^"']*)\1/i.test(next)) {
    next = next.replace(/\bclass\s*=\s*(["'])([^"']*)\1/i, (_, q, cls) => {
      const cleaned = cls
        .replace(/\bql-align-(?:left|center|right)\b/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      const alignClass = normalizedAlign === 'center'
        ? 'ql-align-center'
        : normalizedAlign === 'right'
          ? 'ql-align-right'
          : 'ql-align-left'
      return `class=${q}${cleaned ? `${cleaned} ` : ''}${alignClass}${q}`
    })
  }
  return next
}

/** Map pageStartMode ↔ pageBreakBefore + showSectionTitle flags. */
export function pageStartModeToLayoutFlags(mode) {
  switch (mode) {
    case OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE:
      return { pageBreakBefore: true, showSectionTitle: true }
    case OFFER_PAGE_START_MODE.NEW_PAGE:
      return { pageBreakBefore: true, showSectionTitle: false }
    default:
      return { pageBreakBefore: false, showSectionTitle: false }
  }
}

/** Derive pageStartMode from persisted layout (backward compatible with pageBreakBefore). */
export function derivePageStartMode(sectionLayout = {}, sectionKey = '') {
  if (sectionLayout.pageStartMode && OFFER_PAGE_START_MODE_SET.has(sectionLayout.pageStartMode)) {
    return sectionLayout.pageStartMode
  }
  const breakBefore = sectionLayout.pageBreakBefore
  if (breakBefore === false) return OFFER_PAGE_START_MODE.CONTINUE
  if (sectionLayout.showSectionTitle === false) return OFFER_PAGE_START_MODE.NEW_PAGE
  if (DEFAULT_SECTION_TITLE_TEXT[sectionKey]) return OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE
  return OFFER_PAGE_START_MODE.NEW_PAGE
}

export function applyPageStartModeToSection(section, mode) {
  const flags = pageStartModeToLayoutFlags(mode)
  return {
    ...section,
    pageStartMode: mode,
    pageBreakBefore: flags.pageBreakBefore,
    showSectionTitle: flags.showSectionTitle,
  }
}

/**
 * Normalize persisted metadata.offerLayout (merge with defaults; backward compatible).
 * @param {object|null|undefined} raw
 * @returns {object}
 */
export function normalizeOfferLayout(raw) {
  const defaults = defaultOfferLayout()
  if (!raw || typeof raw !== 'object') return JSON.parse(JSON.stringify(defaults))

  const next = JSON.parse(JSON.stringify(defaults))
  if (raw.defaults && typeof raw.defaults === 'object') {
    if (raw.defaults.fontSizePx != null) {
      next.defaults.fontSizePx = clampFontSizePx(raw.defaults.fontSizePx, next.defaults.fontSizePx)
    }
    if (raw.defaults.fontWeight != null) {
      next.defaults.fontWeight = normalizeFontWeight(raw.defaults.fontWeight, next.defaults.fontWeight)
    }
    if (raw.defaults.lineHeight != null) {
      const lh = Number(raw.defaults.lineHeight)
      if (Number.isFinite(lh) && lh >= 1 && lh <= 2.5) next.defaults.lineHeight = lh
    }
  }

  OFFER_LAYOUT_SECTION_KEYS.forEach(key => {
    const section = raw.sections && raw.sections[key]
    if (!section || typeof section !== 'object') return
    const target = next.sections[key] || {}
    if (section.fontSizePx != null) {
      target.fontSizePx = clampFontSizePx(section.fontSizePx, target.fontSizePx)
    }
    if (section.fontWeight != null) {
      target.fontWeight = normalizeFontWeight(section.fontWeight, target.fontWeight)
    }
    if (section.lineHeight != null) {
      const lh = Number(section.lineHeight)
      if (Number.isFinite(lh) && lh >= 1 && lh <= 2.5) target.lineHeight = lh
    }
    if (typeof section.pageBreakBefore === 'boolean') {
      target.pageBreakBefore = section.pageBreakBefore
    }
    if (section.logoWidthPx != null) {
      const width = Number(section.logoWidthPx)
      if (Number.isFinite(width) && width >= 40 && width <= 240) target.logoWidthPx = Math.round(width)
    }
    if (typeof section.showCompanyName === 'boolean') {
      target.showCompanyName = section.showCompanyName
    }
    if (typeof section.titleText === 'string' && section.titleText.trim()) {
      target.titleText = section.titleText.trim()
    }
    if (typeof section.sectionTitleText === 'string' && section.sectionTitleText.trim()) {
      target.sectionTitleText = section.sectionTitleText.trim()
    }
    if (section.sectionTitleAlign != null) {
      target.sectionTitleAlign = normalizeTitleAlign(section.sectionTitleAlign, target.sectionTitleAlign)
    }
    if (section.sectionTitleFontSizePx != null) {
      target.sectionTitleFontSizePx = clampFontSizePx(
        section.sectionTitleFontSizePx,
        target.sectionTitleFontSizePx,
      )
    }
    if (section.sectionTitleFontWeight != null) {
      target.sectionTitleFontWeight = normalizeFontWeight(
        section.sectionTitleFontWeight,
        target.sectionTitleFontWeight,
      )
    }
    if (section.titleAlign != null) {
      target.titleAlign = normalizeTitleAlign(section.titleAlign, target.titleAlign)
    }
    if (section.pageStartMode && OFFER_PAGE_START_MODE_SET.has(section.pageStartMode)) {
      target.pageStartMode = section.pageStartMode
    }
    if (typeof section.showSectionTitle === 'boolean') {
      target.showSectionTitle = section.showSectionTitle
    }
    next.sections[key] = target
  })

  OFFER_LAYOUT_SECTION_KEYS.forEach(key => {
    const target = next.sections[key] || {}
    const rawSection = raw && raw.sections && raw.sections[key]
    const hadExplicitMode = rawSection
      && rawSection.pageStartMode
      && OFFER_PAGE_START_MODE_SET.has(rawSection.pageStartMode)
    const mode = hadExplicitMode ? rawSection.pageStartMode : derivePageStartMode(target, key)
    const flags = pageStartModeToLayoutFlags(mode)
    target.pageStartMode = mode
    target.pageBreakBefore = flags.pageBreakBefore
    target.showSectionTitle = flags.showSectionTitle
    next.sections[key] = target
  })

  return next
}

/** Merge global defaults with a section override. */
export function resolveSectionLayout(layout, sectionKey) {
  const normalized = normalizeOfferLayout(layout)
  const base = normalized.defaults || {}
  const section = (normalized.sections && normalized.sections[sectionKey]) || {}
  const fallback = defaultOfferLayout().sections[sectionKey] || {}

  return {
    fontSizePx: section.fontSizePx ?? base.fontSizePx ?? fallback.fontSizePx ?? 10,
    fontWeight: normalizeFontWeight(section.fontWeight ?? base.fontWeight ?? fallback.fontWeight),
    lineHeight: section.lineHeight ?? base.lineHeight ?? fallback.lineHeight ?? 1.4,
    pageBreakBefore: typeof section.pageBreakBefore === 'boolean'
      ? section.pageBreakBefore
      : fallback.pageBreakBefore,
    pageStartMode: derivePageStartMode(section, sectionKey),
    showSectionTitle: typeof section.showSectionTitle === 'boolean'
      ? section.showSectionTitle
      : pageStartModeToLayoutFlags(derivePageStartMode(section, sectionKey)).showSectionTitle,
    sectionTitleText: section.sectionTitleText
      ?? fallback.sectionTitleText
      ?? DEFAULT_SECTION_TITLE_TEXT[sectionKey]
      ?? '',
    sectionTitleAlign: normalizeTitleAlign(
      section.sectionTitleAlign ?? fallback.sectionTitleAlign,
      'center',
    ),
    sectionTitleFontSizePx: section.sectionTitleFontSizePx
      ?? fallback.sectionTitleFontSizePx
      ?? 11,
    sectionTitleFontWeight: normalizeFontWeight(
      section.sectionTitleFontWeight ?? fallback.sectionTitleFontWeight,
      'bold',
    ),
    titleText: section.titleText ?? fallback.titleText ?? 'OFFER LETTER',
    titleAlign: normalizeTitleAlign(section.titleAlign ?? fallback.titleAlign, 'center'),
    logoWidthPx: section.logoWidthPx ?? fallback.logoWidthPx ?? 80,
    showCompanyName: typeof section.showCompanyName === 'boolean'
      ? section.showCompanyName
      : fallback.showCompanyName !== false,
  }
}

/**
 * Resolve signature page locations from metadata.
 * Prefers offerSignaturePages; falls back to offerSignaturePlacement (legacy string/array).
 * @param {object|string|string[]|null|undefined} metadataOrRaw
 * @returns {string[]}
 */
export function resolveOfferSignaturePagesFromMetadata(metadataOrRaw) {
  if (metadataOrRaw && typeof metadataOrRaw === 'object' && !Array.isArray(metadataOrRaw)) {
    if (metadataOrRaw.offerSignaturePages != null) {
      return normalizeOfferSignatureLocations(metadataOrRaw.offerSignaturePages)
    }
    return normalizeOfferSignatureLocations(metadataOrRaw.offerSignaturePlacement)
  }
  return normalizeOfferSignatureLocations(metadataOrRaw)
}

/**
 * Normalize persisted metadata.offerSignaturePages / offerSignaturePlacement to a location array.
 * Accepts legacy string, comma-string, string[], or { locations: string[] }.
 * @param {string|string[]|{locations?: string[]}|null|undefined} raw
 * @returns {string[]}
 */
export function normalizeOfferSignatureLocations(raw) {
  let list = []
  if (Array.isArray(raw)) {
    list = raw
  } else if (raw && typeof raw === 'object' && Array.isArray(raw.locations)) {
    list = raw.locations
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) list = []
    else if (trimmed === 'samePageAsClosing' || trimmed === 'firstPage' || trimmed === 'first') {
      list = [OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING]
    } else list = trimmed.split(/[,|]/).map(s => s.trim()).filter(Boolean)
  }
  const out = []
  list.forEach(item => {
    let key = String(item || '').trim()
    if (key === 'samePageAsClosing' || key === 'firstPage' || key === 'first') {
      key = OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING
    }
    if (!OFFER_SIGNATURE_LOCATION_SET.has(key)) return
    if (!out.includes(key)) out.push(key)
  })
  if (!out.length) return [...DEFAULT_OFFER_SIGNATURE_LOCATIONS]
  // Legacy implicit default was lastPage-only. That invented a leftover last
  // signature page (AS/HR + Employee Name/Signature). Product default is
  // first page + after Terms — lastPage-only is treated as that default.
  // Last page still works when combined with another location.
  if (out.length === 1 && out[0] === OFFER_SIGNATURE_PLACEMENT.LAST_PAGE) {
    return [...DEFAULT_OFFER_SIGNATURE_LOCATIONS]
  }
  return out
}

/**
 * Backward-compatible single-value normalizer.
 * When multiple locations are set, returns the "primary" for layout page-break flags:
 * lastPage > withClosing > afterTerms > first.
 * @param {string|string[]|{locations?: string[]}|null|undefined} raw
 * @returns {string}
 */
export function normalizeOfferSignaturePlacement(raw) {
  const locs = normalizeOfferSignatureLocations(raw)
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)) {
    return OFFER_SIGNATURE_PLACEMENT.LAST_PAGE
  }
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING)) {
    return OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING
  }
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS)) {
    return OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS
  }
  return locs[0] || OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING
}

export function offerSignatureHasLocation(placement, location) {
  return normalizeOfferSignatureLocations(placement).includes(location)
}

/**
 * Normalize metadata.offerEmployeeSignConfig / offerEmployeeSignPlacement.
 * @param {object|null|undefined} raw
 */
export function normalizeOfferEmployeeSignPlacement(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  const defaults = DEFAULT_OFFER_EMPLOYEE_SIGN_PLACEMENT
  const layout = (src.layout === 'stacked' || src.layout === 'stack') ? 'stacked' : 'row'
  let locations = src.locations
  if (locations !== 'sameAsStamp' && !Array.isArray(locations)) {
    locations = 'sameAsStamp'
  }
  if (Array.isArray(locations)) {
    locations = normalizeOfferSignatureLocations(locations)
  }
  return {
    withStamp: src.withStamp != null ? !!src.withStamp : defaults.withStamp,
    showName: src.showName != null ? !!src.showName : defaults.showName,
    showSignature: src.showSignature != null ? !!src.showSignature : defaults.showSignature,
    layout,
    locations,
  }
}

/** Alias — persisted key is offerEmployeeSignConfig. */
export function normalizeOfferEmployeeSignConfig(raw) {
  return normalizeOfferEmployeeSignPlacement(raw)
}

/**
 * Resolve employee sign config from metadata (new or legacy key).
 * @param {object|null|undefined} metadata
 */
export function resolveOfferEmployeeSignConfigFromMetadata(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {}
  return normalizeOfferEmployeeSignConfig(
    meta.offerEmployeeSignConfig != null
      ? meta.offerEmployeeSignConfig
      : meta.offerEmployeeSignPlacement,
  )
}

/**
 * Map signature placement → closing/signature pageBreakBefore flags.
 * Multi-select:
 * - lastPage (+ optional withClosing): dedicated last stamp page via closing break.
 *   withClosing also emits a page-1 stamp after opening (see applyMultiOfferSignaturePlacements).
 * - first page / after Terms (default): extras only — never invent a leftover last stamp page.
 * @param {object|null|undefined} layout
 * @param {string|string[]|null|undefined} placement
 * @returns {object}
 */
export function applyOfferSignaturePlacementToLayout(layout, placement) {
  const next = normalizeOfferLayout(layout)
  const locs = normalizeOfferSignatureLocations(placement)
  const wantsLastPage = locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)

  if (wantsLastPage) {
    // Dedicated last stamp page. Do not clear this when withClosing is also
    // selected — page-1 stamp is an EXTRA block after intro, not a layout flag.
    next.sections.closing.pageBreakBefore = true
    next.sections.signature.pageBreakBefore = false
    // Opening + page-1 stamp end on page 1; Employment Policies starts on page 2.
    next.sections.body.pageBreakBefore = true
  } else {
    // Stamp extras must not invent a leftover last stamp page. Honor the user's
    // Starts: New page on Closing — only force signature off.
    next.sections.signature.pageBreakBefore = false
  }
  return next
}

/**
 * Read placement from options or an HTML comment embedded in saved templates.
 * Explicit fallback (from Offer Configure / assemble) wins over a stale marker.
 */
export function detectOfferSignaturePlacement(html, fallback) {
  const hasExplicit = fallback != null && fallback !== ''
    && (Array.isArray(fallback)
      || typeof fallback === 'string'
      || (typeof fallback === 'object' && Array.isArray(fallback.locations)))
  if (hasExplicit) {
    return normalizeOfferSignatureLocations(fallback)
  }
  const match = String(html || '').match(OFFER_SIGNATURE_PLACEMENT_MARKER_RE)
  if (match && match[1]) return normalizeOfferSignatureLocations(match[1])
  return normalizeOfferSignatureLocations(fallback)
}

function buildOfferSignaturePlacementMarker(placement) {
  const locs = normalizeOfferSignatureLocations(placement)
  return `<!-- OFFER_SIGNATURE_PLACEMENT:${locs.join(',')} -->`
}

/** Embed / refresh placement marker so ensureOfferLetterChrome can respect it without metadata. */
export function upsertOfferSignaturePlacementMarker(html, placement) {
  if (!html) return html
  const marker = buildOfferSignaturePlacementMarker(placement)
  let result = String(html).replace(new RegExp(OFFER_SIGNATURE_PLACEMENT_MARKER_RE.source, 'gi'), '')
  if (/<body[^>]*>/i.test(result)) {
    return result.replace(/<body([^>]*)>/i, `<body$1>\n${marker}`)
  }
  return `${marker}\n${result}`
}

function buildOfferPageStartsMarker(layout) {
  const normalized = normalizeOfferLayout(layout)
  const parts = OFFER_PAGE_STARTS_SECTION_KEYS.map((key) => {
    const section = resolveSectionLayout(normalized, key)
    return `${key}=${section.pageStartMode || OFFER_PAGE_START_MODE.CONTINUE}`
  })
  return `<!-- OFFER_PAGE_STARTS:${parts.join(',')} -->`
}

export function parseOfferPageStartsMarker(html) {
  const match = String(html || '').match(OFFER_PAGE_STARTS_MARKER_RE)
  if (!match || !match[1]) return null
  const next = defaultOfferLayout()
  String(match[1]).split(',').forEach((pair) => {
    const [rawKey, rawMode] = String(pair).split('=')
    const key = String(rawKey || '').trim()
    const mode = String(rawMode || '').trim()
    if (!key || !next.sections[key] || !OFFER_PAGE_START_MODE_SET.has(mode)) return
    next.sections[key] = applyPageStartModeToSection(next.sections[key], mode)
  })
  next._source = 'marker'
  return next
}

export function upsertOfferPageStartsMarker(html, layout) {
  if (!html) return html
  const marker = buildOfferPageStartsMarker(layout)
  let result = String(html).replace(new RegExp(OFFER_PAGE_STARTS_MARKER_RE.source, 'gi'), '')
  if (/<body[^>]*>/i.test(result)) {
    return result.replace(/<body([^>]*)>/i, `<body$1>\n${marker}`)
  }
  return `${marker}\n${result}`
}

function buildOfferEmployeeSignMarker(config) {
  const emp = normalizeOfferEmployeeSignConfig(config)
  const flag = v => (v ? '1' : '0')
  return `<!-- OFFER_EMPLOYEE_SIGN:${flag(emp.withStamp)},${flag(emp.showName)},${flag(emp.showSignature)},${emp.layout} -->`
}

/** Parse embedded employee-sign marker; null when absent. */
export function parseOfferEmployeeSignConfigMarker(html) {
  const match = String(html || '').match(OFFER_EMPLOYEE_SIGN_MARKER_RE)
  if (!match) return null
  return normalizeOfferEmployeeSignConfig({
    withStamp: match[1] === '1',
    showName: match[2] === '1',
    showSignature: match[3] === '1',
    layout: match[4],
  })
}

/** Old implicit default was 1,1,1,row — that re-injected last-page employee lines. */
function isLegacyImplicitEmployeeSignOn(emp) {
  if (!emp || typeof emp !== 'object') return false
  return emp.withStamp === true
    && emp.showName === true
    && emp.showSignature === true
    && (emp.layout === 'row' || emp.layout == null || emp.layout === '')
}

/**
 * Read employee-sign config from options or an HTML comment embedded in saved templates.
 * Explicit object fallback (from Offer Configure / assemble) wins over a stale marker.
 * The old all-on marker is treated as unset so generate/regenerate stay line-free
 * unless Offer Configure explicitly passes a config object.
 */
export function detectOfferEmployeeSignConfig(html, fallback) {
  const hasExplicit = fallback != null && typeof fallback === 'object' && !Array.isArray(fallback)
  if (hasExplicit) {
    return normalizeOfferEmployeeSignConfig(fallback)
  }
  const fromMarker = parseOfferEmployeeSignConfigMarker(html)
  if (fromMarker && !isLegacyImplicitEmployeeSignOn(fromMarker)) {
    return fromMarker
  }
  return normalizeOfferEmployeeSignConfig(fallback)
}

/** Embed / refresh employee-sign marker for PDF chrome without metadata. */
export function upsertOfferEmployeeSignConfigMarker(html, config) {
  if (!html) return html
  const marker = buildOfferEmployeeSignMarker(config)
  let result = String(html).replace(new RegExp(OFFER_EMPLOYEE_SIGN_MARKER_RE.source, 'gi'), '')
  if (/<body[^>]*>/i.test(result)) {
    return result.replace(/<body([^>]*)>/i, `<body$1>\n${marker}`)
  }
  return `${marker}\n${result}`
}

/**
 * Remove all employee-acceptance blocks and legacy Employee Name/Signature paragraphs.
 * ensureOfferSignatureBlock re-applies the current config into signature sections.
 */
export function stripEmployeeAcceptanceMarkup(html) {
  if (!html) return html
  let next = String(html)
  next = next.replace(
    /<div[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance\b[^"']*["'][^>]*>[\s\S]*?<\/div>\s*/gi,
    '',
  )
  next = next
    .replace(/<p[^>]*>\s*Employee\s+Name\s*:[\s\S]*?<\/p>\s*/gi, '')
    .replace(/<p[^>]*>\s*Employee\s+Signature\s*:[\s\S]*?<\/p>\s*/gi, '')
    // Quill sometimes wraps labels in spans/strong inside the paragraph.
    .replace(/<p[^>]*>\s*(?:<(?:span|strong|b)[^>]*>\s*)*Employee\s+Name\s*:[\s\S]*?<\/p>\s*/gi, '')
    .replace(/<p[^>]*>\s*(?:<(?:span|strong|b)[^>]*>\s*)*Employee\s+Signature\s*:[\s\S]*?<\/p>\s*/gi, '')
  // Orphan row tables left over from older templates (no wrapper div).
  next = next.replace(
    /<table[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance-row\b[^"']*["'][^>]*>[\s\S]*?<\/table>\s*/gi,
    '',
  )
  // Strip employee lines that were folded into signatory-block via <br>.
  next = next.replace(
    /(<p[^>]*class\s*=\s*["'][^"']*\bsignatory-block\b[^"']*["'][^>]*>)([\s\S]*?)(<\/p>)/gi,
    (_, open, inner, close) => {
      const cleaned = String(inner)
        .replace(/(?:<br\s*\/?>|\n)\s*Employee\s+(?:Name|Signature)\s*:[^<]*/gi, '')
        .replace(/Employee\s+(?:Name|Signature)\s*:[^<]*/gi, '')
      return `${open}${cleaned}${close}`
    },
  )
  return next
}

/** Quill/editor HTML for inserting a visible page-break marker into section content. */
export function buildManualPageBreakEditorHtml() {
  return OFFER_MANUAL_PAGE_BREAK_EDITOR_HTML
}

/** Convert editor page-break markers into print page-break divs (idempotent). */
export function normalizeManualPageBreaksInHtml(html) {
  if (!html) return html
  let result = String(html)
  // Editor visible marker → print break
  result = result.replace(
    /(?:<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*\boffer-manual-page-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    (full) => {
      if (/offer-page-break/i.test(full) && /page-break-before/i.test(full)) {
        return full.includes(OFFER_MANUAL_PAGE_BREAK_MARKER)
          ? full
          : `${OFFER_MANUAL_PAGE_BREAK_MARKER}\n${full}`
      }
      return OFFER_MANUAL_PAGE_BREAK_PRINT_HTML
    },
  )
  // Orphan comment → print break
  result = result.replace(
    /<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->(?!\s*<div[^>]*offer-page-break)/gi,
    `${OFFER_MANUAL_PAGE_BREAK_PRINT_HTML}\n`,
  )
  return result
}

/** Convert print/manual page breaks back to the Quill-visible editor marker. */
export function manualPageBreaksForEditor(html) {
  if (!html) return html
  return String(html).replace(
    /(?:<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*\boffer-(?:manual-)?page-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    OFFER_MANUAL_PAGE_BREAK_EDITOR_HTML,
  )
}

export function getOfferLayoutFromFields(fields = {}) {
  let layout = normalizeOfferLayout(fields.offerLayout)
  const salaryBreak = resolveSectionLayout(layout, 'salary').pageBreakBefore
  if (fields.includePageBreak === false) {
    layout.sections.salary.pageBreakBefore = false
  } else if (fields.includePageBreak === true && salaryBreak !== false) {
    layout.sections.salary.pageBreakBefore = true
  }
  layout = applyOfferSignaturePlacementToLayout(layout, resolveFieldsSignaturePages(fields))
  const source = fields._offerLayoutSource
    || (fields.offerLayout && fields.offerLayout._source)
    || ''
  if (source) layout._source = source
  return layout
}

function resolveSectionPageBreakEnabled(layout, sectionKey, legacyActive) {
  const section = resolveSectionLayout(layout, sectionKey)
  if (section.pageBreakBefore === false) return false
  if (section.pageBreakBefore === true) return !!legacyActive
  return !!legacyActive
}

function cssFontWeight(weight) {
  return normalizeFontWeight(weight) === 'bold' ? 'bold' : 'normal'
}

function cssPx(value) {
  return `${clampFontSizePx(value)}px`
}

/** Build dynamic CSS from offerLayout (injected into assembled HTML). */
export function buildOfferLayoutCss(layout) {
  const normalized = normalizeOfferLayout(layout)
  const body = resolveSectionLayout(normalized, 'intro')
  const header = resolveSectionLayout(normalized, 'header')
  const title = resolveSectionLayout(normalized, 'title')
  const footer = resolveSectionLayout(normalized, 'footer')

  const sectionRules = WRAPPED_LAYOUT_SECTION_KEYS.map(key => {
    const section = resolveSectionLayout(normalized, key)
    const selector = `.offer-section-${key}`
    const rules = [
      `font-size: ${cssPx(section.fontSizePx)}`,
      `line-height: ${section.lineHeight}`,
    ]
    if (!OFFER_LETTER_BODY_SECTION_KEYS.has(key)) {
      rules.unshift(`font-weight: ${cssFontWeight(section.fontWeight)}`)
    } else {
      rules.unshift('font-weight: normal')
    }
    let block = `${selector} { ${rules.join('; ')}; }`
    if (key === 'salary') {
      block += `\n${selector} table, ${selector} th, ${selector} td { font-size: inherit; }`
    }
    return block
  }).join('\n')

  const hideCompanyRule = header.showCompanyName
    ? ''
    : '\n.header-section.offer-hide-company-name .company-name { display: none !important; }'

  return `${OFFER_LAYOUT_CSS_MARKER}
html, body {
  font-size: ${cssPx(body.fontSizePx)};
  line-height: ${body.lineHeight};
}
.header-section .company-name {
  font-size: ${cssPx(header.fontSizePx)};
  font-weight: ${cssFontWeight(header.fontWeight)};
}
.company-logo {
  width: ${header.logoWidthPx}px !important;
  height: auto;
}
${offerLetterTitleAlignSelectors()} {
  text-align: ${normalizeTitleAlign(title.titleAlign, 'center')} !important;
  font-size: ${cssPx(title.fontSizePx)} !important;
  font-weight: ${cssFontWeight(title.fontWeight)} !important;
  margin: 8px 0 14px !important;
  color: #333 !important;
  display: block !important;
  width: 100% !important;
}
.footer-section {
  font-size: ${cssPx(footer.fontSizePx)} !important;
  color: ${OFFER_FOOTER_COLOR} !important;
}
${sectionRules}
${offerContentFlowCss()}
${['jobResponsibilities', 'body', 'terms', 'salary'].map(key => {
    const section = resolveSectionLayout(normalized, key)
    const align = normalizeTitleAlign(section.sectionTitleAlign, 'center')
    const titleSize = cssPx(section.sectionTitleFontSizePx ?? 11)
    const titleWeight = cssFontWeight(section.sectionTitleFontWeight ?? 'bold')
    return `${offerSectionTitleAlignSelectors(key)} {
  text-align: ${align} !important;
  font-size: ${titleSize} !important;
  font-weight: ${titleWeight} !important;
}`
  }).join('\n')}
${hideCompanyRule}
${offerSalaryTableCompactCss()}
${OFFER_LAYOUT_CSS_MARKER}`
}

function wrapContentBetweenMarkers(html, [startMarker, endMarker], className) {
  const start = html.indexOf(startMarker)
  const end = html.indexOf(endMarker)
  if (start < 0 || end < 0 || end <= start) return html
  const contentStart = start + startMarker.length
  const inner = html.slice(contentStart, end).trim()
  if (new RegExp(`class="[^"]*\\b${className.split(' ').pop()}\\b`).test(inner.slice(0, 240))) {
    return html
  }
  const wrapped = `\n<div class="${className}">\n${inner}\n</div>\n`
  return html.slice(0, contentStart) + wrapped + html.slice(end)
}

function wrapOfferSectionBlocks(html) {
  if (!html) return html
  let result = html
  WRAPPED_LAYOUT_SECTION_KEYS.forEach(key => {
    const markers = MARKER_TAGS[key]
    if (!markers) return
    result = wrapContentBetweenMarkers(result, markers, `offer-section offer-section-${key}`)
  })
  return result
}

function upsertOfferLayoutCss(html, layout) {
  const cssBlock = buildOfferLayoutCss(layout)
  if (!html) return cssBlock
  const markerPattern = new RegExp(
    `${escapeRegExp(OFFER_LAYOUT_CSS_MARKER)}[\\s\\S]*?${escapeRegExp(OFFER_LAYOUT_CSS_MARKER)}`,
    'i',
  )
  if (markerPattern.test(html)) {
    return html.replace(markerPattern, cssBlock)
  }
  return injectCssIntoHtml(html, cssBlock)
}

function applyHeaderLayoutClasses(html, layout) {
  const header = resolveSectionLayout(layout, 'header')
  if (!html || header.showCompanyName !== false) return html
  if (/class="[^"]*\boffer-hide-company-name\b/i.test(html)) return html
  return html.replace(
    /(<div[^>]*class="[^"]*\bheader-section\b[^"]*")/i,
    match => match.replace(/class="/i, 'class="offer-hide-company-name '),
  )
}

/**
 * Apply typography + header options from offerLayout to assembled HTML.
 * @param {string} html
 * @param {object|null|undefined} layout
 * @returns {string}
 */
/**
 * Re-insert named Starts: New page units after title/chrome passes.
 * Only adds missing breaks (never removes) so last-page stamp overlay
 * and "Continue on same page" are not undone.
 */
function ensureLayoutPageBreaksPresent(html, layout) {
  if (!html || !layout) return html
  let result = String(html)
  const parsed = parseOfferTemplate(result)
  if (resolveSectionPageBreakEnabled(layout, 'intro', true)) {
    result = upsertIntroPageBreak(result, true, parsed, layout)
  }
  if (resolveSectionPageBreakEnabled(layout, 'jobResponsibilities', true)) {
    result = upsertJobResponsibilitiesPageBreak(result, true, parsed, layout)
  }
  if (resolveSectionPageBreakEnabled(layout, 'body', true)) {
    result = upsertPoliciesPageBreak(result, parsed, true, layout)
  }
  if (resolveSectionPageBreakEnabled(layout, 'terms', true)) {
    result = upsertTermsPageBreak(result, true, parsed, layout)
  }
  if (resolveSectionPageBreakEnabled(layout, 'closing', true)) {
    result = upsertClosingPageBreak(result, true, parsed, layout)
  }
  if (resolveSectionPageBreakEnabled(layout, 'salary', true)) {
    result = upsertPageBreak(result, true)
  } else {
    result = upsertPageBreak(result, false)
  }
  return repairSectionPageBreakDivs(result)
}

export function applyOfferLayoutToHtml(html, layout) {
  if (!html) return html
  const normalized = normalizeOfferLayout(layout)
  let result = wrapOfferSectionBlocks(html)
  result = wrapOfferKeepTogetherBlocks(result)
  result = applyHeaderLayoutClasses(result, normalized)
  result = applyOfferLetterTitleFromLayout(result, normalized)
  result = applySectionTitlesFromLayout(result, normalized)
  result = stripDuplicateOfferSectionTitles(result, collectLayoutSectionTitleMap(normalized))
  // Chrome stripDuplicate used to eat the break between a leftover title and
  // New page + title. Restore named breaks, then resplit print-flow so the
  // standalone offer-page-break sits *between* tables (Chromium ignores
  // page-break-before inside a table cell).
  result = ensureLayoutPageBreaksPresent(result, normalized)
  if (/offer-print-flow/i.test(result) && /offer-page-break|OFFER_[A-Z_]*PAGE_BREAK/i.test(result)) {
    result = ensureOfferPrintFlowSpacers(result)
  }
  result = upsertOfferLayoutCss(result, normalized)
  return result
}

export function emptyTemplateFields() {
  return {
    intro: '',
    jobResponsibilities: '',
    body: '',
    termsAndConditions: '',
    closing: '',
    signatoryName: '',
    signatoryDesignation: '',
    includePageBreak: true,
    salaryStructure: '',
    offerSections: defaultOfferSections(),
    offerSectionOrder: defaultOfferSectionOrder(),
    offerLayout: normalizeOfferLayout(null),
    // Canonical multi-select list. offerSignaturePages is written on save; when
    // absent at runtime, readers fall back to offerSignaturePlacement.
    offerSignaturePlacement: [...DEFAULT_OFFER_SIGNATURE_LOCATIONS],
    offerEmployeeSignPlacement: { ...DEFAULT_OFFER_EMPLOYEE_SIGN_PLACEMENT },
    offerEmployeeSignConfig: { ...DEFAULT_OFFER_EMPLOYEE_SIGN_PLACEMENT },
  }
}

/** Resolve signature page locations from editor fields (pages key preferred). */
export function resolveFieldsSignaturePages(fields = {}) {
  if (fields && fields.offerSignaturePages != null) {
    return normalizeOfferSignatureLocations(fields.offerSignaturePages)
  }
  return normalizeOfferSignatureLocations(fields && fields.offerSignaturePlacement)
}

/** Resolve employee sign config from editor fields (config key preferred). */
export function resolveFieldsEmployeeSignConfig(fields = {}) {
  if (fields && fields.offerEmployeeSignConfig != null) {
    return normalizeOfferEmployeeSignConfig(fields.offerEmployeeSignConfig)
  }
  return normalizeOfferEmployeeSignConfig(fields && fields.offerEmployeeSignPlacement)
}

export function emptyOfferImages() {
  return {
    header: '',
    logo: '',
    watermark: '',
    signature: '',
  }
}

/**
 * Resolve offer letter HTML from metadata (handles en/EN keys and legacy string html).
 * @param {object|string|null} metadata
 * @returns {string}
 */
export function extractOfferHtmlFromMetadata(metadata) {
  const parsed = typeof metadata === 'string'
    ? (() => {
      try {
        return JSON.parse(metadata)
      } catch (e) {
        return {}
      }
    })()
    : (metadata || {})

  const html = parsed.html
  if (!html) return ''
  if (typeof html === 'string') return html.trim()
  if (typeof html === 'object' && html !== null) {
    const fromLang = html.en || html.EN || html.En || html.hi || html.HI
    if (typeof fromLang === 'string' && fromLang.trim()) return fromLang.trim()
    const first = Object.values(html).find(value => typeof value === 'string' && value.trim())
    return first ? first.trim() : ''
  }
  return ''
}

/**
 * Resolve branding images from metadata (logo vs legacy companyLogo key).
 * @param {object|string|null} metadata
 * @returns {{ header: string, logo: string, watermark: string, signature: string }}
 */
/** Coerce a branding image field to a usable string URL / data-URI (or ''). */
function coerceOfferImageSrc(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || /\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/i.test(trimmed)) return ''
    return trimmed
  }
  if (value && typeof value === 'object') {
    const nested = value.url || value.src || value.href || value.data || value.base64 || ''
    if (typeof nested === 'string' && nested.trim()) {
      const trimmed = nested.trim()
      if (/^data:image\//i.test(trimmed) || /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
        return trimmed
      }
      // Bare base64 payload from some API shapes
      if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length > 64) {
        return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`
      }
    }
  }
  return ''
}

export function extractOfferImagesFromMetadata(metadata) {
  const parsed = typeof metadata === 'string'
    ? (() => {
      try {
        return JSON.parse(metadata)
      } catch (e) {
        return {}
      }
    })()
    : (metadata || {})

  let images = parsed.images || {}
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images)
    } catch (e) {
      images = {}
    }
  }

  // Top-level legacy keys some backends still return outside images{}
  // Configure UI stores stamp under images.signature; older rows used stamp /
  // companyStamp / company_stamp / signatoryImage, etc.
  const signature = coerceOfferImageSrc(
    images.signature
      || images.SIGNATURE
      || images.stamp
      || images.Stamp
      || images.companyStamp
      || images.company_stamp
      || images.companySignature
      || images.signatoryImage
      || images.signatory
      || parsed.signature
      || parsed.SIGNATURE
      || parsed.stamp
      || parsed.companyStamp
      || parsed.signatureSrc
      || (typeof parsed.signatureUrl === 'string' ? parsed.signatureUrl : ''),
  )

  return {
    header: coerceOfferImageSrc(images.header || images.HEADER || parsed.header),
    logo: coerceOfferImageSrc(
      images.logo || images.companyLogo || images.LOGO || parsed.logo || parsed.companyLogo,
    ),
    // Placeholder token is WATERMARK; DB/config may use either casing
    watermark: coerceOfferImageSrc(
      images.watermark || images.WATERMARK || images.Watermark || parsed.watermark,
    ),
    signature,
  }
}

const WATERMARK_PLACEHOLDER = '{{METADATA.IMAGES.WATERMARK}}'
const HEADER_PLACEHOLDER = '{{METADATA.IMAGES.HEADER}}'
const LOGO_PLACEHOLDER = '{{METADATA.IMAGES.LOGO}}'
/** Production two-tone banner (navy + teal, edge-to-edge) — used when metadata header image is missing. */
const DEFAULT_HEADER_BANNER_DATA_URI = 'data:image/svg+xml,'
  + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="991" height="47" viewBox="0 0 991 47" preserveAspectRatio="none">'
    + '<path fill="#1A1F5E" d="M0,0 H640 C700,0 700,47 640,47 H0 Z"/>'
    + '<rect fill="#1EB496" x="640" y="0" width="351" height="47"/>'
    + '</svg>',
  )

/** Content clearance around fixed letterhead (NOT @page margins — those break edge-to-edge headers). */
const OFFER_CONTENT_PAD_TOP = '130px'
const OFFER_CONTENT_PAD_RIGHT = '35px'
/** Extra bottom clearance so body text never sits under the fixed footer. */
const OFFER_CONTENT_PAD_BOTTOM = '72px'
const OFFER_CONTENT_PAD_LEFT = '35px'
const OFFER_CONTENT_PADDING = `${OFFER_CONTENT_PAD_TOP} ${OFFER_CONTENT_PAD_RIGHT} ${OFFER_CONTENT_PAD_BOTTOM} ${OFFER_CONTENT_PAD_LEFT}`
/** @deprecated kept as aliases for any external references to old margin constant names */
const OFFER_PAGE_MARGIN_TOP = OFFER_CONTENT_PAD_TOP
const OFFER_PAGE_MARGIN_RIGHT = OFFER_CONTENT_PAD_RIGHT
const OFFER_PAGE_MARGIN_BOTTOM = OFFER_CONTENT_PAD_BOTTOM
const OFFER_PAGE_MARGIN_LEFT = OFFER_CONTENT_PAD_LEFT
const OFFER_PAGE_MARGIN = OFFER_CONTENT_PADDING
const OFFER_BODY_FONT_SIZE = '10px'
const OFFER_BODY_LINE_HEIGHT = '1.4'
const OFFER_LETTER_TITLE_FONT_SIZE = '15px'
const OFFER_FOOTER_FONT_SIZE = '8px'
/** Darker than legacy #ADAEB3 so footer remains readable on white. */
const OFFER_FOOTER_COLOR = '#6B6C70'
const OFFER_COMPANY_NAME_FONT_SIZE = '10px'
const OFFER_LOGO_WIDTH = '80px'

const OFFER_LETTER_TITLE_TEXT = 'OFFER LETTER'
const OFFER_LETTER_TITLE_HTML = `<h2 class="offer-letter-title">${OFFER_LETTER_TITLE_TEXT}</h2>`
const OFFER_LETTER_TITLE_CSS = `${offerLetterTitleAlignSelectors()} { text-align: center !important; font-size: ${OFFER_LETTER_TITLE_FONT_SIZE} !important; font-weight: bold !important; margin: 8px 0 14px; color: #333; display: block; width: 100%; text-transform: uppercase; }`
const OFFER_LETTER_TITLE_TEXT_RE = /^Offer\s+Letter$/i
const WATERMARK_CSS = '.watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.04; z-index: 0; pointer-events: none; max-width: 55%; height: auto; }'
  + ' img.watermark[src=""], img.watermark:not([src]) { display: none !important; }'
const WATERMARK_IMG = `<img src="${WATERMARK_PLACEHOLDER}" class="watermark" alt="" aria-hidden="true" width="500" height="500" style="opacity:0.04" />`

const DEFAULT_FOOTER_HTML = `<div class="footer-section"><div class="footer-line footer-company">SATTVA HUMAN PRIVATE LIMITED</div><div class="footer-line footer-contact">www.sattvahuman.com | hr@sattvahuman.com 3rd Floor, Ruia Chambers, Amanora Park Town, Hadapsar, Pune, Maharashtra</div></div>`

/** Chromium print: thead/tfoot repeat on every page when box-decoration-break:clone fails. */
const OFFER_PRINT_FLOW_START = '<!-- OFFER_PRINT_FLOW_START -->'
const OFFER_PRINT_FLOW_END = '<!-- OFFER_PRINT_FLOW_END -->'

/**
 * Chrome print letterhead strategy (HeadlessChrome / Puppeteer):
 * - @page { margin: 0 } so fixed header at top:0 is page-edge (banner y=0, full width)
 * - position:fixed; top:0; left:0; width:100% — NEVER negative top/left/transform/margin
 *   (negative offsets into @page margin boxes land the header at ~y=703 — PDF 15)
 * - NEVER keep @page { margin: 130px 35px … } with top:0 — that parks the banner at
 *   ~y=97pt with ~26pt side inset (PDF 16 / “header going down”)
 * - Body clearance via content/body padding + box-decoration-break:clone (repeats each page)
 * - Backend page.pdf() must also use margin:0 + preferCSSPageSize (non-zero pdf margins
 *   reproduce the PDF 16 layout even when CSS @page is correct)
 */
const HEADER_SECTION_INLINE_STYLE = 'position:fixed;top:0;left:0;width:100%;margin:0;padding:0;z-index:999'
const HEADER_IMAGE_INLINE_STYLE = 'width:100%;margin:0;padding:0;line-height:0;font-size:0'

const DEFAULT_HEADER_HTML = `<div class="header-section" style="${HEADER_SECTION_INLINE_STYLE}"><div class="header-image" style="${HEADER_IMAGE_INLINE_STYLE}"><img src="${HEADER_PLACEHOLDER}" alt="Header Image" style="width:100%;max-width:100%;height:auto;display:block;margin:0!important;padding:0!important;border:0!important;vertical-align:top" /></div><div class="company-info"><div class="company-logo-section"><img src="${LOGO_PLACEHOLDER}" alt="Company Logo" class="company-logo" /><div class="company-name">SATTVA HUMAN PRIVATE LIMITED</div></div></div></div>`

function offerPageMarginRule() {
  return '@page { margin: 0; }'
}

function offerFragmentPaddingExtras() {
  // Do not use box-decoration-break:clone here. Combined with vertical padding
  // it makes Chromium print invent tens/hundreds of blank letterhead pages.
  return 'box-sizing: border-box;'
}

function offerBodyChromePaddingRule() {
  return `body.offer-chrome-padded { margin: 0 !important; padding: ${OFFER_CONTENT_PADDING} !important; ${offerFragmentPaddingExtras()} }`
}

function offerHeaderSectionCss() {
  return '.header-section { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; z-index: 999; background: white; margin: 0 !important; padding: 0 !important; overflow: visible; box-sizing: border-box; }'
}

function offerHeaderImageCss() {
  return `.header-image { width: 100% !important; margin: 0 !important; padding: 0; display: block; line-height: 0; font-size: 0; overflow: visible; }`
}

function offerHeaderImageImgCss() {
  return '.header-image img { width: 100%; max-width: 100%; max-height: 96px; height: auto; object-fit: cover; object-position: top left; display: block; margin: 0 !important; padding: 0 !important; border: 0 !important; vertical-align: top; }'
}

function offerCompanyInfoCss() {
  // Inset logo/name to match content side padding (banner stays full-bleed above).
  return `.company-info { width: auto; padding: 8px ${OFFER_CONTENT_PAD_LEFT} 6px; background: white; margin: 0; }`
}

function offerCompanyLogoSectionCss() {
  return '.company-logo-section { display: flex; flex-direction: column; align-items: flex-start; min-width: 160px; }'
}

function offerCompanyLogoCss() {
  return `.company-logo { width: ${OFFER_LOGO_WIDTH}; height: auto; margin-bottom: 4px; }`
}

function offerCompanyNameCss() {
  return `.company-name { font-size: ${OFFER_COMPANY_NAME_FONT_SIZE}; font-weight: bold; color: #333; margin: 0; text-transform: uppercase; }`
}

/** Hide logo/name row when a real banner image is present (avoids duplicate letterhead). */
function offerHeaderDedupeCss() {
  return `
.header-section:has(.header-image img[src^="http"]) .company-info,
.header-section:has(.header-image img[src^="https"]) .company-info,
.header-section:has(.header-image img[src^="data:"]) .company-info,
.header-section:has(.header-image img[src^="/"]) .company-info {
  display: none !important;
  height: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
}`
}

/** Compact Annexure A salary tables — tighter rows and headings for PDF fit. */
function offerSalaryTableCompactCss() {
  return `
table.salary-table {
  font-size: 9px !important;
  line-height: 1.25;
  width: 80% !important;
  max-width: 80%;
  table-layout: fixed;
  margin: 4px auto 8px;
  margin-left: auto !important;
  margin-right: auto !important;
  display: table !important;
  float: none !important;
  position: relative;
}
table.salary-table th,
table.salary-table td {
  font-size: 9px !important;
  line-height: 1.25;
  padding: 2px 5px !important;
  vertical-align: middle;
}
table.salary-table th {
  font-size: 9px !important;
  font-weight: 600;
}
table.salary-table th:nth-child(2),
table.salary-table th:nth-child(3),
table.salary-table td:nth-child(2),
table.salary-table td:nth-child(3) {
  white-space: nowrap;
  width: 24%;
}
table.salary-table th:first-child,
table.salary-table td:first-child {
  width: 52%;
}
.content p.salary-structure-title,
.offer-section p.salary-structure-title,
.offer-print-flow-body p.salary-structure-title,
.offer-section-salary p.salary-structure-title,
.offer-section-salary p.salary-structure-title.offer-major-heading,
.content .offer-section-salary p.salary-structure-title.offer-major-heading,
.offer-section-salary p.salary-structure-title.offer-major-heading strong {
  margin: 0.4em 0 0.15em;
  font-size: 10px !important;
  text-align: center !important;
}
.content p.salary-structure-title + table.salary-table,
.offer-section p.salary-structure-title + table.salary-table,
.offer-print-flow-body p.salary-structure-title + table.salary-table {
  margin-top: 2px;
}
.offer-major-heading + table.salary-table {
  margin-top: 2px;
}
/* Notes under Annexure A: same 80% width as the table, centered as a block. */
.offer-section-salary > p:not(.offer-major-heading):not(.salary-structure-title),
.offer-section-salary table.salary-table ~ p:not(.offer-major-heading):not(.salary-structure-title),
.content .offer-section-salary > p:not(.offer-major-heading):not(.salary-structure-title),
.offer-print-flow-body .offer-section-salary > p:not(.offer-major-heading):not(.salary-structure-title),
.offer-preview-page-body .offer-section-salary > p:not(.offer-major-heading):not(.salary-structure-title),
p.salary-structure-note {
  width: 80% !important;
  max-width: 80%;
  margin-left: auto !important;
  margin-right: auto !important;
  box-sizing: border-box;
}`
}

function offerFooterSectionCss() {
  return `.footer-section { position: fixed !important; bottom: 0 !important; left: 0 !important; width: 100% !important; font-size: ${OFFER_FOOTER_FONT_SIZE}; line-height: 1.4; padding: 8px ${OFFER_CONTENT_PAD_LEFT}; color: ${OFFER_FOOTER_COLOR}; background: white; z-index: 999; text-align: center; margin: 0 !important; box-sizing: border-box; }`
}

function offerFooterLineCss() {
  return '.footer-line { display: block; margin: 0; } .footer-company { font-weight: bold; margin-bottom: 2px; }'
}

function offerContentCss() {
  // Side padding always; vertical clearance comes from print-flow thead/tfoot spacers
  // (one table per forced page section so thead repeats on natural continuation pages).
  return `.content { margin: 0; padding: 0 ${OFFER_CONTENT_PAD_RIGHT} 0 ${OFFER_CONTENT_PAD_LEFT}; ${offerFragmentPaddingExtras()} position: relative; z-index: 1; color: #333; }`
    + ` .content:not(:has(.offer-print-flow)) { padding: ${OFFER_CONTENT_PADDING}; }`
}

/** Keep section headings with following bullets; readable paragraph + Quill blank-line spacers. */
function offerContentFlowCss() {
  return `
.offer-print-flow { width: 100%; border-collapse: collapse; border-spacing: 0; table-layout: fixed; page-break-inside: auto !important; break-inside: auto !important; }
.offer-print-flow > tbody, .offer-print-flow > tbody > tr, .offer-print-flow-body { page-break-inside: auto !important; break-inside: auto !important; position: relative; z-index: 1; color: #333; }
.offer-print-flow > thead { display: table-header-group !important; }
.offer-print-flow > tfoot { display: table-footer-group !important; }
.offer-print-flow > thead > tr > td,
.offer-print-flow > tfoot > tr > td,
.offer-print-flow > tbody > tr > td {
  padding: 0;
  border: 0 !important;
  vertical-align: top;
  background: transparent;
}
.offer-print-spacer-top {
  display: block;
  height: ${OFFER_CONTENT_PAD_TOP};
  min-height: ${OFFER_CONTENT_PAD_TOP};
  line-height: ${OFFER_CONTENT_PAD_TOP};
  font-size: 0;
  overflow: hidden;
}
.offer-print-spacer-bottom {
  display: block;
  height: ${OFFER_CONTENT_PAD_BOTTOM};
  min-height: ${OFFER_CONTENT_PAD_BOTTOM};
  line-height: 0;
  font-size: 0;
  overflow: hidden;
}
.offer-print-flow > thead > tr > td { height: ${OFFER_CONTENT_PAD_TOP}; }
/* Forced page-break chunks: in-body clear when thead collapses after a break (PDF 21–31). */
.offer-print-chunk-clear {
  display: block !important;
  height: ${OFFER_CONTENT_PAD_TOP} !important;
  min-height: ${OFFER_CONTENT_PAD_TOP} !important;
  line-height: ${OFFER_CONTENT_PAD_TOP} !important;
  font-size: 0 !important;
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  visibility: hidden !important;
}
/*
 * Keep thead clearance on after-break tables so natural continuation pages
 * (e.g. policies page 3) still clear the fixed letterhead. Zeroing thead
 * (older v7) hid Employment Policies / body text under the header (PDF 31).
 * chunk-clear still backs up the first page when thead collapses after a break.
 */
.offer-print-flow-after-break > thead .offer-print-spacer-top {
  height: ${OFFER_CONTENT_PAD_TOP} !important;
  min-height: ${OFFER_CONTENT_PAD_TOP} !important;
  line-height: ${OFFER_CONTENT_PAD_TOP} !important;
}
.offer-print-flow-after-break > thead > tr > td {
  height: ${OFFER_CONTENT_PAD_TOP} !important;
}
.content p:not(.offer-major-heading), .offer-section p:not(.offer-major-heading), .offer-print-flow-body p:not(.offer-major-heading) { margin: 0 0 0.7em; text-align: left; }
.content p.salary-structure-title, .offer-section p.salary-structure-title, .offer-print-flow-body p.salary-structure-title {
  margin: 1em 0 0.35em;
  font-weight: bold;
  text-align: center !important;
  page-break-after: avoid;
  break-after: avoid-page;
}
.content p.salary-structure-title + table.salary-table,
.offer-section p.salary-structure-title + table.salary-table,
.offer-print-flow-body p.salary-structure-title + table.salary-table { margin-top: 2px; }
${offerSalaryTableCompactCss()}
${offerHeaderDedupeCss()}
.content p.offer-spacer, .offer-section p.offer-spacer, .offer-print-flow-body p.offer-spacer {
  margin: 0;
  padding: 0;
  min-height: 0.9em;
  line-height: 0.9em;
  font-size: 1em;
  height: 0.9em;
  overflow: hidden;
}
.content ul, .content ol, .offer-section ul, .offer-section ol,
.offer-print-flow-body ul, .offer-print-flow-body ol { margin: 0.35em 0 0.7em; padding-left: 1.4em; text-align: left; }
.content li, .offer-section li, .offer-print-flow-body li { margin: 0.15em 0; page-break-inside: avoid; break-inside: avoid; text-align: left; }
.content h1:not(.offer-letter-title):not(.offer-major-heading), .content h2:not(.offer-letter-title):not(.offer-major-heading), .content h3:not(.offer-letter-title):not(.offer-major-heading), .content h4:not(.offer-letter-title):not(.offer-major-heading), .content h5:not(.offer-letter-title):not(.offer-major-heading), .content h6:not(.offer-letter-title):not(.offer-major-heading),
.offer-section h1:not(.offer-letter-title):not(.offer-major-heading), .offer-section h2:not(.offer-letter-title):not(.offer-major-heading), .offer-section h3:not(.offer-letter-title):not(.offer-major-heading), .offer-section h4:not(.offer-letter-title):not(.offer-major-heading), .offer-section h5:not(.offer-letter-title):not(.offer-major-heading), .offer-section h6:not(.offer-letter-title):not(.offer-major-heading),
.offer-print-flow-body h1:not(.offer-letter-title):not(.offer-major-heading), .offer-print-flow-body h2:not(.offer-letter-title):not(.offer-major-heading), .offer-print-flow-body h3:not(.offer-letter-title):not(.offer-major-heading), .offer-print-flow-body h4:not(.offer-letter-title):not(.offer-major-heading), .offer-print-flow-body h5:not(.offer-letter-title):not(.offer-major-heading), .offer-print-flow-body h6:not(.offer-letter-title):not(.offer-major-heading) {
  margin: 0.7em 0 0.25em;
  text-align: left !important;
  page-break-after: avoid;
  break-after: avoid-page;
  orphans: 3;
  widows: 3;
}
.content p:not(.offer-major-heading):has(> strong:only-child),
.offer-section p:not(.offer-major-heading):has(> strong:only-child),
.offer-print-flow-body p:not(.offer-major-heading):has(> strong:only-child),
.content p:not(.offer-major-heading):has(> b:only-child),
.offer-section p:not(.offer-major-heading):has(> b:only-child),
.offer-print-flow-body p:not(.offer-major-heading):has(> b:only-child) {
  margin: 0.7em 0 0.2em;
  text-align: left !important;
  page-break-after: avoid;
  break-after: avoid-page;
}
.content .offer-major-heading,
.offer-section .offer-major-heading,
.offer-print-flow-body .offer-major-heading,
h1.offer-major-heading, h2.offer-major-heading, h3.offer-major-heading,
h4.offer-major-heading, h5.offer-major-heading, h6.offer-major-heading,
p.offer-major-heading {
  font-weight: bold !important;
  font-size: 1.2em !important;
  margin: 0.85em 0 0.55em !important;
}
.offer-keep-together {
  /* Headings still use page-break-after:avoid below. Forcing the whole
     subsection onto one page makes Chromium emit ~100+ blank letterhead pages. */
  page-break-inside: auto;
  break-inside: auto;
}
.offer-keep-together > h1,
.offer-keep-together > h2,
.offer-keep-together > h3,
.offer-keep-together > h4,
.offer-keep-together > h5,
.offer-keep-together > h6,
.offer-keep-together > p:not(.offer-major-heading):has(> strong:only-child),
.offer-keep-together > p:not(.offer-major-heading):has(> b:only-child),
.offer-keep-together > .offer-major-heading {
  page-break-after: avoid !important;
  break-after: avoid-page !important;
}
.offer-section { orphans: 3; widows: 3; }
.signature-section {
  margin-top: 18px;
  page-break-inside: avoid !important;
  break-inside: avoid-page !important;
  break-inside: avoid !important;
  text-align: left !important;
}
.signature-img, .stamp-img, img.company-stamp, img.offer-stamp {
  max-height: 70px;
  margin: 8px 0 4px;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.signature-section .signatory-block { margin: 0.25em 0 0; text-align: left !important; }
.employee-acceptance { margin-top: 18px; page-break-inside: avoid; break-inside: avoid; }
.employee-acceptance-row {
  width: 100%;
  border-collapse: collapse !important;
  border-spacing: 0;
  margin: 0.5em 0;
  table-layout: fixed;
}
.employee-acceptance-row td {
  border: 0 !important;
  padding: 0.35em 16px 0.35em 0 !important;
  text-align: left !important;
  width: 50%;
  vertical-align: top;
  background: transparent !important;
}
.employee-acceptance p { margin: 0.5em 0; text-align: left !important; }
/* Quill / legacy center — only title + annexure/terms majors stay centered. */
.content .ql-align-center:not(.offer-letter-title):not(.offer-major-heading),
.offer-section .ql-align-center:not(.offer-letter-title):not(.offer-major-heading),
.offer-print-flow-body .ql-align-center:not(.offer-letter-title):not(.offer-major-heading),
.content p[style*="text-align: center"]:not(.offer-letter-title):not(.offer-major-heading),
.content p[style*="text-align:center"]:not(.offer-letter-title):not(.offer-major-heading),
.offer-section p[style*="text-align: center"]:not(.offer-letter-title):not(.offer-major-heading),
.offer-section p[style*="text-align:center"]:not(.offer-letter-title):not(.offer-major-heading),
.offer-print-flow-body p[style*="text-align: center"]:not(.offer-letter-title):not(.offer-major-heading),
.offer-print-flow-body p[style*="text-align:center"]:not(.offer-letter-title):not(.offer-major-heading) {
  text-align: left !important;
}
.offer-page-break { page-break-before: always !important; break-before: page !important; height: 0; margin: 0; padding: 0; border: 0; }
/* Unit marker before a stamped after-break table — no CSS break (table is the only one). */
.offer-page-break-unit {
  page-break-before: auto !important;
  break-before: auto !important;
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}
/* Chromium/Puppeteer print ignores empty height:0 breaks. The after-break
   print-flow TABLE is the real PDF page boundary (sibling of the signature table). */
table.offer-print-flow-after-break {
  page-break-before: always !important;
  break-before: page !important;
}
/* Leftover force-break spacers from older generates — inert, never a second page. */
.offer-pdf-force-break {
  display: none !important;
  page-break-before: auto !important;
  break-before: auto !important;
  height: 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
}
.offer-page-break:has(+ table.offer-print-flow-after-break),
.offer-page-break:has(+ table.offer-print-flow),
.offer-page-break:has(+ .offer-pdf-force-break) {
  page-break-before: auto !important;
  break-before: auto !important;
}
/* Editor / preview: show manual page-break markers; print/PDF uses page-break only. */
.offer-manual-page-break:not(.offer-page-break) {
  display: block;
  margin: 14px 0;
  padding: 6px 0;
  border: 0;
  text-align: center;
  color: #6c757d;
  font-size: 11px;
  line-height: 1.2;
  user-select: none;
}
.offer-manual-page-break:not(.offer-page-break) hr {
  border: 0;
  border-top: 2px dashed #adb5bd;
  margin: 0 0 4px;
}
.offer-manual-page-break.offer-page-break {
  page-break-before: always !important;
  break-before: page !important;
  height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
}
.offer-manual-page-break.offer-page-break hr,
.offer-manual-page-break.offer-page-break span { display: none !important; }
/* Top-clear only when print-flow tables are absent (legacy fallback). */
.content:not(:has(.offer-print-flow)) .offer-page-top-clear {
  display: block;
  height: ${OFFER_CONTENT_PAD_TOP};
  line-height: 0;
  font-size: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 0;
}
.content:has(.offer-print-flow) .offer-page-top-clear { display: none !important; height: 0 !important; }
`.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
}

function offerGreetingBlockCss() {
  return '.offer-greeting-block { margin: 0 0 8px; width: 100%; }'
    + ' .offer-greeting-row { width: 100%; border-collapse: collapse !important; border-spacing: 0; margin: 0; table-layout: fixed; }'
    + ' .offer-greeting-row td { border: 0 !important; padding: 0 !important; vertical-align: baseline; background: transparent !important; overflow: visible !important; }'
    + ' .offer-salutation-cell { text-align: left !important; width: 68%; overflow: visible !important; }'
    + ' .offer-date-cell { text-align: right !important; width: 32%; white-space: nowrap; }'
    + ' .offer-greeting-block p { margin: 0; }'
    + ' .offer-greeting-block .offer-salutation-line { text-align: left !important; white-space: normal !important; overflow: visible !important; word-break: normal; }'
    + ' .offer-greeting-block .offer-date-line,'
    + ' .content .offer-greeting-block .offer-date-line { text-align: right !important; margin: 0; }'
}

function buildOfferGreetingInnerHtml(dearText, dateText) {
  const dear = dearText || 'Dear {{CANDIDATE.NAME_ONLY}},'
  const date = dateText || '{{OFFER_DETAILS.OFFERED_DATE_FORMATTED}}'
  return `<table class="offer-greeting-row" role="presentation">
    <tr>
      <td class="offer-salutation-cell"><p class="offer-salutation-line">${dear}</p></td>
      <td class="offer-date-cell"><p class="offer-date-line">Date: ${date}</p></td>
    </tr>
  </table>`
}

function buildOfferGreetingBlockHtml() {
  return `<div class="offer-greeting-block">
  ${buildOfferGreetingInnerHtml()}
</div>`
}

const OFFER_DATE_PARAGRAPH_PATTERN = /<p[^>]*>[\s\S]*?Date:\s*[\s\S]*?(?:\{\{OFFER_DETAILS\.OFFERED_DATE_FORMATTED\}\}|[A-Za-z0-9][^<]{0,40})[\s\S]*?<\/p>/i
const OFFER_SALUTATION_PARAGRAPH_PATTERN = /<p[^>]*>[\s\S]*?Dear[\s\S]*?(?:\{\{CANDIDATE\.(?:NAME_ONLY|NAME)\}\}|[A-Za-z][^<,]{0,80}),?[\s\S]*?<\/p>/i

function syncOfferGreetingStyles(html) {
  if (!html) return html
  // New row layout uses .offer-greeting-row; always refresh when missing or stale (date was left-aligned).
  if (/\.offer-greeting-row\s*\{/.test(html)
    && /\.offer-date-cell\s*\{/.test(html)
    && /\.offer-greeting-block \.offer-date-line/.test(html)) {
    return html
  }
  let result = html
  // Drop prior greeting rule fragments so left-align date CSS cannot win.
  result = result.replace(/\.offer-greeting-block\s*\{[^}]*\}/gi, '')
  result = result.replace(/\.offer-greeting-block\s+p\s*\{[^}]*\}/gi, '')
  result = result.replace(/\.offer-greeting-block\s+\.offer-date-line\s*\{[^}]*\}/gi, '')
  result = result.replace(/\.content\s+p\.offer-date-line,\s*\.offer-greeting-block\s+\.offer-date-line\s*\{[^}]*\}/gi, '')
  result = result.replace(/\.content\s+p\.offer-date-line\s*\{[^}]*\}/gi, '')
  return injectCssIntoHtml(result, offerGreetingBlockCss())
}

function extractGreetingDearText(fragment) {
  const token = fragment.match(/Dear\s*\{\{CANDIDATE\.(?:NAME_ONLY|NAME)\}\}\s*,?/i)
  if (token) {
    return token[0].replace(/,\s*$/, '').trim() + ','
  }
  // Strip tags first — a mid-name <span>/font from Quill made
  // /Dear\s+([A-Za-z][^,<]*?)/ stop at the first letter (PDF 40: "Dear R,").
  const stripped = String(fragment || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const plain = stripped.match(/\bDear\s+(.+?)\s*,/i) || stripped.match(/\bDear\s+([^,]{1,80})/i)
  if (plain && plain[1] && plain[1].trim()) {
    const name = plain[1].replace(/\s+/g, ' ').trim()
      .replace(/\s+Date:.*$/i, '')
      .trim()
    if (name) return `Dear ${name},`
  }
  return 'Dear {{CANDIDATE.NAME_ONLY}},'
}

function extractGreetingDateText(fragment) {
  if (/\{\{OFFER_DETAILS\.OFFERED_DATE_FORMATTED\}\}/i.test(fragment)) {
    return '{{OFFER_DETAILS.OFFERED_DATE_FORMATTED}}'
  }
  const plain = fragment.match(/Date:\s*([^<{]+)/i)
  if (plain) {
    return plain[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
  }
  return '{{OFFER_DETAILS.OFFERED_DATE_FORMATTED}}'
}

function normalizeOfferGreetingBlock(html) {
  return html.replace(
    /(<div[^>]*class\s*=\s*["'][^"']*\boffer-greeting-block\b[^"']*["'][^>]*>)([\s\S]*?)(<\/div>)/gi,
    (match, open, inner) => {
      const dear = extractGreetingDearText(inner)
      const date = extractGreetingDateText(inner)
      return `${open}\n  ${buildOfferGreetingInnerHtml(dear, date)}\n</div>`
    },
  )
}

/**
 * Dear (left) + Offer Date (right) on one row. Removes a separate date-only line above Dear.
 */
function repairOfferGreetingMarkup(html) {
  if (!html) return html
  let result = syncOfferGreetingStyles(html)

  if (/class\s*=\s*["'][^"']*\boffer-greeting-block\b/i.test(result)) {
    return normalizeOfferGreetingBlock(result)
  }

  const dateMatch = result.match(OFFER_DATE_PARAGRAPH_PATTERN)
  const salMatch = result.match(OFFER_SALUTATION_PARAGRAPH_PATTERN)
  if (dateMatch && salMatch) {
    const dateStart = dateMatch.index
    const salStart = salMatch.index
    const firstStart = Math.min(dateStart, salStart)
    const lastEnd = Math.max(
      dateStart + dateMatch[0].length,
      salStart + salMatch[0].length,
    )
    const span = result.slice(firstStart, lastEnd)
    // Only collapse when date + Dear sit near each other (not distant body mentions).
    if (Math.abs(dateStart - salStart) <= 500 && span.length <= 800) {
      const dear = extractGreetingDearText(span)
      const date = extractGreetingDateText(span)
      const block = `<div class="offer-greeting-block">\n  ${buildOfferGreetingInnerHtml(dear, date)}\n</div>`
      result = `${result.slice(0, firstStart)}${block}${result.slice(lastEnd)}`
      return result
    }
  }

  if (salMatch && dateMatch == null) {
    // Salutation only — wrap with placeholder date on the right.
    const dear = extractGreetingDearText(salMatch[0])
    const block = `<div class="offer-greeting-block">\n  ${buildOfferGreetingInnerHtml(dear)}\n</div>`
    result = `${result.slice(0, salMatch.index)}${block}${result.slice(salMatch.index + salMatch[0].length)}`
  }

  return result
}

function offerBodyTypographyCss() {
  return `html, body { font-family: Calibri, Arial, sans-serif; font-size: ${OFFER_BODY_FONT_SIZE}; letter-spacing: 0.2px; line-height: ${OFFER_BODY_LINE_HEIGHT}; color: #333; margin: 0; padding: 0; position: relative; }`
}

/** Resolve header banner src — metadata image or production default two-tone PNG. */
export function resolveOfferHeaderBannerSrc(headerImage) {
  const src = String(headerImage || '').trim()
  return src || DEFAULT_HEADER_BANNER_DATA_URI
}

/** Sync header CSS — fixed header at page top:0 (full width) + compact logo block. */
function repairOfferHeaderStyles(html) {
  if (!html) return html
  let result = html
  const replacements = [
    [/\.header-section\s*\{[^}]*\}/gi, offerHeaderSectionCss()],
    [/\.header-image\s*\{[^}]*\}/gi, offerHeaderImageCss()],
    [/\.header-image\s+img\s*\{[^}]*\}/gi, offerHeaderImageImgCss()],
    [/\.company-info\s*\{[^}]*\}/gi, offerCompanyInfoCss()],
    [/\.company-logo-section\s*\{[^}]*\}/gi, offerCompanyLogoSectionCss()],
    [/\.company-logo\s*\{[^}]*\}/gi, offerCompanyLogoCss()],
    [/\.company-name\s*\{[^}]*\}/gi, offerCompanyNameCss()],
  ]
  const missing = []
  replacements.forEach(([pattern, css]) => {
    pattern.lastIndex = 0
    const hasRule = pattern.test(result)
    pattern.lastIndex = 0
    if (hasRule) {
      result = result.replace(pattern, css)
    } else {
      // Inject any missing letterhead rules (not only header-section/image).
      missing.push(css)
    }
  })
  if (missing.length) {
    result = injectCssIntoHtml(result, missing.join(' '))
  }
  return result
}

function findDivBlockAt(html, startIndex) {
  const slice = html.slice(startIndex)
  const openMatch = slice.match(/^<div\b[^>]*>/i)
  if (!openMatch) return null
  const openTag = openMatch[0]
  let pos = startIndex + openTag.length
  let depth = 1
  while (pos < html.length && depth > 0) {
    const rest = html.slice(pos)
    const nextOpen = rest.search(/<div\b[^>]*>/i)
    const nextClose = rest.search(/<\/div>/i)
    if (nextClose < 0) return null
    if (nextOpen >= 0 && nextOpen < nextClose) {
      depth += 1
      pos += nextOpen + rest.slice(nextOpen).match(/^<div\b[^>]*>/i)[0].length
    } else {
      depth -= 1
      pos += nextClose + 6
    }
  }
  if (depth !== 0) return null
  return {
    start: startIndex,
    end: pos,
    openTag,
    inner: html.slice(startIndex + openTag.length, pos - 6),
    full: html.slice(startIndex, pos),
  }
}

function findDivBlockByClass(html, className, searchFrom = 0) {
  const pattern = new RegExp(
    `<div[^>]*class\\s*=\\s*["'][^"']*\\b${className}\\b[^"']*["'][^>]*>`,
    'i',
  )
  const slice = html.slice(searchFrom)
  const match = slice.match(pattern)
  if (!match || match.index == null) return null
  return findDivBlockAt(html, searchFrom + match.index)
}

function removeDivBlocksByClass(html, className) {
  let result = html
  let block = findDivBlockByClass(result, className)
  while (block) {
    result = `${result.slice(0, block.start)}${result.slice(block.end)}`
    block = findDivBlockByClass(result, className)
  }
  return result
}

function firstImgSrcMatching(html, predicate) {
  const imgs = [...String(html || '').matchAll(/<img[^>]*>/gi)]
  for (let i = 0; i < imgs.length; i += 1) {
    const tag = imgs[i][0]
    const srcMatch = tag.match(/\bsrc\s*=\s*(["'])([^"']*)\1/i)
    if (!srcMatch) continue
    if (predicate(tag, srcMatch[2])) return srcMatch[2]
  }
  return ''
}

function extractHeaderBannerSrcFromMarkup(html) {
  const headerBlock = findDivBlockByClass(html, 'header-section')
  if (headerBlock) {
    const headerImageBlock = findDivBlockByClass(headerBlock.inner, 'header-image')
    const scoped = headerImageBlock ? headerImageBlock.inner : headerBlock.inner
    const fromScoped = firstImgSrcMatching(scoped, tag => /<img/i.test(tag))
    if (fromScoped) return fromScoped
  }
  const fromFlowing = firstImgSrcMatching(html, (tag, src) => (
    /\bheader-img\b/i.test(tag)
    || /\bheader-image\b/i.test(tag)
    || /METADATA\.IMAGES\.HEADER/i.test(src)
  ))
  if (fromFlowing) return fromFlowing
  return html.includes(HEADER_PLACEHOLDER) ? HEADER_PLACEHOLDER : ''
}

function extractLogoSrcFromMarkup(html) {
  const fromLogo = firstImgSrcMatching(html, (tag, src) => {
    if (/watermark|signature-img|\bsignature\b/i.test(tag)) return false
    return (
      /\bcompany-logo\b/i.test(tag)
      || /\blogo\b/i.test(tag)
      || /METADATA\.IMAGES\.LOGO/i.test(src)
    )
  })
  if (fromLogo) return fromLogo
  return html.includes(LOGO_PLACEHOLDER) ? LOGO_PLACEHOLDER : ''
}

function extractCompanyNameFromMarkup(html) {
  const match = html.match(
    /<div[^>]*class\s*=\s*["'][^"']*\bcompany-name\b[^"']*["'][^>]*>([\s\S]*?)<\/div>/i,
  )
  if (match) {
    const text = match[1].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
    if (text) return text
  }
  return 'SATTVA HUMAN PRIVATE LIMITED'
}

function buildUnifiedHeaderHtml({ headerSrc, logoSrc, companyName } = {}) {
  const header = headerSrc || HEADER_PLACEHOLDER
  const logo = logoSrc || LOGO_PLACEHOLDER
  const name = escapeHtml(companyName || 'SATTVA HUMAN PRIVATE LIMITED')
  return `<div class="header-section" style="${HEADER_SECTION_INLINE_STYLE}"><div class="header-image" style="${HEADER_IMAGE_INLINE_STYLE}"><img src="${header}" alt="Header Image" style="width:100%;max-width:100%;height:auto;display:block;margin:0!important;padding:0!important;border:0!important;vertical-align:top" /></div><div class="company-info"><div class="company-logo-section"><img src="${logo}" alt="Company Logo" class="company-logo" /><div class="company-name">${name}</div></div></div></div>`
}

/** Normalize .header-image inline styles — no negative margins (section is full-bleed at top:0). */
function repairHeaderImageInlineStyles(html) {
  if (!html) return html
  return html.replace(
    /(<div[^>]*class\s*=\s*["'][^"']*\bheader-image\b[^"']*["'][^>]*>)/gi,
    (openTag) => {
      if (/\bstyle\s*=/i.test(openTag)) {
        return openTag.replace(
          /\bstyle\s*=\s*(["'])([^"']*)\1/i,
          (match, q) => `style=${q}${HEADER_IMAGE_INLINE_STYLE}${q}`,
        )
      }
      return openTag.replace(/<div/i, `<div style="${HEADER_IMAGE_INLINE_STYLE}"`)
    },
  )
}

/** Force production fixed-header at top:0 (inline beats leaked preview / legacy negative offsets). */
function repairHeaderSectionInlineStyles(html) {
  if (!html) return html
  return html.replace(
    /(<div[^>]*class\s*=\s*["'][^"']*\bheader-section\b[^"']*["'][^>]*>)/gi,
    (openTag) => {
      if (/\bstyle\s*=/i.test(openTag)) {
        return openTag.replace(
          /\bstyle\s*=\s*(["'])([^"']*)\1/i,
          (match, q) => `style=${q}${HEADER_SECTION_INLINE_STYLE}${q}`,
        )
      }
      return openTag.replace(/<div/i, `<div style="${HEADER_SECTION_INLINE_STYLE}"`)
    },
  )
}

function isHeaderSectionComplete(html) {
  const block = findDivBlockByClass(html, 'header-section')
  if (!block) return false
  const inner = block.inner
  // Match HTML class attributes (class="header-image"), not CSS selectors (.header-image).
  const hasBanner = /\bheader-image\b/i.test(inner)
    || /\bheader-img\b/i.test(inner)
    || /METADATA\.IMAGES\.HEADER/i.test(inner)
  const hasCompany = /\bcompany-info\b/i.test(inner)
    || /\bcompany-logo-section\b/i.test(inner)
    || /\bcompany-name\b/i.test(inner)
    || /METADATA\.IMAGES\.LOGO/i.test(inner)
  return hasBanner && hasCompany
}

/** Remove duplicate logo/banner images outside the fixed header — never strip inside it. */
function removeFlowingHeaderImages(html) {
  const headerBlock = findDivBlockByClass(html, 'header-section')
  if (!headerBlock) {
    return stripHeaderLikeImages(html)
  }
  const before = html.slice(0, headerBlock.start)
  const header = html.slice(headerBlock.start, headerBlock.end)
  const after = html.slice(headerBlock.end)
  return `${stripHeaderLikeImages(before)}${header}${stripHeaderLikeImages(after)}`
}

function stripHeaderLikeImages(html) {
  let result = html
  result = result.replace(/<img[^>]*class\s*=\s*["'][^"']*\bheader-img\b[^"']*["'][^>]*>\s*/gi, '')
  result = result.replace(/<img[^>]*class\s*=\s*["'][^"']*\bcompany-logo\b[^"']*["'][^>]*>\s*/gi, '')
  result = result.replace(/<img[^>]*class\s*=\s*["'][^"']*\blogo\b[^"']*["'][^>]*>\s*/gi, (match) => {
    if (/watermark|signature/i.test(match)) return match
    return ''
  })
  return result
}

function removeCompanyInfoOutsideHeader(html) {
  const headerBlock = findDivBlockByClass(html, 'header-section')
  if (!headerBlock) return html
  let result = html
  let block = findDivBlockByClass(result.slice(headerBlock.end), 'company-info')
  while (block) {
    const absStart = headerBlock.end + block.start
    const absEnd = headerBlock.end + block.end
    result = `${result.slice(0, absStart)}${result.slice(absEnd)}`
    block = findDivBlockByClass(result.slice(headerBlock.end), 'company-info')
  }
  return result
}

function insertAfterBodyOpen(html, insertHtml) {
  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body([^>]*)>/i, match => `${match}\n  ${insertHtml}\n`)
  }
  return `${insertHtml}\n${html}`
}

/**
 * Saved templates often split the banner (fixed header-section) from logo/company name
 * (flowing body). Rebuild a single fixed header: banner + logo + company name.
 */
function repairOfferHeaderMarkup(html) {
  if (!html) return html

  if (isHeaderSectionComplete(html)) {
    let result = removeCompanyInfoOutsideHeader(html)
    result = removeFlowingHeaderImages(result)
    result = repairHeaderSectionInlineStyles(result)
    return repairHeaderImageInlineStyles(result)
  }

  const headerSrc = extractHeaderBannerSrcFromMarkup(html)
  const logoSrc = extractLogoSrcFromMarkup(html)
  const companyName = extractCompanyNameFromMarkup(html)

  let result = html
  result = removeDivBlocksByClass(result, 'header-section')
  result = removeDivBlocksByClass(result, 'company-info')
  result = removeFlowingHeaderImages(result)

  const unifiedHeader = buildUnifiedHeaderHtml({ headerSrc, logoSrc, companyName })
  result = insertAfterBodyOpen(result, unifiedHeader)
  result = repairHeaderSectionInlineStyles(result)
  return repairHeaderImageInlineStyles(result)
}

/** Replace legacy float footer with centered two-line production footer. */
function upgradeOfferFooterMarkup(html) {
  if (!html || !hasFooterSectionMarkup(html)) return html
  if (/footer-line\s+footer-company/i.test(html)) return html
  return html.replace(
    /<div\s+class\s*=\s*["']footer-section["'][^>]*>[\s\S]*?<\/div>/i,
    DEFAULT_FOOTER_HTML,
  )
}

/** Sync margins, typography, footer CSS, and title styles on saved templates. */
function upgradeOfferChromeStyles(html) {
  if (!html) return html
  let result = html

  // Park dynamic offerLayout CSS so chrome upgrades do not clobber title font-size etc.
  let parkedLayoutCss = ''
  const layoutCssPattern = new RegExp(
    `${escapeRegExp(OFFER_LAYOUT_CSS_MARKER)}[\\s\\S]*?${escapeRegExp(OFFER_LAYOUT_CSS_MARKER)}`,
    'i',
  )
  result = result.replace(layoutCssPattern, match => {
    parkedLayoutCss = match
    return '/* OFFER_LAYOUT_PARKED */'
  })

  // Canonical @page is applied later by ensureOfferPageMargins (strips all variants).

  const cssUpgrades = [
    [/\.footer-section\s*\{[^}]*\}/gi, offerFooterSectionCss()],
    [/\.content\s*\{[^}]*\}/gi, offerContentCss()],
    [/\.offer-letter-title\s*\{[^}]*\}/gi, OFFER_LETTER_TITLE_CSS],
    [/body\.offer-chrome-padded\s*\{[^}]*\}/gi, offerBodyChromePaddingRule()],
  ]
  cssUpgrades.forEach(([pattern, replacement]) => {
    // Reset lastIndex — /g .test() otherwise skips the only match on .replace().
    pattern.lastIndex = 0
    if (!pattern.test(result)) return
    pattern.lastIndex = 0
    result = result.replace(pattern, replacement)
  })

  // Upgrade base typography WHILE layout CSS is parked so we do not clobber
  // offerLayout title/body font-size rules embedded between OFFER_LAYOUT markers.
  if (/html,\s*body\s*\{/.test(result)) {
    result = result.replace(/html,\s*body\s*\{[^}]*\}/gi, offerBodyTypographyCss())
  } else if (/<\/style>/i.test(result)) {
    result = injectCssIntoHtml(result, offerBodyTypographyCss())
  }

  if (/\.footer-section\s*\{/.test(result) && !/\.footer-line\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, offerFooterLineCss())
  }

  if (!/offer-print-flow\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, offerContentFlowCss())
  }

  if (parkedLayoutCss) {
    if (result.includes('/* OFFER_LAYOUT_PARKED */')) {
      result = result.replace('/* OFFER_LAYOUT_PARKED */', parkedLayoutCss)
    } else {
      result = injectCssIntoHtml(result, parkedLayoutCss)
    }
  }

  result = repairOfferHeaderStyles(result)
  return result
}

function injectCssIntoHtml(html, cssBlock) {
  if (!cssBlock) return html
  if (/<\/style>/i.test(html)) {
    return html.replace(/<\/style>/i, `  ${cssBlock}\n  </style>`)
  }
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `<style>${cssBlock}</style></head>`)
  }
  return `<style>${cssBlock}</style>\n${html}`
}

function hasHeaderSectionMarkup(html) {
  return /class\s*=\s*["'][^"']*\bheader-section\b/i.test(html)
}

function hasFooterSectionMarkup(html) {
  return /class\s*=\s*["'][^"']*\bfooter-section\b/i.test(html)
}

function hasContentWrapperMarkup(html) {
  return OFFER_CONTENT_WRAPPER_OPEN_RE.test(String(html || ''))
}

/** Visible body title only — ignore <title> and body copy that mentions "offer letter". */
function hasOfferLetterTitle(html) {
  return findWellPlacedOfferLetterTitle(html) != null
}

/**
 * A title counts only when it is OUTSIDE the fixed header-section, has visible
 * "OFFER LETTER" / "Offer Letter" text, and sits in the document flow before Dear/Date.
 * Misplaced / empty class="offer-letter-title" nodes must NOT block reinjection.
 */
function findWellPlacedOfferLetterTitle(html) {
  if (!html) return null
  const withoutDocTitle = String(html).replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
  const headerBlock = findDivBlockByClass(withoutDocTitle, 'header-section')
  const headerStart = headerBlock ? headerBlock.start : -1
  const headerEnd = headerBlock ? headerBlock.end : -1

  const candidates = []
  const classRe = /<(h[1-6]|p|div)([^>]*class\s*=\s*["'][^"']*\boffer-letter-title\b[^"']*["'][^>]*)>([\s\S]*?)<\/\1>/gi
  let match = classRe.exec(withoutDocTitle)
  while (match) {
    candidates.push({
      index: match.index,
      end: match.index + match[0].length,
      full: match[0],
      text: match[3].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim(),
    })
    match = classRe.exec(withoutDocTitle)
  }

  const exactHeadingRe = /<(h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi
  match = exactHeadingRe.exec(withoutDocTitle)
  while (match) {
    const text = match[3].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
    if (OFFER_LETTER_TITLE_TEXT_RE.test(text)
      && !/\boffer-letter-title\b/i.test(match[2])) {
      candidates.push({
        index: match.index,
        end: match.index + match[0].length,
        full: match[0],
        text,
      })
    }
    match = exactHeadingRe.exec(withoutDocTitle)
  }

  const dateIdx = withoutDocTitle.search(
    /Date:\s*|class\s*=\s*["'][^"']*\boffer-greeting-block\b|class\s*=\s*["'][^"']*\boffer-date-line\b/i,
  )

  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i]
    if (!OFFER_LETTER_TITLE_TEXT_RE.test(c.text)) continue
    const insideHeader = headerStart >= 0 && c.index >= headerStart && c.index < headerEnd
    if (insideHeader) continue
    if (dateIdx >= 0 && c.index > dateIdx) continue
    return c
  }
  return null
}

/** Strip every letter-title node so chrome can reinsert one in the correct spot. */
function removeOfferLetterTitleElements(html) {
  if (!html) return html
  let result = html
  result = result.replace(
    /<(h[1-6]|p|div)([^>]*class\s*=\s*["'][^"']*\boffer-letter-title\b[^"']*["'][^>]*)>[\s\S]*?<\/\1>\s*/gi,
    '',
  )
  result = result.replace(/<(h[1-6])([^>]*)>\s*(?:OFFER\s+LETTER|Offer\s+Letter)\s*<\/\1>\s*/gi, () => '')
  return result
}

/** Index immediately after the full fixed header-section block (never inside it). */
function indexAfterHeaderSection(html) {
  const block = findDivBlockByClass(html, 'header-section')
  return block ? block.end : -1
}

/** Preferred insert point: start of .content, else before greeting/date, else after header. */
function findOfferLetterTitleInsertIndex(html) {
  const contentOpen = html.match(/<div[^>]*class\s*=\s*["'][^"']*\bcontent\b[^"']*["'][^>]*>/i)
  if (contentOpen && contentOpen.index != null) {
    return contentOpen.index + contentOpen[0].length
  }

  const greeting = html.match(/<div[^>]*class\s*=\s*["'][^"']*\boffer-greeting-block\b[^"']*["'][^>]*>/i)
  if (greeting && greeting.index != null) return greeting.index

  const dateP = html.match(/<p[^>]*>[\s\S]*?Date:\s*/i)
  if (dateP && dateP.index != null) return dateP.index

  const afterHeader = indexAfterHeaderSection(html)
  if (afterHeader >= 0) return afterHeader

  const bodyOpen = html.match(/<body[^>]*>/i)
  if (bodyOpen && bodyOpen.index != null) return bodyOpen.index + bodyOpen[0].length

  return 0
}

/**
 * Always force a single @page { margin: 0 }.
 * Strips every @page rule (incl. :first / size variants) so legacy
 * `margin: 130px 35px 55px 35px` cannot survive beside top:0 fixed headers.
 */
function ensureOfferPageMargins(html) {
  if (!html) return html
  // Drop all @page blocks first — replace-in-place misses @page :first { … }.
  const withoutPageRules = html.replace(/@page[^{]*\{[^}]*\}/gi, '')
  return injectCssIntoHtml(withoutPageRules, offerPageMarginRule())
}

/** Drop legacy .content offsets — padding + box-decoration-break now reserve chrome space. */
function repairLegacyContentMargins(html) {
  if (!html || !/\.content\s*\{/.test(html)) return html
  // Always sync .content to production padding/clone (covers margin-top:150px and padding:12px eras).
  return html.replace(/\.content\s*\{[^}]*\}/gi, offerContentCss())
}

function appendBodyClass(attrs, className) {
  let nextAttrs = attrs || ''
  if (new RegExp(`\\b${className}\\b`).test(nextAttrs)) return nextAttrs
  if (/class\s*=\s*["'][^"']*["']/i.test(nextAttrs)) {
    return nextAttrs.replace(
      /class\s*=\s*(["'])([^"']*)\1/i,
      (_, q, cls) => `class=${q}${cls} ${className}${q}`,
    )
  }
  return `${nextAttrs} class="${className}"`
}

/** Body padding (+ clone) when templates have fixed header but no .content wrapper. */
function ensureBodyChromePadding(html) {
  if (!html || hasContentWrapperMarkup(html)) return html

  let result = html
  if (!/body\.offer-chrome-padded\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, offerBodyChromePaddingRule())
  } else {
    result = result.replace(/body\.offer-chrome-padded\s*\{[^}]*\}/gi, offerBodyChromePaddingRule())
  }
  if (!/\.offer-letter-title\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, OFFER_LETTER_TITLE_CSS)
  }

  if (/<body[^>]*>/i.test(result)) {
    result = result.replace(/<body([^>]*)>/i, (match, attrs) => {
      const nextAttrs = appendBodyClass(attrs, 'offer-chrome-padded')
      return `<body${nextAttrs}>`
    })
  }
  return result
}

/**
 * Visible document title — <title> alone does not appear in PDF body output.
 * Always normalizes placement: centered OFFER LETTER below letterhead, above Dear/Date.
 * Never leaves the title inside position:fixed .header-section (invisible/clipped).
 * Accepts "Offer Letter" or "OFFER LETTER" and normalizes text to OFFER LETTER.
 */
function ensureOfferLetterTitle(html) {
  if (!html) return html

  // Already correct place — normalize casing/center (idempotent for well-formed templates).
  // Do not wholesale-replace .offer-letter-title CSS here — offerLayout may set font-size.
  if (findWellPlacedOfferLetterTitle(html)) {
    let result = html
    if (!/\.offer-letter-title\s*\{/.test(result)) {
      result = injectCssIntoHtml(result, OFFER_LETTER_TITLE_CSS)
    } else if (!/\.offer-letter-title\s*\{[^}]*text-transform\s*:\s*uppercase/i.test(result)) {
      result = result.replace(/\.offer-letter-title\s*\{([^}]*)\}/gi, (full, body) => {
        const trimmed = String(body || '').trim().replace(/;?\s*$/, '')
        return `.offer-letter-title { ${trimmed}; text-transform: uppercase; }`
      })
    }
    // Sync visible title text + alignment from offerLayout when present.
    result = result.replace(
      /<(h[1-6]|p|div)([^>]*class\s*=\s*["'][^"']*\boffer-letter-title\b[^"']*["'][^>]*)>([\s\S]*?)<\/\1>/gi,
      (full, tag, attrs) => {
        const next = mergeOfferLetterTitleAlignAttrs(attrs, 'center')
        return `<${tag}${next}>${OFFER_LETTER_TITLE_TEXT}</${tag}>`
      },
    )
    // Bare headings that say Offer Letter / OFFER LETTER without the class.
    result = result.replace(
      /<(h[1-6])([^>]*)>(\s*)(?:OFFER\s+LETTER|Offer\s+Letter)(\s*)<\/\1>/gi,
      (full, tag, attrs) => {
        if (/\boffer-letter-title\b/i.test(attrs)) return full
        const withClass = /\bclass\s*=\s*(["'])([^"']*)\1/i.test(attrs)
          ? attrs.replace(
            /\bclass\s*=\s*(["'])([^"']*)\1/i,
            (_, q, cls) => `class=${q}${cls} offer-letter-title${q}`,
          )
          : `${attrs} class="offer-letter-title"`
        return `<${tag}${withClass}>${OFFER_LETTER_TITLE_TEXT}</${tag}>`
      },
    )
    return result
  }

  let result = removeOfferLetterTitleElements(html)
  if (!/\.offer-letter-title\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, OFFER_LETTER_TITLE_CSS)
  }

  const titleBlock = `\n  ${OFFER_LETTER_TITLE_HTML}\n`
  const insertAt = findOfferLetterTitleInsertIndex(result)
  return `${result.slice(0, insertAt)}${titleBlock}${result.slice(insertAt)}`
}

/** In-body letterhead clear for print-flow chunks that start after a forced page break. */
const OFFER_PRINT_CHUNK_CLEAR_HTML = `<div class="offer-print-chunk-clear" aria-hidden="true" style="display:block;height:${OFFER_CONTENT_PAD_TOP};min-height:${OFFER_CONTENT_PAD_TOP};line-height:${OFFER_CONTENT_PAD_TOP};font-size:0;overflow:hidden;margin:0;padding:0;border:0;visibility:hidden">&nbsp;</div>`

function stripOfferPrintChunkClears(html) {
  return String(html || '').replace(
    /<div[^>]*class="[^"]*\boffer-print-chunk-clear\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*/gi,
    '',
  )
}

/** Remove empty Quill centered headings that sit above major section titles. */
function stripEmptyCenteredOfferHeadings(html) {
  if (!html) return html
  return String(html).replace(
    /<h[1-6][^>]*class="[^"]*\bql-align-center\b[^"]*"[^>]*>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/h[1-6]>\s*/gi,
    '',
  )
}

/**
 * @param {string} inner
 * @param {{ afterPageBreak?: boolean }} [options]
 *   afterPageBreak — chunk follows a forced break (Policies / Terms / …). Uses an
 *   in-body 130px clear because some Chromium builds collapse thead on new tables
 *   after page-break (PDF 21–29 page 2 titles under letterhead). Page-1 first chunk
 *   keeps thead-only clearance (do not pass afterPageBreak).
 */
function buildOfferPrintFlowTable(inner, options = {}) {
  const afterPageBreak = !!(options && options.afterPageBreak)
  const pageBreakBefore = !!(options && options.pageBreakBefore)
  let body = stripOfferPrintChunkClears(String(inner || '')).trim()
  if (!body) return ''
  // Post-break chunks use thead clearance class only. Apply page-break-before on the
  // chunk that follows a forced break — not page-break-after on the prior table, which
  // doubled breaks with after-break thead clearance and yielded blank pages (PDF 54 p2/p5).
  const tableClass = afterPageBreak
    ? 'offer-print-flow offer-print-flow-after-break'
    : 'offer-print-flow'
  const tableBreak = pageBreakBefore
    ? ' style="page-break-before: always; break-before: page;"'
    : ''
  return `${OFFER_PRINT_FLOW_START}
<table class="${tableClass}" role="presentation"${tableBreak}>
<thead><tr><td><div class="offer-print-spacer-top">&nbsp;</div></td></tr></thead>
<tfoot><tr><td><div class="offer-print-spacer-bottom">&nbsp;</div></td></tr></tfoot>
<tbody><tr><td class="offer-print-flow-body">
${body}
</td></tr></tbody>
</table>
${OFFER_PRINT_FLOW_END}`
}

/**
 * Extract inner HTML of `.offer-print-flow-body` (depth-aware — greeting / salary
 * tables nest `<td>` and must not truncate the body cell).
 */
function extractOfferPrintFlowBodyInner(tableHtml) {
  const bodyOpen = String(tableHtml || '').match(
    /<td\b[^>]*class="[^"]*\boffer-print-flow-body\b[^"]*"[^>]*>/i,
  )
  if (bodyOpen && bodyOpen.index != null) {
    const openEnd = bodyOpen.index + bodyOpen[0].length
    const close = findMatchingTdClose(tableHtml, openEnd)
    if (close >= 0) return stripOfferPrintChunkClears(tableHtml.slice(openEnd, close))
  }
  // Legacy tables without body class — prefer last non-spacer top-level-ish cell
  // via depth-aware scans from each <td>.
  const cells = []
  const tdOpenRe = /<td\b[^>]*>/gi
  let m = tdOpenRe.exec(tableHtml)
  while (m) {
    const openEnd = m.index + m[0].length
    const close = findMatchingTdClose(tableHtml, openEnd)
    if (close >= 0) {
      cells.push(tableHtml.slice(openEnd, close))
      tdOpenRe.lastIndex = close + 5
    }
    m = tdOpenRe.exec(tableHtml)
  }
  if (!cells.length) return ''
  for (let i = cells.length - 1; i >= 0; i -= 1) {
    const cell = cells[i] || ''
    if (!/offer-print-spacer/i.test(cell)) return stripOfferPrintChunkClears(cell)
  }
  return stripOfferPrintChunkClears(cells[cells.length - 1] || '')
}

const PRINT_FLOW_TABLE_OPEN_RE = /<table\b[^>]*class\s*=\s*["'][^"']*\boffer-print-flow\b[^"']*["'][^>]*>/i

const PRINT_FLOW_THEAD_RE = /<thead\b[^>]*>\s*<tr\b[^>]*>\s*<td\b[^>]*>\s*<div\b[^>]*class="[^"]*\boffer-print-spacer-top\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*<\/td>\s*<\/tr>\s*<\/thead>/gi
const PRINT_FLOW_TFOOT_RE = /<tfoot\b[^>]*>\s*<tr\b[^>]*>\s*<td\b[^>]*>\s*<div\b[^>]*class="[^"]*\boffer-print-spacer-bottom\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*<\/td>\s*<\/tr>\s*<\/tfoot>/gi
const PRINT_FLOW_BODY_OPEN_RE = /<tbody\b[^>]*>\s*<tr\b[^>]*>\s*<td\b[^>]*class="[^"]*\boffer-print-flow-body\b[^"]*"[^>]*>/gi
const PRINT_FLOW_BODY_CLOSE_RE = /<\/td>\s*<\/tr>\s*<\/tbody>\s*<\/table>/gi

/**
 * Find the next regex match at or after `start`.
 * @returns {{ index: number, match: string }|null}
 */
function indexOfRe(html, start, re) {
  const slice = String(html || '').slice(start)
  const flags = re.flags.includes('g') ? re.flags.replace(/g/g, '') : re.flags
  const match = slice.match(new RegExp(re.source, flags))
  if (!match || match.index == null) return null
  return { index: start + match.index, match: match[0] }
}

/**
 * Drop leftover print-flow wrapper closes only.
 * A global `</td></tr></tbody></table>` replace also ate salary-table (and any
 * other nested data-table) closers. That left Structure 2 / notes / Terms
 * inside the first table's last cell — indented, orphan numbers, overlay.
 */
function stripOrphanPrintFlowBodyCloses(html) {
  const source = String(html || '')
  if (!source) return source
  let out = ''
  let i = 0
  let tableDepth = 0

  while (i < source.length) {
    const open = indexOfRe(source, i, /<table\b/i)
    const close = indexOfRe(source, i, /<\/table>/i)
    const bodyClose = indexOfRe(source, i, PRINT_FLOW_BODY_CLOSE_RE)

    let next = null
    if (open) next = { kind: 'open', ...open }
    if (close && (!next || close.index < next.index)) next = { kind: 'close', ...close }
    if (bodyClose && (!next || bodyClose.index < next.index)) next = { kind: 'bodyClose', ...bodyClose }

    if (!next) {
      out += source.slice(i)
      break
    }

    out += source.slice(i, next.index)

    if (next.kind === 'bodyClose') {
      if (tableDepth <= 0) {
        i = next.index + next.match.length
        continue
      }
      out += next.match
      tableDepth -= 1
      i = next.index + next.match.length
      continue
    }

    if (next.kind === 'open') {
      tableDepth += 1
      out += next.match
      i = next.index + next.match.length
      continue
    }

    if (tableDepth > 0) {
      tableDepth -= 1
      out += next.match
    }
    i = next.index + next.match.length
  }
  return out
}

/**
 * If flatten still left an unclosed salary-table, close it before the next
 * structure title / table / section marker so later blocks cannot nest inside.
 */
function closeUnclosedSalaryTables(html) {
  if (!html || !/<table\b[^>]*class="[^"]*\bsalary-table\b/i.test(html)) return html
  let result = String(html)
  let guard = 0
  while (guard < 24) {
    guard += 1
    const openRe = /<table\b[^>]*class="[^"]*\bsalary-table\b[^"]*"[^>]*>/gi
    let match = openRe.exec(result)
    let fixed = false
    while (match) {
      const openEnd = match.index + match[0].length
      const close = findMatchingTableClose(result, openEnd)
      if (close >= 0) {
        openRe.lastIndex = close + 8
        match = openRe.exec(result)
        continue
      }
      const rest = result.slice(openEnd)
      const boundary = rest.search(
        /<(?:p|h[1-6])[^>]*class="[^"]*\bsalary-structure-title\b|<table\b|<!--\s*OFFER_[A-Z_]+/i,
      )
      const insertAt = openEnd + (boundary >= 0 ? boundary : rest.length)
      result = `${result.slice(0, insertAt)}</td></tr></tbody></table>\n${result.slice(insertAt)}`
      fixed = true
      break
    }
    if (!fixed) break
  }
  return result
}

/**
 * Strip print-flow table chrome even when tags are unclosed.
 * Saved EDF India HTML had 113 <table> opens / 88 closes (25 orphan wrappers).
 * Depth-matching cannot unwrap an unclosed outer table, so chrome must be
 * removed by pattern. After-break tables do not restore an unmarked page-break
 * — named Starts: New page divs already sit outside the table.
 *
 * Nested salary / greeting tables must keep their own closes — only orphan
 * wrapper closes (table depth 0 after the open was stripped) are removed.
 */
function stripOfferPrintFlowChrome(html) {
  if (!html) return ''
  let result = String(html)
  result = result.replace(new RegExp(escapeRegExp(OFFER_PRINT_FLOW_START), 'gi'), '')
  result = result.replace(new RegExp(escapeRegExp(OFFER_PRINT_FLOW_END), 'gi'), '')
  // Strip wrapper opens only. Do NOT turn leftover after-break tables into
  // unmarked page-break divs — that recreated empty letterhead pages (page 3
  // Transportation-only, and the 157-page S3 file). User Starts: New page
  // units already sit outside the table as marked offer-page-break divs.
  result = result.replace(new RegExp(PRINT_FLOW_TABLE_OPEN_RE.source, 'gi'), '')
  result = result.replace(PRINT_FLOW_THEAD_RE, '')
  result = result.replace(PRINT_FLOW_TFOOT_RE, '')
  result = result.replace(PRINT_FLOW_BODY_OPEN_RE, '')
  result = stripOrphanPrintFlowBodyCloses(result)
  result = result.replace(new RegExp(OFFER_PAGE_TOP_CLEAR_EMPTY_RE.source, 'gi'), '')
  result = result.replace(
    /<div[^>]*class="[^"]*\boffer-print-chunk-clear\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    '',
  )
  return collapseConsecutiveOfferPageBreaks(result)
}

/**
 * One pass: unwrap top-level *balanced* print-flow tables only.
 * Nested / leftover tables stay in the extracted body for a later pass.
 */
function unwrapOfferPrintFlowTablesOnce(innerHtml) {
  if (!innerHtml) return ''
  const result = String(innerHtml)
  let out = ''
  let i = 0
  let unwrapped = 0
  while (i < result.length) {
    const slice = result.slice(i)
    const openRe = new RegExp(PRINT_FLOW_TABLE_OPEN_RE.source, 'i')
    const openMatch = slice.match(openRe)
    if (!openMatch || openMatch.index == null) {
      out += result.slice(i)
      break
    }
    const tableStart = i + openMatch.index
    out += result.slice(i, tableStart)
    const openEnd = tableStart + openMatch[0].length
    const tableClose = findMatchingTableClose(result, openEnd)
    if (tableClose < 0) {
      out += result.slice(tableStart)
      break
    }
    const tableHtml = result.slice(tableStart, tableClose + 8)
    const body = extractOfferPrintFlowBodyInner(tableHtml)
    if (String(body || '').replace(/\s+/g, '')) {
      out += body
    }
    unwrapped += 1
    i = tableClose + 8
  }
  return unwrapped ? out : result
}

/**
 * Unwrap existing print-flow tables so content can be re-split at page breaks.
 * Forced breaks inside a single table prevent Chromium from repeating thead
 * (content lands under the letterhead — PDF 25 pp.2/7).
 *
 * IMPORTANT: must be depth-aware. A naive `([\s\S]*?)</td>` truncates at the
 * first nested cell (e.g. offer-greeting-row), corrupting HTML on the second
 * ensureOfferLetterChrome pass and collapsing letterhead clearance (PDF 28 p.2).
 *
 * Also strips unclosed nested wrappers. A single unwrap used to leave nested
 * `.offer-print-flow` tables in the body, then ensureOfferPrintFlowSpacers
 * wrapped another outer table. Saved EDF India HTML reached 25 nesting levels
 * / 110 tables / 85 forced breaks — Puppeteer emitted 157 letterhead-only pages.
 */
export function unwrapOfferPrintFlowTables(innerHtml) {
  if (!innerHtml) return ''
  let result = stripOfferPrintFlowChrome(innerHtml)

  let guard = 0
  while (guard < 40 && new RegExp(PRINT_FLOW_TABLE_OPEN_RE.source, 'i').test(result)) {
    guard += 1
    const next = unwrapOfferPrintFlowTablesOnce(result)
    if (next === result) {
      result = stripOfferPrintFlowChrome(result)
      break
    }
    result = next
  }
  return closeUnclosedSalaryTables(collapseConsecutiveOfferPageBreaks(result))
}

/** Unwrap print-flow tables inside `.content` so marker reorder works (PDF 51). */
function flattenOfferContentPrintFlow(html) {
  if (!html || !/offer-print-flow/i.test(html)) return html
  const contentOpen = html.match(OFFER_CONTENT_WRAPPER_OPEN_RE)
  if (!contentOpen || contentOpen.index == null) return html
  const openEnd = contentOpen.index + contentOpen[0].length
  const contentClose = findMatchingDivClose(html, openEnd)
  if (contentClose < 0) return html
  const flat = unwrapOfferPrintFlowTables(html.slice(openEnd, contentClose)).trim()
  if (!flat) return html
  return `${html.slice(0, openEnd)}\n${flat}\n</div>${html.slice(contentClose + 6)}`
}

/**
 * Drop unmarked leftover wrapper breaks immediately before after-break tables
 * that already have inline page-break-before (double CSS break → blank page).
 * User-configured <!-- OFFER_*PAGE_BREAK --> units stay — Chromium/Puppeteer
 * honors the standalone div, not page-break-before on the following table.
 */
function stripRedundantPageBreakBeforeAfterFlowTable(html) {
  if (!html) return html
  return String(html).replace(
    /((?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?)(<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*)(?=\s*(?:<!--\s*OFFER_PRINT_FLOW_START\s*-->\s*)?<table\b[^>]*>)/gi,
    (full, marker, _div, offset, source) => {
      if (isUserConfiguredPageBreakMarker(marker)) return full
      const after = String(source).slice(offset + full.length)
      const tableTag = (after.match(/<table\b[^>]*>/i) || [])[0] || ''
      if (
        /offer-print-flow-after-break/i.test(tableTag)
        && /page-break-before\s*:\s*always/i.test(tableTag)
      ) {
        return ''
      }
      return full
    },
  )
}

const OFFER_PAGE_BREAK_SPLIT_RE = /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?(?:<div[^>]*class="[^"]*\boffer-pdf-force-break\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?|<!--\s*OFFER_PAGE_BREAK\s*-->/gi

/** True when a print-flow chunk has real letter text (not leftover wrapper chrome). */
function offerPrintFlowChunkIsMeaningful(html) {
  return !!String(html || '')
    .replace(/<div[^>]*class="[^"]*\boffer-print-chunk-clear\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(/<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, '')
}

/**
 * Split flat content at forced page breaks and wrap each chunk in its own
 * print-flow table so thead letterhead clearance repeats on every page.
 *
 * User-configured Starts: New page units keep a standalone marker plus
 * page-break-before on the following print-flow TABLE. Chromium/Puppeteer
 * print ignores empty height:0 offer-page-break divs, so the table (a sibling
 * after the signature table, not a cell) is the PDF page boundary.
 * Leftover unmarked wrapper breaks are dropped — they created empty letterheads.
 */
function buildSplitOfferPrintFlowHtml(flatInner) {
  const flat = String(flatInner || '')
  const chunks = []
  let lastIndex = 0
  OFFER_PAGE_BREAK_SPLIT_RE.lastIndex = 0
  let match = OFFER_PAGE_BREAK_SPLIT_RE.exec(flat)
  while (match) {
    chunks.push({ type: 'content', html: flat.slice(lastIndex, match.index) })
    const markerMatch = match[0].match(/<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->/i)
    chunks.push({
      type: 'break',
      marker: markerMatch ? markerMatch[0] : '',
      userConfigured: !!markerMatch,
    })
    lastIndex = match.index + match[0].length
    match = OFFER_PAGE_BREAK_SPLIT_RE.exec(flat)
  }
  chunks.push({ type: 'content', html: flat.slice(lastIndex) })

  const parts = []
  let pending = []
  let seenContent = false
  chunks.forEach((chunk) => {
    if (chunk.type === 'break') {
      pending.push(chunk)
      return
    }
    if (!offerPrintFlowChunkIsMeaningful(chunk.html)) {
      pending = pending.filter(item => item.userConfigured)
      return
    }
    if (!seenContent) {
      pending = pending.filter(item => item.userConfigured)
    }
    seenContent = true

    const userBreaks = pending.filter(item => item.userConfigured)
    pending = []

    const emitBreak = (breakChunk) => {
      const markerHtml = breakChunk.marker ? `${breakChunk.marker}\n` : ''
      // Marker only — page-break-before lives on the following table, not here.
      parts.push(`\n${markerHtml}${PAGE_BREAK_UNIT_HTML}\n`)
    }
    // Only Starts: New page (named markers). Leftover unmarked wrapper
    // breaks from nested print-flow tables created empty letterhead pages.
    userBreaks.forEach(emitBreak)
    const forcedThisChunk = userBreaks.length > 0

    const table = buildOfferPrintFlowTable(chunk.html, {
      afterPageBreak: forcedThisChunk,
      pageBreakBefore: forcedThisChunk,
    })
    if (table) parts.push(table)
  })
  return parts.join('\n')
}

/**
 * Find depth-aware close index for a div that opened at openEnd (after `>`).
 * @returns {number} index of `</div>` start, or -1
 */
function findMatchingDivClose(html, openEnd) {
  let depth = 1
  let i = openEnd
  while (i < html.length) {
    const nextOpen = html.slice(i).search(/<div\b/i)
    const nextClose = html.slice(i).search(/<\/div>/i)
    if (nextClose < 0) return -1
    if (nextOpen >= 0 && nextOpen < nextClose) {
      depth += 1
      i += nextOpen + 4
    } else {
      depth -= 1
      i += nextClose + 6
      if (depth === 0) return i - 6
    }
  }
  return -1
}

/** Depth-aware `</td>` index for a `<td...>` that opened at openEnd (after `>`). */
function findMatchingTdClose(html, openEnd) {
  let depth = 1
  let i = openEnd
  while (i < html.length) {
    const nextOpen = html.slice(i).search(/<td\b/i)
    const nextClose = html.slice(i).search(/<\/td>/i)
    if (nextClose < 0) return -1
    if (nextOpen >= 0 && nextOpen < nextClose) {
      depth += 1
      i += nextOpen + 3
    } else {
      depth -= 1
      i += nextClose + 5
      if (depth === 0) return i - 5
    }
  }
  return -1
}

/** Depth-aware `</table>` index for a `<table...>` that opened at openEnd (after `>`). */
function findMatchingTableClose(html, openEnd) {
  let depth = 1
  let i = openEnd
  while (i < html.length) {
    const nextOpen = html.slice(i).search(/<table\b/i)
    const nextClose = html.slice(i).search(/<\/table>/i)
    if (nextClose < 0) return -1
    if (nextOpen >= 0 && nextOpen < nextClose) {
      depth += 1
      i += nextOpen + 6
    } else {
      depth -= 1
      i += nextClose + 8
      if (depth === 0) return i - 8
    }
  }
  return -1
}

/**
 * Puppeteer/Chromium print ignores empty height:0 `.offer-page-break` divs.
 * Stamp the following print-flow TABLE (sibling after signatures, not a cell)
 * with page-break-before so Generate/Download PDF must start a new page.
 */
export function hardenOfferPdfForcedBreaks(html) {
  if (!html) return html
  // Drop leftover non-empty force-break spacers (they opened a blank A4).
  let result = String(html).replace(PDF_FORCE_BREAK_RE, '')
  // Empty marker before a stamped table must not also break.
  result = result.replace(
    /(<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?(<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*)(?=(?:\s|<!--[\s\S]*?-->)*<table\b[^>]*\boffer-print-flow\b)/gi,
    (full, marker = '', _div, offset, source) => {
      const after = String(source).slice(offset + full.length)
      const tableTag = (after.match(/<table\b[^>]*>/i) || [])[0] || ''
      const tableAlreadyBreaks = /offer-print-flow-after-break/i.test(tableTag)
        || /page-break-before\s*:\s*always/i.test(tableTag)
      if (marker || tableAlreadyBreaks) {
        return `${marker || ''}${PAGE_BREAK_UNIT_HTML}\n`
      }
      return full
    },
  )
  return result.replace(
    /(<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*(?:<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*)?)((?:\s|<!--[\s\S]*?-->)*)(<table\b[^>]*\boffer-print-flow\b[^>]*>)/gi,
    (full, markerBlock, mid, tableTag) => {
      let nextTable = tableTag
      if (!/offer-print-flow-after-break/i.test(nextTable)) {
        nextTable = nextTable.replace(
          /class\s*=\s*(["'])([^"']*)\1/i,
          (_, q, cls) => `class=${q}${cls} offer-print-flow-after-break${q}`,
        )
      }
      if (/page-break-before\s*:\s*always/i.test(nextTable)) {
        if (!/break-before\s*:\s*page/i.test(nextTable)) {
          nextTable = nextTable.replace(
            /page-break-before\s*:\s*always/i,
            'page-break-before: always; break-before: page',
          )
        }
        return `${markerBlock}${mid}${nextTable}`
      }
      if (/\bstyle\s*=/i.test(nextTable)) {
        nextTable = nextTable.replace(
          /\bstyle\s*=\s*(["'])([^"']*)\1/i,
          (_, q, style) => `style=${q}${style}; page-break-before: always; break-before: page${q}`,
        )
      } else {
        nextTable = nextTable.replace(
          /<table\b/i,
          '<table style="page-break-before: always; break-before: page;"',
        )
      }
      return `${markerBlock}${mid}${nextTable}`
    },
  )
}

function findPoliciesSplitIndexInFlow(inner) {
  const source = String(inner || '')
  const hits = [
    source.search(/<!--\s*OFFER_POLICIES_PAGE_BREAK\s*-->/i),
    source.search(/<!--\s*OFFER_BODY_START\s*-->/i),
    source.search(/<(?:p|h[1-6])[^>]*>[\s\S]{0,120}?Employment\s+Policies\s+(?:and|&)\s+Provisions/i),
  ].filter(i => i >= 0)
  return hits.length ? Math.min(...hits) : -1
}

/**
 * If signatures and Employment Policies still share one print-flow table
 * (generatePDFFromHTML repair used to strip the named break, then spacers
 * re-wrapped everything), split so Policies is a sibling table with a hard break.
 */
export function splitSharedPrintFlowBeforePolicies(html) {
  if (!html || !/offer-print-flow/i.test(html)) return html
  let result = String(html)
  const openRe = /<table\b[^>]*\boffer-print-flow\b[^>]*>/gi
  const ranges = []
  let match = openRe.exec(result)
  while (match) {
    const openEnd = match.index + match[0].length
    const close = findMatchingTableClose(result, openEnd)
    if (close >= 0) ranges.push({ start: match.index, end: close + 8 })
    match = openRe.exec(result)
  }
  for (let i = ranges.length - 1; i >= 0; i -= 1) {
    const range = ranges[i]
    const tableHtml = result.slice(range.start, range.end)
    const inner = extractOfferPrintFlowBodyInner(tableHtml)
    const hasPrior = /signature-section|OFFER_SIGNATURE_|Kindly reach out to your recruitment partner/i.test(inner)
    // Require a named Starts: New page unit. Do not invent a break for Same page
    // just because the Policies title follows signatures in one table.
    if (!hasPrior || !/OFFER_POLICIES_PAGE_BREAK/i.test(inner)) continue
    const splitAt = findPoliciesSplitIndexInFlow(inner)
    if (splitAt < 0) continue
    const before = inner.slice(0, splitAt).trim()
    let after = inner.slice(splitAt).trim()
    if (!before || !after) continue
    after = after.replace(
      /^(?:\s|<!--\s*OFFER_POLICIES_PAGE_BREAK\s*-->\s*)*(?:<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*)?(?:<div[^>]*class="[^"]*\boffer-pdf-force-break\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?/i,
      '',
    )
    const beforeTable = buildOfferPrintFlowTable(before, {
      afterPageBreak: false,
      pageBreakBefore: false,
    })
    const afterTable = buildOfferPrintFlowTable(after, {
      afterPageBreak: true,
      pageBreakBefore: true,
    })
    if (!beforeTable || !afterTable) continue
    result = `${result.slice(0, range.start)}${beforeTable}\n${buildPoliciesPageBreakBlock()}${afterTable}${result.slice(range.end)}`
  }
  return result
}

function findSalarySplitIndexInFlow(inner) {
  const source = String(inner || '')
  const hits = [
    source.search(/<!--\s*OFFER_PAGE_BREAK\s*-->/i),
    source.search(/<!--\s*OFFER_SALARY_START\s*-->/i),
    source.search(/<(?:p|h[1-6])[^>]*>[\s\S]{0,160}?Annexure\s+A/i),
    source.search(/<(?:p|h[1-6])[^>]*>[\s\S]{0,160}?Salary\s+Structure/i),
  ].filter(i => i >= 0)
  return hits.length ? Math.min(...hits) : -1
}

/**
 * Notes + Annexure often share one print-flow table. Preview JS-splits on
 * `<!-- OFFER_PAGE_BREAK -->`; Chromium ignores that marker inside a cell.
 * Split so Salary is a sibling table with one page-break-before.
 */
export function splitSharedPrintFlowBeforeSalary(html) {
  if (!html || !/offer-print-flow/i.test(html)) return html
  let result = String(html)
  const openRe = /<table\b[^>]*\boffer-print-flow\b[^>]*>/gi
  const ranges = []
  let match = openRe.exec(result)
  while (match) {
    const openEnd = match.index + match[0].length
    const close = findMatchingTableClose(result, openEnd)
    if (close >= 0) ranges.push({ start: match.index, end: close + 8 })
    match = openRe.exec(result)
  }
  for (let i = ranges.length - 1; i >= 0; i -= 1) {
    const range = ranges[i]
    const tableHtml = result.slice(range.start, range.end)
    const inner = extractOfferPrintFlowBodyInner(tableHtml)
    if (!/OFFER_PAGE_BREAK/i.test(inner) && !/OFFER_SALARY_START/i.test(inner)) continue
    // Same page: no named annexure break. Do not invent one from the title.
    if (!/<!--\s*OFFER_PAGE_BREAK\s*-->/i.test(inner)) continue
    const splitAt = findSalarySplitIndexInFlow(inner)
    if (splitAt < 0) continue
    const before = inner.slice(0, splitAt).trim()
    let after = inner.slice(splitAt).trim()
    if (!before || !after) continue
    if (!offerPrintFlowChunkIsMeaningful(before) || !offerPrintFlowChunkIsMeaningful(after)) continue
    after = after.replace(
      /^(?:\s|<!--\s*OFFER_PAGE_BREAK\s*-->\s*)*(?:<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*)?(?:<div[^>]*class="[^"]*\boffer-pdf-force-break\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?/i,
      '',
    )
    const beforeTable = buildOfferPrintFlowTable(before, {
      afterPageBreak: false,
      pageBreakBefore: false,
    })
    const afterTable = buildOfferPrintFlowTable(after, {
      afterPageBreak: true,
      pageBreakBefore: true,
    })
    if (!beforeTable || !afterTable) continue
    result = `${result.slice(0, range.start)}${beforeTable}\n${buildPageBreakBlock(true)}${afterTable}${result.slice(range.end)}`
  }
  return result
}

/** Re-apply Salary Starts on generate (stamp-only PDF options omit offerLayout). */
export function applySalaryAnnexurePageStart(html, fields = {}) {
  if (!html) return html
  const layout = getOfferLayoutFromFields(fields)
  const showSalary = isSectionVisible(fields, 'salary')
  const shouldBreak = showSalary && resolveSectionPageBreakEnabled(
    layout,
    'salary',
    fields.includePageBreak !== false,
  )
  let result = upsertPageBreak(html, shouldBreak)
  result = splitSharedPrintFlowBeforeSalary(result)
  return result
}

/**
 * Wrap .content in thead/tfoot spacer table(s). Forced page breaks sit BETWEEN
 * tables (never inside tbody) so Chromium repeats letterhead clearance on every page.
 */
export function ensureOfferPrintFlowSpacers(html) {
  if (!html || typeof html !== 'string') return html

  let result = html
  if (!/offer-print-flow\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, offerContentFlowCss())
  }

  const contentOpen = result.match(OFFER_CONTENT_WRAPPER_OPEN_RE)
  if (!contentOpen || contentOpen.index == null) return hardenOfferPdfForcedBreaks(result)

  const openEnd = contentOpen.index + contentOpen[0].length
  const contentClose = findMatchingDivClose(result, openEnd)
  if (contentClose < 0) return hardenOfferPdfForcedBreaks(result)

  const rawInner = result.slice(openEnd, contentClose)
  const flatInner = collapseConsecutiveOfferPageBreaks(
    unwrapOfferPrintFlowTables(rawInner).trim(),
  )
  if (!flatInner) return hardenOfferPdfForcedBreaks(result)

  const splitHtml = buildSplitOfferPrintFlowHtml(flatInner)
  let openTag = contentOpen[0]
  openTag = openTag.replace(/\bclass\s*=\s*(["'])([^"']*)\1/i, (_, q, cls) => {
    const cleaned = cls.replace(/\boffer-content-padded\b/g, ' ').replace(/\s+/g, ' ').trim()
    return `class=${q}${cleaned}${q}`
  })
  const rebuilt = `${result.slice(0, contentOpen.index)}${openTag}\n${splitHtml}\n</div>${result.slice(contentClose + 6)}`
  return hardenOfferPdfForcedBreaks(
    splitSharedPrintFlowBeforeSalary(splitSharedPrintFlowBeforePolicies(rebuilt)),
  )
}

/**
 * Ensure assembled / generated offer HTML includes the watermark placeholder
 * and fixed-position CSS used by production templates. Idempotent.
 * @param {string} html
 * @returns {string}
 */
export function ensureOfferWatermarkMarkup(html) {
  if (!html || typeof html !== 'string') return html

  let result = html
  const hasPlaceholder = result.includes(WATERMARK_PLACEHOLDER)
    || /\{\{\s*METADATA\.IMAGES\.WATERMARK\s*\}\}/i.test(result)

  if (!/\.watermark\s*\{/.test(result)) {
    result = injectCssIntoHtml(result, WATERMARK_CSS)
  }

  if (!hasPlaceholder) {
    const imgTag = `\n  ${WATERMARK_IMG}\n`
    if (/<body[^>]*>/i.test(result)) {
      result = result.replace(/<body[^>]*>/i, match => `${match}${imgTag}`)
    } else {
      result = `${imgTag}${result}`
    }
  }

  return result
}

/**
 * Ensure fixed header (edge-to-edge banner at page top:0), footer (small type),
 * and watermark chrome used by production offer PDFs. Idempotent.
 * @param {string} html
 * @param {{ offerSignaturePlacement?: string|string[], offerEmployeeSignPlacement?: object, signatoryName?: string, signatoryDesignation?: string }} [options]
 * @returns {string}
 */
export function ensureOfferLetterChrome(html, options = {}) {
  if (!html || typeof html !== 'string') return html

  const signaturePlacement = detectOfferSignaturePlacement(
    html,
    options && options.offerSignaturePlacement,
  )
  const employeeSignPlacement = detectOfferEmployeeSignConfig(
    html,
    options && options.offerEmployeeSignPlacement,
  )
  // Preserve inlined stamp before signature blocks are rebuilt with the token.
  const signatureSrc = extractOfferSignatureImageSrc(html, options)
  const signatureFields = {
    signatoryName: options.signatoryName || '',
    signatoryDesignation: options.signatoryDesignation || '',
    offerEmployeeSignPlacement: employeeSignPlacement,
    offerEmployeeSignConfig: employeeSignPlacement,
    signatureSrc,
  }

  let result = html
  const needsHeaderCss = !/\.header-section\s*\{/.test(result)
  const needsFooterCss = !/\.footer-section\s*\{/.test(result)
  const needsAtPage = !/@page\s*\{/.test(result)
  const needsContentCss = !/\.content\s*\{/.test(result)

  if (needsHeaderCss || needsFooterCss || needsAtPage || needsContentCss) {
    // Inject only the rules that are missing to avoid bloating good templates.
    const parts = []
    if (needsAtPage) parts.push(offerPageMarginRule())
    if (needsHeaderCss) {
      parts.push(offerHeaderSectionCss())
      parts.push(offerHeaderImageCss())
      parts.push(offerHeaderImageImgCss())
      parts.push(offerCompanyInfoCss())
      parts.push(offerCompanyLogoSectionCss())
      parts.push(offerCompanyLogoCss())
      parts.push(offerCompanyNameCss())
    }
    if (needsFooterCss) {
      parts.push(offerFooterSectionCss())
      parts.push(offerFooterLineCss())
    }
    if (needsContentCss) {
      parts.push(offerContentCss())
      parts.push(OFFER_LETTER_TITLE_CSS)
      parts.push(offerGreetingBlockCss())
    } else if (!/\.offer-greeting-block\s*\{/.test(result)) {
      parts.push(offerGreetingBlockCss())
    }
    if (!/html,\s*body\s*\{/.test(result)) {
      parts.push(offerBodyTypographyCss())
    }
    result = injectCssIntoHtml(result, parts.join(' '))
  }

  // Upgrade saved templates to current production chrome (margins, footer, fonts).
  result = upgradeOfferChromeStyles(result)
  result = repairOfferHeaderMarkup(result)
  result = repairOfferGreetingMarkup(result)
  result = ensureOfferPageMargins(result)
  result = repairLegacyContentMargins(result)
  result = upgradeOfferFooterMarkup(result)

  if (hasHeaderSectionMarkup(result)) {
    result = ensureBodyChromePadding(result)
  }

  if (!hasHeaderSectionMarkup(result)) {
    // Fallback when body tag is missing or markup could not be normalized.
    result = removeFlowingHeaderImages(result)

    const needsBodyPad = !hasContentWrapperMarkup(result)
    if (needsBodyPad) {
      if (!/body\.offer-chrome-padded\s*\{/.test(result)) {
        result = injectCssIntoHtml(result, offerBodyChromePaddingRule())
      } else {
        result = result.replace(/body\.offer-chrome-padded\s*\{[^}]*\}/gi, offerBodyChromePaddingRule())
      }
    }

    if (/<body[^>]*>/i.test(result)) {
      result = result.replace(/<body([^>]*)>/i, (match, attrs) => {
        let nextAttrs = attrs || ''
        if (needsBodyPad && !/\boffer-chrome-padded\b/.test(nextAttrs)) {
          if (/class\s*=\s*["'][^"']*["']/i.test(nextAttrs)) {
            nextAttrs = nextAttrs.replace(
              /class\s*=\s*(["'])([^"']*)\1/i,
              (_, q, cls) => `class=${q}${cls} offer-chrome-padded${q}`,
            )
          } else {
            nextAttrs = `${nextAttrs} class="offer-chrome-padded"`
          }
        }
        return `<body${nextAttrs}>\n  ${DEFAULT_HEADER_HTML}\n`
      })
    } else {
      result = `${DEFAULT_HEADER_HTML}\n${result}`
    }
  }

  if (!hasFooterSectionMarkup(result)) {
    if (/<\/body>/i.test(result)) {
      result = result.replace(/<\/body>/i, `  ${DEFAULT_FOOTER_HTML}\n</body>`)
    } else {
      result = `${result}\n${DEFAULT_FOOTER_HTML}`
    }
  } else {
    result = upgradeOfferFooterMarkup(result)
  }

  result = ensureOfferLetterTitle(result)
  result = ensureOfferWatermarkMarkup(result)
  // Normalize legacy watermark alt text that Chromium may paint when src is empty.
  result = result.replace(
    /(<img[^>]*class\s*=\s*["'][^"']*\bwatermark\b[^"']*["'][^>]*?)\s+alt\s*=\s*["'][^"']*["']/gi,
    '$1 alt=""',
  )
  // Print layout repairs (PDF path may skip assembleOfferTemplate).
  result = normalizeManualPageBreaksInHtml(result)
  // Drop body-leading page breaks before print-flow split (avoids blank page 2).
  result = stripLeadingPageBreaksInsideMarkedSection(result, MARKER_TAGS.body)
  result = repairOfferSectionReadingOrder(result)
  // Keep Quill blank lines visible even when PDF skips assembleOfferTemplate.
  result = preserveOfferBlankLineSpacers(result)
  result = markOfferMajorHeadings(result)
  result = stripDuplicateOfferSectionTitles(result)
  result = ensureOfferSectionTitlesPresent(result)
  result = markOfferMajorHeadings(result)
  result = preserveOfferAuthorBodyFormatting(result)
  result = wrapOfferKeepTogetherBlocks(result)
  // Drop prior employee lines (incl. orphans outside .signature-section) before rebuild.
  result = stripEmployeeAcceptanceMarkup(result)
  result = ensureOfferSignatureBlock(result, signatureFields)
  result = collapseDuplicateEmployeeAcceptance(result)
  // Re-order after signature injection (new blocks may land before salary).
  result = repairOfferSectionReadingOrder(result)
  // Joining must stay with policies after reorder — never orphan before annexure (PDF 48 p5).
  result = ensureJoiningDateBeforeAnnexure(result)
  result = stripOrphanSignatureImages(result)
  result = ensureDefaultSectionPageBreaks(result, signaturePlacement)
  result = repairSectionPageBreakDivs(result)
  result = collapseConsecutiveOfferPageBreaks(result)
  // Split print-flow AFTER break cleanup so forced breaks sit between tables.
  result = ensureOfferPrintFlowSpacers(result)
  result = stripRedundantPageBreakBeforeAfterFlowTable(result)
  result = ensurePageBreakTopClears(result)
  // Drop empty Quill centered headings that push Policies title down / under letterhead.
  result = stripEmptyCenteredOfferHeadings(result)
  // Body Quill often starts with a manual "Page break" while Policies already has
  // pageBreakBefore — that produced a blank page between stamp and Policies (PDF 40).
  result = stripLeadingPageBreaksInsideMarkedSection(result, MARKER_TAGS.body)
  // Dual-stamp extras MUST be applied after print-flow rebuild — ensureOfferPrintFlowSpacers
  // previously dropped signature-section-extra inserted after OFFER_INTRO_END (PDF 35).
  result = applyMultiOfferSignaturePlacements(result, signaturePlacement, signatureFields)
  // Stamp insert can sit between forced breaks; re-collapse + drop empty after-break chunks
  // that otherwise become a blank page before Policies (PDF 40/43 page 2).
  result = collapseConsecutiveOfferPageBreaks(result)
  result = stripEmptyOfferPrintFlowChunks(result)
  // Always refresh keep-together / left subsection + centered major-heading rules.
  result = upsertOfferPrintFlowRules(result)
  result = upsertOfferSignaturePlacementMarker(result, signaturePlacement)
  result = upsertOfferEmployeeSignConfigMarker(result, employeeSignPlacement)
  return result
}

/**
 * Harden Terms + Signature page breaks for saved HTML that lost break markers
 * (e.g. legacy removeCanonicalPageBreak wiped sibling section breaks).
 * Honors offerSignaturePlacement so withClosing is not re-forced onto a stamp page.
 * @param {string} html
 * @param {string} [placement]
 */
function ensureDefaultSectionPageBreaks(html, placement) {
  if (!html) return html
  let result = html
  const locs = normalizeOfferSignatureLocations(placement)

  const ensureBefore = (startIdx, marker, buildBlock) => {
    if (startIdx < 0) return
    const windowStart = Math.max(0, startIdx - 320)
    const before = result.slice(windowStart, startIdx)
    if (before.includes(marker) || /offer-page-break/i.test(before.slice(-160))) return
    const cleanedBefore = before
      .replace(/<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>\s*$/i, '')
    result = `${result.slice(0, windowStart)}${cleanedBefore}${buildBlock()}${result.slice(startIdx)}`
  }

  // Annexure new page only when Starts is not explicitly Same page.
  const starts = parseOfferPageStartsMarker(result)
  const salaryContinue = !!(
    starts
    && starts.sections
    && starts.sections.salary
    && starts.sections.salary.pageStartMode === OFFER_PAGE_START_MODE.CONTINUE
  )
  const salaryIdx = result.indexOf(MARKER_TAGS.salary[0])
  if (salaryIdx >= 0 && !salaryContinue) {
    ensureBefore(salaryIdx, PAGE_BREAK_MARKER, () => `\n${PAGE_BREAK_MARKER}\n${PAGE_BREAK_UNIT_HTML}\n`)
  }

  const termsIdx = result.indexOf(MARKER_TAGS.terms[0])
  if (termsIdx >= 0) {
    ensureBefore(termsIdx, TERMS_PAGE_BREAK_MARKER, buildTermsPageBreakBlock)
  } else {
    const termsHeading = result.search(/>\s*Terms\s+and\s+Conditions\s+(?:Of\s+)?Employment\s*</i)
    if (termsHeading >= 0) {
      const tagStart = result.lastIndexOf('<', termsHeading)
      ensureBefore(tagStart >= 0 ? tagStart : termsHeading, TERMS_PAGE_BREAK_MARKER, buildTermsPageBreakBlock)
    }
  }

  const closingIdx = result.indexOf(MARKER_TAGS.closing[0])
  const signatureIdx = result.indexOf(MARKER_TAGS.signature[0])
  const sigSection = result.search(/<div[^>]*class="[^"]*\bsignature-section\b(?![^"]*signature-section-extra)[^"]*"[^>]*>/i)

  const wantsLastPage = locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)

  // Default (first page + after Terms) must not invent a leftover last stamp page.
  // After-Terms stamp is an extra after TERMS_END — never a break before primary.
  // Keep a user-configured Closing Starts: New page marker; only drop signature leftover.
  if (!wantsLastPage) {
    result = stripPageBreakMarker(result, SIGNATURE_PAGE_BREAK_MARKER)
    result = removeSignaturePageBreakFromGap(result, null)
    return result
  }

  // Dedicated last stamp page only when Last page is explicitly combined
  // with another location (lastPage-only migrates to the product default).
  if (closingIdx >= 0 && (signatureIdx < 0 || closingIdx < signatureIdx)) {
    ensureBefore(closingIdx, CLOSING_PAGE_BREAK_MARKER, buildClosingPageBreakBlock)
  } else if (signatureIdx >= 0) {
    ensureBefore(signatureIdx, SIGNATURE_PAGE_BREAK_MARKER, buildSignaturePageBreakBlock)
  } else if (sigSection >= 0) {
    ensureBefore(sigSection, SIGNATURE_PAGE_BREAK_MARKER, buildSignaturePageBreakBlock)
  }

  return result
}

function removeDivBlocksByClassName(html, className) {
  if (!html || !className) return html
  let result = String(html)
  const openRe = new RegExp(
    `<div\\b[^>]*class="[^"]*\\b${escapeRegExp(className)}\\b[^"]*"[^>]*>`,
    'i',
  )
  let guard = 0
  while (guard < 40) {
    guard += 1
    const match = result.match(openRe)
    if (!match || match.index == null) break
    const openStart = match.index
    const openEnd = openStart + match[0].length
    const closeStart = findMatchingDivClose(result, openEnd)
    if (closeStart < 0) break
    const closeEnd = closeStart + 6
    let end = closeEnd
    while (end < result.length && /\s/.test(result[end])) end += 1
    result = `${result.slice(0, openStart)}${result.slice(end)}`
  }
  return result
}

/**
 * Remove print-flow tables whose body is only clears / whitespace (blank PDF pages).
 */
function stripEmptyOfferPrintFlowChunks(html) {
  if (!html) return html
  let result = String(html)
  let guard = 0
  let searchFrom = 0
  while (guard < 400) {
    guard += 1
    const slice = result.slice(searchFrom)
    const match = slice.match(/<table\b[^>]*class="[^"]*\boffer-print-flow\b[^"]*"[^>]*>/i)
    if (!match || match.index == null) break
    const tableStart = searchFrom + match.index
    const openEnd = tableStart + match[0].length
    const tableClose = findMatchingTableClose(result, openEnd)
    if (tableClose < 0) break
    const tableHtml = result.slice(tableStart, tableClose + 8)
    const inner = extractOfferPrintFlowBodyInner(tableHtml)
      .replace(/<div[^>]*class="[^"]*\boffer-print-chunk-clear\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>/gi, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, '')
    if (inner) {
      searchFrom = tableClose + 8
      continue
    }
    let end = tableClose + 8
    while (end < result.length && /\s/.test(result[end])) end += 1
    result = `${result.slice(0, tableStart)}${result.slice(end)}`
    // Stay at tableStart to scan whatever shifted into this position.
    searchFrom = tableStart
  }
  return result
}

/**
 * Remove leading manual/section page-break units inside a marked section so a
 * section-level pageBreakBefore is not doubled (blank page).
 */
function stripLeadingPageBreaksInsideMarkedSection(html, markers) {
  if (!html || !markers) return html
  const [startMarker, endMarker] = markers
  const start = html.indexOf(startMarker)
  if (start < 0) return html
  const end = html.indexOf(endMarker, start + startMarker.length)
  if (end < 0) return html
  const innerStart = start + startMarker.length
  const inner = html.slice(innerStart, end)
  // Strip only unmarked leftover + leading Quill manual breaks.
  // Named Starts: New page markers (POLICIES/SALARY/TERMS/…) must stay.
  const leadingBreaks = /^(?:\s|<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->\s*)*(?:<div[^>]*class="[^"]*\boffer-(?:manual-)?page-break\b[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?)+/i
  const next = inner.replace(leadingBreaks, '')
  if (next === inner) return html
  return `${html.slice(0, innerStart)}${next}${html.slice(end)}`
}

/** Drop trailing manual page breaks inside a marked section (PDF 51 p4 blank before annexure). */
function stripTrailingPageBreaksInsideMarkedSection(html, markers) {
  if (!html || !markers) return html
  const [startMarker, endMarker] = markers
  const start = html.indexOf(startMarker)
  if (start < 0) return html
  const end = html.indexOf(endMarker, start + startMarker.length)
  if (end < 0) return html
  const innerStart = start + startMarker.length
  const inner = html.slice(innerStart, end)
  const trailingBreaks = /(?:\s|<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->\s*)*(?:<div[^>]*class="[^"]*\boffer-(?:manual-)?page-break\b[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?)+\s*$/i
  const next = inner.replace(trailingBreaks, '')
  if (next === inner) return html
  return `${html.slice(0, innerStart)}${next}${html.slice(end)}`
}

/**
 * Index of the last offer page-break unit before `atIndex`, or -1.
 * Must tolerate print-flow table wrappers between the break and the section
 * anchor (PDF 38 — trailing-only match missed the break and put the stamp
 * alone on the policies page).
 */
function findPageBreakStartBefore(html, atIndex) {
  if (!html || atIndex < 0) return -1
  const windowStart = Math.max(0, atIndex - 1200)
  const before = html.slice(windowStart, atIndex)
  const re = /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>/gi
  let lastIdx = -1
  let match = re.exec(before)
  while (match) {
    lastIdx = windowStart + match.index
    match = re.exec(before)
  }
  return lastIdx
}

/**
 * Close a dangling OFFER_INTRO_START when OFFER_INTRO_END was lost on save/chrome.
 * Inserts END just before the policies page-break / body so dual-stamp can anchor.
 */
function repairMissingIntroEndMarker(html) {
  if (!html) return html
  if (!html.includes(MARKER_TAGS.intro[0]) || html.includes(MARKER_TAGS.intro[1])) {
    return html
  }
  const candidates = [
    html.indexOf(POLICIES_PAGE_BREAK_MARKER),
    html.indexOf(MARKER_TAGS.jobResponsibilities[0]),
    html.indexOf(MARKER_TAGS.body[0]),
    html.search(/Employment\s+Policies\s+(?:and|&)\s+Provisions/i),
  ].filter(i => i >= 0)
  if (!candidates.length) return html
  let insertAt = Math.min(...candidates)
  const breakStart = findPageBreakStartBefore(html, insertAt)
  if (breakStart >= 0) insertAt = breakStart
  return `${html.slice(0, insertAt)}\n${MARKER_TAGS.intro[1]}\n${html.slice(insertAt)}`
}

/**
 * Place page-1 dual stamp on the opening page (before policies page break).
 * Avoid insert-before-BODY when BODY follows a forced break — that left the stamp
 * alone on page 2 (PDF 38).
 * @returns {{ html: string, how: string }}
 */
function placeOpeningPageDualStamp(html, primaryBlock) {
  let result = String(html || '')
  const insertAt = (idx, how) => {
    if (idx < 0) return null
    const before = result.slice(Math.max(0, idx - 160), idx)
    if (/signature-section-extra/i.test(before)) return how
    result = `${result.slice(0, idx)}\n${primaryBlock}\n${result.slice(idx)}`
    return how
  }
  const insertAfter = (markerEnd, how) => {
    const idx = result.indexOf(markerEnd)
    if (idx < 0) return null
    const at = idx + markerEnd.length
    const ahead = result.slice(at, at + 120)
    if (/signature-section-extra/i.test(ahead)) return how
    result = `${result.slice(0, at)}\n${primaryBlock}\n${result.slice(at)}`
    return how
  }

  const bodyStartIdx = () => result.indexOf(MARKER_TAGS.body[0])
  const stampIsAfterPoliciesBreak = () => {
    const extraIdx = result.search(/signature-section-extra/)
    const bodyStart = bodyStartIdx()
    const policiesTitle = result.search(/Employment\s+Policies\s+(?:and|&)\s+Provisions/i)
    const anchor = [bodyStart, policiesTitle].filter(i => i >= 0).sort((a, b) => a - b)[0]
    if (extraIdx < 0 || anchor == null) return false
    const breakStart = findPageBreakStartBefore(result, anchor)
    return breakStart >= 0 && extraIdx > breakStart
  }

  let how = insertAfter(MARKER_TAGS.intro[1], 'afterIntro')
  if (how && !stampIsAfterPoliciesBreak()) return { html: result, how }
  if (how && stampIsAfterPoliciesBreak()) {
    result = removeDivBlocksByClassName(result, 'signature-section-extra')
    how = null
  }

  how = insertAfter(MARKER_TAGS.jobResponsibilities[1], 'afterJobResp')
  if (how && !stampIsAfterPoliciesBreak()) return { html: result, how }
  if (how && stampIsAfterPoliciesBreak()) {
    result = removeDivBlocksByClassName(result, 'signature-section-extra')
    how = null
  }

  how = insertAt(result.indexOf(POLICIES_PAGE_BREAK_MARKER), 'beforePoliciesBreak')
  if (how) return { html: result, how }

  // Generic page-break before body / policies title (marker comment lost).
  const bodyStart = bodyStartIdx()
  const policiesTitle = result.search(/Employment\s+Policies\s+(?:and|&)\s+Provisions/i)
  const anchors = [bodyStart, policiesTitle].filter(i => i >= 0).sort((a, b) => a - b)
  for (let i = 0; i < anchors.length; i += 1) {
    const breakStart = findPageBreakStartBefore(result, anchors[i])
    if (breakStart >= 0) {
      how = insertAt(breakStart, 'beforePageBreakPrecedingBody')
      if (how) return { html: result, how }
    }
  }

  // Last resort: before BODY_START (may sit after a break if none detected).
  const bodyIdx = result.indexOf(MARKER_TAGS.body[0])
  if (bodyIdx >= 0) {
    how = insertAt(bodyIdx, 'beforeBody')
    if (how) return { html: result, how }
  }

  return { html: result, how: 'FAILED' }
}

/**
 * Place extra stamp copies at selected anchors (policies / terms / salary / withClosing).
 * Primary marked signature block stays where assemble put it; extras are unmarked copies.
 */
function applyMultiOfferSignaturePlacements(html, placement, fields = {}) {
  if (!html) return html
  const locs = normalizeOfferSignatureLocations(placement)
  let result = String(html)

  // Strip previous extras / every-page overlay so chrome is idempotent.
  result = removeDivBlocksByClassName(result, 'signature-section-extra')
  result = removeDivBlocksByClassName(result, 'offer-stamp-every-page')

  // First page / After Terms extras keep AS/HR + stamp. Employee Name/Signature
  // lines follow Stamp settings (default off) — never force them on extras.
  const hasPrimarySignature = result.includes(MARKER_TAGS.signature[0])
    || /<div[^>]*class\s*=\s*["'][^"']*\bsignature-section\b(?![^"']*\bsignature-section-extra\b)[^"']*["'][^>]*>/i.test(result)
  const wantsFirstPage = locs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING)
  const emp = resolveFieldsEmployeeSignConfig(fields)
  const wantsEmployee = !!(emp.withStamp && (emp.showName || emp.showSignature))
  let employeeExtraUsed = hasPrimarySignature
  let stampExtrasPlaced = 0
  const buildExtraBlock = (opts = {}) => {
    stampExtrasPlaced += 1
    const forFirstPage = opts.forFirstPage === true
    const forAfterTerms = opts.forAfterTerms === true
    const forFullSignOff = forFirstPage || forAfterTerms || opts.forFullSignOff === true
    let includeEmployee
    let omitSignatoryBlock
    if (forFullSignOff) {
      includeEmployee = wantsEmployee
      omitSignatoryBlock = false
      if (includeEmployee) employeeExtraUsed = true
    } else if (wantsFirstPage || hasPrimarySignature) {
      // Middle anchors (policies/salary) stay stamp-only when a full block exists elsewhere.
      includeEmployee = false
      omitSignatoryBlock = true
    } else {
      includeEmployee = wantsEmployee && !employeeExtraUsed
      if (includeEmployee) employeeExtraUsed = true
      omitSignatoryBlock = stampExtrasPlaced > 1
    }
    return buildSignatureHtml(fields, {
      extraClass: 'signature-section-extra',
      includeEmployee,
      omitSignatoryBlock,
    })
  }
  const insertAfterMarkerEnd = (markerEnd, block) => {
    const idx = result.indexOf(markerEnd)
    if (idx < 0) return false
    const at = idx + markerEnd.length
    // Avoid stacking duplicate extras back-to-back at the same spot.
    const ahead = result.slice(at, at + 120)
    if (/signature-section-extra/i.test(ahead)) return true
    result = `${result.slice(0, at)}\n${block}\n${result.slice(at)}`
    return true
  }
  const insertBeforeMarker = (markerStart, block) => {
    const idx = result.indexOf(markerStart)
    if (idx < 0) return false
    const before = result.slice(Math.max(0, idx - 160), idx)
    if (/signature-section-extra/i.test(before)) return true
    result = `${result.slice(0, idx)}\n${block}\n${result.slice(idx)}`
    return true
  }

  // First page BEFORE other extras so opening never becomes stamp-only.
  if (wantsFirstPage) {
    result = repairMissingIntroEndMarker(result)
    const placed = placeOpeningPageDualStamp(result, buildExtraBlock({ forFirstPage: true }))
    result = placed.html
  }

  // Primary block already lives after closing (lastPage). Add extras elsewhere.
  // ONLY when the user selected that placement — never after policies unless AFTER_POLICIES.
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_POLICIES)) {
    insertAfterMarkerEnd(MARKER_TAGS.body[1], buildExtraBlock())
  }
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS)
    && !locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)
    && !locs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING)) {
    // Primary may already be after terms via assemble — only add extra when primary is elsewhere.
    const sigIdx = result.indexOf(MARKER_TAGS.signature[0])
    const termsEnd = result.indexOf(MARKER_TAGS.terms[1])
    if (sigIdx >= 0 && termsEnd >= 0 && sigIdx > termsEnd + 40) {
      insertAfterMarkerEnd(MARKER_TAGS.terms[1], buildExtraBlock({ forAfterTerms: true }))
    }
  } else if (locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS)
    && (locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)
      || locs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING))) {
    // After Terms + first page (default): extra after T&C keeps AS/HR + stamp.
    // Employee lines stay off unless Stamp settings enable them.
    insertAfterMarkerEnd(
      MARKER_TAGS.terms[1],
      buildExtraBlock({ forAfterTerms: true }),
    )
  }
  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_SALARY)) {
    insertAfterMarkerEnd(MARKER_TAGS.salary[1], buildExtraBlock())
  }

  if (locs.includes(OFFER_SIGNATURE_PLACEMENT.EVERY_PAGE)) {
    if (!/offer-stamp-every-page\s*\{/.test(result)) {
      result = injectCssIntoHtml(result, OFFER_STAMP_EVERY_PAGE_CSS)
    }
    const stamp = buildEveryPageStampHtml(fields)
    if (/<body[^>]*>/i.test(result)) {
      result = result.replace(/<body([^>]*)>/i, `<body$1>\n${stamp}\n`)
    } else {
      result = `${stamp}\n${result}`
    }
  }

  return result
}

const OFFER_PRINT_FLOW_RULES_MARKER = '/* OFFER_PRINT_FLOW_RULES:v9 */'
/** Also replace legacy print-flow rule blocks on chrome upgrade. */
const OFFER_PRINT_FLOW_RULES_MARKERS = [
  OFFER_PRINT_FLOW_RULES_MARKER,
  '/* OFFER_PRINT_FLOW_RULES:v8 */',
  '/* OFFER_PRINT_FLOW_RULES:v7 */',
  '/* OFFER_PRINT_FLOW_RULES:v6 */',
  '/* OFFER_PRINT_FLOW_RULES:v5 */',
  '/* OFFER_PRINT_FLOW_RULES:v4 */',
  '/* OFFER_PRINT_FLOW_RULES:v3 */',
  '/* OFFER_PRINT_FLOW_RULES:v2 */',
]

function upsertOfferPrintFlowRules(html) {
  if (!html) return html
  const block = `${OFFER_PRINT_FLOW_RULES_MARKER}\n${offerContentFlowCss()}\n${OFFER_PRINT_FLOW_RULES_MARKER}`
  let result = html
  let replaced = false
  OFFER_PRINT_FLOW_RULES_MARKERS.forEach(marker => {
    const pattern = new RegExp(
      `${escapeRegExp(marker)}[\\s\\S]*?${escapeRegExp(marker)}`,
      'i',
    )
    if (pattern.test(result)) {
      result = result.replace(pattern, block)
      replaced = true
    }
  })
  if (replaced) return result
  return injectCssIntoHtml(result, block)
}

/** Centered major section titles (subsection headers like Employee Benefits stay left). */
const OFFER_MAJOR_HEADING_TEXT = [
  /Employment\s+Policies\s+(?:and|&)\s+Provisions/i,
  /^Job\s+Responsibilit(?:y|ies)\.?$/i,
  /Terms\s+and\s+Conditions\s+(?:Of\s+)?Employment/i,
  /Annexure\s+A\s*[:\-—]?\s*(?:Salary|Compensation)\s+Structure/i,
  /Annexure\s+A\s*[:\-—]?\s*Salary\s+Structure/i,
  /^Salary\s+Structure(?:\s+\d+)?$/i,
]

function isOfferMajorHeadingText(text) {
  const plain = String(text || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!plain) return false
  // Long body notes (validity / Important paragraphs) must never get major-title
  // CSS (center + bold + 1.2em) — that was forcing Offer Config copy off-style (PDF 45).
  if (plain.length > 100) return false
  return OFFER_MAJOR_HEADING_TEXT.some(pattern => pattern.test(plain))
}

function stripInlineTextAlignCenter(attrs = '') {
  if (!/\bstyle\s*=\s*(["'])/i.test(attrs)) return attrs
  return attrs.replace(/\bstyle\s*=\s*(["'])([^"']*)\1/i, (_, q, style) => {
    const cleaned = String(style)
      .replace(/text-align\s*:\s*[^;]+;?/gi, '')
      .replace(/;\s*;/g, ';')
      .trim()
      .replace(/^;|;$/g, '')
    return cleaned ? `style=${q}${cleaned}${q}` : ''
  })
}

function stripQuillCenterClass(attrs = '') {
  if (!/\bql-align-center\b/i.test(attrs) || !/\bclass\s*=\s*(["'])([^"']*)\1/i.test(attrs)) {
    return attrs
  }
  return attrs.replace(/\bclass\s*=\s*(["'])([^"']*)\1/i, (_, q, cls) => {
    const cleaned = cls.replace(/\bql-align-center\b/gi, ' ').replace(/\s+/g, ' ').trim()
    return cleaned ? `class=${q}${cleaned}${q}` : ''
  })
}

function demoteNonMajorParagraphHeadings(attrs = '', inner = '') {
  if (/\boffer-letter-title\b/i.test(attrs)) return attrs
  // Per-table Annexure titles must keep major/center styling even when the
  // text is "Salary Structure 2" (not a canonical section title).
  if (/\bsalary-structure-title\b/i.test(attrs)) return attrs
  if (isOfferMajorHeadingText(inner)) return attrs
  let next = attrs
  if (/\boffer-major-heading\b/i.test(next)) {
    next = next.replace(/\bclass\s*=\s*(["'])([^"']*)\1/i, (_, q, cls) => {
      const cleaned = cls.replace(/\boffer-major-heading\b/gi, ' ').replace(/\s+/g, ' ').trim()
      return cleaned ? `class=${q}${cleaned}${q}` : ''
    })
  }
  next = stripQuillCenterClass(next)
  next = stripInlineTextAlignCenter(next)
  next = next.replace(/\sclass\s*=\s*(["'])\s*\1/gi, '')
  return next
}

/**
 * Mark only major section titles for centering. Subsection / bullet headers stay left.
 * Also demotes stale offer-major-heading on non-major titles.
 */
export function markOfferMajorHeadings(html) {
  if (!html) return html
  let result = String(html)

  result = result.replace(
    /<(h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1>/gi,
    (full, tag, attrs = '', inner) => {
      if (/\boffer-letter-title\b/i.test(attrs)) return full
      const isMajor = isOfferMajorHeadingText(inner)
      if (!isMajor) {
        // Strip major + ql-align-center / inline center on non-major headings (PDF 45).
        const nextAttrs = demoteNonMajorParagraphHeadings(attrs || '', inner)
        if (nextAttrs === (attrs || '')) return full
        const trimmed = nextAttrs.trim()
        return `<${tag}${trimmed ? ` ${trimmed}` : ''}>${inner}</${tag}>`
      }
      if (/\boffer-major-heading\b/i.test(attrs)) return full
      if (/\bclass\s*=\s*(["'])([^"']*)\1/i.test(attrs)) {
        const nextAttrs = attrs.replace(
          /\bclass\s*=\s*(["'])([^"']*)\1/i,
          (_, q, cls) => `class=${q}${cls} offer-major-heading${q}`,
        )
        return `<${tag}${nextAttrs}>${inner}</${tag}>`
      }
      return `<${tag} class="offer-major-heading"${attrs}>${inner}</${tag}>`
    },
  )

  // Bold-only paragraphs that are major titles (Quill often emits these).
  result = result.replace(
    /<p(\s[^>]*)?>\s*<(strong|b)(\s[^>]*)?>([\s\S]*?)<\/\2>\s*<\/p>/gi,
    (full, pAttrs = '', _tag, _bAttrs, inner) => {
      const isMajor = isOfferMajorHeadingText(inner)
      if (!isMajor) {
        const nextAttrs = demoteNonMajorParagraphHeadings(pAttrs || '', inner)
        const openEnd = full.indexOf('>')
        const rest = full.slice(openEnd + 1)
        if (nextAttrs === (pAttrs || '')) return full
        const trimmed = nextAttrs.trim()
        return `<p${trimmed ? ` ${trimmed}` : ''}>${rest}`
      }
      if (/\boffer-major-heading\b/i.test(pAttrs || '')) return full
      const openEnd = full.indexOf('>')
      const open = full.slice(0, openEnd + 1)
      const rest = full.slice(openEnd + 1)
      if (/\bclass\s*=\s*(["'])([^"']*)\1/i.test(open)) {
        return open.replace(
          /\bclass\s*=\s*(["'])([^"']*)\1/i,
          (_, q, cls) => `class=${q}${cls} offer-major-heading${q}`,
        ) + rest
      }
      return `<p class="offer-major-heading"${pAttrs || ''}>${rest}`
    },
  )

  // Mixed-content paragraphs (e.g. "<strong>Important:</strong> This offer…") keep
  // stale offer-major-heading / ql-align-center from Quill — demote when not a major title.
  result = result.replace(
    /<p(\s[^>]*)>([\s\S]*?)<\/p>/gi,
    (full, attrs = '', inner) => {
      if (!/\boffer-major-heading\b/i.test(attrs)
        && !/\bql-align-center\b/i.test(attrs)
        && !/\bstyle\s*=\s*["'][^"']*text-align\s*:\s*center/i.test(attrs)) {
        return full
      }
      const nextAttrs = demoteNonMajorParagraphHeadings(attrs, inner)
      if (nextAttrs === attrs) return full
      const trimmed = nextAttrs.trim()
      return `<p${trimmed ? ` ${trimmed}` : ''}>${inner}</p>`
    },
  )

  return result
}

/**
 * Restore Offer Config body formatting for validity / Important notes.
 * Unwraps whole-paragraph bold and leftover center/bold styles after major demotion.
 */
export function preserveOfferAuthorBodyFormatting(html) {
  if (!html) return html
  return String(html).replace(
    /<(p|div|h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1>/gi,
    (full, tag, attrs = '', inner) => {
      if (/\boffer-letter-title\b|\boffer-major-heading\b/i.test(attrs || '')) return full
      const plain = String(inner || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      if (plain.length < 80) return full
      if (!/\bimportant\s*:/i.test(plain) && !/offer\s+is\s+valid\s+only\s+if\s+you\s+join/i.test(plain)) {
        return full
      }

      let nextAttrs = demoteNonMajorParagraphHeadings(attrs || '', inner)
      if (/\bstyle\s*=\s*(["'])([^"']*)\1/i.test(nextAttrs)) {
        nextAttrs = nextAttrs.replace(/\bstyle\s*=\s*(["'])([^"']*)\1/i, (_, q, style) => {
          const cleaned = String(style)
            .replace(/font-weight\s*:\s*(bold|[6-9]00)\s*;?/gi, '')
            .replace(/;\s*;/g, ';')
            .trim()
            .replace(/^;|;$/g, '')
          return cleaned ? `style=${q}${cleaned}${q}` : ''
        })
      }

      let nextInner = inner
      const wholeStrong = String(nextInner || '').match(
        /^\s*<(strong|b)(\s[^>]*)?>([\s\S]*?)<\/\1>\s*$/i,
      )
      if (wholeStrong) nextInner = wholeStrong[3]

      const outTag = /^h[1-6]$/i.test(tag) ? 'p' : tag
      const trimmed = nextAttrs.trim()
      return `<${outTag}${trimmed ? ` ${trimmed}` : ''}>${nextInner}</${outTag}>`
    },
  )
}

function findAllSignatureSectionRanges(html) {
  if (!html) return []
  const ranges = []
  const openRe = /<div[^>]*class\s*=\s*["'][^"']*\bsignature-section\b[^"']*["'][^>]*>/gi
  let openMatch = openRe.exec(html)
  while (openMatch) {
    const openStart = openMatch.index
    const openEnd = openStart + openMatch[0].length
    let depth = 1
    let i = openEnd
    while (i < html.length) {
      const nextOpen = html.slice(i).search(/<div\b/i)
      const nextClose = html.slice(i).search(/<\/div>/i)
      if (nextClose < 0) break
      if (nextOpen >= 0 && nextOpen < nextClose) {
        depth += 1
        i += nextOpen + 4
      } else {
        depth -= 1
        i += nextClose + 6
        if (depth === 0) {
          ranges.push({
            start: openStart,
            end: i,
            isExtra: /\bsignature-section-extra\b/i.test(openMatch[0]),
          })
          break
        }
      }
    }
    openMatch = openRe.exec(html)
  }
  return ranges
}

const SIGNATORY_LABEL_PARAGRAPH_RE = /<p[^>]*class="[^"]*\bsignatory-block\b[^"]*"[^>]*>[\s\S]*?<\/p>\s*/gi

/** Authorised Signatory paragraph + optional following Human Resources paragraph (Quill-safe). */
const SIGNATORY_AS_HR_PAIR_RE = /<p\b[^>]*>\s*(?:<(?:strong|b|span)[^>]*>\s*)*Authori[sz]ed\s+Signatory\b[\s\S]*?<\/p>\s*(?:<p\b[^>]*>\s*(?:<(?:strong|b|span)[^>]*>\s*)*Human\s+Resources\b[\s\S]*?<\/p>\s*)?/gi

const ORPHAN_HUMAN_RESOURCES_P_RE = /<p\b[^>]*>\s*(?:<(?:strong|b|span)[^>]*>\s*)*Human\s+Resources\s*(?:<\/(?:strong|b|span)>\s*)*<\/p>\s*/gi

function stripSignatoryLabelParagraphs(fragment) {
  if (!fragment) return fragment
  let s = String(fragment)
  s = s.replace(SIGNATORY_LABEL_PARAGRAPH_RE, '')
  s = s.replace(SIGNATORY_AS_HR_PAIR_RE, '')
  // Leftover HR lines when AS was removed alone by an older pass.
  s = s.replace(ORPHAN_HUMAN_RESOURCES_P_RE, '')
  return s
}

/**
 * Strip leaked AS/HR from letter copy, but never touch real `.signature-section`
 * stamp chrome (First page / After Terms / etc. may sit inside intro/terms).
 */
function stripSignatoryLabelParagraphsOutsideSignature(fragment) {
  if (!fragment) return fragment
  const raw = String(fragment)
  const parts = []
  const re = /<div\b[^>]*\bsignature-section\b[^>]*>[\s\S]*?<\/div>/gi
  let last = 0
  let m = re.exec(raw)
  while (m) {
    parts.push(stripSignatoryLabelParagraphs(raw.slice(last, m.index)))
    parts.push(m[0])
    last = m.index + m[0].length
    m = re.exec(raw)
  }
  parts.push(stripSignatoryLabelParagraphs(raw.slice(last)))
  return parts.join('')
}

function stripSignatoryLabelsFromMarkedSections(html) {
  if (!html) return html
  let result = String(html)
  ;['intro', 'closing', 'jobResponsibilities', 'body', 'terms', 'salary'].forEach(key => {
    const markers = MARKER_TAGS[key]
    if (!markers) return
    const start = result.indexOf(markers[0])
    if (start < 0) return
    const end = result.indexOf(markers[1], start + markers[0].length)
    if (end < 0) return
    const innerStart = start + markers[0].length
    const inner = result.slice(innerStart, end)
    const cleaned = stripSignatoryLabelParagraphsOutsideSignature(inner)
    if (cleaned === inner) return
    result = `${result.slice(0, innerStart)}${cleaned}${result.slice(end)}`
  })
  return result
}

function recoverSignatoryFieldsFromInner(inner, fields = {}) {
  const recovered = { ...fields }
  if (recovered.signatoryName) {
    recovered.signatoryName = sanitizeCompanySignatoryFieldValue(recovered.signatoryName)
    recovered.signatoryDesignation = sanitizeCompanySignatoryFieldValue(
      recovered.signatoryDesignation,
    )
    return recovered
  }
  const plain = stripHtmlTags(inner)
  const parsed = splitCompanySignatoryPlainLines(
    plain.split(/\n/).map(l => l.trim()).filter(Boolean),
  )
  if (parsed.signatoryName) {
    recovered.signatoryName = parsed.signatoryName
    recovered.signatoryDesignation = parsed.signatoryDesignation
  }
  return recovered
}

function findSignatureSectionRange(html) {
  if (!html) return null
  const all = findAllSignatureSectionRanges(html)
  return all.find(r => !r.isExtra) || all[0] || null
}

const OFFER_SIGNATURE_IMAGE_TOKEN = '{{METADATA.IMAGES.SIGNATURE}}'
const OFFER_SIGNATURE_IMAGE_TOKEN_RE = /\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/i
const OFFER_STAMP_IMG_CLASS_RE = /\b(?:signature-img|stamp-img|company-stamp|offer-stamp)\b/i

/**
 * Find a usable stamp/signature image src already inlined in HTML (or options).
 * Skips empty values and the METADATA.IMAGES.SIGNATURE placeholder token.
 * Used so ensureOfferLetterChrome can rebuild signature blocks without wiping
 * base64/http stamp URLs that configure preview already filled in.
 * @param {string} html
 * @param {object} [fallbacks]
 * @returns {string}
 */
export function extractOfferSignatureImageSrc(html, fallbacks = {}) {
  const unescapeAttr = (value) => String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

  const trySrc = (raw) => coerceOfferImageSrc(unescapeAttr(raw))

  const tags = String(html || '').match(/<img\b[^>]*>/gi) || []
  // Prefer dedicated stamp/signature classes first.
  for (let i = 0; i < tags.length; i += 1) {
    const tag = tags[i]
    if (!OFFER_STAMP_IMG_CLASS_RE.test(tag)) continue
    const src = (tag.match(/\bsrc\s*=\s*["']([^"']*)["']/i) || [])[1] || ''
    const trimmed = trySrc(src)
    if (trimmed) return trimmed
  }
  // Any <img> inside a signature-section / offer-stamp block.
  const sectionRe = /<div[^>]*class\s*=\s*["'][^"']*\b(?:signature-section|offer-stamp-every-page)\b[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi
  let sectionMatch = sectionRe.exec(String(html || ''))
  while (sectionMatch) {
    const innerImgs = sectionMatch[1].match(/<img\b[^>]*>/gi) || []
    for (let j = 0; j < innerImgs.length; j += 1) {
      const src = (innerImgs[j].match(/\bsrc\s*=\s*["']([^"']*)["']/i) || [])[1] || ''
      const trimmed = trySrc(src)
      if (trimmed) return trimmed
    }
    // CSS background-image on stamp containers (legacy templates).
    const bg = (sectionMatch[0].match(/background(?:-image)?\s*:\s*url\(\s*['"]?([^'")\s]+)['"]?\s*\)/i) || [])[1]
    const bgSrc = trySrc(bg)
    if (bgSrc) return bgSrc
    sectionMatch = sectionRe.exec(String(html || ''))
  }

  const images = (fallbacks && fallbacks.images) || {}
  const candidates = [
    fallbacks.signatureSrc,
    fallbacks.signatureUrl,
    fallbacks.signature,
    fallbacks.stamp,
    fallbacks.companyStamp,
    images.signature,
    images.SIGNATURE,
    images.stamp,
    images.companyStamp,
    images.company_stamp,
    images.signatoryImage,
    fallbacks.templateData && fallbacks.templateData.images && fallbacks.templateData.images.signature,
    fallbacks.templateData && fallbacks.templateData.signature,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const trimmed = coerceOfferImageSrc(candidates[i])
    if (trimmed) return trimmed
  }
  return ''
}

/**
 * Force-set stamp src on every signature/stamp <img>, or create a signature
 * section when none exists. Attribute order independent.
 * @param {string} html
 * @param {string} stampSrc
 * @returns {string}
 */
export function forceInjectOfferStampSrc(html, stampSrc) {
  const src = coerceOfferImageSrc(stampSrc)
  if (!html || !src) return html || ''
  const escaped = escapeHtml(src)
  let result = String(html)
  result = result.replace(/\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/gi, () => src)

  let touched = 0
  result = result.replace(/<img\b[^>]*>/gi, (tag) => {
    if (!OFFER_STAMP_IMG_CLASS_RE.test(tag)) return tag
    touched += 1
    if (/\bsrc\s*=\s*["'][^"']*["']/i.test(tag)) {
      return tag.replace(/\bsrc\s*=\s*["'][^"']*["']/i, `src="${escaped}"`)
    }
    return tag.replace(/<img\b/i, `<img src="${escaped}"`)
  })

  if (!touched) {
    // Inject into first signature-section if present but missing an img.
    const openRe = /<div[^>]*class\s*=\s*["'][^"']*\bsignature-section\b[^"']*["'][^>]*>/i
    const openMatch = result.match(openRe)
    if (openMatch) {
      const insertAt = result.indexOf(openMatch[0]) + openMatch[0].length
      const img = `\n  <img class="signature-img" src="${escaped}" alt="" />\n`
      result = `${result.slice(0, insertAt)}${img}${result.slice(insertAt)}`
      touched = 1
    }
  }

  if (!touched) {
    // Last resort: append a minimal signature block before footer / </body>.
    const block = `\n<!-- OFFER_SIGNATURE_START -->\n`
      + `<div class="signature-section">\n`
      + `  <img class="signature-img" src="${escaped}" alt="" />\n`
      + `</div>\n`
      + `<!-- OFFER_SIGNATURE_END -->\n`
    const footer = result.search(/<div[^>]*class\s*=\s*["'][^"']*\bfooter-section\b/i)
    if (footer >= 0) {
      result = `${result.slice(0, footer)}${block}${result.slice(footer)}`
    } else if (/<\/body>/i.test(result)) {
      result = result.replace(/<\/body>/i, `${block}</body>`)
    } else {
      result = `${result}${block}`
    }
  }

  return result
}

function resolveSignatureImageSrcAttr(fields = {}, options = {}) {
  const raw = coerceOfferImageSrc(
    options.signatureSrc
      || (fields && fields.signatureSrc)
      || (fields && fields.signature)
      || (fields && fields.images && fields.images.signature)
      || '',
  )
  if (raw) {
    return escapeHtml(raw)
  }
  return OFFER_SIGNATURE_IMAGE_TOKEN
}

/**
 * Remove signature/stamp images that sit outside .signature-section (leaks onto
 * Annexure / blank pages).
 */
export function stripOrphanSignatureImages(html) {
  if (!html) return html
  const result = String(html)
  const stripFrom = (fragment) => fragment
    .replace(/<img[^>]*class\s*=\s*["'][^"']*\bsignature-img\b[^"']*["'][^>]*>\s*/gi, '')
    .replace(/<img[^>]*class\s*=\s*["'][^"']*\bstamp-img\b[^"']*["'][^>]*>\s*/gi, '')
    .replace(/<img[^>]*src\s*=\s*["'][^"']*\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}[^"']*["'][^>]*>\s*/gi, '')

  const sigStart = result.indexOf(MARKER_TAGS.signature[0])
  const sigEnd = result.indexOf(MARKER_TAGS.signature[1])
  if (sigStart >= 0 && sigEnd > sigStart) {
    const end = sigEnd + MARKER_TAGS.signature[1].length
    return `${stripFrom(result.slice(0, sigStart))}${result.slice(sigStart, end)}${stripFrom(result.slice(end))}`
  }

  // Prefer preserving every .signature-section (primary + extras) when markers are
  // missing/broken — never strip all stamps just because OFFER_SIGNATURE_END was lost.
  const ranges = findAllSignatureSectionRanges(result)
  if (!ranges.length) {
    return stripFrom(result)
  }
  let out = ''
  let cursor = 0
  ranges
    .slice()
    .sort((a, b) => a.start - b.start)
    .forEach(range => {
      out += stripFrom(result.slice(cursor, range.start))
      out += result.slice(range.start, range.end)
      cursor = range.end
    })
  out += stripFrom(result.slice(cursor))
  return out
}

/**
 * Detect marked section order in document order.
 * @param {string} html
 * @returns {string[]}
 */
function detectOfferContentOrder(html) {
  const found = []
  OFFER_CONTENT_ORDER_KEYS.forEach(key => {
    const unit = findSectionUnit(html, key)
    if (unit) found.push({ key, start: unit.start })
  })
  found.sort((a, b) => a.start - b.start)
  return found.map(item => item.key)
}

/**
 * Ensure salary → terms → closing → signature when markers are present.
 * Fixes PDF-24 where stamp/signature preceded Annexure A.
 */
export function repairOfferSectionReadingOrder(html) {
  if (!html) return html
  const current = detectOfferContentOrder(html)
  if (current.length < 2) return html
  const head = current.filter(key => !OFFER_TRAILING_SECTION_ORDER.includes(key))
  const tail = OFFER_TRAILING_SECTION_ORDER.filter(key => current.includes(key))
  const desired = [...head, ...tail]
  if (desired.join('|') === current.join('|')) return html
  return applyOfferSectionOrder(html, desired)
}

/**
 * Legacy fallback: add top-clear after forced breaks only when print-flow tables
 * are absent (split print-flow thead already clears letterhead).
 */
function ensurePageBreakTopClears(html) {
  if (!html) return html
  if (/class\s*=\s*["'][^"']*\boffer-print-flow\b/i.test(html)) {
    // Hide/remove redundant top-clears that double-count with thead (blank pages).
    return String(html).replace(
      /(<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>)\s*<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>/gi,
      '$1',
    )
  }
  return String(html).replace(
    /(<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>)(?!\s*<div[^>]*class="[^"]*offer-page-top-clear[^"]*")/gi,
    `$1\n${PAGE_BREAK_TOP_CLEAR_HTML}`,
  )
}

const JOINING_DATE_PARAGRAPH_RE = /<p[^>]*>\s*Your date of joining will be[\s\S]*?<\/p>/i
const PAGE_BREAK_BEFORE_JOINING_RE = /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?(?=\s*<p[^>]*>\s*Your date of joining will be)/gi

/** Drop forced breaks that orphan the joining line on its own page (PDF 48 p5). */
function stripPageBreakBeforeJoiningParagraph(html) {
  if (!html) return html
  return String(html).replace(PAGE_BREAK_BEFORE_JOINING_RE, '')
}

function findSalaryPageBreakStart(html, salaryStart) {
  if (salaryStart < 0) return -1
  const windowStart = Math.max(0, salaryStart - 420)
  const before = html.slice(windowStart, salaryStart)
  const markerInBefore = before.lastIndexOf(PAGE_BREAK_MARKER)
  if (markerInBefore >= 0) return windowStart + markerInBefore
  const trailingBreak = before.match(
    /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i,
  )
  if (trailingBreak) return salaryStart - trailingBreak[0].length
  return salaryStart
}

/**
 * Keep the joining-date / reporting line with the offer body — never on Annexure A.
 */
export function ensureJoiningDateBeforeAnnexure(html) {
  if (!html) return html
  let result = stripPageBreakBeforeJoiningParagraph(html)
  const match = String(result).match(JOINING_DATE_PARAGRAPH_RE)
  if (!match || match.index == null) return result

  const joiningHtml = match[0]
  const joiningStart = match.index
  const joiningEnd = joiningStart + joiningHtml.length
  const salaryStart = result.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart < 0) return result

  const bodyStart = result.indexOf(MARKER_TAGS.body[0])
  const bodyEnd = result.indexOf(MARKER_TAGS.body[1])
  const insideBody = bodyStart >= 0 && bodyEnd > bodyStart
    && joiningStart >= bodyStart + MARKER_TAGS.body[0].length
    && joiningEnd <= bodyEnd

  const moveJoiningBeforeBodyEnd = () => {
    if (bodyEnd < 0) return null
    const without = `${result.slice(0, joiningStart)}${result.slice(joiningEnd)}`
    const endIdx = without.indexOf(MARKER_TAGS.body[1])
    if (endIdx < 0) return null
    return `${without.slice(0, endIdx)}\n${joiningHtml}\n${without.slice(endIdx)}`
  }

  // Joining leaked into intro/closing chunk (PDF 52 p1) — always anchor at body end.
  if (!insideBody && bodyEnd >= 0) {
    const moved = moveJoiningBeforeBodyEnd()
    if (moved) {
      return stripPageBreakBeforeJoiningParagraph(moved)
    }
  }

  // Prefer end of Employment Policies body — keeps joining with policies, not alone before annexure (PDF 47 p5).
  if (bodyEnd >= 0 && joiningEnd > bodyEnd) {
    const moved = moveJoiningBeforeBodyEnd()
    if (moved) {
      return stripPageBreakBeforeJoiningParagraph(moved)
    }
  }

  if (insideBody) {
    return stripPageBreakBeforeJoiningParagraph(result)
  }

  const breakStart = findSalaryPageBreakStart(result, salaryStart)
  if (joiningEnd <= breakStart) return stripPageBreakBeforeJoiningParagraph(result)

  // Inside annexure / after salary break — move to just before the annexure page break.
  const without = `${result.slice(0, joiningStart)}${result.slice(joiningEnd)}`
  const salaryIdx = without.indexOf(MARKER_TAGS.salary[0])
  if (salaryIdx < 0) return stripPageBreakBeforeJoiningParagraph(result)
  const insertAt = findSalaryPageBreakStart(without, salaryIdx)
  result = `${without.slice(0, insertAt)}\n${joiningHtml}\n${without.slice(insertAt)}`

  // Drop a forced page break that would leave joining alone on a blank page
  // immediately before the annexure break (PDF 45 page 5).
  const joinIdx = result.search(JOINING_DATE_PARAGRAPH_RE)
  const salBreak = findSalaryPageBreakStart(result, result.indexOf(MARKER_TAGS.salary[0]))
  if (joinIdx >= 0 && joinIdx < salBreak) {
    const between = result.slice(joinIdx + joiningHtml.length, salBreak)
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, '')
    if (!between) {
      result = result.replace(
        /(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?(?=\s*<p[^>]*>\s*Your date of joining will be)/i,
        '',
      )
    }
  }
  return stripPageBreakBeforeJoiningParagraph(result)
}

function stripHtmlTags(html) {
  if (!html) return ''
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function textToParagraphs(text) {
  const lines = String(text || '').split(/\n+/).map(l => l.trim()).filter(Boolean)
  if (!lines.length) return ''
  return lines.map(line => `<p>${escapeHtml(line)}</p>`).join('\n')
}

function introToHtml(text) {
  const content = String(text || '').trim()
  if (!content) return ''
  if (/<[a-z][\s\S]*>/i.test(content)) return sanitizeOfferSectionHtml(content)
  return textToParagraphs(content)
}

function htmlToPlainParagraphs(html) {
  return stripHtmlTags(html)
}

function hasMarkers(html) {
  return Object.values(MARKER_TAGS).some(([startMarker]) => html.includes(startMarker))
    || html.includes(PAGE_BREAK_MARKER)
}

function extractBetweenMarkers(html, [startMarker, endMarker]) {
  const start = html.indexOf(startMarker)
  const end = html.indexOf(endMarker)
  if (start < 0 || end < 0 || end <= start) return null
  return {
    start: start + startMarker.length,
    end,
    content: html.slice(start + startMarker.length, end).trim(),
  }
}

function detectPageBreak(html) {
  if (!html) return false
  if (html.includes(PAGE_BREAK_MARKER)) return true
  if (SECTION_PAGE_BREAK_MARKERS.some(marker => html.includes(marker))) return true
  return PAGE_BREAK_PATTERNS.some(pattern => pattern.test(html))
}

const SECTION_PAGE_BREAK_MARKERS = [
  INTRO_PAGE_BREAK_MARKER,
  JOB_RESP_PAGE_BREAK_MARKER,
  POLICIES_PAGE_BREAK_MARKER,
  TERMS_PAGE_BREAK_MARKER,
  CLOSING_PAGE_BREAK_MARKER,
  SIGNATURE_PAGE_BREAK_MARKER,
]

function removePageBreakFromFragment(fragment) {
  if (!fragment) return ''
  let result = fragment
  SECTION_PAGE_BREAK_MARKERS.forEach(marker => {
    result = result.replace(marker, '')
  })
  PAGE_BREAK_PATTERNS.forEach(pattern => {
    result = result.replace(pattern, '')
  })
  return result
}

function stripLeadingPageBreak(html) {
  if (!html) return ''
  let result = String(html).replace(/^\s+/, '')
  let stripped = true
  while (stripped) {
    stripped = false
    for (const marker of SECTION_PAGE_BREAK_MARKERS) {
      if (result.startsWith(marker)) {
        result = result.slice(marker.length).replace(/^\s+/, '')
        stripped = true
        break
      }
    }
    if (!stripped) {
      for (const pattern of PAGE_BREAK_PATTERNS) {
        const match = result.match(new RegExp(`^\\s*(?:${pattern.source})`, 'i'))
        if (match) {
          result = result.slice(match[0].length).replace(/^\s+/, '')
          stripped = true
          break
        }
      }
    }
  }
  return result
}

/** Gap immediately before Employment Policies (body) — after job resp if present, else after intro. */
function findGapBeforeBody(html, regions) {
  const bodyStartMarker = MARKER_TAGS.body[0]
  const bodyStartIdx = html.indexOf(bodyStartMarker)
  const gapEnd = bodyStartIdx >= 0 ? bodyStartIdx : regions?.body?.start
  if (gapEnd == null) return null

  const jobRespEndMarker = MARKER_TAGS.jobResponsibilities[1]
  const jobRespEndIdx = html.indexOf(jobRespEndMarker)
  const introEndMarker = MARKER_TAGS.intro[1]
  const introEndIdx = html.indexOf(introEndMarker)

  let gapStart = null
  if (jobRespEndIdx >= 0) {
    gapStart = jobRespEndIdx + jobRespEndMarker.length
  } else if (introEndIdx >= 0) {
    gapStart = introEndIdx + introEndMarker.length
  } else if (regions?.jobResponsibilities) {
    gapStart = regions.jobResponsibilities.end
  } else if (regions?.intro) {
    gapStart = regions.intro.end
  }

  if (gapStart == null || gapEnd < gapStart) return null
  return {
    start: gapStart,
    end: gapEnd,
    content: html.slice(gapStart, gapEnd),
  }
}

function findGapBeforeJobResponsibilities(html, regions) {
  const jobRespStartMarker = MARKER_TAGS.jobResponsibilities[0]
  const jobRespStartIdx = html.indexOf(jobRespStartMarker)
  if (jobRespStartIdx >= 0) {
    const introEndMarker = MARKER_TAGS.intro[1]
    const introEndIdx = html.indexOf(introEndMarker)
    if (introEndIdx >= 0 && jobRespStartIdx > introEndIdx) {
      const start = introEndIdx + introEndMarker.length
      return {
        start,
        end: jobRespStartIdx,
        content: html.slice(start, jobRespStartIdx),
      }
    }
  }

  if (regions?.jobResponsibilities) {
    const sectionStart = regions.intro?.end
    if (sectionStart != null && regions.jobResponsibilities.start > sectionStart) {
      return {
        start: sectionStart,
        end: regions.jobResponsibilities.start,
        content: html.slice(sectionStart, regions.jobResponsibilities.start),
      }
    }
  }

  return null
}

/**
 * Gap immediately before a section start marker (after the nearest prior
 * section end marker). Unlike the old body→terms span, this does not swallow
 * salary (or other) page breaks that sit earlier in the document.
 */
function findImmediateGapBeforeSection(html, startMarker) {
  const startIdx = html.indexOf(startMarker)
  if (startIdx < 0) return null

  const before = html.slice(0, startIdx)
  let gapStart = 0
  let foundEnd = false
  Object.values(MARKER_TAGS).forEach(([, endMarker]) => {
    const idx = before.lastIndexOf(endMarker)
    if (idx >= 0) {
      foundEnd = true
      gapStart = Math.max(gapStart, idx + endMarker.length)
    }
  })
  if (!foundEnd) {
    // No prior marked section — use a short window so we only touch local breaks.
    gapStart = Math.max(0, startIdx - 240)
  }

  return {
    start: gapStart,
    end: startIdx,
    content: html.slice(gapStart, startIdx),
  }
}

function findGapBeforeTerms(html, regions) {
  const immediate = findImmediateGapBeforeSection(html, MARKER_TAGS.terms[0])
  if (immediate) return immediate

  if (regions?.terms) {
    const start = Math.max(0, regions.terms.start - 240)
    return {
      start,
      end: regions.terms.start,
      content: html.slice(start, regions.terms.start),
    }
  }

  return null
}

function findGapBeforeIntro(html, regions) {
  const introStartMarker = MARKER_TAGS.intro[0]
  const introStartIdx = html.indexOf(introStartMarker)
  const gapEnd = introStartIdx >= 0 ? introStartIdx : regions?.intro?.start
  if (gapEnd == null) return null

  const contentOpen = html.match(/<div[^>]*class="[^"]*\bcontent\b[^"]*"[^>]*>/i)
  const contentStart = contentOpen ? contentOpen.index + contentOpen[0].length : 0
  const beforeIntro = html.slice(contentStart, gapEnd)
  const greetingBlockMatch = beforeIntro.match(
    /<div[^>]*class\s*=\s*["'][^"']*\boffer-greeting-block\b[^"']*["'][^>]*>[\s\S]*?<\/div>/i,
  )
  const dearMatch = beforeIntro.match(/<p[^>]*>[\s\S]*?Dear[\s\S]*?<\/p>/i)
  const gapStart = greetingBlockMatch
    ? contentStart + greetingBlockMatch.index + greetingBlockMatch[0].length
    : dearMatch
      ? contentStart + dearMatch.index + dearMatch[0].length
      : contentStart

  if (gapEnd <= gapStart) return null
  return {
    start: gapStart,
    end: gapEnd,
    content: html.slice(gapStart, gapEnd),
  }
}

function findGapBeforeClosing(html, regions) {
  const closingStartMarker = MARKER_TAGS.closing[0]
  const closingStartIdx = html.indexOf(closingStartMarker)
  const gapEnd = closingStartIdx >= 0 ? closingStartIdx : regions?.closing?.start
  if (gapEnd == null) return null

  const termsEndMarker = MARKER_TAGS.terms[1]
  const termsEndIdx = html.indexOf(termsEndMarker)
  const bodyEndMarker = MARKER_TAGS.body[1]
  const bodyEndIdx = html.indexOf(bodyEndMarker)

  let gapStart = null
  if (termsEndIdx >= 0) {
    gapStart = termsEndIdx + termsEndMarker.length
  } else if (bodyEndIdx >= 0) {
    gapStart = bodyEndIdx + bodyEndMarker.length
  } else if (regions?.terms) {
    gapStart = regions.terms.end
  } else if (regions?.body) {
    gapStart = regions.body.end
  }

  if (gapStart == null || gapEnd < gapStart) return null
  return {
    start: gapStart,
    end: gapEnd,
    content: html.slice(gapStart, gapEnd),
  }
}

function findGapBeforeSignature(html, regions) {
  const immediate = findImmediateGapBeforeSection(html, MARKER_TAGS.signature[0])
  if (immediate) return immediate

  if (regions?.signature) {
    const start = Math.max(0, regions.signature.start - 240)
    return {
      start,
      end: regions.signature.start,
      content: html.slice(start, regions.signature.start),
    }
  }

  const sigSection = html.search(/class\s*=\s*["'][^"']*\bsignature-section\b/i)
  if (sigSection >= 0) {
    const tagStart = html.lastIndexOf('<', sigSection)
    const start = Math.max(0, (tagStart >= 0 ? tagStart : sigSection) - 240)
    const end = tagStart >= 0 ? tagStart : sigSection
    return {
      start,
      end,
      content: html.slice(start, end),
    }
  }

  return null
}

function findPageBreakRegion(html) {
  if (!html) return null

  const markerIdx = html.indexOf(PAGE_BREAK_MARKER)
  if (markerIdx >= 0) {
    let start = markerIdx
    let end = markerIdx + PAGE_BREAK_MARKER.length

    const afterMarker = html.slice(end)
    for (const pattern of PAGE_BREAK_PATTERNS) {
      const match = afterMarker.match(pattern)
      if (match && match.index === 0) {
        end += match[0].length
        break
      }
    }

    const trimmedAfter = html.slice(end).replace(/^\s+/, '')
    if (trimmedAfter !== html.slice(end)) {
      end += html.slice(end).length - trimmedAfter.length
    }

    return {
      start,
      end,
      content: html.slice(start, end),
    }
  }

  for (const pattern of PAGE_BREAK_PATTERNS) {
    const match = html.match(pattern)
    if (!match || match.index == null) continue
    return {
      start: match.index,
      end: match.index + match[0].length,
      content: match[0],
    }
  }

  return null
}

function findBodyEndIndex(html) {
  const bodyClose = html.search(/<\/body>/i)
  return bodyClose >= 0 ? bodyClose : html.length
}

function findTagStartBefore(html, textIndex, maxLookback = 400) {
  if (textIndex <= 0) return 0
  const before = html.slice(Math.max(0, textIndex - maxLookback), textIndex)
  const tagStarts = []
  let match
  const pattern = /<(?:h[1-6]|div|p|section|table)[^>]*>/gi
  // eslint-disable-next-line no-cond-assign
  while ((match = pattern.exec(before)) !== null) {
    tagStarts.push(Math.max(0, textIndex - maxLookback) + match.index)
  }
  if (tagStarts.length) return tagStarts[tagStarts.length - 1]

  const pageBreakBefore = before.lastIndexOf('page-break-before')
  if (pageBreakBefore >= 0) {
    const divStart = before.lastIndexOf('<div', pageBreakBefore)
    if (divStart >= 0) return Math.max(0, textIndex - maxLookback) + divStart
  }

  return textIndex
}

function findSectionByPatterns(html, patterns) {
  for (const pattern of patterns) {
    const match = html.match(pattern)
    if (!match || match.index == null) continue
    const textIdx = match.index
    const start = findTagStartBefore(html, textIdx)
    return { start, textIdx, matched: match[0] }
  }
  return null
}

function findAnnexureSalarySection(html) {
  return findSectionByPatterns(html, ANNEXURE_SALARY_PATTERNS)
}

function findTermsSection(html) {
  return findSectionByPatterns(html, TERMS_SECTION_PATTERNS)
}

function trimRegionContent(html, start, end) {
  const slice = html.slice(start, end)
  const trimmed = slice.trim()
  if (!trimmed) return null
  const trimStart = slice.indexOf(trimmed)
  return {
    start: start + trimStart,
    end: start + trimStart + trimmed.length,
    content: trimmed,
  }
}

function findSalaryRegion(html) {
  const fromMarkers = extractBetweenMarkers(html, MARKER_TAGS.salary)
  if (fromMarkers) {
    const markerStart = html.indexOf(MARKER_TAGS.salary[0])
    const markerEnd = html.indexOf(MARKER_TAGS.salary[1]) + MARKER_TAGS.salary[1].length
    return {
      start: markerStart,
      end: markerEnd,
      content: fromMarkers.content,
    }
  }

  const bodyEnd = findBodyEndIndex(html)
  const annexure = findAnnexureSalarySection(html)
  const terms = findTermsSection(html)
  const closing = findClosingRegion(html)
  const signature = findSignatureRegion(html)

  if (annexure) {
    let end = bodyEnd
    const candidates = [
      terms?.start,
      closing?.start,
      signature?.start,
    ].filter(idx => idx != null && idx > annexure.start)
    if (candidates.length) end = Math.min(...candidates)
    return trimRegionContent(html, annexure.start, end)
  }

  // Fallback: salary table / CONFIG placeholders without an Annexure heading
  const configIdx = html.search(/CONFIG\.SALARY_STRUCTURE|SALARY_STRUCTURE/i)
  if (configIdx >= 0) {
    const regionStart = findTagStartBefore(html, configIdx, 600)
    let end = bodyEnd
    const candidates = [
      terms?.start,
      closing?.start,
      signature?.start,
    ].filter(idx => idx != null && idx > regionStart)
    if (candidates.length) end = Math.min(...candidates)
    const region = trimRegionContent(html, regionStart, end)
    if (region && region.content.length > 20) return region
  }

  const tableMatch = html.match(/<table[\s\S]*?<\/table>/i)
  if (tableMatch && tableMatch.index != null) {
    const tableStart = tableMatch.index
    const contextBefore = html.slice(Math.max(0, tableStart - 300), tableStart)
    if (SALARY_HINT_PATTERN.test(contextBefore) || /Annexure|compensation|CTC/i.test(contextBefore)) {
      let end = tableStart + tableMatch[0].length
      const afterTable = html.slice(end, bodyEnd)
      const notesMatch = afterTable.match(/^[\s\S]{0,800}?(?=<div[^>]*page-break|<h[1-6]|Terms\s+and\s+Conditions|$)/i)
      if (notesMatch) end += notesMatch[0].length
      const candidates = [terms?.start, closing?.start, signature?.start]
        .filter(idx => idx != null && idx > tableStart)
      if (candidates.length) end = Math.min(end, ...candidates)
      return trimRegionContent(html, findTagStartBefore(html, tableStart, 400), end)
    }
  }

  return null
}

function findTermsRegion(html) {
  const fromMarkers = extractBetweenMarkers(html, MARKER_TAGS.terms)
  if (fromMarkers) {
    const markerStart = html.indexOf(MARKER_TAGS.terms[0])
    const markerEnd = html.indexOf(MARKER_TAGS.terms[1]) + MARKER_TAGS.terms[1].length
    return {
      start: markerStart,
      end: markerEnd,
      content: fromMarkers.content,
    }
  }

  const terms = findTermsSection(html)
  if (!terms) return null

  const bodyEnd = findBodyEndIndex(html)
  const closing = findClosingRegion(html)
  const signature = findSignatureRegion(html)
  const signatureSectionStart = findSignatureSectionStart(html)
  let end = bodyEnd
  const candidates = [
    signatureSectionStart >= 0 ? signatureSectionStart : null,
    closing?.start,
    signature?.start,
  ].filter(idx => idx != null && idx > terms.start)
  if (candidates.length) end = Math.min(...candidates)

  return trimRegionContent(html, terms.start, end)
}

function parseFromMarkers(html) {
  const fields = emptyTemplateFields()
  const intro = extractBetweenMarkers(html, MARKER_TAGS.intro)
  const jobResponsibilities = extractBetweenMarkers(html, MARKER_TAGS.jobResponsibilities)
  const body = extractBetweenMarkers(html, MARKER_TAGS.body)
  const closing = extractBetweenMarkers(html, MARKER_TAGS.closing)
  const signature = extractBetweenMarkers(html, MARKER_TAGS.signature)
  const salary = extractBetweenMarkers(html, MARKER_TAGS.salary)
  const terms = extractBetweenMarkers(html, MARKER_TAGS.terms)

  if (intro) fields.intro = manualPageBreaksForEditor(intro.content)
  if (jobResponsibilities) {
    fields.jobResponsibilities = manualPageBreaksForEditor(
      stripLeadingPageBreak(jobResponsibilities.content),
    )
  }
  if (body) fields.body = manualPageBreaksForEditor(stripLeadingPageBreak(body.content))
  if (terms) {
    fields.termsAndConditions = manualPageBreaksForEditor(stripLeadingPageBreak(terms.content))
  }
  if (closing) {
    const closingHtml = String(closing.content || '').trim()
    fields.closing = /<[a-z][\s\S]*>/i.test(closingHtml)
      ? manualPageBreaksForEditor(stripLeadingPageBreak(closingHtml))
      : htmlToPlainParagraphs(closingHtml)
  }
  if (signature) {
    const parsed = splitCompanySignatoryPlainLines(
      htmlToPlainParagraphs(signature.content).split('\n').map(l => l.trim()).filter(Boolean),
    )
    fields.signatoryName = parsed.signatoryName
    fields.signatoryDesignation = parsed.signatoryDesignation
  }
  if (salary) {
    fields.salaryStructure = salary.content
  } else {
    const salaryRegion = findSalaryRegion(html)
    if (salaryRegion) fields.salaryStructure = salaryRegion.content
  }
  if (!terms) {
    const termsRegion = findTermsRegion(html)
    if (termsRegion) fields.termsAndConditions = stripLeadingPageBreak(termsRegion.content)
  }

  // Fallback: marker blocks can be empty while content lives in heuristic regions
  if (!fields.intro.trim()) {
    const introRegion = findIntroRegion(html)
    if (introRegion?.content) fields.intro = introRegion.content
  }
  if (!fields.body.trim()) {
    const heuristic = parseHeuristic(html)
    const candidate = heuristic.fields.body || ''
    // Empty BODY markers + print-flow HTML used to fill fields.body with table
    // chrome; re-assemble then ejected Policies/Terms outside `.content`.
    if (
      candidate.trim()
      && !isBrokenOfferSectionHtml(candidate)
      && offerSectionPlainLen(candidate) >= 20
    ) {
      fields.body = candidate
    }
  }

  fields.includePageBreak = detectPageBreak(html)

  return { mode: 'structured', fields, regions: null }
}

function findTagRegion(html, searchFrom, tagPattern) {
  const slice = html.slice(searchFrom)
  const match = slice.match(tagPattern)
  if (!match) return null
  const start = searchFrom + match.index
  const end = start + match[0].length
  return { start, end, content: match[0], inner: match[1] || match[0] }
}

function findIntroRegion(html) {
  const namePatterns = [
    /\{\{CANDIDATE\.NAME_ONLY\}\}/i,
    /\{\{CANDIDATE\.NAME\}\}/i,
    /Dear\s+\{\{CANDIDATE/i,
  ]
  let searchFrom = 0
  for (const pat of namePatterns) {
    const m = html.match(pat)
    if (m) {
      searchFrom = html.indexOf(m[0]) + m[0].length
      break
    }
  }
  if (!searchFrom) {
    const bodyIdx = html.search(/<body[^>]*>/i)
    searchFrom = bodyIdx >= 0 ? bodyIdx : 0
  }

  const greetingBlock = html.match(
    /<div[^>]*class\s*=\s*["'][^"']*\boffer-greeting-block\b[^"']*["'][^>]*>[\s\S]*?<\/div>/i,
  )
  if (greetingBlock) {
    searchFrom = html.indexOf(greetingBlock[0]) + greetingBlock[0].length
  } else {
    const greetingEnd = html.indexOf('</p>', searchFrom)
    if (greetingEnd >= 0 && greetingEnd - searchFrom < 800) {
      searchFrom = greetingEnd + 4
    }
  }

  const introEndMarkers = [
    MARKER_TAGS.body[0],
    MARKER_TAGS.salary[0],
    MARKER_TAGS.terms[0],
    PAGE_BREAK_MARKER,
    POLICIES_PAGE_BREAK_MARKER,
  ]
  let introEnd = html.length
  introEndMarkers.forEach(marker => {
    const idx = html.indexOf(marker, searchFrom)
    if (idx >= 0) introEnd = Math.min(introEnd, idx)
  })
  ;[/Annexure\s+A/i, /Salary\s+Structure/i, /Terms\s+and\s+Conditions/i].forEach(pattern => {
    const idx = html.search(pattern)
    if (idx >= searchFrom) introEnd = Math.min(introEnd, idx)
  })

  const slice = html.slice(searchFrom, introEnd)
  const blocks = [...slice.matchAll(/<p[^>]*>[\s\S]*?<\/p>/gi)]
  if (!blocks.length) {
    return findTagRegion(html, searchFrom, /<p[^>]*>([\s\S]*?)<\/p>/i)
  }

  const introContent = blocks.map(block => block[0]).join('\n').trim()
  if (!introContent) return null

  const firstStart = searchFrom + blocks[0].index
  const lastEnd = searchFrom + blocks[blocks.length - 1].index + blocks[blocks.length - 1][0].length
  return {
    start: firstStart,
    end: lastEnd,
    content: introContent,
    inner: introContent,
  }
}

function findClosingRegion(html) {
  for (const pat of CLOSING_TEXT_PATTERNS) {
    const idx = html.search(pat)
    if (idx < 0) continue
    const before = html.lastIndexOf('<p', idx)
    const divBefore = html.lastIndexOf('<div', idx)
    const start = Math.max(before, divBefore)
    if (start < 0) continue
    const after = html.indexOf('</p>', idx)
    const divAfter = html.indexOf('</div>', idx)
    let end = after >= 0 ? after + 4 : (divAfter >= 0 ? divAfter + 6 : idx + 200)
    if (end <= start) end = idx + 200
    return {
      start,
      end: Math.min(end, findBodyEndIndex(html)),
      content: html.slice(start, end),
      inner: html.slice(start, end),
    }
  }
  return null
}

function findSignatureRegion(html) {
  const sigImgIdx = html.indexOf('{{METADATA.IMAGES.SIGNATURE}}')
  if (sigImgIdx < 0) return null

  const before = html.slice(0, sigImgIdx)
  const matches = [...before.matchAll(/<(?:p|div)[^>]*>([\s\S]*?)<\/(?:p|div)>/gi)]
  if (!matches.length) return null

  const last = matches[matches.length - 1]
  const start = before.lastIndexOf(last[0])
  if (start < 0) return null
  return {
    start,
    end: start + last[0].length,
    content: last[0],
    inner: last[1],
  }
}

function findSignatureSectionStart(html) {
  const sigImgIdx = html.indexOf('{{METADATA.IMAGES.SIGNATURE}}')
  if (sigImgIdx >= 0) {
    const sigSection = html.slice(0, sigImgIdx).search(/class="[^"]*signature-section[^"]*"/i)
    if (sigSection >= 0) {
      const divStart = html.lastIndexOf('<div', sigImgIdx)
      if (divStart >= 0) return divStart
    }
  }
  const sigSection = html.search(/class="[^"]*signature-section[^"]*"/i)
  if (sigSection >= 0) {
    const divStart = html.lastIndexOf('<div', sigSection)
    if (divStart >= 0) return divStart
  }
  return -1
}

function parseHeuristic(html) {
  const fields = emptyTemplateFields()
  const regions = {}

  const intro = findIntroRegion(html)
  const closing = findClosingRegion(html)
  const signature = findSignatureRegion(html)
  const salary = findSalaryRegion(html)
  const terms = findTermsRegion(html)
  const signatureSectionStart = findSignatureSectionStart(html)

  if (intro) {
    regions.intro = { start: intro.start, end: intro.end }
    fields.intro = intro.content
  }

  if (closing && closing.content) {
    regions.closing = { start: closing.start, end: closing.end }
    const closingRaw = String(closing.content || '').trim()
    fields.closing = /<[a-z][\s\S]*>/i.test(closingRaw)
      ? manualPageBreaksForEditor(stripLeadingPageBreak(closingRaw))
      : stripHtmlTags(closing.inner)
  }

  if (signature) {
    regions.signature = { start: signature.start, end: signature.end }
    const parsed = splitCompanySignatoryPlainLines(
      stripHtmlTags(signature.inner).split('\n').map(l => l.trim()).filter(Boolean),
    )
    fields.signatoryName = parsed.signatoryName
    fields.signatoryDesignation = parsed.signatoryDesignation
  }

  fields.includePageBreak = detectPageBreak(html)

  if (salary) {
    regions.salary = { start: salary.start, end: salary.end }
    fields.salaryStructure = salary.content
  }

  if (terms) {
    regions.terms = { start: terms.start, end: terms.end }
    fields.termsAndConditions = stripLeadingPageBreak(terms.content)
  }

  const bodyOpen = html.search(/<body[^>]*>/i)
  const defaultBodyStart = bodyOpen >= 0 ? html.indexOf('>', bodyOpen) + 1 : 0
  let greetingEnd = html.indexOf('</p>', defaultBodyStart)
  let afterGreeting = greetingEnd >= 0 ? greetingEnd + 4 : defaultBodyStart
  // First </p> is often the closing salutation ("Yours sincerely") — that must not
  // become bodyStart or the heuristic body region starts after closing and swallows
  // signature/footer when markers are later injected.
  if (closing && closing.start != null && afterGreeting > closing.start) {
    const contentMatch = html.match(
      /<div\b[^>]*class\s*=\s*["'][^"']*\bcontent\b[^"']*["'][^>]*>/i,
    )
    afterGreeting = contentMatch && contentMatch.index != null
      ? contentMatch.index + contentMatch[0].length
      : defaultBodyStart
  }

  const bodyStart = intro ? intro.end : afterGreeting
  const markerBounds = [
    html.indexOf(MARKER_TAGS.closing[0]),
    html.indexOf(MARKER_TAGS.signature[0]),
    html.indexOf(MARKER_TAGS.salary[0]),
    html.indexOf(MARKER_TAGS.terms[0]),
    html.indexOf(MARKER_TAGS.jobResponsibilities[0]),
    html.search(/<div\b[^>]*class\s*=\s*["'][^"']*\bfooter-section\b/i),
  ].filter(idx => idx >= 0 && idx > bodyStart)
  const sectionBoundaries = [
    salary?.start,
    terms?.start,
    closing?.start,
    signature?.start,
    signatureSectionStart >= 0 ? signatureSectionStart : null,
    ...markerBounds,
  ].filter(idx => idx != null && idx > bodyStart)

  let bodyEnd = sectionBoundaries.length ? Math.min(...sectionBoundaries) : findBodyEndIndex(html)
  if (bodyEnd <= bodyStart) {
    bodyEnd = findBodyEndIndex(html)
  }

  const bodyContent = html.slice(bodyStart, bodyEnd).trim()
  // Refuse footer/chrome scraps and spans that already contain sibling markers.
  const bodyLooksSafe = bodyContent.length > 20
    && !/<!--\s*OFFER_(?:SIGNATURE|CLOSING|SALARY|TERMS|JOB_RESP)_/i.test(bodyContent)
    && !/class\s*=\s*["'][^"']*\b(?:footer-section|header-section|signature-section)\b/i.test(bodyContent)
    && !/<\/(?:body|html)\b/i.test(bodyContent)
  if (bodyLooksSafe) {
    regions.body = { start: bodyStart, end: bodyEnd }
    fields.body = stripLeadingPageBreak(bodyContent)
  }

  const parsedCount = [
    intro,
    closing?.content,
    signature,
    salary,
    terms,
    bodyLooksSafe,
  ].filter(Boolean).length
  if (parsedCount < 2) {
    return { mode: 'raw', fields: emptyTemplateFields(), regions: null }
  }

  return { mode: 'structured', fields, regions }
}

/**
 * @param {string} html
 * @returns {{ mode: 'empty'|'structured'|'raw', fields: object, regions: object|null }}
 */
export function parseOfferTemplate(html) {
  if (!html || !html.trim()) {
    return { mode: 'empty', fields: emptyTemplateFields(), regions: null }
  }
  if (hasMarkers(html)) {
    return parseFromMarkers(html)
  }
  return parseHeuristic(html)
}

function buildEmployeeAcceptanceHtml(employeePlacement = null) {
  const emp = normalizeOfferEmployeeSignPlacement(employeePlacement)
  if (!emp.showName && !emp.showSignature) return ''
  if (emp.layout === 'stacked') {
    const parts = []
    if (emp.showName) parts.push('<p>Employee Name: _________________________</p>')
    if (emp.showSignature) parts.push('<p>Employee Signature: ________________________</p>')
    return `<div class="employee-acceptance employee-acceptance-stacked">
  ${parts.join('\n  ')}
</div>`
  }
  const cells = []
  if (emp.showName) cells.push('<td>Employee Name: _________________________</td>')
  if (emp.showSignature) cells.push('<td>Employee Signature: ________________________</td>')
  if (cells.length === 1) {
    cells[0] = cells[0].replace('<td>', '<td colspan="2">')
  }
  return `<div class="employee-acceptance">
  <table class="employee-acceptance-row" role="presentation">
    <tr>
      ${cells.join('\n      ')}
    </tr>
  </table>
</div>`
}

/** True when a plain line is an employee-acceptance label (not company designation). */
function isEmployeeAcceptancePlainLine(line) {
  return /employee\s+(name|signature)\s*:/i.test(String(line || ''))
}

/**
 * Split signature plain lines into company signatory name + designation only.
 * Employee Name / Signature lines are controlled by stamp settings — never designation.
 */
function splitCompanySignatoryPlainLines(lines) {
  const cleaned = dedupeSignatoryPlainLines(
    (lines || []).filter(line => !isEmployeeAcceptancePlainLine(line)),
  )
  return {
    signatoryName: cleaned[0] || '',
    signatoryDesignation: cleaned.slice(1).join('\n'),
  }
}

/** Strip employee-acceptance lines from a stored designation / signatory field value. */
function sanitizeCompanySignatoryFieldValue(value) {
  return String(value || '')
    .split(/\n/)
    .map(line => line.trim())
    .filter(line => line && !isEmployeeAcceptancePlainLine(line))
    .join('\n')
}

/** Collapse repeated Authorised Signatory / Human Resources pairs and consecutive dupes. */
function dedupeSignatoryPlainLines(lines) {
  const cleaned = []
  ;(lines || []).forEach(line => {
    const t = String(line || '').trim()
    if (!t) return
    if (isEmployeeAcceptancePlainLine(t)) return
    if (cleaned.length && cleaned[cleaned.length - 1].toLowerCase() === t.toLowerCase()) return
    cleaned.push(t)
  })
  // Keep a single AS/HR pair when the field was polluted with 4–5 copies
  // (showed as stacked "Authorised Signatory / Human Resources" with no stamp crop).
  const out = []
  let haveAsHrPair = false
  for (let i = 0; i < cleaned.length; i += 1) {
    const isAs = /authori[sz]ed\s+signatory/i.test(cleaned[i])
    const nextHr = i + 1 < cleaned.length && /human\s+resources/i.test(cleaned[i + 1])
    if (isAs && nextHr) {
      if (!haveAsHrPair) {
        out.push(cleaned[i], cleaned[i + 1])
        haveAsHrPair = true
      }
      i += 1
      continue
    }
    if (isAs && haveAsHrPair) continue
    if (/human\s+resources/i.test(cleaned[i]) && haveAsHrPair) continue
    out.push(cleaned[i])
  }
  return out
}

/**
 * One signatory-block per signature-section; collapse repeated AS/HR lines inside.
 */
function collapseRepeatedSignatoryLabelsInSignatureSections(html) {
  if (!html) return html
  let result = String(html)
  const ranges = findAllSignatureSectionRanges(result)
  if (!ranges.length) return result
  ;[...ranges].sort((a, b) => b.start - a.start).forEach((range) => {
    const openEnd = result.indexOf('>', range.start) + 1
    if (openEnd <= 0) return
    const closeIdx = range.end >= 6 ? range.end - 6 : -1
    if (closeIdx < openEnd || !/^<\/div>/i.test(result.slice(closeIdx, range.end))) return
    let inner = result.slice(openEnd, closeIdx)
    const collected = []
    const takeBlock = (block) => {
      const text = String(block)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
      text.split(/\n/).map(l => l.trim()).filter(Boolean).forEach(l => collected.push(l))
      return ''
    }
    inner = inner.replace(/<p[^>]*class="[^"]*\bsignatory-block\b[^"]*"[^>]*>[\s\S]*?<\/p>\s*/gi, takeBlock)
    inner = inner.replace(SIGNATORY_AS_HR_PAIR_RE, takeBlock)
    if (!collected.length) return
    const deduped = dedupeSignatoryPlainLines(collected)
    if (!deduped.length) {
      result = `${result.slice(0, openEnd)}${inner}${result.slice(closeIdx)}`
      return
    }
    const labelHtml = `<p class="signatory-block">${deduped.map((line, idx) => (
      idx === 0 ? `<strong>${escapeHtml(line)}</strong>` : escapeHtml(line)
    )).join('<br>')}</p>\n  `
    if (/<img\b[^>]*\b(?:signature-img|stamp-img)\b/i.test(inner)) {
      inner = inner.replace(/(<img\b[^>]*\b(?:signature-img|stamp-img)\b[^>]*>\s*)/i, `$1${labelHtml}`)
    } else {
      inner = `${labelHtml}${inner}`
    }
    result = `${result.slice(0, openEnd)}${inner}${result.slice(closeIdx)}`
  })
  return result
}

/**
 * After closing: stamp image → designation/signatory name → employee name + signature.
 * Always emits a signature-section so PDF closing is never blank when closing is shown.
 * @param {object} [fields]
 * @param {{ includeEmployee?: boolean, extraClass?: string }} [options]
 */
function buildSignatureHtml(fields, options = {}) {
  const rawLines = []
  if (fields && fields.signatoryName) {
    String(fields.signatoryName).split(/\n/).forEach(line => {
      const t = line.trim()
      if (t && !isEmployeeAcceptancePlainLine(t)) rawLines.push(t)
    })
  }
  if (fields && fields.signatoryDesignation) {
    String(fields.signatoryDesignation).split('\n').forEach(line => {
      const t = line.trim()
      if (t && !isEmployeeAcceptancePlainLine(t)) rawLines.push(t)
    })
  }
  let plainLines = dedupeSignatoryPlainLines(rawLines)
  if (options.omitSignatoryBlock) {
    plainLines = []
  } else if (!plainLines.length) {
    plainLines = ['Authorised Signatory', 'Human Resources']
  } else if (
    plainLines.length === 1
    && /authori[sz]ed\s+signatory/i.test(plainLines[0])
  ) {
    // Configure often stores only "Authorised Signatory" — still show HR on first page.
    plainLines = ['Authorised Signatory', 'Human Resources']
  }

  const emp = resolveFieldsEmployeeSignConfig(fields || {})
  const includeEmployee = options.includeEmployee !== false && emp.withStamp
    && (emp.showName || emp.showSignature)

  const omitImage = fields && fields._omitSignatureImage === true
  const sigImg = omitImage
    ? ''
    : `<img class="signature-img" src="${resolveSignatureImageSrcAttr(fields, options)}" alt="" />`

  // Always emit label HTML from plainLines (including default AS/HR). True-duplicate
  // dedupe is handled by dedupeSignatoryPlainLines above — do not omit labels when
  // a stamp image is present (that left white space above Authorised Signatory).
  const labelHtml = plainLines.length
    ? `<p class="signatory-block">${plainLines.map((line, idx) => (
      idx === 0 ? `<strong>${escapeHtml(line)}</strong>` : escapeHtml(line)
    )).join('<br>')}</p>`
    : ''

  const extraClass = options.extraClass ? ` ${options.extraClass}` : ''
  return `<div class="signature-section${extraClass}">
  ${sigImg}
  ${labelHtml}
  ${includeEmployee ? buildEmployeeAcceptanceHtml(emp) : ''}
</div>`
}

/** Compact fixed stamp for every-page placement (no employee lines). */
function buildEveryPageStampHtml(fields) {
  const name = (fields && fields.signatoryName)
    ? escapeHtml(fields.signatoryName)
    : 'Authorised Signatory'
  return `<div class="offer-stamp-every-page" aria-hidden="true">
  <img class="signature-img" src="${resolveSignatureImageSrcAttr(fields)}" alt="" />
  <div class="offer-stamp-every-page-name">${name}</div>
</div>`
}

const OFFER_STAMP_EVERY_PAGE_CSS = `
.offer-stamp-every-page {
  position: fixed !important;
  right: 35px !important;
  bottom: 48px !important;
  z-index: 998;
  width: 140px;
  text-align: right;
  pointer-events: none;
}
.offer-stamp-every-page .signature-img,
.offer-stamp-every-page .stamp-img {
  max-height: 48px;
  margin: 0 0 2px auto;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.offer-stamp-every-page-name { font-size: 8px; line-height: 1.2; color: #333; }
`.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()

/**
 * Wrap subsection heading + following list(s) so Chrome print keeps them together
 * (e.g. "Daily Meter Submission" title must not orphan from its bullets).
 * Also keeps major section titles with the first following block.
 * Idempotent — skips already-wrapped groups.
 */
export function wrapOfferKeepTogetherBlocks(html) {
  if (!html) return html
  const heading = '(?:'
    + '<(?:h[1-6])(?:\\s[^>]*)?>[\\s\\S]*?<\\/(?:h[1-6])>'
    + '|<p(?:\\s[^>]*)?>\\s*<(?:strong|b)(?:\\s[^>]*)?>[\\s\\S]*?<\\/(?:strong|b)>\\s*<\\/p>'
    + ')'
  const lists = '(?:\\s*<(?:ul|ol)(?:\\s[^>]*)?>[\\s\\S]*?<\\/(?:ul|ol)>)+'
  const listPattern = new RegExp(`(${heading}${lists})`, 'gi')

  let result = String(html).replace(listPattern, (match, group, offset, full) => {
    const before = String(full).slice(Math.max(0, offset - 96), offset)
    if (/offer-keep-together\b[^>]*>\s*$/i.test(before)) return match
    if (/class\s*=\s*["'][^"']*\boffer-keep-together\b/i.test(group)) return match
    return `<div class="offer-keep-together">${group}</div>`
  })

  // Major titles (Terms / Employment Policies / Annexure) + first following block.
  const majorFollow = new RegExp(
    `((?:<(?:h[1-6])(?:\\s[^>]*)?class="[^"]*\\boffer-major-heading\\b[^"]*"[^>]*>[\\s\\S]*?<\\/(?:h[1-6])>`
    + `|<p(?:\\s[^>]*)?class="[^"]*\\boffer-major-heading\\b[^"]*"[^>]*>[\\s\\S]*?<\\/p>))`
    + `(\\s*(?:<(?:p|ul|ol)(?:\\s[^>]*)?>[\\s\\S]*?<\\/(?:p|ul|ol)>))`,
    'gi',
  )
  result = result.replace(majorFollow, (match, title, follow, offset, full) => {
    const before = String(full).slice(Math.max(0, offset - 96), offset)
    if (/offer-keep-together\b[^>]*>\s*$/i.test(before)) return match
    if (/offer-keep-together/i.test(match)) return match
    // Per-table titles must stay above their own salary-table, not glued to
    // the next note / Terms block if flatten left a gap.
    if (/\bsalary-structure-title\b/i.test(title)) return match
    // Never keep-together a print-flow table — page-break-inside:avoid on a
    // taller-than-page wrapper makes Chromium emit dozens of blank pages.
    if (/\boffer-print-flow\b/i.test(follow)) return match
    return `<div class="offer-keep-together">${title}${follow}</div>`
  })

  return result
}

/** Quill blank-line marker — must stay visible in print/PDF (do not delete). */
const OFFER_SPACER_HTML = '<p class="offer-spacer">&nbsp;</p>'

/**
 * Convert Quill empty paragraphs to fixed-height spacers and collapse runs.
 * Safe on full documents (PDF chrome path) and section fragments (assemble).
 */
function preserveOfferBlankLineSpacers(html) {
  if (!html) return html
  let result = String(html)
  // Quill blank lines: <p><br></p>, <p><br/></p>, <p>&nbsp;</p>, empty <p></p>
  result = result.replace(
    /<p(?![^>]*\boffer-spacer\b)(?:\s[^>]*)?>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*\s*<\/p>/gi,
    OFFER_SPACER_HTML,
  )
  // One intentional blank line between blocks — collapse 2+ consecutive spacers to one
  result = result.replace(
    /(?:<p class="offer-spacer">&nbsp;<\/p>\s*){2,}/gi,
    `${OFFER_SPACER_HTML}\n`,
  )
  return result
}

/**
 * Canonical section titles that often appear both in Quill content and as
 * leftover shell/orphan headings. Fuzzy: "&" ↔ "and", optional trailing period.
 */
const OFFER_SECTION_TITLE_PATTERNS = [
  { key: 'body', pattern: /^Employment\s+Policies\s+(?:and|&)\s+Provisions\.?$/i },
  { key: 'jobResponsibilities', pattern: /^Job\s+Responsibilit(?:y|ies)\.?$/i },
  { key: 'terms', pattern: /^Terms\s+and\s+Conditions\s+(?:Of\s+)?Employment\.?$/i },
  { key: 'salary', pattern: /^(?:Annexure\s+A\s*[:\-—]?\s*)?(?:Salary|Compensation)\s+Structure\.?$/i },
]

const OFFER_SECTION_HEADING_BLOCK_RE = /(?:<(h[1-6])(?:\s[^>]*)?>[\s\S]*?<\/\1>|<p(?:\s[^>]*)?class="[^"]*\boffer-major-heading\b[^"]*"[^>]*>[\s\S]*?<\/p>|<p(?:\s[^>]*)?>\s*<(?:strong|b)(?:\s[^>]*)?>[\s\S]*?<\/(?:strong|b)>\s*<\/p>)/gi

/** Any heading/paragraph — used to find leftover plain titles like `<p>Job Responsibilities</p>`. */
const OFFER_ANY_HEADING_BLOCK_RE = /<(h[1-6]|p)(?:\s[^>]*)?>[\s\S]*?<\/\1>/gi

function normalizeOfferHeadingPlainText(htmlOrText) {
  return String(htmlOrText || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\u00a0/g, ' ')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim()
}

function collectLayoutSectionTitleMap(layout) {
  const map = {}
  ;['body', 'jobResponsibilities', 'terms', 'salary'].forEach(key => {
    const section = layout ? resolveSectionLayout(layout, key) : {}
    const text = String(
      (section && section.sectionTitleText) || DEFAULT_SECTION_TITLE_TEXT[key] || '',
    ).trim()
    if (text) map[key] = text
  })
  return map
}

function matchOfferSectionTitleKey(htmlOrText, customTitleMap = null) {
  const plain = normalizeOfferHeadingPlainText(htmlOrText)
  if (!plain || plain.length > 120) return null
  const hit = OFFER_SECTION_TITLE_PATTERNS.find(entry => entry.pattern.test(plain))
  if (hit) return hit.key
  if (customTitleMap && typeof customTitleMap === 'object') {
    const lower = plain.toLowerCase()
    const found = Object.keys(customTitleMap).find(key => (
      normalizeOfferHeadingPlainText(customTitleMap[key]).toLowerCase() === lower
    ))
    if (found) return found
  }
  return null
}

function findOfferSectionTitleBlocks(html, customTitleMap = null) {
  const source = String(html || '')
  const blocks = []
  OFFER_ANY_HEADING_BLOCK_RE.lastIndex = 0
  let match = OFFER_ANY_HEADING_BLOCK_RE.exec(source)
  while (match) {
    // Per-table salary titles are not the annexure section heading.
    if (/\bsalary-structure-title\b/i.test(match[0])) {
      match = OFFER_ANY_HEADING_BLOCK_RE.exec(source)
      continue
    }
    const key = matchOfferSectionTitleKey(match[0], customTitleMap)
    if (key) {
      blocks.push({
        start: match.index,
        end: match.index + match[0].length,
        html: match[0],
        key,
      })
    }
    match = OFFER_ANY_HEADING_BLOCK_RE.exec(source)
  }
  return blocks
}

function leadingOfferHeadingBlock(html, key = null, customTitleMap = null) {
  const trimmed = String(html || '').replace(/^(?:\s|<!--[\s\S]*?-->)+/i, '')
  const blocks = findOfferSectionTitleBlocks(trimmed, customTitleMap)
  const first = blocks[0]
  if (!first || first.start !== 0) return null
  if (key && first.key !== key) return null
  return first.html
}

function hasOfferPageBreakMarkup(html) {
  return /offer-(?:preview-)?page-break|OFFER_[A-Z_]*PAGE_BREAK|page-break-before\s*:\s*always|break-before\s*:\s*page/i
    .test(String(html || ''))
}

function stripPageBreakMarkupForCompare(html) {
  return String(html || '')
    .replace(/<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->/gi, '')
    .replace(/<div[^>]*class="[^"]*\boffer-(?:preview-)?page-break\b[^"]*"[^>]*>\s*<\/div>/gi, '')
    .replace(/<div[^>]*style="[^"]*(?:page-break-before\s*:\s*always|break-before\s*:\s*page)[^"]*"[^>]*>\s*<\/div>/gi, '')
}

/**
 * True when only comments / empty wrappers sit between two titles.
 * Page-break units are NOT ignorable — collapsing across them deletes
 * Starts: New page / New page + title and leaves the section on the prior page.
 */
function isIgnorableBetweenTitles(html, { allowPageBreaks = false } = {}) {
  let source = String(html || '')
  if (!allowPageBreaks && hasOfferPageBreakMarkup(source)) return false
  if (allowPageBreaks) source = stripPageBreakMarkupForCompare(source)
  const stripped = source
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\/?(?:div|span|br|hr)\b[^>]*>/gi, '')
    .replace(/&nbsp;|&#160;|\u00a0/gi, '')
    .replace(/\s+/g, '')
  return stripped === ''
}

function stripSectionTitleBlocksFromHtml(html, key, customTitleMap = null) {
  const blocks = findOfferSectionTitleBlocks(html, customTitleMap)
    .filter(block => block.key === key)
  if (!blocks.length) return String(html || '')
  let result = String(html || '')
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const block = blocks[i]
    result = `${result.slice(0, block.start)}${result.slice(block.end)}`
  }
  return result
}

function injectSectionTitleIntoInner(inner, titleHtml) {
  const sectionOpen = String(inner || '').match(
    /^\s*<div\b[^>]*class="[^"]*\boffer-section\b[^"]*"[^>]*>/i,
  )
  if (sectionOpen && sectionOpen.index != null) {
    const at = sectionOpen.index + sectionOpen[0].length
    return `${inner.slice(0, at)}\n${titleHtml}\n${inner.slice(at)}`
  }
  const keepOpen = String(inner || '').match(
    /^\s*<div\b[^>]*class="[^"]*\boffer-keep-together\b[^"]*"[^>]*>/i,
  )
  if (keepOpen && keepOpen.index != null) {
    const at = keepOpen.index + keepOpen[0].length
    return `${inner.slice(0, at)}\n${titleHtml}\n${inner.slice(at)}`
  }
  const lead = String(inner || '').match(/^(?:\s|<!--[\s\S]*?-->)*/)
  const at = lead ? lead[0].length : 0
  return `${inner.slice(0, at)}${titleHtml}\n${inner.slice(at)}`
}

/**
 * Drop consecutive duplicate major section titles (Employment Policies / Job
 * Responsibilities / Terms). Keeps the first; strips the later copy when only
 * whitespace/comments separate them. Also removes an orphan title immediately
 * before a marked section whose Quill content already starts with the same title.
 * Never removes a title when it is the only remaining copy for that section key.
 */
export function stripDuplicateOfferSectionTitles(html, customTitleMap = null) {
  if (!html) return html
  let result = String(html)

  const matches = findOfferSectionTitleBlocks(result, customTitleMap)
  const countByKey = matches.reduce((acc, item) => {
    acc[item.key] = (acc[item.key] || 0) + 1
    return acc
  }, {})

  const removeRanges = []
  for (let i = 1; i < matches.length; i += 1) {
    const prev = matches[i - 1]
    const cur = matches[i]
    if (prev.key !== cur.key) continue
    if ((countByKey[cur.key] || 0) <= 1) continue
    const between = result.slice(prev.end, cur.start)
    if (!isIgnorableBetweenTitles(between)) continue
    removeRanges.push(cur)
    countByKey[cur.key] -= 1
  }
  for (let i = removeRanges.length - 1; i >= 0; i -= 1) {
    const rem = removeRanges[i]
    result = `${result.slice(0, rem.start)}${result.slice(rem.end)}`
  }

  const markerMap = {
    body: MARKER_TAGS.body,
    jobResponsibilities: MARKER_TAGS.jobResponsibilities,
    terms: MARKER_TAGS.terms,
    salary: MARKER_TAGS.salary,
  }
  Object.keys(markerMap).forEach(key => {
    const markers = markerMap[key]
    const startIdx = result.indexOf(markers[0])
    if (startIdx < 0) return
    const endIdx = result.indexOf(markers[1], startIdx)
    if (endIdx < 0) return
    const inner = result.slice(startIdx + markers[0].length, endIdx)
    const innerTitles = findOfferSectionTitleBlocks(inner, customTitleMap)
      .filter(block => block.key === key)
    if (!innerTitles.length) return

    const windowStart = Math.max(0, startIdx - 800)
    const before = result.slice(windowStart, startIdx)
    const beforeTitles = findOfferSectionTitleBlocks(before, customTitleMap)
      .filter(block => block.key === key)
    if (!beforeTitles.length) return
    const lastOrphan = beforeTitles[beforeTitles.length - 1]
    const afterOrphan = before.slice(lastOrphan.end)
    // Leftover title immediately before the marked section (whitespace and/or
    // a Starts: New page unit). Strip only the title — never slice through
    // OFFER_*PAGE_BREAK / offer-page-break (that put Policies on the stamp page).
    if (
      !isIgnorableBetweenTitles(afterOrphan)
      && !isIgnorableBetweenTitles(afterOrphan, { allowPageBreaks: true })
    ) return
    const orphanStart = windowStart + lastOrphan.start
    const orphanEnd = windowStart + lastOrphan.end
    result = `${result.slice(0, orphanStart)}${result.slice(orphanEnd)}`
  })

  return result
}

const OFFER_SECTION_TITLE_HTML = {
  body: '<p class="ql-align-center offer-major-heading"><strong>Employment Policies and Provisions</strong></p>',
  jobResponsibilities: '<p class="ql-align-center offer-major-heading"><strong>Job Responsibilities</strong></p>',
  terms: '<h3 class="offer-major-heading">Terms and Conditions of Employment</h3>',
  salary: '<h3 class="offer-major-heading">Annexure A — Salary Structure</h3>',
}

function buildSectionTitleHtml(key, titleText, align = 'center', titleWeight = 'bold') {
  const text = String(titleText || '').trim()
  if (!text) return ''
  const normalizedAlign = normalizeTitleAlign(align, 'center')
  const alignClass = normalizedAlign === 'center'
    ? 'ql-align-center'
    : normalizedAlign === 'right'
      ? 'ql-align-right'
      : 'ql-align-left'
  const escaped = escapeHtml(text)
  const inner = cssFontWeight(titleWeight) === 'bold' ? `<strong>${escaped}</strong>` : escaped
  const alignAttrs = mergeOfferLetterTitleAlignAttrs('', normalizedAlign)
  if (key === 'terms' || key === 'salary') {
    return `<h3 class="offer-major-heading ${alignClass}"${alignAttrs}>${inner}</h3>`
  }
  return `<p class="${alignClass} offer-major-heading"${alignAttrs}>${inner}</p>`
}

function applyOfferLetterTitleFromLayout(html, layout) {
  if (!html) return html
  const titleSection = resolveSectionLayout(layout, 'title')
  const titleText = String(titleSection.titleText || OFFER_LETTER_TITLE_TEXT).trim() || OFFER_LETTER_TITLE_TEXT
  const titleAlign = normalizeTitleAlign(titleSection.titleAlign, 'center')
  let result = String(html)
  if (!/class="[^"]*\boffer-letter-title\b/i.test(result)) {
    return ensureOfferLetterTitle(result)
  }
  result = result.replace(
    /<(h[1-6]|p|div)([^>]*class\s*=\s*["'][^"']*\boffer-letter-title\b[^"']*["'][^>]*)>([\s\S]*?)<\/\1>/gi,
    (full, tag, attrs) => `<${tag}${mergeOfferLetterTitleAlignAttrs(attrs, titleAlign)}>${escapeHtml(titleText)}</${tag}>`,
  )
  return result
}

function stripLeadingSectionTitleBlock(inner, key, customTitleMap = null) {
  return stripSectionTitleBlocksFromHtml(inner, key, customTitleMap)
}

/**
 * Single source of truth for section titles:
 * strip every leftover / Quill / layout copy for the key, then insert exactly
 * one title when pageStartMode is "New page + title".
 */
export function applySectionTitlesFromLayout(html, layout) {
  if (!html || !layout) return html
  let result = String(html)
  const customTitleMap = collectLayoutSectionTitleMap(layout)
  const markerMap = {
    body: MARKER_TAGS.body,
    jobResponsibilities: MARKER_TAGS.jobResponsibilities,
    terms: MARKER_TAGS.terms,
    salary: MARKER_TAGS.salary,
  }
  Object.keys(markerMap).forEach(key => {
    const markers = markerMap[key]
    const startIdx = result.indexOf(markers[0])
    if (startIdx < 0) return
    const endIdx = result.indexOf(markers[1], startIdx)
    if (endIdx < 0) return
    const innerStart = startIdx + markers[0].length
    const section = resolveSectionLayout(layout, key)
    const mode = section.pageStartMode || derivePageStartMode(section, key)
    const titleText = section.sectionTitleText || DEFAULT_SECTION_TITLE_TEXT[key] || ''

    const windowStart = Math.max(0, startIdx - 800)
    const before = stripSectionTitleBlocksFromHtml(
      result.slice(windowStart, startIdx),
      key,
      customTitleMap,
    )
    let inner = stripSectionTitleBlocksFromHtml(
      result.slice(innerStart, endIdx),
      key,
      customTitleMap,
    )

    if (mode === OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE) {
      const titleHtml = buildSectionTitleHtml(
        key,
        titleText,
        section.sectionTitleAlign,
        section.sectionTitleFontWeight,
      )
      if (titleHtml) inner = injectSectionTitleIntoInner(inner, titleHtml)
    }

    result = `${result.slice(0, windowStart)}${before}${result.slice(startIdx, innerStart)}${inner}${result.slice(endIdx)}`
  })
  return stripDuplicateOfferSectionTitles(result, customTitleMap)
}

function sectionHasOfferTitle(innerHtml, key, customTitleMap = null) {
  return findOfferSectionTitleBlocks(innerHtml, customTitleMap).some(block => block.key === key)
}

function injectLeadingOfferSectionTitle(inner, key) {
  const titleHtml = OFFER_SECTION_TITLE_HTML[key]
  if (!titleHtml) return inner
  return injectSectionTitleIntoInner(inner, titleHtml)
}

/**
 * Ensure marked Policies / Job Responsibilities / Terms sections keep exactly
 * one visible section title. Injects a canonical title when Quill content (or a
 * prior dedupe pass) left the section without one.
 */
export function ensureOfferSectionTitlesPresent(html) {
  if (!html) return html
  let result = String(html)
  const markerMap = {
    body: MARKER_TAGS.body,
    jobResponsibilities: MARKER_TAGS.jobResponsibilities,
    terms: MARKER_TAGS.terms,
    salary: MARKER_TAGS.salary,
  }
  Object.keys(markerMap).forEach(key => {
    const markers = markerMap[key]
    const startIdx = result.indexOf(markers[0])
    if (startIdx < 0) return
    const endIdx = result.indexOf(markers[1], startIdx)
    if (endIdx < 0) return
    const innerStart = startIdx + markers[0].length
    const inner = result.slice(innerStart, endIdx)
    const innerPlain = String(inner).replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').trim()
    // Empty sections: layout owns the single title for "New page + title".
    if (!innerPlain) return
    if (sectionHasOfferTitle(inner, key)) return
    const nextInner = injectLeadingOfferSectionTitle(inner, key)
    result = `${result.slice(0, innerStart)}${nextInner}${result.slice(endIdx)}`
  })
  return result
}

/**
 * Normalize Quill section HTML for PDF:
 * - Convert empty / br-only paragraphs to a fixed-height spacer (preserves editor blank lines)
 * - Collapse runs of spacers so we keep one blank line, not orphan empty pages
 * - Soft-collapse extreme <br> / newline runs
 * - Dedupe consecutive section titles (Policies / Job Resp / Terms)
 */
function sanitizeOfferSectionHtml(html) {
  if (!html) return ''
  let result = normalizeManualPageBreaksInHtml(String(html))
  result = preserveOfferBlankLineSpacers(result)
  // Collapse 3+ consecutive breaks (keep a soft paragraph gap)
  result = result.replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>')
  result = result.replace(/\n{3,}/g, '\n\n')
  result = markOfferMajorHeadings(result)
  result = stripDuplicateOfferSectionTitles(result)
  result = ensureOfferSectionTitlesPresent(result)
  result = markOfferMajorHeadings(result)
  result = preserveOfferAuthorBodyFormatting(result)
  result = wrapOfferKeepTogetherBlocks(result)
  return result.trim()
}

function buildPageBreakBlock(includePageBreak) {
  if (!includePageBreak) return ''
  return `\n${PAGE_BREAK_MARKER}\n${PAGE_BREAK_UNIT_HTML}\n`
}

function buildSectionPageBreakBlock(marker) {
  return `\n${marker}\n${PAGE_BREAK_UNIT_HTML}\n`
}

function buildJobRespPageBreakBlock() {
  return buildSectionPageBreakBlock(JOB_RESP_PAGE_BREAK_MARKER)
}

function buildPoliciesPageBreakBlock() {
  return buildSectionPageBreakBlock(POLICIES_PAGE_BREAK_MARKER)
}

function buildTermsPageBreakBlock() {
  return buildSectionPageBreakBlock(TERMS_PAGE_BREAK_MARKER)
}

function buildIntroPageBreakBlock() {
  return buildSectionPageBreakBlock(INTRO_PAGE_BREAK_MARKER)
}

function buildClosingPageBreakBlock() {
  return buildSectionPageBreakBlock(CLOSING_PAGE_BREAK_MARKER)
}

function buildSignaturePageBreakBlock() {
  return buildSectionPageBreakBlock(SIGNATURE_PAGE_BREAK_MARKER)
}

function buildSalaryBlock(fields) {
  const content = fields.salaryStructure || defaultSalaryStructureHtml()
  return `\n<!-- OFFER_SALARY_START -->\n${content}\n<!-- OFFER_SALARY_END -->\n`
}

function defaultSalaryStructureHtml() {
  return buildSalaryTableHtml(emptySalaryStructureData())
}

/** Standard salary component rows for Annexure A tables. */
export const STANDARD_SALARY_ROW_DEFS = [
  { component: 'Basic + DA', rowType: 'normal' },
  { component: 'VDA', rowType: 'normal' },
  { component: 'HRA', rowType: 'normal' },
  { component: 'Other Allowance', rowType: 'normal' },
  { component: 'STB', rowType: 'normal' },
  { component: 'Gross Salary', rowType: 'highlight' },
  { component: 'Employee (PF)', rowType: 'normal' },
  { component: 'Employee (ESI)', rowType: 'normal' },
  { component: 'Net Salary (In-hand)', rowType: 'ctc' },
  { component: 'Petrol Allowance', rowType: 'normal' },
  { component: 'Total In-hand', rowType: 'ctc' },
  { component: 'Employer (PF)', rowType: 'normal' },
  { component: 'Employer (ESI)', rowType: 'normal' },
  { component: 'CTC', rowType: 'ctc' },
]

const DEFAULT_SALARY_COLUMN_LABELS = {
  component: 'Components',
  monthly: 'Monthly (₹)',
  yearly: 'Yearly (₹)',
}

function defaultSalaryRows() {
  return STANDARD_SALARY_ROW_DEFS.map(({ component, rowType }) => ({
    component,
    monthly: '',
    yearly: '',
    rowType,
  }))
}

function defaultSalaryNotes() {
  return {
    professionalTax: {
      show: true,
      prefix: 'Note:',
      text: 'Professional TAX will be deducted as per the government regulations of the relevant States.',
    },
    lwf: {
      show: true,
      prefix: 'Note:',
      text: 'Labour Welfare Fund (LWF) will be deducted as per applicable state regulations.',
    },
    ppe: {
      show: true,
      prefix: '',
      text: 'PPE Deduction will happen only for first two months that will be 600/- per month.',
    },
    incentive: {
      show: true,
      prefix: 'Note:',
      text: 'If any additional meters are installed (more than 5 meters per day), you will get ₹130 per meter as incentive.',
    },
    additionalNotes: {
      show: true,
      prefix: '',
      text: '',
    },
  }
}

/**
 * One named salary table on Annexure A (title + columns + rows).
 * @param {string} [title]
 * @returns {{ title: string, columnLabels: object, rows: Array }}
 */
export function emptySalaryStructureBlock(title = '') {
  return {
    title: title != null ? String(title) : '',
    columnLabels: { ...DEFAULT_SALARY_COLUMN_LABELS },
    rows: defaultSalaryRows(),
  }
}

/**
 * Normalize one salary-structure block (per-table title + rows).
 * @param {unknown} raw
 * @param {{ columnLabels?: object, rows?: Array }} [fallback]
 */
export function normalizeSalaryStructureBlock(raw, fallback = {}) {
  const defaults = emptySalaryStructureBlock()
  const baseLabels = {
    ...defaults.columnLabels,
    ...(fallback.columnLabels && typeof fallback.columnLabels === 'object' ? fallback.columnLabels : {}),
  }
  const source = raw && typeof raw === 'object' ? raw : {}
  const columnLabels = {
    ...baseLabels,
    ...(source.columnLabels && typeof source.columnLabels === 'object' ? source.columnLabels : {}),
  }
  const fallbackRows = Array.isArray(fallback.rows) && fallback.rows.length
    ? fallback.rows
    : defaults.rows
  const rows = Array.isArray(source.rows) && source.rows.length
    ? source.rows.map(row => ({
      component: row.component != null ? String(row.component) : '',
      monthly: row.monthly != null ? String(row.monthly) : '',
      yearly: row.yearly != null ? String(row.yearly) : '',
      rowType: row.rowType || inferSalaryRowType(row.component),
    }))
    : fallbackRows.map(row => ({ ...row }))
  return {
    title: source.title != null
      ? String(source.title)
      : (source.heading != null ? String(source.heading) : ''),
    columnLabels,
    rows,
  }
}

export function emptySalaryStructureData() {
  const first = emptySalaryStructureBlock('')
  return {
    heading: 'Annexure A — Salary Structure',
    columnLabels: { ...first.columnLabels },
    rows: first.rows.map(row => ({ ...row })),
    // One or more named tables on the same annexure (default: single untitled → heading-only).
    structures: [first],
    notes: defaultSalaryNotes(),
  }
}

/**
 * Drop a repeated "Note:" prefix and a duplicated sentence
 * ("Note:Note: X: X" from regenerate of stored note text).
 */
function sanitizeSalaryNoteText(prefix, text) {
  let body = String(text || '').replace(/\s+/g, ' ').trim()
  if (!body) return ''
  const p = String(prefix || '').trim()
  if (p) {
    const prefixRe = new RegExp(`^(?:${escapeRegExp(p)}\\s*)+`, 'i')
    body = body.replace(prefixRe, '').trim()
  }
  const colonParts = body.split(/\s*:\s*/)
  if (colonParts.length === 2) {
    const left = colonParts[0].trim()
    const right = colonParts[1].trim()
    if (left && right && left.toLowerCase() === right.toLowerCase()) {
      body = left
    } else if (left && right && right.toLowerCase().startsWith(left.toLowerCase())) {
      body = right
    }
  }
  if (body.length >= 24) {
    const mid = Math.floor(body.length / 2)
    const left = body.slice(0, mid).trim()
    const right = body.slice(mid).replace(/^[:.\s]+/, '').trim()
    if (left && right && left.toLowerCase() === right.toLowerCase()) {
      body = left
    }
  }
  return body
}

/** Backward compatible — string notes or { show, prefix, text }. */
export function normalizeSalaryNoteEntry(value, fallbackPrefix = 'Note:') {
  if (value == null) {
    return { show: false, prefix: fallbackPrefix, text: '' }
  }
  if (typeof value === 'string') {
    const text = sanitizeSalaryNoteText(fallbackPrefix, value)
    return { show: !!text, prefix: fallbackPrefix, text }
  }
  if (typeof value === 'object') {
    const prefix = value.prefix != null ? String(value.prefix) : fallbackPrefix
    const text = sanitizeSalaryNoteText(prefix, value.text != null ? value.text : '')
    return {
      show: value.show !== false && !!text,
      prefix,
      text,
    }
  }
  return { show: false, prefix: fallbackPrefix, text: '' }
}

export function normalizeSalaryStructureData(raw) {
  const defaults = emptySalaryStructureData()
  if (!raw || typeof raw !== 'object') return defaults

  const notes = {}
  Object.keys(defaults.notes).forEach(key => {
    const fallback = defaults.notes[key]
    notes[key] = normalizeSalaryNoteEntry(raw.notes && raw.notes[key], fallback.prefix)
  })

  const topColumnLabels = {
    ...defaults.columnLabels,
    ...(raw.columnLabels && typeof raw.columnLabels === 'object' ? raw.columnLabels : {}),
  }
  const topRows = Array.isArray(raw.rows) && raw.rows.length
    ? raw.rows.map(row => ({
      component: row.component != null ? String(row.component) : '',
      monthly: row.monthly != null ? String(row.monthly) : '',
      yearly: row.yearly != null ? String(row.yearly) : '',
      rowType: row.rowType || inferSalaryRowType(row.component),
    }))
    : null

  let structures
  if (Array.isArray(raw.structures) && raw.structures.length) {
    structures = raw.structures.map((block, index) => normalizeSalaryStructureBlock(
      block,
      index === 0
        ? { columnLabels: topColumnLabels, rows: topRows || defaults.rows }
        : { columnLabels: topColumnLabels },
    ))
  } else {
    // Legacy single-table shape → one structure (title empty so HTML stays heading-only).
    structures = [
      normalizeSalaryStructureBlock(
        {
          title: '',
          columnLabels: topColumnLabels,
          rows: topRows || defaults.rows,
        },
        { columnLabels: topColumnLabels, rows: topRows || defaults.rows },
      ),
    ]
  }

  const primary = structures[0] || emptySalaryStructureBlock('')
  return {
    heading: raw.heading != null ? String(raw.heading) : defaults.heading,
    // Mirror primary structure for callers that still read top-level rows/labels.
    columnLabels: { ...primary.columnLabels },
    rows: primary.rows.map(row => ({ ...row })),
    structures,
    notes,
  }
}

/** Total named component rows across all salary structures (for review badges). */
export function countSalaryStructureComponentRows(data) {
  const normalized = normalizeSalaryStructureData(data)
  return (normalized.structures || []).reduce((sum, block) => {
    const rows = Array.isArray(block.rows) ? block.rows : []
    return sum + rows.filter(row => row && String(row.component || '').trim()).length
  }, 0)
}

/**
 * Build a default title for a newly added structure.
 * @param {number} index zero-based
 */
export function defaultSalaryStructureTitle(index = 0) {
  if (index <= 0) return 'Salary Structure'
  if (index === 1) return 'Salary Structure 2'
  return `Salary Structure ${index + 1}`
}

function normalizeSalaryTitlePlain(value) {
  return String(value || '')
    .replace(/[—–\-:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/**
 * Skip only an empty title or an exact duplicate of the annexure heading.
 * Substring checks used to drop "Salary Structure 2" when the heading was
 * "Salary Structure" / "Annexure A — Salary Structure".
 */
function isRedundantSalaryStructureTitle(title, heading) {
  const t = normalizeSalaryTitlePlain(title)
  const h = normalizeSalaryTitlePlain(heading)
  if (!t) return true
  if (t === h) return true
  return false
}

function extractSalaryStructureTitle(htmlSlice) {
  if (!htmlSlice) return ''
  const match = htmlSlice.match(
    /<(?:p|h[1-6])[^>]*class="[^"]*salary-structure-title[^"]*"[^>]*>([\s\S]*?)<\/(?:p|h[1-6])>/i,
  )
  return match ? salaryCellText(match[1]) : ''
}

/** Annexure / section heading — never a per-table .salary-structure-title. */
function extractNonStructureHeading(htmlSlice) {
  if (!htmlSlice) return ''
  const headings = [...htmlSlice.matchAll(/<(h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi)]
  for (let i = headings.length - 1; i >= 0; i -= 1) {
    if (/\bsalary-structure-title\b/i.test(headings[i][2] || '')) continue
    const text = salaryCellText(headings[i][3])
    if (text) return text
  }
  const majors = [...htmlSlice.matchAll(
    /<p([^>]*class="[^"]*offer-major-heading[^"]*"[^>]*)>([\s\S]*?)<\/p>/gi,
  )]
  for (let i = majors.length - 1; i >= 0; i -= 1) {
    if (/\bsalary-structure-title\b/i.test(majors[i][1] || '')) continue
    const text = salaryCellText(majors[i][2])
    if (text) return text
  }
  const boldOnly = [...htmlSlice.matchAll(
    /<p([^>]*)>\s*<(?:strong|b)[^>]*>([\s\S]*?)<\/(?:strong|b)>\s*<\/p>/gi,
  )]
  for (let i = boldOnly.length - 1; i >= 0; i -= 1) {
    if (/\bsalary-structure-title\b/i.test(boldOnly[i][1] || '')) continue
    const text = salaryCellText(boldOnly[i][2])
    if (text && !/^note:/i.test(text)) return text
  }
  return ''
}

function salaryCellText(html) {
  if (!html) return ''
  return String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?(?:strong|b|em|i|span)[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Parse a salary amount string (Indian grouping, optional ₹ prefix).
 * @param {string|number} text
 * @returns {number|null}
 */
export function parseSalaryAmount(text) {
  if (text == null || text === '') return null
  const cleaned = String(text)
    .replace(/₹/g, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '')
    .trim()
  if (!cleaned) return null
  const num = parseFloat(cleaned)
  return Number.isFinite(num) ? num : null
}

/**
 * Format a salary amount with Indian locale grouping (no ₹ — table headers carry currency).
 * @param {number} num
 * @returns {string}
 */
export function formatSalaryAmount(num) {
  if (num == null || !Number.isFinite(num)) return ''
  const rounded = Math.round(num * 100) / 100
  const hasDecimals = rounded % 1 !== 0
  return rounded.toLocaleString('en-IN', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  })
}

/**
 * Calculate yearly amount from a monthly value string.
 * @param {string|number} monthlyText
 * @returns {string}
 */
export function calcYearlyFromMonthly(monthlyText) {
  const monthly = parseSalaryAmount(monthlyText)
  if (monthly == null) return ''
  return formatSalaryAmount(monthly * 12)
}

/**
 * Sync yearly = monthly × 12 on a single salary row.
 * @param {object} row
 * @returns {object}
 */
export function syncSalaryRowYearly(row) {
  if (!row) return row
  return {
    ...row,
    yearly: calcYearlyFromMonthly(row.monthly),
  }
}

/**
 * Sync yearly amounts for all salary rows.
 * @param {Array} rows
 * @returns {Array}
 */
export function syncSalaryRowsYearly(rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(syncSalaryRowYearly)
}

/** Component-name order persisted as metadata.offerSalaryComponentOrder. */
export function extractOfferSalaryComponentOrder(rows) {
  if (!Array.isArray(rows)) return []
  return rows
    .map(row => String(row && row.component != null ? row.component : '').trim())
    .filter(Boolean)
}

/**
 * Normalize saved component order. Unknown names dropped; missing rows appended
 * in their current relative order (backward compatible when order is absent).
 * @param {unknown} raw
 * @param {Array<{component?: string}>} rows
 * @returns {string[]}
 */
export function normalizeOfferSalaryComponentOrder(raw, rows = []) {
  const current = extractOfferSalaryComponentOrder(rows)
  if (!Array.isArray(raw) || !raw.length) return current
  const available = new Map()
  current.forEach((name) => {
    const key = name.toLowerCase()
    if (!available.has(key)) available.set(key, name)
  })
  const seen = new Set()
  const result = []
  raw.forEach((name) => {
    if (typeof name !== 'string') return
    const key = name.trim().toLowerCase()
    if (!key || !available.has(key) || seen.has(key)) return
    seen.add(key)
    result.push(available.get(key))
  })
  current.forEach((name) => {
    const key = name.toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      result.push(name)
    }
  })
  return result
}

/**
 * Reorder salary editor rows to match a component-name order list.
 * @param {Array} rows
 * @param {unknown} order
 * @returns {Array}
 */
export function reorderSalaryRowsByComponentOrder(rows, order) {
  if (!Array.isArray(rows) || !rows.length) return Array.isArray(rows) ? [...rows] : []
  const normalized = normalizeOfferSalaryComponentOrder(order, rows)
  if (!normalized.length) return [...rows]
  const remaining = rows.map((row, index) => ({
    row,
    index,
    key: String(row && row.component != null ? row.component : '').trim().toLowerCase(),
  }))
  const ordered = []
  normalized.forEach((name) => {
    const key = name.toLowerCase()
    const idx = remaining.findIndex(item => item.key && item.key === key)
    if (idx >= 0) {
      ordered.push(remaining[idx].row)
      remaining.splice(idx, 1)
    }
  })
  remaining.forEach(item => ordered.push(item.row))
  return ordered
}

/**
 * Move a salary component row one step up or down.
 * @param {Array} rows
 * @param {number} index
 * @param {'up'|'down'} direction
 * @returns {Array}
 */
export function moveSalaryRowOrder(rows, index, direction) {
  const next = Array.isArray(rows) ? [...rows] : []
  if (index < 0 || index >= next.length) return next
  const swapWith = direction === 'up' ? index - 1 : index + 1
  if (swapWith < 0 || swapWith >= next.length) return next
  const tmp = next[index]
  next[index] = next[swapWith]
  next[swapWith] = tmp
  return next
}

export function inferSalaryRowType(componentName, trHtml = '') {
  const classMatch = String(trHtml).match(/class="([^"]*)"/i)
  const classes = classMatch ? classMatch[1] : ''
  if (/ctc-highlight/i.test(classes)) return 'ctc'
  if (/table-highlight/i.test(classes)) return 'highlight'

  const name = String(componentName || '').trim().toLowerCase()
  if (name === 'ctc') return 'ctc'
  if (/total in-hand|net salary/i.test(name)) return 'ctc'
  if (/gross salary|^total$/i.test(name)) return 'highlight'
  return 'normal'
}

function parseSalaryTableRows(tableHtml) {
  const rows = []
  const trPattern = /<tr[^>]*>([\s\S]*?)<\/tr>/gi
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = trPattern.exec(tableHtml)) !== null) {
    const trFull = match[0]
    const trInner = match[1]
    if (/<th[\s>]/i.test(trInner)) continue

    const cells = []
    const tdPattern = /<td[^>]*>([\s\S]*?)<\/td>/gi
    let tdMatch
    // eslint-disable-next-line no-cond-assign
    while ((tdMatch = tdPattern.exec(trInner)) !== null) {
      cells.push(tdMatch[1])
    }
    if (cells.length < 2) continue

    const component = salaryCellText(cells[0])
    const monthly = salaryCellText(cells[1])
    const yearly = cells.length >= 3 ? salaryCellText(cells[2]) : ''

    if (!component && !monthly && !yearly) continue

    rows.push({
      component,
      monthly,
      yearly,
      rowType: inferSalaryRowType(component, trFull),
    })
  }
  return rows
}

function stripNotePrefix(text) {
  return String(text || '')
    .replace(/^note:\s*/i, '')
    .trim()
}

function parseSalaryNotes(htmlAfterTable) {
  const notes = emptySalaryStructureData().notes
  const additionalLines = []

  const pPattern = /<p[^>]*>([\s\S]*?)<\/p>/gi
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = pPattern.exec(htmlAfterTable)) !== null) {
    const rawHtml = match[1]
    const text = salaryCellText(rawHtml)
    if (!text || /CONFIG\.SALARY/i.test(text)) continue

    const prefixMatch = rawHtml.match(/<strong[^>]*>([\s\S]*?)<\/strong>/i)
    const prefix = prefixMatch ? salaryCellText(prefixMatch[1]).replace(/:$/, '') + ':' : ''
    const body = prefixMatch
      ? salaryCellText(rawHtml.replace(/<strong[^>]*>[\s\S]*?<\/strong>\s*/i, ''))
      : text

    if (/professional\s*tax|prof\.?\s*tax/i.test(text)) {
      notes.professionalTax = { show: true, prefix: prefix || 'Note:', text: stripNotePrefix(body || text) }
    } else if (/lwf|labour welfare|labor welfare/i.test(text)) {
      notes.lwf = { show: true, prefix: prefix || 'Note:', text: stripNotePrefix(body || text) }
    } else if (/ppe|personal protective/i.test(text)) {
      notes.ppe = { show: true, prefix: prefix || '', text: stripNotePrefix(body || text) }
    } else if (/incentive|additional meter|installation target|more than \d+\s*meter/i.test(text)) {
      notes.incentive = { show: true, prefix: prefix || 'Note:', text: stripNotePrefix(body || text) }
    } else {
      additionalLines.push(stripNotePrefix(body || text))
    }
  }

  notes.additionalNotes = { show: additionalLines.length > 0, prefix: '', text: additionalLines.join('\n') }
  return notes
}

function parseSalaryColumnLabels(tableHtml) {
  const defaults = emptySalaryStructureData().columnLabels
  const thPattern = /<th[^>]*>([\s\S]*?)<\/th>/gi
  const headers = []
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = thPattern.exec(tableHtml)) !== null) {
    headers.push(salaryCellText(match[1]))
  }
  if (headers.length < 3) return defaults
  return {
    component: headers[0] || defaults.component,
    monthly: headers[1] || defaults.monthly,
    yearly: headers[2] || defaults.yearly,
  }
}

function findSalaryTableMatch(html) {
  const all = findAllSalaryTableMatches(html)
  return all.length ? all[0] : null
}

/**
 * Find every salary annexure table in HTML (supports multiple structures).
 * @returns {RegExpMatchArray[]}
 */
function findAllSalaryTableMatches(html) {
  if (!html) return []
  const source = closeUnclosedSalaryTables(String(html))
  const classTables = []
  const classOpenRe = /<table\b[^>]*class="[^"]*salary-table[^"]*"[^>]*>/gi
  let classOpen = classOpenRe.exec(source)
  while (classOpen) {
    const close = findMatchingTableClose(source, classOpen.index + classOpen[0].length)
    if (close < 0) {
      classOpenRe.lastIndex = classOpen.index + classOpen[0].length
      classOpen = classOpenRe.exec(source)
      continue
    }
    const match = [source.slice(classOpen.index, close + 8)]
    match.index = classOpen.index
    classTables.push(match)
    classOpenRe.lastIndex = close + 8
    classOpen = classOpenRe.exec(source)
  }
  if (classTables.length) return classTables

  const tables = []
  const openRe = /<table\b[^>]*>/gi
  let open = openRe.exec(source)
  while (open) {
    const close = findMatchingTableClose(source, open.index + open[0].length)
    if (close < 0) break
    const match = [source.slice(open.index, close + 8)]
    match.index = open.index
    tables.push(match)
    openRe.lastIndex = close + 8
    open = openRe.exec(source)
  }
  if (!tables.length) return []

  const hinted = tables.filter(t => {
    const before = source.slice(Math.max(0, (t.index || 0) - 400), t.index || 0)
    return SALARY_HINT_PATTERN.test(before) || /Annexure\s+A|compensation|CTC/i.test(before)
  })
  if (hinted.length) return hinted
  return tables.length === 1 ? [tables[0]] : []
}

function extractHeadingBefore(htmlSlice) {
  if (!htmlSlice) return ''
  // Prefer explicit per-structure title class, then any heading, then bold-only paragraph.
  const structureTitle = htmlSlice.match(
    /<(?:p|h[1-6])[^>]*class="[^"]*salary-structure-title[^"]*"[^>]*>([\s\S]*?)<\/(?:p|h[1-6])>/i,
  )
  if (structureTitle) return salaryCellText(structureTitle[1])

  const headings = [...htmlSlice.matchAll(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi)]
  if (headings.length) return salaryCellText(headings[headings.length - 1][1])

  const boldOnly = [...htmlSlice.matchAll(
    /<p[^>]*>\s*<(?:strong|b)[^>]*>([\s\S]*?)<\/(?:strong|b)>\s*<\/p>/gi,
  )]
  if (boldOnly.length) {
    const text = salaryCellText(boldOnly[boldOnly.length - 1][1])
    if (text && !/^note:/i.test(text)) return text
  }
  return ''
}

/**
 * Parse salary annexure HTML into structured editor fields.
 * Supports one or more salary-table blocks on the same annexure.
 * @returns {{ parsed: boolean, data: object, warning: string|null }}
 */
export function parseSalaryTableHtml(html) {
  if (!html || !html.trim()) {
    return { parsed: true, data: emptySalaryStructureData(), warning: null }
  }

  const tableMatches = findAllSalaryTableMatches(html)
  if (!tableMatches.length) {
    return {
      parsed: false,
      data: emptySalaryStructureData(),
      warning: 'No salary table found in the existing HTML. Standard rows are shown below; use Advanced to edit the original HTML, or fill in the table and save to migrate.',
    }
  }

  const structures = []
  let annexureHeading = ''
  let lastTableEnd = 0

  tableMatches.forEach((tableMatch, index) => {
    const tableStart = tableMatch.index || 0
    const tableEnd = tableStart + tableMatch[0].length
    lastTableEnd = tableEnd
    const beforeStart = index === 0 ? 0 : (tableMatches[index - 1].index || 0) + tableMatches[index - 1][0].length
    const beforeTable = html.slice(beforeStart, tableStart)

    const rows = syncSalaryRowsYearly(parseSalaryTableRows(tableMatch[0]))
    if (!rows.length) return

    const taggedTitle = extractSalaryStructureTitle(beforeTable)
    const plainHeading = extractNonStructureHeading(beforeTable)
    if (index === 0) {
      // Do not let a per-table .salary-structure-title become the annexure heading.
      annexureHeading = plainHeading
        || (SALARY_HINT_PATTERN.test(beforeTable) ? 'Annexure A — Salary Structure' : emptySalaryStructureData().heading)
    }

    const columnLabels = parseSalaryColumnLabels(tableMatch[0])
    let structureTitle = ''
    if (tableMatches.length === 1) {
      // Legacy: only annexure heading, no separate structure title.
      structureTitle = ''
    } else if (index === 0) {
      structureTitle = taggedTitle
        || (plainHeading && plainHeading !== annexureHeading ? plainHeading : '')
    } else {
      // Do not invent "Salary Structure 2" when the user left the title blank.
      structureTitle = taggedTitle || plainHeading || extractHeadingBefore(beforeTable) || ''
    }

    structures.push(normalizeSalaryStructureBlock({
      title: structureTitle,
      rows,
      columnLabels,
    }))
  })

  if (!structures.length) {
    return {
      parsed: false,
      data: emptySalaryStructureData(),
      warning: 'Could not extract salary table rows from HTML.',
    }
  }

  const afterTable = html.slice(lastTableEnd)
  const notes = parseSalaryNotes(afterTable)

  return {
    parsed: true,
    data: normalizeSalaryStructureData({
      heading: annexureHeading || emptySalaryStructureData().heading,
      structures,
      notes,
      columnLabels: structures[0].columnLabels,
      rows: structures[0].rows,
    }),
    warning: null,
  }
}

function formatSalaryNoteHtml(noteEntry) {
  const note = normalizeSalaryNoteEntry(noteEntry)
  if (!note.show || !note.text.trim()) return ''
  const prefix = note.prefix.trim()
  const body = escapeHtml(note.text.trim())
  if (prefix) {
    return `<p class="salary-structure-note"><strong>${escapeHtml(prefix)}</strong> ${body}</p>`
  }
  return `<p class="salary-structure-note">${body}</p>`
}

function formatSalaryAdditionalNotesHtml(noteEntry) {
  const note = normalizeSalaryNoteEntry(noteEntry, '')
  if (!note.show || !note.text.trim()) return []
  return note.text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    const prefix = note.prefix.trim()
    if (prefix) {
      return `<p class="salary-structure-note"><strong>${escapeHtml(prefix)}</strong> ${escapeHtml(line)}</p>`
    }
    return `<p class="salary-structure-note">${escapeHtml(line)}</p>`
  })
}

function buildSingleSalaryTableHtml(structure, fallbackLabels = DEFAULT_SALARY_COLUMN_LABELS) {
  const block = normalizeSalaryStructureBlock(structure, { columnLabels: fallbackLabels })
  const labels = block.columnLabels || fallbackLabels
  const rows = Array.isArray(block.rows) ? block.rows : []

  let bodyRows = ''
  rows.forEach(row => {
    const rowType = row.rowType || inferSalaryRowType(row.component)
    const rowClass = rowType === 'ctc'
      ? 'ctc-highlight'
      : rowType === 'highlight'
        ? 'table-highlight'
        : ''
    const classAttr = rowClass ? ` class="${rowClass}"` : ''
    const component = row.component || ''
    const compCell = (rowType === 'highlight' || rowType === 'ctc')
      ? `<strong>${escapeHtml(component)}</strong>`
      : escapeHtml(component)
    const monthly = row.monthly || ''
    const yearly = calcYearlyFromMonthly(monthly) || row.yearly || ''

    bodyRows += `\n    <tr${classAttr}>
      <td>${compCell}</td>
      <td>${escapeHtml(monthly)}</td>
      <td>${escapeHtml(yearly)}</td>
    </tr>`
  })

  return `<table class="salary-table">
  <thead>
    <tr>
      <th>${escapeHtml(labels.component || 'Components')}</th>
      <th>${escapeHtml(labels.monthly || 'Monthly (₹)')}</th>
      <th>${escapeHtml(labels.yearly || 'Yearly (₹)')}</th>
    </tr>
  </thead>
  <tbody>${bodyRows}
  </tbody>
</table>`
}

/**
 * Build salary annexure HTML from structured editor fields.
 * Renders one or more named salary tables (same annexure), then shared footnotes.
 * @param {{ heading?: string, rows?: Array, notes?: object, columnLabels?: object, structures?: Array }} data
 * @returns {string}
 */
export function buildSalaryTableHtml(data = emptySalaryStructureData()) {
  const normalized = normalizeSalaryStructureData(data)
  const heading = normalized.heading || 'Annexure A — Salary Structure'
  const structures = Array.isArray(normalized.structures) && normalized.structures.length
    ? normalized.structures
    : [normalizeSalaryStructureBlock({
      title: '',
      rows: normalized.rows,
      columnLabels: normalized.columnLabels,
    })]

  let html = `<h3 class="offer-major-heading">${escapeHtml(heading)}</h3>`

  structures.forEach((structure, index) => {
    const title = String(structure.title || '').trim()
    const showStructureTitle = !!(title && !isRedundantSalaryStructureTitle(title, heading))
    if (showStructureTitle) {
      html += `\n<p class="ql-align-center offer-major-heading salary-structure-title"><strong>${escapeHtml(title)}</strong></p>`
    }
    html += `\n${buildSingleSalaryTableHtml(structure, normalized.columnLabels)}`
    if (index < structures.length - 1) html += '\n'
  })

  const notes = normalized.notes || {}
  const noteBlocks = []
  ;['professionalTax', 'lwf', 'ppe', 'incentive'].forEach(key => {
    const block = formatSalaryNoteHtml(notes[key])
    if (block) noteBlocks.push(block)
  })
  noteBlocks.push(...formatSalaryAdditionalNotesHtml(notes.additionalNotes))
  if (noteBlocks.length) {
    html += `\n${noteBlocks.join('\n')}`
  }

  return html
}

function replaceBetweenMarkers(html, [startMarker, endMarker], newContent) {
  const start = html.indexOf(startMarker)
  if (start < 0) return html
  const end = html.indexOf(endMarker, start + startMarker.length)
  if (end < 0) return html
  return html.slice(0, start + startMarker.length) + `\n${newContent}\n` + html.slice(end)
}

function replaceRegion(html, region, newContent) {
  if (!region || region.start == null || region.end == null) return html
  return html.slice(0, region.start) + newContent + html.slice(region.end)
}

/**
 * Remove only the salary annexure page-break unit.
 * Must NOT strip other section breaks (terms/policies/closing/signature) that
 * share the offer-page-break class — that previously wiped Terms pageBreakBefore.
 */
function removeCanonicalPageBreak(html) {
  if (!html) return html
  let result = html
  const paired = new RegExp(
    `${escapeRegExp(PAGE_BREAK_MARKER)}\\s*<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\\s*<\\/div>`
    + `(?:\\s*<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\\s*(?:&nbsp;|&#160;|\\u00a0|\\s)*<\\/div>)?`,
    'gi',
  )
  result = result.replace(paired, '')
  result = result.split(PAGE_BREAK_MARKER).join('')
  // Legacy salary break without HTML comment: div immediately before salary markers.
  result = result.replace(
    /<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?(?=<!--\s*OFFER_SALARY_START\s*-->)/gi,
    '',
  )
  return result.replace(/\n{3,}/g, '\n\n')
}

function removePoliciesPageBreakFromGap(html, regions) {
  const gap = findGapBeforeBody(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function removeJobRespPageBreakFromGap(html, regions) {
  const gap = findGapBeforeJobResponsibilities(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function removeTermsPageBreakFromGap(html, regions) {
  const gap = findGapBeforeTerms(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function removeIntroPageBreakFromGap(html, regions) {
  const gap = findGapBeforeIntro(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function removeClosingPageBreakFromGap(html, regions) {
  const gap = findGapBeforeClosing(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function removeSignaturePageBreakFromGap(html, regions) {
  const gap = findGapBeforeSignature(html, regions)
  if (!gap) return html

  const cleanedGap = removePageBreakFromFragment(gap.content)
  return `${html.slice(0, gap.start)}${cleanedGap}${html.slice(gap.end)}`
}

function upsertPoliciesPageBreak(html, parseResult, includePolicies = true, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'body', includePolicies)

  // generatePDFFromHTML rebuilds fields without metadata.offerLayout (stamp-only
  // options). Default body is CONTINUE, which used to STRIP a named Policies
  // break assemble just added — regenerate then reprinted signatures + Policies
  // in one table. Keep the named break unless Configure explicitly said Same page.
  if (!shouldBreak) {
    // Only generatePDFFromHTML's default layout (no saved/marker starts) must
    // keep an already-assembled named break. Assemble/Configure with CONTINUE
    // still strips it.
    if (
      html.includes(POLICIES_PAGE_BREAK_MARKER)
      && layout
      && layout._source === 'default'
    ) {
      return repairSectionPageBreakDivs(html)
    }
    let stripped = stripPageBreakMarker(html, POLICIES_PAGE_BREAK_MARKER)
    stripped = removePoliciesPageBreakFromGap(stripped, regions)
    return stripped
  }

  // Always strip prior POLICIES break first (idempotent re-place immediately
  // before BODY). Inserting at the intro→body gap *start* left the first-page
  // stamp in the gap, so preview/PDF put signatures + Policies on one page.
  let result = stripPageBreakMarker(html, POLICIES_PAGE_BREAK_MARKER)
  result = removePoliciesPageBreakFromGap(result, regions)

  const bodyStart = result.indexOf(MARKER_TAGS.body[0])
  const insertAt = bodyStart >= 0
    ? bodyStart
    : (regions?.body?.start != null ? regions.body.start : -1)
  if (insertAt < 0) return result

  const windowStart = Math.max(0, insertAt - 280)
  const before = result.slice(windowStart, insertAt)
  const cleanedBefore = before
    .replace(/(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
    .replace(/<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
  return `${result.slice(0, windowStart)}${cleanedBefore}${buildPoliciesPageBreakBlock()}${result.slice(insertAt)}`
}

function upsertJobResponsibilitiesPageBreak(html, hasJobResp, parseResult, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'jobResponsibilities', hasJobResp)

  let result = stripPageBreakMarker(html, JOB_RESP_PAGE_BREAK_MARKER)
  result = removeJobRespPageBreakFromGap(result, regions)

  if (!shouldBreak) return result

  const jobStart = result.indexOf(MARKER_TAGS.jobResponsibilities[0])
  const insertAt = jobStart >= 0
    ? jobStart
    : (regions?.jobResponsibilities?.start != null ? regions.jobResponsibilities.start : -1)
  if (insertAt < 0) return result

  const windowStart = Math.max(0, insertAt - 280)
  const before = result.slice(windowStart, insertAt)
  const cleanedBefore = before
    .replace(/(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
    .replace(/<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
  return `${result.slice(0, windowStart)}${cleanedBefore}${buildJobRespPageBreakBlock()}${result.slice(insertAt)}`
}

function upsertTermsPageBreak(html, hasTerms, parseResult, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'terms', hasTerms)

  // Always strip prior TERMS break markers first (idempotent re-place).
  let result = stripPageBreakMarker(html, TERMS_PAGE_BREAK_MARKER)
  result = removeTermsPageBreakFromGap(result, regions)

  if (!shouldBreak) return result

  const termsStart = result.indexOf(MARKER_TAGS.terms[0])
  if (termsStart < 0) {
    // Heuristic region without markers — insert before region start.
    if (regions?.terms?.start != null) {
      const pageBreakBlock = buildTermsPageBreakBlock()
      return `${result.slice(0, regions.terms.start)}${pageBreakBlock}${result.slice(regions.terms.start)}`
    }
    return result
  }

  // Drop a dangling offer-page-break div immediately before terms (from a
  // previous salary/body break that was left adjacent after reorder).
  const windowStart = Math.max(0, termsStart - 280)
  const before = result.slice(windowStart, termsStart)
  const cleanedBefore = before
    .replace(/(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
    .replace(/<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
  const pageBreakBlock = buildTermsPageBreakBlock()
  return `${result.slice(0, windowStart)}${cleanedBefore}${pageBreakBlock}${result.slice(termsStart)}`
}

function upsertIntroPageBreak(html, showIntro, parseResult, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'intro', showIntro)

  if (!shouldBreak) {
    return removeIntroPageBreakFromGap(html, regions)
  }

  const gap = findGapBeforeIntro(html, regions)
  if (!gap) return html

  const gapHasInlineBreak = detectPageBreak(gap.content)
  const hasCanonicalIntroBreak = gap.content.includes(INTRO_PAGE_BREAK_MARKER)

  if (gapHasInlineBreak && !hasCanonicalIntroBreak) {
    return html
  }

  let result = removeIntroPageBreakFromGap(html, regions)
  const updatedGap = findGapBeforeIntro(result, regions)
  if (!updatedGap) return result

  const pageBreakBlock = buildIntroPageBreakBlock()
  return `${result.slice(0, updatedGap.start)}${pageBreakBlock}${result.slice(updatedGap.start)}`
}

function upsertClosingPageBreak(html, showClosing, parseResult, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'closing', showClosing)

  if (!shouldBreak) {
    return removeClosingPageBreakFromGap(html, regions)
  }

  const gap = findGapBeforeClosing(html, regions)
  if (!gap) return html

  const gapHasInlineBreak = detectPageBreak(gap.content)
  const hasCanonicalClosingBreak = gap.content.includes(CLOSING_PAGE_BREAK_MARKER)

  if (gapHasInlineBreak && !hasCanonicalClosingBreak) {
    return html
  }

  let result = removeClosingPageBreakFromGap(html, regions)
  const updatedGap = findGapBeforeClosing(result, regions)
  if (updatedGap) {
    const pageBreakBlock = buildClosingPageBreakBlock()
    return `${result.slice(0, updatedGap.start)}${pageBreakBlock}${result.slice(updatedGap.start)}`
  }
  const closingStart = result.indexOf(MARKER_TAGS.closing[0])
  if (closingStart >= 0 && !result.includes(CLOSING_PAGE_BREAK_MARKER)) {
    return `${result.slice(0, closingStart)}${buildClosingPageBreakBlock()}${result.slice(closingStart)}`
  }
  return result
}

function upsertSignaturePageBreak(html, showSignature, parseResult, layout = null) {
  const { regions } = parseResult || {}
  const shouldBreak = resolveSectionPageBreakEnabled(layout, 'signature', showSignature)

  let result = stripPageBreakMarker(html, SIGNATURE_PAGE_BREAK_MARKER)
  result = removeSignaturePageBreakFromGap(result, regions)

  if (!shouldBreak) return result

  const signatureStart = result.indexOf(MARKER_TAGS.signature[0])
  if (signatureStart >= 0) {
    const windowStart = Math.max(0, signatureStart - 280)
    const before = result.slice(windowStart, signatureStart)
    const cleanedBefore = before
      .replace(/(?:<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
      .replace(/<div[^>]*style="[^"]*page-break-before:\s*always[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?$/i, '')
    const pageBreakBlock = buildSignaturePageBreakBlock()
    return `${result.slice(0, windowStart)}${cleanedBefore}${pageBreakBlock}${result.slice(signatureStart)}`
  }

  // Heuristic: signature-section without markers (saved templates / chrome repair).
  const sigMatch = result.match(/<div[^>]*class="[^"]*\bsignature-section\b[^"]*"[^>]*>/i)
  if (!sigMatch || sigMatch.index == null) return result
  const insertAt = sigMatch.index
  const windowStart = Math.max(0, insertAt - 280)
  const before = result.slice(windowStart, insertAt)
  if (before.includes(SIGNATURE_PAGE_BREAK_MARKER) || /offer-page-break/i.test(before.slice(-120))) {
    return result
  }
  const pageBreakBlock = buildSignaturePageBreakBlock()
  return `${result.slice(0, insertAt)}${pageBreakBlock}${result.slice(insertAt)}`
}

/**
 * Restore page-break divs when HTML comments survived but divs were stripped
 * (legacy removeCanonicalPageBreak wiped all offer-page-break nodes).
 */
function repairSectionPageBreakDivs(html) {
  if (!html) return html
  let result = html
  SECTION_PAGE_BREAK_MARKERS.forEach(marker => {
    if (!result.includes(marker)) return
    const idx = result.indexOf(marker)
    const after = result.slice(idx + marker.length)
    if (/^\s*<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>/i.test(after)) return
    result = `${result.slice(0, idx + marker.length)}\n${PAGE_BREAK_HTML}\n${result.slice(idx + marker.length)}`
  })
  // Salary marker uses PAGE_BREAK_MARKER (not in SECTION_PAGE_BREAK_MARKERS).
  if (result.includes(PAGE_BREAK_MARKER)) {
    const idx = result.indexOf(PAGE_BREAK_MARKER)
    const after = result.slice(idx + PAGE_BREAK_MARKER.length)
    if (!/^\s*<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>/i.test(after)) {
      result = `${result.slice(0, idx + PAGE_BREAK_MARKER.length)}\n${PAGE_BREAK_HTML}\n${result.slice(idx + PAGE_BREAK_MARKER.length)}`
    }
  }
  return result
}

/**
 * Normalize stamp → designation → employee name|signature.
 * @param {string} inner
 * @param {object} [fields]
 */
function normalizeOfferSignatureSectionInner(inner, fields = {}) {
  let next = String(inner || '')
  const emp = normalizeOfferEmployeeSignConfig(
    fields.offerEmployeeSignConfig != null
      ? fields.offerEmployeeSignConfig
      : fields.offerEmployeeSignPlacement,
  )
  const employeeHtml = emp.withStamp ? buildEmployeeAcceptanceHtml(emp) : ''
  // Drop legacy "Sincerely," lead-in — stamp comes first after closing.
  next = next.replace(/<p[^>]*>\s*Sincerely,?\s*<\/p>\s*/gi, '')
  // Ensure designation/name paragraph has signatory-block class when present.
  next = next.replace(
    /<p(\s[^>]*)?>(\s*(?:<strong>|<b>)?[^<]*(?:Authorised Signatory|Authorized Signatory|Human Resources|\{\{)[\s\S]*?)<\/p>/i,
    (full, attrs = '', body) => {
      if (/\bsignatory-block\b/i.test(attrs || '')) return full
      if (/\bclass\s*=\s*(["'])([^"']*)\1/i.test(attrs || '')) {
        return `<p${attrs.replace(
          /\bclass\s*=\s*(["'])([^"']*)\1/i,
          (_, q, cls) => `class=${q}${cls} signatory-block${q}`,
        )}>${body}</p>`
      }
      return `<p class="signatory-block"${attrs || ''}>${body}</p>`
    },
  )
  // Strip any existing employee-acceptance / legacy lines, then re-apply config.
  next = next.replace(
    /<div[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance\b[^"']*["'][^>]*>[\s\S]*?<\/div>\s*/gi,
    '',
  )
  next = next
    .replace(/<p[^>]*>\s*Employee\s+Name\s*:[\s\S]*?<\/p>\s*/gi, '')
    .replace(/<p[^>]*>\s*Employee\s+Signature\s*:[\s\S]*?<\/p>\s*/gi, '')
  if (employeeHtml) {
    next = `${next.trim()}\n  ${employeeHtml}`
  }
  return next
}

/**
 * Keep a single employee-acceptance block per signature-section (and collapse
 * consecutive plain Name/Signature paragraph pairs). Prevents double lines when
 * legacy markup survives a strip+rebuild pass.
 */
function collapseDuplicateEmployeeAcceptance(html) {
  if (!html) return html
  let result = String(html)
  const ranges = findAllSignatureSectionRanges(result)
  if (ranges.length) {
    ;[...ranges].sort((a, b) => b.start - a.start).forEach(range => {
      const openEnd = result.indexOf('>', range.start) + 1
      if (openEnd <= 0) return
      // range.end is past the section's closing </div>
      const closeIdx = range.end >= 6 ? range.end - 6 : -1
      if (closeIdx < openEnd || !/^<\/div>/i.test(result.slice(closeIdx, range.end))) return
      let inner = result.slice(openEnd, closeIdx)
      let seen = false
      inner = inner.replace(
        /<div[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance\b[^"']*["'][^>]*>[\s\S]*?<\/div>\s*/gi,
        (block) => {
          if (seen) return ''
          seen = true
          return block
        },
      )
      inner = inner.replace(
        /(?:<p[^>]*>\s*Employee\s+Name\s*:[\s\S]*?<\/p>\s*<p[^>]*>\s*Employee\s+Signature\s*:[\s\S]*?<\/p>\s*){2,}/gi,
        (match) => {
          const first = match.match(
            /<p[^>]*>\s*Employee\s+Name\s*:[\s\S]*?<\/p>\s*<p[^>]*>\s*Employee\s+Signature\s*:[\s\S]*?<\/p>\s*/i,
          )
          return first ? first[0] : match
        },
      )
      result = `${result.slice(0, openEnd)}${inner}${result.slice(closeIdx)}`
    })
    return result
  }
  return result.replace(
    /(<div[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance\b[^"']*["'][^>]*>[\s\S]*?<\/div>\s*){2,}/gi,
    (match) => {
      const first = match.match(
        /<div[^>]*class\s*=\s*["'][^"']*\bemployee-acceptance\b[^"']*["'][^>]*>[\s\S]*?<\/div>\s*/i,
      )
      return first ? first[0] : match
    },
  )
}

/**
 * Ensure stamp/signature + employee acceptance exist for PDF (closing without
 * signature markers previously produced nearly blank last pages).
 * Layout: stamp → designation → Employee Name | Employee Signature (config-driven).
 * @param {string} html
 * @param {object} [fields]
 */
function ensureOfferSignatureBlock(html, fields = {}) {
  if (!html) return html
  let result = stripSignatoryLabelsFromMarkedSections(html)
  const ranges = findAllSignatureSectionRanges(result)
  if (ranges.length) {
    const sorted = [...ranges].sort((a, b) => b.start - a.start)
    sorted.forEach(range => {
      const openEnd = result.indexOf('>', range.start) + 1
      if (openEnd <= 0) return
      const inner = result.slice(openEnd, range.end)
      const recovered = recoverSignatoryFieldsFromInner(inner, fields)
      // Keep stamp src from outer fields (chrome) or from this section's img.
      const sectionSrc = extractOfferSignatureImageSrc(inner)
        || (fields && fields.signatureSrc)
        || ''
      const rebuilt = buildSignatureHtml(
        { ...recovered, signatureSrc: sectionSrc || recovered.signatureSrc },
        {
          extraClass: range.isExtra ? 'signature-section-extra' : '',
          signatureSrc: sectionSrc,
          // Employee lines only on the primary stamp — extras are stamp-only copies.
          includeEmployee: !range.isExtra,
        },
      )
      result = `${result.slice(0, range.start)}${rebuilt}${result.slice(range.end)}`
    })
    return result
  }
  if (/class\s*=\s*["'][^"']*\bsignature-section\b/i.test(result)) {
    // Fallback when range walk fails — inject config-driven employee lines after
    // stripping any leftover acceptance markup (avoid stacking duplicates).
    const emp = resolveFieldsEmployeeSignConfig(fields)
    const employeeHtml = emp.withStamp ? buildEmployeeAcceptanceHtml(emp) : ''
    if (!employeeHtml) return result
    result = stripEmployeeAcceptanceMarkup(result)
    return result.replace(
      /(<div[^>]*class\s*=\s*["'][^"']*\bsignature-section\b(?![^"']*signature-section-extra)[^"']*["'][^>]*>[\s\S]*?)(<\/div>)/i,
      (_, openInner, close) => `${openInner}\n  ${employeeHtml}\n${close}`,
    )
  }

  // Do not invent a signature block when Closing & Signature section was omitted,
  // unless a placement actually needs a primary end stamp (Last page / policies /
  // salary / after-terms-only). withClosing + afterTerms use extras only — inventing
  // here stacked extra Authorised Signatory / HR blocks (user saw 4× with no
  // "After Employment Policies" selected).
  if (!result.includes(MARKER_TAGS.closing[0])) {
    const locs = resolveFieldsSignaturePages(fields)
    const mayInventPrimary = !!(fields && fields._allowSignatureWithoutClosing)
      || locs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)
      || locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_POLICIES)
      || locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_SALARY)
      || (
        locs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS)
        && !locs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING)
      )
    if (!mayInventPrimary) return result
  }

  const block = `\n<!-- OFFER_SIGNATURE_START -->\n${buildSignatureHtml(fields)}\n<!-- OFFER_SIGNATURE_END -->\n`

  // Prefer after closing (stamp follows closing line), then after terms, else before footer.
  const closingEnd = result.indexOf(MARKER_TAGS.closing[1])
  if (closingEnd >= 0) {
    const insertAt = closingEnd + MARKER_TAGS.closing[1].length
    return `${result.slice(0, insertAt)}${block}${result.slice(insertAt)}`
  }
  const termsEnd = result.indexOf(MARKER_TAGS.terms[1])
  if (termsEnd >= 0) {
    const insertAt = termsEnd + MARKER_TAGS.terms[1].length
    return `${result.slice(0, insertAt)}${block}${result.slice(insertAt)}`
  }
  const footer = result.search(/<div[^>]*class="[^"]*\bfooter-section\b/i)
  if (footer >= 0) {
    return `${result.slice(0, footer)}${block}${result.slice(footer)}`
  }
  const bodyClose = result.search(/<\/body>/i)
  if (bodyClose >= 0) {
    return `${result.slice(0, bodyClose)}${block}${result.slice(bodyClose)}`
  }
  return `${result}${block}`
}

function hasInlinePageBreaks(html) {
  return PAGE_BREAK_PATTERNS.some(pattern => pattern.test(html))
}

function removePageBreak(html) {
  return removeCanonicalPageBreak(html)
}

function findPageBreakInsertIndex(html) {
  const salaryStart = html.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) return salaryStart

  const sigImgIdx = html.indexOf('{{METADATA.IMAGES.SIGNATURE}}')
  if (sigImgIdx >= 0) {
    const sigTagEnd = html.indexOf('>', sigImgIdx)
    if (sigTagEnd >= 0) {
      const afterSigImg = sigTagEnd + 1
      const tail = html.slice(afterSigImg)
      const salaryAfterSig = tail.search(SALARY_HINT_PATTERN)
      const tableAfterSig = tail.search(/<table[\s>]/i)
      const breakAfterSig = findPageBreakRegion(tail)
      const nextSectionIdx = [salaryAfterSig, tableAfterSig]
        .filter(idx => idx >= 0)
        .sort((a, b) => a - b)[0]
      if (nextSectionIdx != null && (breakAfterSig == null || nextSectionIdx < breakAfterSig.index)) {
        return afterSigImg + nextSectionIdx
      }
      if (breakAfterSig == null) return afterSigImg
    }
  }

  const pageBreak = findPageBreakRegion(html)
  if (pageBreak) return pageBreak.start

  const salaryMatch = html.search(SALARY_HINT_PATTERN)
  if (salaryMatch >= 0) return salaryMatch

  const bodyClose = html.search(/<\/body>/i)
  return bodyClose >= 0 ? bodyClose : html.length
}

function hasSalaryPageBreak(html) {
  if (!html) return false
  if (html.includes(PAGE_BREAK_MARKER)) return true
  return /<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\s*<\/div>\s*<!--\s*OFFER_SALARY_START\s*-->/i.test(html)
}

function upsertPageBreak(html, includePageBreak) {
  // Only manage the salary annexure break — other section breaks share the
  // offer-page-break class and must not suppress or be removed here.
  if (!includePageBreak) {
    return removeCanonicalPageBreak(html)
  }

  if (hasSalaryPageBreak(html)) {
    return repairSectionPageBreakDivs(html)
  }

  const insertAt = findPageBreakInsertIndex(html)
  return `${html.slice(0, insertAt)}${buildPageBreakBlock(true)}${html.slice(insertAt)}`
}

function buildJobResponsibilitiesBlock(fields) {
  // Keep markers even when Quill is empty so layout can inject a single title.
  const content = fields.jobResponsibilities || ''
  return `\n<!-- OFFER_JOB_RESP_START -->\n${content}\n<!-- OFFER_JOB_RESP_END -->\n`
}

function upsertJobResponsibilitiesSection(html, fields, parseResult) {
  const showJobResp = isSectionVisible(fields, 'jobResponsibilities')
  const jobRespContent = sanitizeOfferSectionHtml(fields.jobResponsibilities || '')
  const markedBlock = buildJobResponsibilitiesBlock({
    ...fields,
    jobResponsibilities: jobRespContent,
  })

  if (html.includes(MARKER_TAGS.jobResponsibilities[0])) {
    return replaceBetweenMarkers(html, MARKER_TAGS.jobResponsibilities, jobRespContent)
  }

  const { regions } = parseResult || {}
  if (regions?.jobResponsibilities) {
    if (!showJobResp && !jobRespContent.trim()) {
      return replaceRegion(html, regions.jobResponsibilities, '')
    }
    return replaceRegion(html, regions.jobResponsibilities, markedBlock.trim())
  }

  if (!showJobResp) return html

  // Heuristic replace before insert — avoids duplicate Job Responsibilities titles.
  const reparsed = parseHeuristic(html)
  if (reparsed.regions?.jobResponsibilities) {
    return replaceRegion(html, reparsed.regions.jobResponsibilities, markedBlock.trim())
  }

  const introEnd = html.indexOf(MARKER_TAGS.intro[1])
  if (introEnd >= 0) {
    const insertAt = introEnd + MARKER_TAGS.intro[1].length
    return `${html.slice(0, insertAt)}${markedBlock}${html.slice(insertAt)}`
  }

  const bodyStart = html.indexOf(MARKER_TAGS.body[0])
  if (bodyStart >= 0) {
    return `${html.slice(0, bodyStart)}${markedBlock}${html.slice(bodyStart)}`
  }

  const termsStart = html.indexOf(MARKER_TAGS.terms[0])
  if (termsStart >= 0) {
    return `${html.slice(0, termsStart)}${markedBlock}${html.slice(termsStart)}`
  }

  const salaryStart = html.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) {
    return `${html.slice(0, salaryStart)}${markedBlock}${html.slice(salaryStart)}`
  }

  const insertAt = findContentCloseInsertIndex(html)
  return `${html.slice(0, insertAt)}${markedBlock}${html.slice(insertAt)}`
}

/** Insert index just before `.content` closes (keeps sections inside page-wise preview). */
function findContentCloseInsertIndex(html) {
  if (!html) return -1
  const contentMatch = String(html).match(
    /<div\b[^>]*class\s*=\s*["'][^"']*\bcontent\b[^"']*["'][^>]*>/i,
  )
  if (contentMatch && contentMatch.index != null) {
    const openEnd = contentMatch.index + contentMatch[0].length
    const closeStart = findMatchingDivClose(html, openEnd)
    if (closeStart >= 0) return closeStart
  }
  const footer = String(html).search(/<div[^>]*class="[^"]*\bfooter-section\b/i)
  if (footer >= 0) return footer
  const bodyClose = String(html).search(/<\/body>/i)
  return bodyClose >= 0 ? bodyClose : html.length
}

/**
 * Page-wise preview only keeps children of `.content`. Move marked letter
 * sections that landed fully outside (before open / after close) back inside.
 * Overlapping units are skipped — deleting them shreds the content wrapper.
 * @param {string} html
 * @returns {string}
 */
export function relocateMarkedSectionsIntoContent(html) {
  if (!html) return html
  let result = String(html)
  const contentMatch = result.match(OFFER_CONTENT_WRAPPER_OPEN_RE)
  if (!contentMatch || contentMatch.index == null) return result
  const contentOpenEnd = contentMatch.index + contentMatch[0].length
  const contentClose = findMatchingDivClose(result, contentOpenEnd)
  if (contentClose < 0) return result

  const outside = []
  OFFER_CONTENT_ORDER_KEYS.forEach(key => {
    const found = findSectionUnit(result, key)
    if (!found) return
    // Fully inside `.content` — leave alone.
    if (found.start >= contentOpenEnd && found.end <= contentClose) return
    // Fully before open, or fully after close — safe to move.
    const fullyBefore = found.end <= contentMatch.index
    const fullyAfter = found.start >= contentClose + 6
    if (!fullyBefore && !fullyAfter) return
    outside.push({ key, ...found })
  })
  if (!outside.length) return result

  // Remove orphans from the end so earlier offsets stay valid.
  outside.sort((a, b) => b.start - a.start)
  const removed = []
  for (let i = 0; i < outside.length; i += 1) {
    const unit = outside[i]
    removed.push({ key: unit.key, unit: unit.unit, start: unit.start })
    result = `${result.slice(0, unit.start)}${result.slice(unit.end)}`
  }

  // Restore document order (intro → … → signature).
  removed.sort((a, b) => a.start - b.start)
  const block = `\n${removed.map(r => r.unit).join('\n')}\n`

  const cm = result.match(OFFER_CONTENT_WRAPPER_OPEN_RE)
  if (!cm || cm.index == null) return html
  const openEnd = cm.index + cm[0].length
  const closeAt = findMatchingDivClose(result, openEnd)
  if (closeAt < 0) return html
  return `${result.slice(0, closeAt)}${block}${result.slice(closeAt)}`
}

/**
 * If unbalanced chrome extraction swallowed the letter into header/footer,
 * reject the pull so body content stays in the main flow.
 * Do NOT use a small maxLen — real letterheads embed large base64 images.
 */
function extractSafeChromeBlock(html, className) {
  const pull = extractFirstClassBlock(html, className)
  if (!pull.block) return pull
  const swallowedLetter = /OFFER_(?:BODY|INTRO|TERMS|SALARY)_START|Employment Policies and Provisions|Annexure A\s*[—\-]|Terms and Conditions Of Employment/i.test(pull.block)
  if (swallowedLetter) {
    return { block: '', html: String(html || '') }
  }
  return pull
}

/** Pull every safe chrome block of a class; keep first for cloning, strip rest from flow. */
function extractAllSafeChromeBlocks(html, className) {
  let remaining = String(html || '')
  let firstBlock = ''
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const pull = extractSafeChromeBlock(remaining, className)
    if (!pull.block) break
    if (!firstBlock) firstBlock = pull.block
    remaining = pull.html
  }
  return { block: firstBlock, html: remaining }
}

/** Remove letterhead/footer chrome accidentally left inside a page chunk (force strip). */
function stripEmbeddedOfferChrome(html) {
  let result = String(html || '')
  let guard = 0
  while (guard < 12) {
    guard += 1
    const headerPull = extractFirstClassBlock(result, 'header-section')
    const footerPull = extractFirstClassBlock(headerPull.html, 'footer-section')
    if (!headerPull.block && !footerPull.block) break
    result = footerPull.html
  }
  return result
}

/** Drop print-only spacers that inflate preview pages (PDF still uses them). */
function stripPreviewFlowArtifacts(html) {
  return String(html || '')
    .replace(/<div[^>]*class="[^"]*\boffer-print-chunk-clear\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(/<div[^>]*class="[^"]*\boffer-print-spacer-top\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(/<div[^>]*class="[^"]*\boffer-print-spacer-bottom\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
}

function sanitizePreviewPageChunk(html) {
  return stripPreviewFlowArtifacts(stripEmbeddedOfferChrome(html))
}

/**
 * Restore a `.content` wrapper when chrome/print-flow passes removed it.
 * Page-wise preview only paginates children of `.content`.
 * @param {string} html
 * @returns {string}
 */
export function ensureOfferContentWrapper(html) {
  if (!html || hasContentWrapperMarkup(html)) return html
  const full = String(html)
  const bodyMatch = full.match(/<body([^>]*)>([\s\S]*)<\/body>/i)
  if (!bodyMatch) return full

  let inner = bodyMatch[2]
  const headerPull = extractSafeChromeBlock(inner, 'header-section')
  inner = headerPull.html
  const footerPull = extractSafeChromeBlock(inner, 'footer-section')
  inner = footerPull.html
  const stampPull = extractSafeChromeBlock(inner, 'offer-stamp-every-page')
  inner = stampPull.html
  const watermarkPull = extractFirstClassElement(inner, 'watermark', 'img')
  inner = watermarkPull.html

  const main = String(inner || '').trim()
  if (!main) return full

  const wrapped = [
    headerPull.block || '',
    watermarkPull.block || '',
    `<div class="content">\n${main}\n</div>`,
    stampPull.block || '',
    footerPull.block || '',
  ].filter(Boolean).join('\n')

  return full.replace(/<body[^>]*>[\s\S]*<\/body>/i, `<body${bodyMatch[1]}>\n${wrapped}\n</body>`)
}

/**
 * After a second chrome/layout pass, re-insert Policies / body when markers were dropped.
 * @param {string} html
 * @param {object} fields
 * @returns {string}
 */
export function repairOfferBodyAfterChrome(html, fields = {}) {
  if (!html || !isSectionVisible(fields, 'body')) return html
  const bodyHtml = fields.body || ''
  if (!offerSectionPlainLen(bodyHtml)) return html

  let result = String(html)
  const markedPlain = offerMarkedSectionPlain(result, MARKER_TAGS.body)
  const markedHasPolicies = offerMarkedBodyHasPolicies(result)
  const fieldHasPolicies = /Employment Policies and Provisions/i.test(String(bodyHtml))
  const fieldPlain = String(bodyHtml)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 48)
    .toLowerCase()
  const markedText = String(
    (extractBetweenMarkers(result, MARKER_TAGS.body) || {}).content || result,
  )
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  const fieldAlreadyWritten = fieldPlain.length >= 12 && markedText.includes(fieldPlain)
  const layout = getOfferLayoutFromFields(fields)
  if (markedPlain > 80 && fieldAlreadyWritten && (!fieldHasPolicies || markedHasPolicies)) {
    result = upsertPoliciesPageBreak(result, parseOfferTemplate(result), true, layout)
    return ensureOfferContentWrapper(relocateMarkedSectionsIntoContent(result))
  }

  result = flattenOfferContentPrintFlow(result)
  result = ensureOfferContentWrapper(result)
  result = upsertBodySection(result, bodyHtml, fields)
  result = assertAssembledBodyContent(result, fields, layout, true)
  result = upsertPoliciesPageBreak(result, parseOfferTemplate(result), true, layout)
  result = relocateMarkedSectionsIntoContent(result)
  return ensureOfferContentWrapper(result)
}

function buildTermsBlock(fields) {
  // Keep markers even when Quill is empty so layout can inject a single title.
  const content = fields.termsAndConditions || ''
  return `\n<!-- OFFER_TERMS_START -->\n${content}\n<!-- OFFER_TERMS_END -->\n`
}

function upsertTermsSection(html, fields, parseResult) {
  const showTerms = isSectionVisible(fields, 'terms')
  const termsContent = sanitizeOfferSectionHtml(fields.termsAndConditions || '')
  const markedBlock = buildTermsBlock({ ...fields, termsAndConditions: termsContent })

  if (html.includes(MARKER_TAGS.terms[0])) {
    return replaceBetweenMarkers(html, MARKER_TAGS.terms, termsContent)
  }

  const { regions } = parseResult || {}
  if (regions?.terms) {
    if (!showTerms && !termsContent.trim()) {
      return replaceRegion(html, regions.terms, '')
    }
    return replaceRegion(html, regions.terms, markedBlock.trim())
  }

  if (!showTerms) return html

  // Heuristic replace before insert — avoids duplicate Terms titles.
  const reparsed = parseHeuristic(html)
  if (reparsed.regions?.terms) {
    return replaceRegion(html, reparsed.regions.terms, markedBlock.trim())
  }

  // Prefer default reading order: after body/policies, before closing/signature/salary.
  // Never append after salary — that broke terms pageBreakBefore (salary break
  // sat in the body→terms gap and suppressed the terms break).
  const bodyEnd = html.indexOf(MARKER_TAGS.body[1])
  if (bodyEnd >= 0) {
    const insertAt = bodyEnd + MARKER_TAGS.body[1].length
    return `${html.slice(0, insertAt)}${markedBlock}${html.slice(insertAt)}`
  }

  const closingStart = html.indexOf(MARKER_TAGS.closing[0])
  if (closingStart >= 0) {
    return `${html.slice(0, closingStart)}${markedBlock}${html.slice(closingStart)}`
  }

  const signatureStart = html.indexOf(MARKER_TAGS.signature[0])
  if (signatureStart >= 0) {
    return `${html.slice(0, signatureStart)}${markedBlock}${html.slice(signatureStart)}`
  }

  const salaryStart = html.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) {
    return `${html.slice(0, salaryStart)}${markedBlock}${html.slice(salaryStart)}`
  }

  // Must stay inside `.content` — before </body> is outside and page-wise preview drops it.
  const insertAt = findContentCloseInsertIndex(html)
  return `${html.slice(0, insertAt)}${markedBlock}${html.slice(insertAt)}`
}

/**
 * After the marked annexure is written, drop leftover salary tables / CONFIG
 * tokens / annexure headings that sit outside OFFER_SALARY markers. Preview
 * used to show the editor table plus the original template structure; generate
 * then dropped tokens (no CONFIG.* replacement) so salary vanished.
 */
function stripOrphanSalaryOutsideMarkers(html) {
  if (!html || !html.includes(MARKER_TAGS.salary[0])) return html
  const start = html.indexOf(MARKER_TAGS.salary[0])
  const endMarker = html.indexOf(MARKER_TAGS.salary[1], start)
  if (endMarker < 0) return html
  const markedEnd = endMarker + MARKER_TAGS.salary[1].length
  const marked = html.slice(start, markedEnd)

  const stripChunk = chunk => {
    let result = String(chunk || '')
    result = result.replace(/<table[^>]*class="[^"]*salary-table[^"]*"[^>]*>[\s\S]*?<\/table>/gi, '')
    result = result.replace(
      /<(?:p|h[1-6])[^>]*class="[^"]*salary-structure-title[^"]*"[^>]*>[\s\S]*?<\/(?:p|h[1-6])>/gi,
      '',
    )
    result = result.replace(
      /<(h[1-6]|p)(?:\s[^>]*)?>[\s\S]*?<\/\1>/gi,
      (full) => (matchOfferSectionTitleKey(full) === 'salary' ? '' : full),
    )
    result = result.replace(/\{\{\s*CONFIG\.SALARY_STRUCTURE[^}]*\}\}/gi, '')
    return result
  }

  return `${stripChunk(html.slice(0, start))}${marked}${stripChunk(html.slice(markedEnd))}`
}

function upsertSalarySection(html, fields, parseResult) {
  const showSalary = isSectionVisible(fields, 'salary')
  const raw = fields.salaryStructure || ''
  const salaryContent = String(raw).trim()
    ? raw
    : (showSalary ? defaultSalaryStructureHtml() : '')
  if (!showSalary && !html.includes(MARKER_TAGS.salary[0]) && !(parseResult && parseResult.regions && parseResult.regions.salary)) {
    return html
  }
  const markedBlock = `\n<!-- OFFER_SALARY_START -->\n${salaryContent}\n<!-- OFFER_SALARY_END -->\n`

  let result = html
  if (html.includes(MARKER_TAGS.salary[0])) {
    result = replaceBetweenMarkers(html, MARKER_TAGS.salary, salaryContent)
  } else {
    // Live heuristic only — editor `regions` offsets go stale after show/hide toggles.
    const reparsed = parseHeuristic(html)
    if (reparsed.regions?.salary) {
      result = replaceRegion(html, reparsed.regions.salary, markedBlock.trim())
    } else if (!showSalary) {
      return html
    } else {
      // Inside `.content` only — paginateOfferPreviewDocument keeps content children alone.
      const insertAt = findContentCloseInsertIndex(html)
      result = `${html.slice(0, insertAt)}${markedBlock}${html.slice(insertAt)}`
    }
  }
  return stripOrphanSalaryOutsideMarkers(result)
}

function injectMarkers(html, regions) {
  let result = html
  const ordered = [
    ['salary', MARKER_TAGS.salary],
    ['terms', MARKER_TAGS.terms],
    ['pageBreak', null],
    ['signature', MARKER_TAGS.signature],
    ['closing', MARKER_TAGS.closing],
    ['body', MARKER_TAGS.body],
    ['jobResponsibilities', MARKER_TAGS.jobResponsibilities],
    ['intro', MARKER_TAGS.intro],
  ]

  ordered.forEach(([key, markers]) => {
    const region = regions[key]
    if (!region || key === 'pageBreak') return
    const [startMarker, endMarker] = markers
    const chunk = result.slice(region.start, region.end)
    result = result.slice(0, region.start)
      + startMarker + chunk + endMarker
      + result.slice(region.end)
  })

  return result
}

/** True when Quill-edited sections can be written via marker replacement. */
function hasContentSectionMarkers(html) {
  if (!html) return false
  return html.includes(MARKER_TAGS.intro[0]) || html.includes(MARKER_TAGS.body[0])
}

/**
 * Inject only missing section markers (end→start so earlier offsets stay valid).
 * Production templates often have salary markers but no intro/body markers.
 * Load fills Quill via heuristic fallbacks; without this, save's
 * replaceBetweenMarkers silently no-ops and letter edits never persist.
 */
function injectMissingMarkers(html, regions) {
  if (!html || !regions) return html

  let result = html
  const ordered = [
    ['salary', MARKER_TAGS.salary],
    ['terms', MARKER_TAGS.terms],
    ['signature', MARKER_TAGS.signature],
    ['closing', MARKER_TAGS.closing],
    ['body', MARKER_TAGS.body],
    ['jobResponsibilities', MARKER_TAGS.jobResponsibilities],
    ['intro', MARKER_TAGS.intro],
  ]

  const isSafeInjectionRegion = (chunk, key) => {
    if (!chunk || !String(chunk).trim()) return false
    // Never wrap sibling section markers, signature chrome, or document chrome.
    if (/<!--\s*OFFER_(?:INTRO|BODY|JOB_RESP|TERMS|CLOSING|SIGNATURE|SALARY)_/i.test(chunk)) {
      return false
    }
    if (/class\s*=\s*["'][^"']*\b(?:footer-section|header-section|signature-section)\b/i.test(chunk)) {
      return false
    }
    if (/<\/(?:body|html)\b/i.test(chunk)) return false
    if ((key === 'body' || key === 'intro') && String(chunk).trim().length < 40) return false
    return true
  }

  ordered.forEach(([key, markers]) => {
    const [startMarker, endMarker] = markers
    if (result.includes(startMarker)) return
    const region = regions[key]
    if (!region || region.start == null || region.end == null) return
    if (region.start < 0 || region.end > result.length || region.end < region.start) return
    const chunk = result.slice(region.start, region.end)
    if (!isSafeInjectionRegion(chunk, key)) return
    result = result.slice(0, region.start)
      + startMarker + chunk + endMarker
      + result.slice(region.end)
  })

  return result
}

/**
 * Ensure intro/body (and other editable) markers exist before replacement.
 * @param {string} html
 * @returns {string}
 */
function ensureContentMarkers(html) {
  if (!html) return html
  const hasIntro = html.includes(MARKER_TAGS.intro[0])
  const hasBody = html.includes(MARKER_TAGS.body[0])
  // Intro-only templates used to skip body injection; Save then had no Policies slot.
  if (hasIntro && hasBody) return html
  const reparsed = parseHeuristic(html)
  if (reparsed.mode !== 'structured' || !reparsed.regions) return html
  return injectMissingMarkers(html, reparsed.regions)
}

/**
 * Replace a heuristic section when markers are still missing after ensure.
 * Avoids writing into an already-marked intro/body block.
 */
function replaceHeuristicSection(html, sectionKey, newContent) {
  if (sectionKey === 'intro' && html.includes(MARKER_TAGS.intro[0])) return html
  if (sectionKey === 'body' && html.includes(MARKER_TAGS.body[0])) return html
  const reparsed = parseHeuristic(html)
  const region = reparsed.regions && reparsed.regions[sectionKey]
  if (!region) return html
  // Do not clobber content already wrapped in another section's markers
  const slice = html.slice(region.start, region.end)
  if (sectionKey === 'body' && slice.includes(MARKER_TAGS.intro[0])) return html
  if (sectionKey === 'intro' && slice.includes(MARKER_TAGS.body[0])) return html
  return replaceRegion(html, region, newContent)
}

function upsertIntroSection(html, introContent) {
  const content = introToHtml(introContent)
  if (html.includes(MARKER_TAGS.intro[0])) {
    return replaceBetweenMarkers(html, MARKER_TAGS.intro, content)
  }
  const marked = `<!-- OFFER_INTRO_START -->\n${content}\n<!-- OFFER_INTRO_END -->`
  const reparsed = parseHeuristic(html)
  if (reparsed.regions?.intro) {
    return replaceRegion(html, reparsed.regions.intro, marked)
  }
  const jobRespStart = html.indexOf(MARKER_TAGS.jobResponsibilities[0])
  if (jobRespStart >= 0) {
    return `${html.slice(0, jobRespStart)}${marked}\n${html.slice(jobRespStart)}`
  }
  const bodyStart = html.indexOf(MARKER_TAGS.body[0])
  if (bodyStart >= 0) {
    return `${html.slice(0, bodyStart)}${marked}\n${html.slice(bodyStart)}`
  }
  const salaryStart = html.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) {
    return `${html.slice(0, salaryStart)}${marked}\n${html.slice(salaryStart)}`
  }
  return html
}

function removeMarkedSection(html, markers) {
  const start = html.indexOf(markers[0])
  const end = html.indexOf(markers[1])
  if (start < 0 || end < 0 || end < start) return html
  const after = end + markers[1].length
  return `${html.slice(0, start)}${html.slice(after)}`.replace(/\n{3,}/g, '\n\n')
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function stripPageBreakMarker(html, marker) {
  if (!html || !html.includes(marker)) return html
  const paired = new RegExp(
    `${escapeRegExp(marker)}\\s*<div[^>]*class="[^"]*offer-page-break[^"]*"[^>]*>\\s*<\\/div>`
    + `(?:\\s*<div[^>]*class="[^"]*offer-page-top-clear[^"]*"[^>]*>\\s*(?:&nbsp;|&#160;|\\u00a0|\\s)*<\\/div>)?`,
    'gi',
  )
  let result = html.replace(paired, '')
  result = result.split(marker).join('')
  return result.replace(/\n{3,}/g, '\n\n')
}

/**
 * Omit hidden letter sections (and their section page breaks) from assembled HTML
 * so preview / saved metadata.html / PDF generation exclude them.
 * @param {string} html
 * @param {object} fields
 * @returns {string}
 */
export function applyOfferSectionVisibility(html, fields) {
  if (!html) return html
  let result = html

  if (!isSectionVisible(fields, 'intro')) {
    result = removeMarkedSection(result, MARKER_TAGS.intro)
    result = stripPageBreakMarker(result, INTRO_PAGE_BREAK_MARKER)
  }
  if (!isSectionVisible(fields, 'jobResponsibilities')) {
    result = removeMarkedSection(result, MARKER_TAGS.jobResponsibilities)
    result = stripPageBreakMarker(result, JOB_RESP_PAGE_BREAK_MARKER)
  }
  if (!isSectionVisible(fields, 'body')) {
    result = removeMarkedSection(result, MARKER_TAGS.body)
    result = stripPageBreakMarker(result, POLICIES_PAGE_BREAK_MARKER)
  }
  if (!isSectionVisible(fields, 'terms')) {
    result = removeMarkedSection(result, MARKER_TAGS.terms)
    result = stripPageBreakMarker(result, TERMS_PAGE_BREAK_MARKER)
  }
  if (!isSectionVisible(fields, 'salary')) {
    result = removeMarkedSection(result, MARKER_TAGS.salary)
    result = removeCanonicalPageBreak(result)
  }
  if (!isSectionVisible(fields, 'closing')) {
    // Capture real stamp src BEFORE clearing chrome — fields.signatureSrc is often
    // empty here while HTML still has base64/https from Configure / prior chrome.
    const stampSrcFromHtml = extractOfferSignatureImageSrc(result, fields)
    const markerMatch = String(result).match(OFFER_SIGNATURE_PLACEMENT_MARKER_RE)
    const stampLocs = (markerMatch && markerMatch[1])
      ? normalizeOfferSignatureLocations(markerMatch[1])
      : resolveFieldsSignaturePages(fields)
    const stampSrc = coerceOfferImageSrc(
      (fields && fields.signatureSrc)
      || (fields && fields.images && fields.images.signature)
      || (fields && fields.signature)
      || stampSrcFromHtml
      || '',
    )

    // Closing module OFF = hide closing *text* only. Clear stacked signature chrome,
    // then re-place stamps from Stamp settings (avoids 5× Authorised Signatory).
    result = removeMarkedSection(result, MARKER_TAGS.closing)
    result = stripPageBreakMarker(result, CLOSING_PAGE_BREAK_MARKER)
    result = removeMarkedSection(result, MARKER_TAGS.signature)
    result = stripPageBreakMarker(result, SIGNATURE_PAGE_BREAK_MARKER)
    result = removeDivBlocksByClassName(result, 'signature-section-extra')
    result = removeDivBlocksByClassName(result, 'signature-section')
    result = removeDivBlocksByClassName(result, 'offer-stamp-every-page')

    const sigFields = {
      ...(fields || {}),
      signatureSrc: stampSrc,
      signature: stampSrc,
      images: {
        ...((fields && fields.images) || {}),
        signature: stampSrc,
      },
      offerSignaturePlacement: stampLocs,
      offerSignaturePages: stampLocs,
      _allowSignatureWithoutClosing: true,
    }
    if (Array.isArray(stampLocs) && stampLocs.length > 0) {
      // Only invent a primary end-block when Last page / section anchors need it.
      // withClosing + afterTerms use extras only (needsPrimary false).
      const needsPrimary = stampLocs.includes(OFFER_SIGNATURE_PLACEMENT.LAST_PAGE)
        || stampLocs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_POLICIES)
        || stampLocs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_SALARY)
        || (
          stampLocs.includes(OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS)
          && !stampLocs.includes(OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING)
        )
      if (needsPrimary) {
        result = ensureOfferSignatureBlock(result, {
          ...sigFields,
          _allowSignatureWithoutClosing: true,
        })
      }
      result = applyMultiOfferSignaturePlacements(result, stampLocs, sigFields)
      result = collapseDuplicateEmployeeAcceptance(result)
      // Opening/body/terms often contain leaked AS/HR paragraphs from older chrome
      // (looks like stamp under Transportation / "second topic" even when After
      // Employment Policies is unchecked). Strip those; keep only real stamp blocks.
      result = stripSignatoryLabelsFromMarkedSections(result)
      // Polluted signatoryName / stacked signatory-block lines → 4–5× AS/HR in ONE section.
      result = collapseRepeatedSignatoryLabelsInSignatureSections(result)
    }
    // Whole-doc orphan AS/HR outside any signature-section (unmarked leftovers).
    result = stripSignatoryLabelParagraphsOutsideSignature(result)
    result = collapseRepeatedSignatoryLabelsInSignatureSections(result)
  }

  return result
}

function closingToHtml(closing) {
  const raw = String(closing || '').trim()
  if (!raw) return ''
  if (/<[a-z][\s\S]*>/i.test(raw)) return sanitizeOfferSectionHtml(raw)
  return textToParagraphs(raw)
}

function upsertClosingSection(html, fields) {
  const content = closingToHtml(fields.closing)
  if (html.includes(MARKER_TAGS.closing[0])) {
    return replaceBetweenMarkers(html, MARKER_TAGS.closing, content)
  }
  if (!(fields.closing || '').trim()) return html
  const marked = `\n<!-- OFFER_CLOSING_START -->\n${content}\n<!-- OFFER_CLOSING_END -->\n`
  const signatureStart = html.indexOf(MARKER_TAGS.signature[0])
  if (signatureStart >= 0) {
    return `${html.slice(0, signatureStart)}${marked}${html.slice(signatureStart)}`
  }
  const salaryStart = html.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) {
    return `${html.slice(0, salaryStart)}${marked}${html.slice(salaryStart)}`
  }
  const sigImg = html.indexOf('{{METADATA.IMAGES.SIGNATURE}}')
  if (sigImg >= 0) {
    const tagStart = html.lastIndexOf('<', sigImg)
    const insertAt = tagStart >= 0 ? tagStart : sigImg
    return `${html.slice(0, insertAt)}${marked}${html.slice(insertAt)}`
  }
  return replaceHeuristicSection(html, 'closing', content)
}

function upsertSignatureSection(html, fields) {
  // Keep any real stamp already in HTML when fields.signatureSrc is empty
  // (metadata.images omitted on generate) — otherwise we write TOKEN and wipe it.
  const stampSrc = extractOfferSignatureImageSrc(html, fields || {})
  const fieldsWithStamp = stampSrc
    ? { ...(fields || {}), signatureSrc: stampSrc, signature: stampSrc }
    : (fields || {})
  const content = buildSignatureHtml(fieldsWithStamp)
  const marked = `\n<!-- OFFER_SIGNATURE_START -->\n${content}\n<!-- OFFER_SIGNATURE_END -->\n`

  // Drop orphan signature images outside the marked block — they are now inside
  // buildSignatureHtml so duplicates (and reorder "orphan" blanks) are avoided.
  let result = html
  if (result.includes(MARKER_TAGS.signature[0])) {
    result = replaceBetweenMarkers(result, MARKER_TAGS.signature, content)
  } else {
    const salaryStart = result.indexOf(MARKER_TAGS.salary[0])
    if (salaryStart >= 0) {
      result = `${result.slice(0, salaryStart)}${marked}${result.slice(salaryStart)}`
    } else {
      const closingEnd = result.indexOf(MARKER_TAGS.closing[1])
      if (closingEnd >= 0) {
        const insertAt = closingEnd + MARKER_TAGS.closing[1].length
        result = `${result.slice(0, insertAt)}${marked}${result.slice(insertAt)}`
      } else {
        const bodyClose = result.search(/<\/body>/i)
        const insertAt = bodyClose >= 0 ? bodyClose : result.length
        result = `${result.slice(0, insertAt)}${marked}${result.slice(insertAt)}`
      }
    }
  }

  // Remove duplicate signature <img> tags that sit outside the signature markers.
  const sigBlock = extractBetweenMarkers(result, MARKER_TAGS.signature)
  if (sigBlock) {
    const before = result.slice(0, result.indexOf(MARKER_TAGS.signature[0]))
    const after = result.slice(result.indexOf(MARKER_TAGS.signature[1]) + MARKER_TAGS.signature[1].length)
    const stripOrphanSigImg = (fragment) => fragment.replace(
      /<img[^>]*class\s*=\s*["'][^"']*\bsignature-img\b[^"']*["'][^>]*>\s*/gi,
      '',
    ).replace(
      /<img[^>]*src\s*=\s*["']\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}["'][^>]*>\s*/gi,
      '',
    )
    result = `${stripOrphanSigImg(before)}${result.slice(
      result.indexOf(MARKER_TAGS.signature[0]),
      result.indexOf(MARKER_TAGS.signature[1]) + MARKER_TAGS.signature[1].length,
    )}${stripOrphanSigImg(after)}`
  }

  return result
}

function upsertBodySection(html, bodyContent, fields = null) {
  let rawBody = String(bodyContent || '')
  // Page-break units belong *before* OFFER_BODY_START (layout flag), not inside
  // Quill HTML. Leaving them in the draft made flatten/repair treat Policies as
  // broken or hid the split from page-wise preview.
  rawBody = rawBody.replace(
    /^(?:\s|<!--\s*OFFER_[A-Z_]*PAGE_BREAK\s*-->\s*)*(?:<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>\s*<\/div>\s*(?:<div[^>]*class="[^"]*\boffer-page-top-clear\b[^"]*"[^>]*>\s*(?:&nbsp;|&#160;|\u00a0|\s)*<\/div>\s*)?)+/i,
    '',
  )
  // Strip print-flow / nested-marker pollution — do not wipe the Policies text.
  if (isBrokenOfferSectionHtml(rawBody)) {
    const stripped = stripBrokenOfferSectionChrome(rawBody)
    rawBody = offerSectionPlainLen(stripped) > 0 ? stripped : ''
  }
  let content = sanitizeOfferSectionHtml(rawBody)
  const showBody = !fields || isSectionVisible(fields, 'body')
  if (!content.trim() && showBody) {
    content = `<p class="offer-major-heading">${DEFAULT_SECTION_TITLE_TEXT.body}</p>`
      + '<p>Your compensation and benefits will be as per company policy.</p>'
  }
  let result = html
  if (result.includes(MARKER_TAGS.body[0])) {
    const start = result.indexOf(MARKER_TAGS.body[0])
    const end = result.indexOf(MARKER_TAGS.body[1], start + MARKER_TAGS.body[0].length)
    if (end > start) {
      const span = result.slice(start + MARKER_TAGS.body[0].length, end)
      // Corrupt ensureContentMarkers wraps can enclose signature/closing/footer.
      // Drop the bad body markers and continue with a safe insert path.
      if (
        /<!--\s*OFFER_(?:SIGNATURE|CLOSING|SALARY|TERMS|JOB_RESP|INTRO)_/i.test(span)
        || /class\s*=\s*["'][^"']*\b(?:footer-section|header-section|signature-section)\b/i.test(span)
        || /<\/(?:body|html)\b/i.test(span)
      ) {
        result = `${result.slice(0, start)}${span}${result.slice(end + MARKER_TAGS.body[1].length)}`
      } else {
        return replaceBetweenMarkers(result, MARKER_TAGS.body, content)
      }
    } else {
      // Flatten / unclosed print-flow can drop OFFER_BODY_END. Orphan START
      // made replaceBetweenMarkers no-op — Policies never landed in HTML.
      result = `${result.slice(0, start)}${result.slice(start + MARKER_TAGS.body[0].length)}`
    }
  }
  const marked = `\n<!-- OFFER_BODY_START -->\n${content}\n<!-- OFFER_BODY_END -->\n`
  // Prefer replacing an existing heuristic body region so we do not insert a
  // second copy (duplicate Employment Policies title + content).
  const reparsed = parseHeuristic(result)
  if (reparsed.regions?.body) {
    const regionSlice = result.slice(reparsed.regions.body.start, reparsed.regions.body.end)
    const regionSafe = !/<!--\s*OFFER_(?:SIGNATURE|CLOSING|SALARY|TERMS)_/i.test(regionSlice)
      && !/class\s*=\s*["'][^"']*\b(?:footer-section|signature-section)\b/i.test(regionSlice)
    if (regionSafe) {
      return replaceRegion(result, reparsed.regions.body, marked.trim())
    }
  }
  const jobRespEnd = result.indexOf(MARKER_TAGS.jobResponsibilities[1])
  if (jobRespEnd >= 0) {
    const insertAt = jobRespEnd + MARKER_TAGS.jobResponsibilities[1].length
    return `${result.slice(0, insertAt)}${marked}${result.slice(insertAt)}`
  }
  const introEnd = result.indexOf(MARKER_TAGS.intro[1])
  if (introEnd >= 0) {
    const insertAt = introEnd + MARKER_TAGS.intro[1].length
    return `${result.slice(0, insertAt)}${marked}${result.slice(insertAt)}`
  }
  const salaryStart = result.indexOf(MARKER_TAGS.salary[0])
  if (salaryStart >= 0) {
    return `${result.slice(0, salaryStart)}${marked}${result.slice(salaryStart)}`
  }
  const termsStart = result.indexOf(MARKER_TAGS.terms[0])
  if (termsStart >= 0) {
    return `${result.slice(0, termsStart)}${marked}${result.slice(termsStart)}`
  }
  // Prefer inserting before closing/signature so default policies never land inside stamp.
  const closingStart = result.indexOf(MARKER_TAGS.closing[0])
  if (closingStart >= 0) {
    return `${result.slice(0, closingStart)}${marked}${result.slice(closingStart)}`
  }
  const signatureStart = result.indexOf(MARKER_TAGS.signature[0])
  if (signatureStart >= 0) {
    return `${result.slice(0, signatureStart)}${marked}${result.slice(signatureStart)}`
  }
  if (!content.trim()) return result
  const insertAt = findContentCloseInsertIndex(result)
  return `${result.slice(0, insertAt)}${marked}${result.slice(insertAt)}`
}

/**
 * @param {string} html - current full HTML template
 * @param {object} fields - editable content fields
 * @param {{ mode: string, regions: object|null }} parseResult
 * @returns {string}
 */
function chromeOptionsFromFields(fields = {}) {
  const signatureSrc = extractOfferSignatureImageSrc('', {
    signatureSrc: fields.signatureSrc,
    signature: fields.signature,
    images: fields.images,
  })
  return {
    offerSignaturePlacement: resolveFieldsSignaturePages(fields),
    offerEmployeeSignPlacement: resolveFieldsEmployeeSignConfig(fields),
    signatoryName: fields.signatoryName || '',
    signatoryDesignation: fields.signatoryDesignation || '',
    signatureSrc,
    signature: signatureSrc,
    images: fields.images,
  }
}

/**
 * Build editable fields from persisted offer metadata (mirrors OfferConfigure.initTemplateEditor).
 * @param {object} metadata
 * @param {object} parsedFields
 * @param {string} [html]
 */
export function buildOfferFieldsFromMetadata(metadata = {}, parsedFields = {}, html = '') {
  const meta = metadata && typeof metadata === 'object' ? metadata : {}
  const placement = resolveOfferSignaturePagesFromMetadata(meta)
  const employeeSign = resolveOfferEmployeeSignConfigFromMetadata(meta)
  const images = extractOfferImagesFromMetadata(meta)
  // Prefer an already-inlined stamp in saved HTML over empty metadata.images —
  // upsertSignatureSection used to rebuild with TOKEN and wipe a working src.
  const signatureSrc = extractOfferSignatureImageSrc(html, {
    signatureSrc: images.signature,
    signature: images.signature,
    images,
  }) || images.signature || ''
  const imagesWithStamp = signatureSrc
    ? { ...images, signature: signatureSrc }
    : images
  const fromMarker = parseOfferPageStartsMarker(html)
  const layoutSource = meta.offerLayout
    ? 'saved'
    : (fromMarker ? 'marker' : 'default')
  const layout = applyOfferSignaturePlacementToLayout(
    normalizeOfferLayout(meta.offerLayout || fromMarker),
    placement,
  )
  layout._source = layoutSource
  const fields = fillStaleSectionDraftsForPdf(
    applyOfferSectionDrafts(
      {
        ...parsedFields,
        offerSections: normalizeOfferSections(meta.offerSections),
        offerSectionOrder: normalizeOfferSectionOrder(meta.offerSectionOrder),
        offerLayout: layout,
        _offerLayoutSource: layoutSource,
        offerSignaturePlacement: placement,
        offerSignaturePages: placement,
        offerEmployeeSignPlacement: employeeSign,
        offerEmployeeSignConfig: employeeSign,
        images: imagesWithStamp,
        signatureSrc,
        signature: signatureSrc,
      },
      meta.offerSectionDrafts,
    ),
    meta.offerSectionDrafts,
    html,
  )
  if (fields.includePageBreak === false && fields.offerLayout && fields.offerLayout.sections) {
    fields.offerLayout.sections.salary = {
      ...fields.offerLayout.sections.salary,
      pageBreakBefore: false,
    }
  }
  fields._offerLayoutSource = layoutSource
  if (fields.offerLayout) fields.offerLayout._source = layoutSource
  return fields
}

/**
 * Sync saved HTML with Offer Configure section drafts/order before PDF generation.
 * Preview/save uses assembleOfferTemplate; PDF path previously skipped it and lost body/terms.
 * @param {string} html
 * @param {object} [metadata]
 * @returns {string}
 */
export function prepareOfferHtmlForPdf(html, metadata = {}) {
  const source = String(html || '')
  if (!source.trim()) return source
  const flatSource = /offer-print-flow/i.test(source) ? flattenOfferContentPrintFlow(source) : source
  const parsed = parseOfferTemplate(flatSource)
  const fields = fillStaleSectionDrafts(
    buildOfferFieldsFromMetadata(metadata, parsed.fields || {}, flatSource),
    metadata.offerSectionDrafts,
    flatSource,
  )
  const richest = resolveRichestSalaryStructureHtml(fields, metadata, flatSource)
  if (richest && richest.html) {
    fields.salaryStructure = richest.html
  }
  // Same assemble path as Offer Template live preview so Starts: New page
  // markers and every salary structure survive Client Dashboard generate.
  let assembled = assembleOfferTemplate(flatSource, fields, parsed)
  // Safeguard: Policies ON + draft content but marked body still empty/missing title.
  assembled = repairOfferBodyForPdfFromDraft(assembled, fields, metadata)
  assembled = repairOfferSalaryForPdfFromDraft(assembled, fields, metadata)
  assembled = relocateMarkedSectionsIntoContent(ensureOfferContentWrapper(assembled))
  assembled = ensureOfferPrintFlowSpacers(assembled)
  return hardenOfferPdfForcedBreaks(assembled)
}

export function assembleOfferTemplate(html, fields, parseResult) {
  const chromeOpts = chromeOptionsFromFields(fields)

  if (!html) {
    return finalizeAssembledOfferHtml(
      buildDefaultShell(fields),
      fields,
      chromeOpts,
      getOfferLayoutFromFields(fields),
    )
  }

  if (parseResult.mode === 'raw') {
    return applyOfferLayoutToHtml(
      ensureOfferLetterChrome(html, chromeOpts),
      getOfferLayoutFromFields(fields),
    )
  }

  const layout = getOfferLayoutFromFields(fields)
  const showSalary = isSectionVisible(fields, 'salary')
  const salaryPageBreak = showSalary && resolveSectionPageBreakEnabled(
    layout,
    'salary',
    fields.includePageBreak !== false,
  )

  // Flatten print-flow tables before marker upsert/reorder — moving marked
  // sections while they sit inside <table class="offer-print-flow"> shreds
  // div balance and dumps Salary/Terms after </body> (live preview then drops them).
  let result = flattenOfferContentPrintFlow(ensureContentMarkers(html))
  const showIntro = isSectionVisible(fields, 'intro')
  const showJobResp = isSectionVisible(fields, 'jobResponsibilities')
  const showBody = isSectionVisible(fields, 'body')
  const showTerms = isSectionVisible(fields, 'terms')
  const showClosing = isSectionVisible(fields, 'closing')

  if (hasMarkers(result) || hasContentSectionMarkers(result)) {
    result = upsertIntroSection(result, fields.intro)
    result = upsertJobResponsibilitiesSection(result, fields, parseResult)
    result = upsertBodySection(result, fields.body, fields)
    result = upsertClosingSection(result, fields)
    result = upsertSignatureSection(result, fields)
    result = upsertSalarySection(result, fields, parseResult)
    result = upsertTermsSection(result, fields, parseResult)
    const sectionParseResult = (() => {
      const reparsed = parseHeuristic(result)
      if (reparsed.regions) return { ...parseResult, regions: reparsed.regions }
      return parseResult
    })()
    result = upsertIntroPageBreak(result, showIntro, sectionParseResult, layout)
    result = upsertJobResponsibilitiesPageBreak(
      result,
      showJobResp,
      sectionParseResult,
      layout,
    )
    result = upsertPoliciesPageBreak(result, sectionParseResult, showBody, layout)
    result = upsertTermsPageBreak(
      result,
      showTerms,
      sectionParseResult,
      layout,
    )
    result = upsertClosingPageBreak(result, showClosing, sectionParseResult, layout)
    result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
    result = upsertPageBreak(result, salaryPageBreak)
    result = applyOfferSectionOrder(result, fields.offerSectionOrder)
    // Re-assert section breaks AFTER reorder — policies break was lost when
    // closing/signature moved after terms (PDF 49 skipped Employment Policies).
    const reparseAfterOrder = (() => {
      const reparsed = parseHeuristic(result)
      if (reparsed.regions) return { ...parseResult, regions: reparsed.regions }
      return parseResult
    })()
    result = upsertPoliciesPageBreak(result, reparseAfterOrder, showBody, layout)
    // Re-assert remaining breaks after salary upsert — removeCanonicalPageBreak
    // must never wipe terms/signature breaks (salary-only removal).
    result = upsertTermsPageBreak(
      result,
      showTerms,
      sectionParseResult,
      layout,
    )
    result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
    result = upsertPageBreak(result, salaryPageBreak)
    result = repairSectionPageBreakDivs(result)
    result = assertAssembledBodyContent(result, fields, layout, showBody)
    return finalizeAssembledOfferHtml(result, fields, chromeOpts, layout)
  }

  const { regions } = parseResult
  if (!regions) {
    // injectMarkersAndAssemble → assembleOfferTemplate (already finalized).
    return injectMarkersAndAssemble(result, fields)
  }

  if (regions.signature) {
    result = replaceRegion(result, regions.signature, buildSignatureHtml(fields))
  }
  if (regions.closing) {
    result = replaceRegion(result, regions.closing, closingToHtml(fields.closing))
  }
  if (regions.terms) {
    result = replaceRegion(result, regions.terms, sanitizeOfferSectionHtml(fields.termsAndConditions || ''))
  }
  if (regions.body) {
    result = replaceRegion(result, regions.body, sanitizeOfferSectionHtml(fields.body || ''))
  }
  if (regions.jobResponsibilities) {
    result = replaceRegion(
      result,
      regions.jobResponsibilities,
      sanitizeOfferSectionHtml(fields.jobResponsibilities || ''),
    )
  }
  if (regions.intro) {
    result = replaceRegion(result, regions.intro, introToHtml(fields.intro))
  }

  result = upsertSalarySection(result, fields, parseResult)
  result = upsertJobResponsibilitiesSection(result, fields, parseResult)
  result = upsertTermsSection(result, fields, parseResult)
  result = upsertClosingSection(result, fields)
  result = upsertSignatureSection(result, fields)

  let sectionParseResult = parseResult
  if (!hasMarkers(result)) {
    const reparsedForSections = parseHeuristic(result)
    if (reparsedForSections.regions) {
      sectionParseResult = { ...parseResult, regions: reparsedForSections.regions }
    }
  }
  result = upsertIntroPageBreak(result, showIntro, sectionParseResult, layout)
  result = upsertJobResponsibilitiesPageBreak(
    result,
    showJobResp,
    sectionParseResult,
    layout,
  )
  result = upsertPoliciesPageBreak(result, sectionParseResult, showBody, layout)
  result = upsertTermsPageBreak(
    result,
    showTerms,
    sectionParseResult,
    layout,
  )
  result = upsertClosingPageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertPageBreak(result, salaryPageBreak)

  if (!hasMarkers(result)) {
    const reparsed = parseHeuristic(result)
    if (reparsed.regions) {
      result = injectMarkers(result, reparsed.regions)
    }
  }

  result = applyOfferSectionOrder(result, fields.offerSectionOrder)
  result = upsertTermsPageBreak(
    result,
    showTerms,
    sectionParseResult,
    layout,
  )
  result = upsertSignaturePageBreak(result, showClosing, sectionParseResult, layout)
  result = upsertPageBreak(result, salaryPageBreak)
  result = repairSectionPageBreakDivs(result)

  return finalizeAssembledOfferHtml(result, fields, chromeOpts, layout)
}

/**
 * Chrome first (print-flow / stamps), then strip OFF *content* sections.
 * Closing OFF removes closing text only — stamp blocks stay when Stamp settings
 * / signature image are present (see applyOfferSectionVisibility).
 */
function finalizeAssembledOfferHtml(html, fields, chromeOpts, layout) {
  let result = ensureOfferLetterChrome(html || '', chromeOpts)
  result = applyOfferSectionVisibility(result, fields)
  // Content sections can still hold leaked AS/HR from older saves.
  result = stripSignatoryLabelsFromMarkedSections(result)
  result = stripSignatoryLabelParagraphsOutsideSignature(result)
  result = collapseRepeatedSignatoryLabelsInSignatureSections(result)
  result = stripEmptyOfferPrintFlowChunks(result)
  result = applyOfferLayoutToHtml(result, layout)
  result = upsertOfferPageStartsMarker(result, layout)
  // Chrome / layout / stale markers can leave Policies outside `.content`;
  // page-wise preview only keeps content children.
  return relocateMarkedSectionsIntoContent(result)
}

function injectMarkersAndAssemble(html, fields) {
  const reparsed = parseHeuristic(html)
  if (reparsed.mode !== 'structured' || !reparsed.regions) {
    return html
  }
  let result = injectMarkers(html, reparsed.regions)
  return assembleOfferTemplate(result, fields, { mode: 'structured', regions: reparsed.regions })
}

function offerDefaultShellStyles() {
  return `
    ${offerPageMarginRule()}
    * { box-sizing: border-box; }
    ${offerBodyTypographyCss()}
    ${offerHeaderSectionCss()}
    ${offerHeaderImageCss()}
    ${offerHeaderImageImgCss()}
    ${offerCompanyInfoCss()}
    ${offerCompanyLogoSectionCss()}
    ${offerCompanyLogoCss()}
    ${offerCompanyNameCss()}
    ${offerFooterSectionCss()}
    ${offerFooterLineCss()}
    ${offerContentCss()}
    ${OFFER_LETTER_TITLE_CSS}
    ${offerGreetingBlockCss()}
    ${WATERMARK_CSS}
    ${offerContentFlowCss()}
    table { border-collapse: collapse; width: 100%; margin: 12px 0; }
    th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
    th { background: #f5f5f5; }
    table.salary-table th, table.salary-table td { text-align: right; }
    table.salary-table th:first-child, table.salary-table td:first-child { text-align: left; }
    ${offerSalaryTableCompactCss()}
    ${offerHeaderDedupeCss()}
    tr.table-highlight td { background-color: #fff8e1; font-weight: 600; }
    tr.ctc-highlight td { background-color: #e8f4fd; font-weight: 600; }
    .signature-img, .stamp-img, img.company-stamp, img.offer-stamp {
      max-height: 70px; margin: 8px 0 4px; display: block !important;
      opacity: 1 !important; visibility: visible !important;
      -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
    }
    .signature-section .signatory-block { margin: 0.25em 0 0; text-align: left !important; }
    .employee-acceptance-row { width: 100%; border-collapse: collapse !important; table-layout: fixed; }
    .employee-acceptance-row td { border: 0 !important; padding: 0.35em 16px 0.35em 0 !important; width: 50%; text-align: left !important; }
    .offer-page-break { page-break-before: always !important; break-before: page !important; height: 0; margin: 0; padding: 0; }
    .offer-page-break-unit { page-break-before: auto !important; break-before: auto !important; height: 0 !important; }
    table.offer-print-flow-after-break { page-break-before: always !important; break-before: page !important; }
    .offer-page-break:has(+ table.offer-print-flow-after-break),
    .offer-page-break:has(+ table.offer-print-flow),
    .offer-page-break:has(+ .offer-pdf-force-break) { page-break-before: auto !important; break-before: auto !important; }
    .offer-pdf-force-break { display:none !important; page-break-before:auto !important; break-before:auto !important; height:0 !important; }`
}

function buildJoiningDateParagraphHtml() {
  return '<p>Your date of joining will be <strong>{joiningDate}</strong>. You will report to <strong>{reportingManager}</strong> at <strong>{locations}</strong> for the role of <strong>{jobTitle}</strong>.</p>'
}

function buildOrderedShellContentBlocks(fields) {
  const includePageBreak = fields.includePageBreak !== false
  const showIntro = isSectionVisible(fields, 'intro')
  const showJobResp = isSectionVisible(fields, 'jobResponsibilities')
  const showBody = isSectionVisible(fields, 'body')
  const showTerms = isSectionVisible(fields, 'terms')
  const showClosing = isSectionVisible(fields, 'closing')
  const order = normalizeOfferSectionOrder(fields.offerSectionOrder)
  const layout = getOfferLayoutFromFields(fields)

  const builders = {
    intro: () => {
      if (!showIntro) return ''
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'intro', true)
        ? buildIntroPageBreakBlock()
        : ''
      return `${pageBreak}<!-- OFFER_INTRO_START -->
  ${introToHtml(fields.intro || 'We are pleased to offer you the position detailed below. Please review the terms and conditions carefully.')}
  <!-- OFFER_INTRO_END -->`
    },
    jobResponsibilities: () => {
      if (!showJobResp) return ''
      const cleaned = sanitizeOfferSectionHtml(fields.jobResponsibilities || '')
      const hasContent = !!cleaned.trim()
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'jobResponsibilities', hasContent)
        ? buildJobRespPageBreakBlock()
        : ''
      return `${pageBreak}${buildJobResponsibilitiesBlock({
        ...fields,
        jobResponsibilities: cleaned,
      })}`
    },
    body: () => {
      if (!showBody) return ''
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'body', true)
        ? buildPoliciesPageBreakBlock()
        : ''
      const bodyHtml = sanitizeOfferSectionHtml(
        fields.body || '<p>Your compensation and benefits will be as per company policy. Detailed salary structure and terms are included on the following page.</p>',
      )
      return `${pageBreak}<!-- OFFER_BODY_START -->
  ${bodyHtml}
  <!-- OFFER_BODY_END -->`
    },
    terms: () => {
      if (!showTerms) return ''
      const hasContent = !!(fields.termsAndConditions || '').trim()
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'terms', hasContent)
        ? buildTermsPageBreakBlock()
        : ''
      return `${pageBreak}${buildTermsBlock({
        ...fields,
        termsAndConditions: sanitizeOfferSectionHtml(fields.termsAndConditions || ''),
      })}`
    },
    closing: () => {
      if (!showClosing) return ''
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'closing', true)
        ? buildClosingPageBreakBlock()
        : ''
      return `${pageBreak}<!-- OFFER_CLOSING_START -->
  ${textToParagraphs(fields.closing || 'We look forward to welcoming you to the team.')}
  <!-- OFFER_CLOSING_END -->`
    },
    signature: () => {
      if (!showClosing) return ''
      const pageBreak = resolveSectionPageBreakEnabled(layout, 'signature', true)
        ? buildSignaturePageBreakBlock()
        : ''
      return `${pageBreak}<!-- OFFER_SIGNATURE_START -->
  ${buildSignatureHtml(fields)}
  <!-- OFFER_SIGNATURE_END -->`
    },
    salary: () => {
      if (!isSectionVisible(fields, 'salary')) return ''
      const pageBreak = includePageBreak ? buildPageBreakBlock(true) : ''
      return `${pageBreak}${buildSalaryBlock(fields)}`
    },
  }

  const blocks = []
  let joiningPlaced = false
  const placeJoining = () => {
    if (joiningPlaced) return
    blocks.push(buildJoiningDateParagraphHtml())
    joiningPlaced = true
  }

  order.forEach(key => {
    // If body is hidden, still keep joining with the letter (before Annexure/Terms).
    if (!joiningPlaced && (key === 'salary' || key === 'terms' || key === 'closing' || key === 'signature')) {
      placeJoining()
    }
    const block = builders[key] ? builders[key]() : ''
    if (block) blocks.push(block)
    // Normal path: joining/reporting line ends the offer body, before Annexure break.
    if (key === 'body') placeJoining()
  })

  if (!joiningPlaced) placeJoining()

  return blocks.join('\n')
}

export function buildDefaultShell(fields = emptyTemplateFields()) {
  const contentBlocks = buildOrderedShellContentBlocks(fields)

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>OFFER LETTER</title>
  <style>${offerDefaultShellStyles()}
  </style>
</head>
<body>
  ${DEFAULT_HEADER_HTML}
  <img src="{{METADATA.IMAGES.WATERMARK}}" class="watermark" alt="" aria-hidden="true" width="500" height="500" />
  <div class="content">
  ${OFFER_LETTER_TITLE_HTML}
  ${buildOfferGreetingBlockHtml()}
  ${contentBlocks}
  </div>
  ${DEFAULT_FOOTER_HTML}
</body>
</html>`
}

/**
 * CSS injected into preview iframe so salary tables, highlights, and page breaks render correctly.
 * Screen mode uses discrete A4-like page cards (see paginateOfferPreviewDocument).
 */
const PREVIEW_BASE_STYLES = `
  *, *::before, *::after { box-sizing: border-box; }
  html { background: #e8eaed; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10pt;
    line-height: 1.4;
    color: #333;
    margin: 0;
    padding: 0;
    background: #e8eaed;
  }
  body.offer-preview-paged {
    padding: 10px 8px 18px;
  }
  .offer-preview-desk { width: 100%; }
  .offer-preview-page {
    position: relative;
    width: 100%;
    max-width: 210mm;
    min-height: 297mm;
    margin: 0 auto 14px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .offer-preview-page:last-child { margin-bottom: 0; }
  .offer-preview-page-num {
    position: absolute;
    top: 8px;
    right: 10px;
    z-index: 6;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9aa0a6;
    pointer-events: none;
  }
  .offer-preview-page-body {
    flex: 1 1 auto;
    padding: 0 22px 12px;
    position: relative;
    z-index: 1;
  }
  .offer-preview-page:not([data-page="1"]) .offer-preview-page-body {
    padding-top: 16px;
  }
  .offer-preview-page:not([data-page="1"]) .offer-major-heading {
    margin-top: 0 !important;
  }
  .offer-preview-page .header-section {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    width: 100% !important;
    margin: 0 !important;
    z-index: 2;
  }
  .offer-preview-page .footer-section {
    position: relative !important;
    bottom: auto !important;
    left: auto !important;
    width: 100% !important;
    margin-top: auto;
    padding: 8px 22px 10px;
    z-index: 2;
  }
  /* Letterhead is cloned per page — hide print-flow spacer rows used for fixed chrome. */
  .offer-preview-page .offer-print-flow > thead,
  .offer-preview-page .offer-print-flow > tfoot { display: none !important; }
  .offer-preview-page .offer-print-spacer-top,
  .offer-preview-page .offer-print-spacer-bottom { display: none !important; height: 0 !important; }
  .offer-preview-page .offer-print-flow { margin: 0; }
  .offer-preview-page .offer-print-flow > tbody > tr > td { padding: 0; border: 0 !important; }
  .offer-preview-page .watermark {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    max-width: 55%;
    opacity: 0.04;
    z-index: 0;
    pointer-events: none;
  }
  .offer-preview-page .offer-stamp-every-page {
    position: absolute !important;
    right: 22px !important;
    bottom: 52px !important;
    z-index: 4;
    width: 140px;
    text-align: right;
    pointer-events: none;
  }
  h1:not(.offer-letter-title):not(.offer-major-heading), h2:not(.offer-letter-title):not(.offer-major-heading), h3:not(.offer-letter-title):not(.offer-major-heading), h4:not(.offer-letter-title):not(.offer-major-heading), h5:not(.offer-letter-title):not(.offer-major-heading), h6:not(.offer-letter-title):not(.offer-major-heading) { margin: 1em 0 0.5em; color: #222; text-align: left; }
  p:not(.offer-major-heading) { margin: 0 0 0.7em; text-align: left; }
  p.offer-spacer { margin: 0; padding: 0; min-height: 0.9em; line-height: 0.9em; height: 0.9em; overflow: hidden; }
  p:not(.offer-major-heading):has(> strong:only-child), p:not(.offer-major-heading):has(> b:only-child) { text-align: left; }
  .offer-major-heading { font-weight: bold; }
  .offer-keep-together { page-break-inside: auto; break-inside: auto; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; font-size: 10pt; }
  th { background: #f5f5f5; font-weight: 600; }
  table.salary-table th, table.salary-table td { text-align: right; }
  table.salary-table th:first-child, table.salary-table td:first-child { text-align: left; }
  ${offerSalaryTableCompactCss()}
  ${offerHeaderDedupeCss()}
  tr.table-highlight td, tr.table-highlight th { background-color: #fff8e1; font-weight: 600; }
  tr.ctc-highlight td, tr.ctc-highlight th { background-color: #e8f4fd; font-weight: 600; }
  .header-img { max-width: 100%; height: auto; margin-bottom: 12px; display: block; }
  .header-section { position: relative !important; top: auto !important; left: auto !important; width: 100%; z-index: 1; }
  .header-image { width: 100% !important; margin: 0 !important; line-height: 0; font-size: 0; overflow: visible; }
  .header-image img { width: 100%; max-width: 100%; height: auto; display: block; margin: 0; padding: 0; border: 0; }
  .company-info { width: auto; padding: 8px 22px 6px; background: white; margin: 0; }
  .footer-section { position: relative !important; bottom: auto !important; font-size: 8px; color: #6B6C70; margin-top: 20px; width: 100%; text-align: center; padding: 8px 0; }
  .footer-line { display: block; margin: 0; }
  .footer-company { font-weight: bold; margin-bottom: 2px; }
  .logo, .company-logo { max-height: 50px; margin-bottom: 8px; }
  .offer-greeting-block { margin: 0 0 8px; width: 100%; }
  .offer-greeting-row { width: 100%; border-collapse: collapse; border-spacing: 0; table-layout: fixed; }
  .offer-greeting-row td { border: 0 !important; padding: 0 !important; vertical-align: baseline; background: transparent !important; }
  .offer-salutation-cell { text-align: left !important; width: 62%; }
  .offer-date-cell { text-align: right !important; width: 38%; white-space: nowrap; }
  .offer-greeting-block p { margin: 0; }
  .offer-greeting-block .offer-salutation-line { text-align: left !important; }
  .offer-greeting-block .offer-date-line { text-align: right !important; margin: 0; }
  .signature-img, .stamp-img, img.company-stamp, img.offer-stamp {
    max-height: 70px; margin: 8px 0 4px; display: block !important;
    opacity: 1 !important; visibility: visible !important;
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
  }
  .signature-section { margin-top: 24px; text-align: left !important; }
  .signature-section .signatory-block { margin: 0.25em 0 0; text-align: left !important; }
  .employee-acceptance { margin-top: 14px; }
  .employee-acceptance-row { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .employee-acceptance-row td { border: 0 !important; padding: 0.35em 16px 0.35em 0 !important; width: 50%; text-align: left !important; }
  .offer-page-break, .offer-preview-page-break { display: none !important; height: 0 !important; margin: 0 !important; padding: 0 !important; border: 0 !important; }
  img { max-width: 100%; height: auto; }
  strong, b { font-weight: 600; }
`

const PREVIEW_PAGE_BREAK_VISUAL = '<div class="offer-preview-page-break" aria-hidden="true"></div>'
const PREVIEW_PAGE_BREAK_SPLIT_RE = /<div[^>]*class="[^"]*\boffer-preview-page-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/i

/**
 * Pull the first block matching a class name out of HTML.
 * @returns {{ block: string, html: string }}
 */
function extractFirstClassBlock(html, className, tagName = 'div') {
  const source = String(html || '')
  const openRe = new RegExp(
    `<${tagName}\\b[^>]*class\\s*=\\s*["'][^"']*\\b${escapeRegExp(className)}\\b[^"']*["'][^>]*>`,
    'i',
  )
  const match = source.match(openRe)
  if (!match || match.index == null) return { block: '', html: source }
  const openStart = match.index
  const openEnd = openStart + match[0].length
  const closeStart = tagName.toLowerCase() === 'div'
    ? findMatchingDivClose(source, openEnd)
    : source.toLowerCase().indexOf(`</${tagName.toLowerCase()}>`, openEnd)
  if (closeStart < 0) return { block: '', html: source }
  const closeEnd = closeStart + (`</${tagName}>`).length
  let end = closeEnd
  while (end < source.length && /\s/.test(source[end])) end += 1
  return {
    block: source.slice(openStart, closeEnd),
    html: `${source.slice(0, openStart)}${source.slice(end)}`,
  }
}

/** Pull first matching element (self-closing or with close tag) by class. */
function extractFirstClassElement(html, className, tagName = 'img') {
  const source = String(html || '')
  const re = new RegExp(
    `<${tagName}\\b[^>]*class\\s*=\\s*["'][^"']*\\b${escapeRegExp(className)}\\b[^"']*["'][^>]*(?:\\/\\s*>|>\\s*<\\/${tagName}>)`,
    'i',
  )
  const match = source.match(re)
  if (!match || match.index == null) return { block: '', html: source }
  let end = match.index + match[0].length
  while (end < source.length && /\s/.test(source[end])) end += 1
  return {
    block: match[0],
    html: `${source.slice(0, match.index)}${source.slice(end)}`,
  }
}

/**
 * Split preview body at forced page-break markers into discrete A4-like page cards.
 * Clones letterhead / footer / watermark / every-page stamp onto each page so
 * First / Last / Every page stamp placements are visually meaningful.
 */
function paginateOfferPreviewDocument(html) {
  if (!html) return html
  const full = String(html)
  const bodyMatch = full.match(/<body([^>]*)>([\s\S]*)<\/body>/i)
  if (!bodyMatch) {
    // Unclosed print-flow / chrome can drop </body> on the first assemble.
    // Still wrap so the live preview shows page cards instead of a gray box.
    const wrapped = wrapPreviewDocument(
      extractBodyContent(full),
      extractStylesFromHtml(full),
    )
    if (wrapped === full) return full
    return paginateOfferPreviewDocument(wrapped)
  }

  let bodyInner = bodyMatch[2]
  // Reject chrome pulls that swallowed Policies / annexure (unbalanced divs).
  const headerPull = extractAllSafeChromeBlocks(bodyInner, 'header-section')
  bodyInner = headerPull.html
  const footerPull = extractAllSafeChromeBlocks(bodyInner, 'footer-section')
  bodyInner = footerPull.html
  const stampPull = extractSafeChromeBlock(bodyInner, 'offer-stamp-every-page')
  bodyInner = stampPull.html
  const watermarkPull = extractFirstClassElement(bodyInner, 'watermark', 'img')
  bodyInner = watermarkPull.html

  let contentOpen = ''
  let contentClose = ''
  // Strip leading comments / whitespace so placement markers don't block .content detection.
  let main = bodyInner
    .replace(/^(?:\s*|<!--[\s\S]*?-->)*/i, '')
    .trim()
  const contentMatch = main.match(/<div\b[^>]*class\s*=\s*["'][^"']*\bcontent\b[^"']*["'][^>]*>/i)
  if (contentMatch && contentMatch.index != null) {
    contentOpen = contentMatch[0]
    const openEnd = contentMatch.index + contentMatch[0].length
    const closeStart = findMatchingDivClose(main, openEnd)
    if (closeStart >= 0) {
      contentClose = '</div>'
      main = main.slice(openEnd, closeStart).trim()
    }
  }

  const chunks = main
    .split(PREVIEW_PAGE_BREAK_SPLIT_RE)
    .map(chunk => sanitizePreviewPageChunk(chunk.trim()))
    .filter(chunk => chunk.replace(/<!--[\s\S]*?-->/g, '').trim().length > 0)

  const pages = (chunks.length ? chunks : [sanitizePreviewPageChunk(main) || '&nbsp;']).map((chunk, idx) => {
    const pageNo = idx + 1
    const bodyHtml = `${contentOpen}${chunk}${contentClose}`
    return `<div class="offer-preview-page" data-page="${pageNo}">
  <div class="offer-preview-page-num">Page ${pageNo}</div>
  ${headerPull.block || ''}
  ${watermarkPull.block || ''}
  <div class="offer-preview-page-body">
  ${bodyHtml}
  </div>
  ${stampPull.block || ''}
  ${footerPull.block || ''}
</div>`
  })

  const desk = `<div class="offer-preview-desk">\n${pages.join('\n')}\n</div>`
  let bodyAttrs = bodyMatch[1] || ''
  if (!/\boffer-preview-paged\b/i.test(bodyAttrs)) {
    if (/class\s*=\s*["'][^"']*["']/i.test(bodyAttrs)) {
      bodyAttrs = bodyAttrs.replace(
        /class\s*=\s*(["'])([^"']*)\1/i,
        (_, q, cls) => `class=${q}${cls} offer-preview-paged${q}`,
      )
    } else {
      bodyAttrs = `${bodyAttrs} class="offer-preview-paged"`
    }
  }
  return full.replace(/<body[^>]*>[\s\S]*<\/body>/i, `<body${bodyAttrs}>\n${desk}\n</body>`)
}

function isFullHtmlDocument(html) {
  return /<!DOCTYPE\s+html/i.test(html) || /<html[\s>]/i.test(html)
}

function extractStylesFromHtml(html) {
  const styles = []
  const stylePattern = /<style[^>]*>([\s\S]*?)<\/style>/gi
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = stylePattern.exec(html)) !== null) {
    if (match[1]?.trim()) styles.push(match[1].trim())
  }
  return styles.join('\n')
}

function stripEditorMarkers(html) {
  if (!html) return ''
  let result = html
  Object.values(MARKER_TAGS).forEach(([start, end]) => {
    result = result.split(start).join('')
    result = result.split(end).join('')
  })
  result = result.replace(PAGE_BREAK_MARKER, '')
  SECTION_PAGE_BREAK_MARKERS.forEach(marker => {
    result = result.replace(marker, '')
  })
  return result
}

function convertPageBreaksForPreview(html) {
  if (!html) return ''
  let result = html
  // Global replace — templates have multiple forced breaks (policies / terms / stamp).
  const replaceAll = (source, find, replacement) => {
    if (!find) return source
    if (typeof find === 'string') {
      return source.split(find).join(replacement)
    }
    const flags = find.flags.includes('g') ? find.flags : `${find.flags}g`
    return source.replace(new RegExp(find.source, flags), replacement)
  }
  result = replaceAll(result, PAGE_BREAK_MARKER, PREVIEW_PAGE_BREAK_VISUAL)
  SECTION_PAGE_BREAK_MARKERS.forEach(marker => {
    result = replaceAll(result, marker, PREVIEW_PAGE_BREAK_VISUAL)
  })
  result = replaceAll(result, OFFER_MANUAL_PAGE_BREAK_MARKER, PREVIEW_PAGE_BREAK_VISUAL)
  // Only real page-break units → preview split markers. Skip standalone top-clear
  // (spacer); converting it with a greedy inner match deleted Employment Policies.
  PAGE_BREAK_PATTERNS.forEach(pattern => {
    if (pattern === OFFER_PAGE_TOP_CLEAR_EMPTY_RE) return
    result = replaceAll(result, pattern, PREVIEW_PAGE_BREAK_VISUAL)
  })
  // Attribute-order / single-quote leftovers that PAGE_BREAK_PATTERNS miss.
  result = result.replace(
    /<div\b[^>]*\boffer-page-break\b[^>]*>\s*<\/div>/gi,
    PREVIEW_PAGE_BREAK_VISUAL,
  )
  // Leftover force-break spacers must not sit BETWEEN preview splits (blank card).
  result = result.replace(
    /<div[^>]*class="[^"]*\boffer-pdf-force-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    PREVIEW_PAGE_BREAK_VISUAL,
  )
  // Drop empty letterhead spacers (not page splits).
  result = result.replace(new RegExp(OFFER_PAGE_TOP_CLEAR_EMPTY_RE.source, 'gi'), '')
  // Print-flow chunks: after-break class and/or inline page-break-before.
  // Marker-only rebuilds keep the comment; after-break tables still split preview cards.
  result = result.replace(
    /<table\b[^>]*class="[^"]*\boffer-print-flow-after-break\b[^"]*"[^>]*>/gi,
    (tag) => `${PREVIEW_PAGE_BREAK_VISUAL}\n${tag}`,
  )
  result = result.replace(
    /<table\b(?![^>]*\boffer-print-flow-after-break\b)[^>]*style="[^"]*(?:page-break-before\s*:\s*always|break-before\s*:\s*page)[^"]*"[^>]*>/gi,
    (tag) => `${PREVIEW_PAGE_BREAK_VISUAL}\n${tag}`,
  )
  result = ensurePreviewSplitBeforeNewPageSections(result)
  // Collapse back-to-back preview breaks (marker + table both emitted a split).
  result = result.replace(
    /(?:<div class="offer-preview-page-break"[^>]*>\s*<\/div>\s*){2,}/gi,
    `${PREVIEW_PAGE_BREAK_VISUAL}\n`,
  )
  return result
}

/**
 * Screen preview hides print-only `offer-page-break` (height:0). If a
 * New page / New page + title unit sits immediately before a marked section
 * or its title but was not turned into a split marker, force one so the
 * live preview shows a new A4 card (generate still uses the CSS break).
 */
function ensurePreviewSplitBeforeNewPageSections(html) {
  if (!html) return html
  let source = String(html)
  const titleRe = /<(?:p|h[1-6])[^>]*>[\s\S]{0,120}?(?:Employment\s+Policies\s+(?:and|&)\s+Provisions|Job\s+Responsibilities|Terms\s+and\s+Conditions(?:\s+of\s+Employment)?|Annexure\s+A)/gi
  const anchors = [
    source.indexOf(MARKER_TAGS.body[0]),
    source.indexOf(MARKER_TAGS.jobResponsibilities[0]),
    source.indexOf(MARKER_TAGS.terms[0]),
    source.indexOf(MARKER_TAGS.salary[0]),
  ]
  let titleMatch = titleRe.exec(source)
  while (titleMatch) {
    if (titleMatch.index != null) anchors.push(titleMatch.index)
    titleMatch = titleRe.exec(source)
  }
  const points = anchors.filter(i => i >= 0).sort((a, b) => b - a)
  points.forEach((insertAt) => {
    const windowStart = Math.max(0, insertAt - 480)
    const before = source.slice(windowStart, insertAt)
    if (/offer-preview-page-break/i.test(before)) return
    // Only split when a real forced-break unit is still sitting before the section.
    // Do not infer from stamp adjacency — "Continue on same page" is valid.
    if (!/offer-page-break|OFFER_[A-Z_]*PAGE_BREAK/i.test(before)) return
    source = `${source.slice(0, insertAt)}${PREVIEW_PAGE_BREAK_VISUAL}\n${source.slice(insertAt)}`
  })
  return source
}

function injectPreviewStyles(html, templateStyles = '') {
  const combinedStyles = [templateStyles, PREVIEW_BASE_STYLES].filter(Boolean).join('\n')
  const styleBlock = `<style>\n${combinedStyles}\n</style>`

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${styleBlock}\n</head>`)
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, match => `${match}\n${styleBlock}`)
  }
  if (/<html[\s>]/i.test(html)) {
    return html.replace(/<html[^>]*>/i, match => `${match}\n<head>\n${styleBlock}\n</head>`)
  }
  return wrapPreviewDocument(html, templateStyles)
}

function wrapPreviewDocument(bodyContent, templateStyles = '') {
  const combinedStyles = [templateStyles, PREVIEW_BASE_STYLES].filter(Boolean).join('\n')
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Offer Letter Preview</title>
  <style>
${combinedStyles}
  </style>
</head>
<body>
${bodyContent}
</body>
</html>`
}

function extractBodyContent(html) {
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  if (bodyMatch) return bodyMatch[1]
  return html
}

/**
 * Strip executable content from offer preview HTML so sandboxed srcdoc
 * iframes (no allow-scripts) do not log blocked-script console errors.
 * Page-wise preview (paginateOfferPreviewDocument) is HTML/CSS-only — no scripts needed.
 */
export function stripScriptsFromPreviewHtml(html) {
  if (!html || typeof html !== 'string') return ''
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/?>/gi, '')
    // External classic scripts / module preload that browsers may try to run
    .replace(/<link\b[^>]*\brel\s*=\s*(["'])(?:modulepreload|preload|importmap)\1[^>]*>/gi, '')
    .replace(/<link\b[^>]*\bas\s*=\s*(["'])script\1[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\b(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1=$2#$2')
}

/**
 * Fill placeholders with sample values for preview.
 * @param {string} html
 * @param {{ jobTitle?: string, location?: string, images?: object, mode?: 'screen'|'pdf' }} [options]
 *   mode 'screen' (default): page-wise A4 cards for the live preview iframe.
 *   mode 'pdf': keep real page-breaks + section/placement markers so PDF chrome
 *   can still honor First page / After T&C / employee-line settings.
 */
export function buildPreviewHtml(html, { jobTitle, location, images, mode = 'screen' } = {}) {
  if (!html) return ''

  const sampleImage = 'data:image/svg+xml,'
    + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60"><rect width="200" height="60" fill="#ebe9f1"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6e6b7b" font-size="12">Image</text></svg>')

  const replacements = {
    '{{CANDIDATE.NAME_ONLY}}': 'John Doe',
    '{{CANDIDATE.NAME}}': 'John Doe',
    '{{OFFER_DETAILS.OFFERED_DATE_FORMATTED}}': 'January 15, 2026',
    '{joiningDate}': 'February 1, 2026',
    '{reportingManager}': 'Jane Smith',
    '{jobTitle}': jobTitle || 'Field Engineer',
    '{locations}': location || 'Mumbai, India',
    '{{METADATA.IMAGES.HEADER}}': resolveOfferHeaderBannerSrc(images?.header),
    '{{METADATA.IMAGES.LOGO}}': images?.logo || sampleImage,
    '{{METADATA.IMAGES.WATERMARK}}': images?.watermark || '',
    // No sampleImage for stamp — a placeholder made Configure preview look stamped
    // when metadata/form had no signature, while generate correctly showed none.
    '{{METADATA.IMAGES.SIGNATURE}}': images?.signature || '',
    '{{CONFIG.SALARY_STRUCTURE.EN.NOTE}}': 'Professional TAX will be deducted as per the government regulations of the relevant States.',
    '{{CONFIG.SALARY_STRUCTURE.EN.FIVE_METER_INSTALLATION.HEADER}}': 'Salary Structure for Minimum 5 Meters installed per day',
    '{{CONFIG.SALARY_STRUCTURE.EN.LESS_THAN_FIVE_METER_INSTALLATION.HEADER}}': 'Salary Structure for Less than 5 Meters installed per day',
    '{{CONFIG.SALARY_STRUCTURE.EN.PPE_KIT}}': 'PPE Deduction will happen only for first two months that will be 600/- per month.',
    '{{CONFIG.SALARY_STRUCTURE.EN.ADDITIONAL_METERS_INCENTIVE}}': 'Note: If any additional meters are installed (more than 5 meters per day), you will get ₹130 per meter as incentive.',
    '{{CONFIG.SALARY_STRUCTURE.FIVE_METER_INSTALLATION.COMPONENTS.IN_HAND_PAY_FORMATTED}}': '13,000',
    '{{CONFIG.SALARY_STRUCTURE.FIVE_METER_INSTALLATION.COMPONENTS.SPECIAL_ALLOWANCE_FORMATTED}}': '2,000',
    '{{CONFIG.SALARY_STRUCTURE.FIVE_METER_INSTALLATION.COMPONENTS.TOTAL_FORMATTED}}': '15,000',
    '{{CONFIG.SALARY_STRUCTURE.LESS_THAN_FIVE_METER_INSTALLATION.COMPONENTS.IN_HAND_PAY_FORMATTED}}': '13,000',
    '{{CONFIG.SALARY_STRUCTURE.LESS_THAN_FIVE_METER_INSTALLATION.COMPONENTS.TOTAL_FORMATTED}}': '13,000',
  }

  let result = html
  Object.entries(replacements).forEach(([token, value]) => {
    result = result.split(token).join(value || '')
  })

  const forPdf = mode === 'pdf'
  const templateStyles = extractStylesFromHtml(result)

  if (forPdf) {
    // Keep CSS page-breaks + OFFER_* markers so generatePDFFromHTML chrome can
    // re-apply First page / After T&C / employee-line settings correctly.
    result = stripScriptsFromPreviewHtml(result)
    if (isFullHtmlDocument(result)) return result
    return wrapPreviewDocument(extractBodyContent(result), templateStyles)
  }

  // Unwrap print-flow tables so section page-break markers sit in flat `.content`
  // HTML that convertPageBreaksForPreview can turn into preview split points.
  result = flattenOfferContentPrintFlow(result)
  result = repairSectionPageBreakDivs(result)
  // Relocate *before* converting breaks. After convert, findSectionUnit no longer
  // sees OFFER_POLICIES_PAGE_BREAK, so a later relocate left the split outside
  // `.content` and page-wise preview kept signatures + Policies on one card.
  result = relocateMarkedSectionsIntoContent(result)
  result = convertPageBreaksForPreview(result)
  result = stripEditorMarkers(result)
  result = stripScriptsFromPreviewHtml(result)

  if (!isFullHtmlDocument(result)) {
    result = wrapPreviewDocument(extractBodyContent(result), templateStyles)
  } else {
    result = injectPreviewStyles(result, templateStyles)
  }
  result = paginateOfferPreviewDocument(result)
  return stripScriptsFromPreviewHtml(result)
}

